// @ts-nocheck
import { createContext, useContext, useState, type ReactNode } from "react";

export type Lang = "es" | "en" | "fr" | "ar" | "zh";

interface Translations {
  [key: string]: string | Translations | string[];
}

/* ─────────────────────────── SPANISH (PRIMARY) ─────────────────────────── */
const ES: Translations = {
  header: {
    firm: "Global Mobility", help: "¿Necesita ayuda?", saved: "Guardado ✓", saving: "Guardando…",
    close: "Cerrar",
    deleteTitle: "¿Estás seguro?",
    deleteBody1: "Se eliminarán todos tus datos personales, documentos y el historial de tu caso. Esta acción no se puede deshacer.",
    deleteBody2: "Tu cuenta se desactivará inmediatamente y tus datos se eliminarán en un plazo de 15 días.",
    deleteBody3: "Si cambias de opinión, contacta a hola@globalmobility.law para reactivar tu cuenta antes de que se complete la eliminación.",
    deleteConfirm: "Sí, eliminar mi cuenta",
    deleteCountdown: "Sí, eliminar mi cuenta ({{n}})",
    cancel: "Cancelar",
    addFamily: "Añadir familiar",
    addFamilySoon: "Próximamente — contacta a tu abogada por WhatsApp para añadir familiares.",
    privacy: "Política de Privacidad",
    logout: "Cerrar sesión",
    deleteAccount: "Eliminar mi cuenta",
  },
  exitModal: {
    title: "¡Tu progreso está guardado!",
    body: "Puedes volver en cualquier momento y continuar donde lo dejaste.",
    stay: "Continuar con mi solicitud",
    leave: "Salir",
  },
  progress: {
    of: "de",
    steps: ["Confirmar datos", "Tu situación", "Dirección", "Revisión y envío"],
    applyingFor: "Solicitando para",
  },
  help: {
    title: "¿Necesita ayuda?",
    placeholder: "Describe tu pregunta o problema…",
    helper: "Te responderemos por mensaje o correo.",
    send: "Enviar mensaje",
    sent: "¡Mensaje enviado! Te responderemos pronto.",
    section: "Sección actual",
  },
  nav: {
    back: "Atrás",
    continue: "Continuar",
    submit: "Continuar",
    backToOrganizer: "Volver a Mi Organizador",
    breadcrumbOrganizer: "Organizador",
    breadcrumbDocuments: "Documentos",
  },
  footer: {
    privacyPolicy: "Política de Privacidad",
    termsOfService: "Términos de Servicio",
    cookiePolicy: "Política de Cookies",
    legalNotice: "Aviso Legal",
    copyright: "© 2026 Global Mobility Group LLC",
  },
  documentScanner: {
    title: "Escanear documento",
    desktopOnly: "Para escanear varias páginas y obtener un solo PDF, abre esta pantalla desde el navegador de tu teléfono.",
    scannerBtn: "Escanear",
    addPages: "Añadir páginas",
    addAnotherPage: "Añadir otra página",
    fromGallery: "Desde galería",
    buildPdf: "Crear PDF",
    processing: "Procesando…",
    scanMergeSaving: "Guardando tu escaneo…",
    connectionProblemTitle: "Problema de conexión",
    connectionProblemBody:
      "No podemos conectar con nuestro servidor, probablemente por una señal móvil débil. Si cancelas ahora, perderás los escaneos.",
    connectionProblemRetry: "Intentar de nuevo",
    connectionProblemCancel: "Cancelar y perder escaneos",
    devicePdfFailureKicker: "Generación de PDF",
    devicePdfFailureTitle: "No pudimos crear tu PDF",
    devicePdfFailureBody:
      "Algo salió mal al generar el archivo en tu dispositivo. Tus fotos siguen aquí. Intenta de nuevo, o cierra otras pestañas del navegador si tu teléfono tiene poca memoria. Nuestro equipo ha recibido una notificación automática.",
    devicePdfFailureRetry: "Intentar de nuevo",
    devicePdfFailureCancel: "Cancelar",
    pageLabel: "Página {{n}}",
    remove: "Quitar",
    moveUp: "Subir",
    moveDown: "Bajar",
    close: "Cerrar",
    needOnePage: "Añade al menos una foto de página.",
    errorGeneric: "No se pudieron procesar las imágenes. Prueba de nuevo con menos fotos o más nitidez.",
    multiPageHint: "Captura varias páginas y las unimos en un solo PDF tal como las tomaste.",
    cameraTitle: "Escanear",
    cameraLoadingLibs: "Cargando escáner…",
    cameraStarting: "Abriendo la cámara…",
    cameraTapToCapture: "Enfoca el documento y pulsa el botón blanco de abajo para capturar.",
    cameraVideoNotReady: "La vista previa de la cámara no está lista. Cierra e inténtalo de nuevo.",
    cameraAlignHint: "Coloca el documento dentro del marco verde",
    cameraAlignSubtitle:
      "Encuadre ceñido: el documento llena el marco verde con poco margen. Sin dedos en foto; solo un ligero doblez.",
    cameraAlignSubtitleSpread:
      "Modo varias páginas: encaja el documento dentro del marco verde con un margen fino y uniforme.",
    cameraFrameModeAria: "Forma de la guía de encuadre",
    cameraFrameModeSingle: "Una página",
    cameraFrameModeSpread: "Abierta",
    cameraDocDetected: "Documento detectado — toca para capturar",
    cameraPermissionDenied: "Acceso a la cámara denegado. Concede permiso en los ajustes del navegador.",
    cameraStreamEnded:
      "La cámara se detuvo (por ejemplo, si la pantalla estuvo en reposo o la pestaña en segundo plano). Pulsa Reintentar.",
    cameraPlayBlocked: "No se pudo reanudar la vista previa. Pulsa Reintentar.",
    cameraRetryButton: "Reintentar",
    cameraNotFound: "No se encontró cámara en este dispositivo.",
    cameraInitError: "No se pudo iniciar la cámara.",
    captureError: "No se pudo capturar la página.",
    cameraShutter: "Capturar página",
    cameraPageCaptured: "Página guardada. Puedes hacer otra foto o pulsar Cerrar.",
    flattenedBadge: "Recortada",
    flattenedBadgeHint: "Recortada y enderezada automáticamente en este dispositivo.",
    previewThumbnailAria: "Ampliar vista previa",
    previewHint: "Comprueba que la foto sea nítida y muestre la página completa.",
    previewRotate: "Girar 90°",
    previewRotateAria: "Girar esta página 90 grados en el sentido de las agujas del reloj",
    rotateError: "No se pudo girar la imagen. Prueba con otra foto o desde la galería.",
    previewRemovePage: "Eliminar esta página",
    cornerAdjust: "Ajustar esquinas y perspectiva",
    cornerAdjustAria: "Abrir ajuste de esquinas para enderezar y recortar",
    cornerEditorTitle: "Enderezar y recortar",
    cornerEditorHint:
      "Arrastra cada esquina hasta los bordes del documento. Enderezamos la página y reducimos el fondo vacío.",
    cornerEditorApply: "Aplicar",
    cornerEditorReset: "Restablecer esquinas",
    cornerEditorCancel: "Volver",
    cornerEditorBusy: "Procesando…",
    cornerEditorError: "No se pudo enderezar la imagen. Prueba otras esquinas o restablece.",
    cornerEditorInvalidQuad: "Las esquinas deben formar un cuadrilátero válido y ordenado dentro de la foto.",
    cornerHandleAria: "Esquina",
    cornerEditorApplyContinue: "Aplicar y continuar",
    cornerEditorDone: "Listo",
    cornerEditorAddPage: "+ Añadir página",
    cornerEditorResetHeader: "Restablecer",
    passportCornerDragHint:
      "Arrastra las esquinas para alinearlas con los bordes del pasaporte.",
    passportCornerRetake: "Repetir",
    passportIntroTitle: "Hagamos una foto de la primera página",
    passportIntroHelp:
      "Abre el pasaporte en la primera página. Si hace falta, usa un objeto puntiagudo para mantenerlo abierto y haz una foto de las dos páginas abiertas. No uses los dedos.",
    passportTakePhoto: "Hacer foto",
    passportContinue: "Continuar",
    passportSubmitPhotos: "Enviar foto(s)",
    passportRetakePhoto: "Repetir foto",
    passportReviewPrev: "Página anterior",
    passportReviewNext: "Página siguiente",
    passportReviewPageStatus: "{{current}} / {{total}}",
    passportPagesCapturedSummary: "{{n}} página(s) capturada(s).",
    passportSheetReviewHint:
      "Tras cada foto ajustarás las esquinas; luego puedes hacer otra. Cuando hayas terminado todas, pulsa «Revisar / ajustar» y abajo «Crear PDF».",
    passportReviewAdjust: "Revisar / ajustar",
    passportSheetMinimalHint:
      "Las fotos siguen aquí. Pulsa «Crear PDF», o Cancelar para volver y elegir subir archivo o escanear de nuevo.",
    passportPreviewReviewHint:
      "Pulsa «Editar» para enderezar y recortar. Si la foto falló o está girada, «Repetir» arriba a la izquierda en esa pantalla. Pulsa «?» arriba a la derecha para ver ejemplos de foto. Arrastra miniaturas para ordenar. «Crear PDF» al terminar.",
    reviewScreenTitle: "Revisar",
    reviewPageOf: "Página {{current}} de {{total}}",
    reviewPagesSectionLabel: "PÁGINAS",
    reviewReorderHint:
      "Desliza la fila para ver todas las miniaturas. Toca y arrastra el icono ⠿ de una miniatura para cambiar el orden.",
    reviewReorderHoldA11y: "Mantén pulsado el icono para reordenar páginas.",
    reviewInsertPage: "Añadir página",
    reviewEdit: "Editar",
    passportReviewView: "Ver",
    reviewDeletePageAria: "Eliminar página",
    reviewEditMenuTitle: "Acciones",
    reviewEditRemove: "Quitar página",
    reviewDeleteConfirmTitle: "¿Eliminar esta página?",
    reviewDeleteConfirmBody:
      "Esta página se quitará del escaneo. Podrás volver a capturarla después.",
    reviewDeleteConfirmDelete: "Eliminar",
    reviewDeleteConfirmCancel: "Cancelar",
    reviewLeaveConfirmTitle: "¿Descartar las fotos?",
    reviewLeaveConfirmBody: "Perderás todas las páginas que has capturado.",
    reviewLeaveConfirmStay: "Seguir escaneando",
    reviewLeaveConfirmDiscard: "Descartar fotos",
    passportLowPageWarnTitle: "¿Están todas las páginas incluidas?",
    passportLowPageWarnBody:
      "Normalmente necesitamos fotos de cada página de tu pasaporte, incluidas las en blanco. Parece que podrían faltar algunas páginas.",
    passportLowPageWarnReview: "Revisar páginas",
    passportLowPageWarnAnyway: "Crear PDF igualmente",
    passportCameraHeader: "Pasaporte",
    passportCameraHintNext: "Encaja las páginas abiertas en el marco verde y haz otra foto cuando estés listo/a.",
    passportCaptureToast: "Foto guardada — revisa el recorte.",
    passportCancel: "Cancelar",
    passportPageTitle: "PÁGINA {{n}}",
    passportViewfinderPill:
      "Abre el pasaporte plano y captura ambas páginas.",
    passportViewfinderPillSubsequent:
      "Captura la siguiente página abierta en una sola foto.",
    passportCaptureFooterHint:
      "Mantén las páginas planas sobre una superficie. Podrás ajustar los bordes en el siguiente paso.",
    passportExamplesAria: "Ejemplos de foto",
    passportViewExamplesLink: "Ver ejemplos de foto",
    passportCameraHelpAria: "Ayuda para capturar el pasaporte",
    passportFlashAria: "Activar o desactivar flash",
    passportHelpGotIt: "Entendido",
  },
  help: {
    empadronamientoTerm: "empadronamiento",
    empadronamiento: "Es tu certificado de registro municipal. Acredita que vives en un municipio de España. Se solicita en tu ayuntamiento, normalmente con cita previa. Pide el certificado histórico — muestra todas tus direcciones y fechas.",
    vidaLaboralTerm: "vida laboral",
    vidaLaboral: "Es un documento oficial de la Seguridad Social que muestra todo tu historial de empleo en España. Es gratuito y se puede obtener en sede.seg-social.gob.es, por SMS al 600 123 456, o en persona en una oficina de la Seguridad Social.",
    altaAutonomoTerm: "alta de autónomo",
    altaAutonomo: "Es el documento que acredita tu registro como trabajador por cuenta propia en España. Se obtiene a través de la Agencia Tributaria y muestra la fecha exacta en que empezaste tu actividad.",
    modelo130Term: "modelo 130/303",
    modelo130: "Son las declaraciones trimestrales de impuestos para autónomos. El modelo 130 es para el IRPF y el 303 para el IVA. Se descargan desde la sede electrónica de la Agencia Tributaria.",
    libroFamiliaTerm: "libro de familia",
    libroFamilia: "Es un documento oficial expedido por el Registro Civil que acredita el matrimonio y los hijos. Si te casaste o tuviste hijos en otro país, necesitarás el equivalente apostillado y traducido.",
    apostilladosTerm: "apostillados",
    apostillados: "La apostilla es un sello oficial que valida documentos extranjeros para uso internacional, según el Convenio de La Haya. Se obtiene en el ministerio o autoridad competente del país emisor del documento. Si tu país no está en el Convenio de La Haya, necesitas legalización consular en su lugar.",
    tarjetaRojaTerm: "tarjeta roja",
    tarjetaRoja: "Es el documento que acredita que has solicitado protección internacional (asilo) en España. Se renueva cada 6 meses mientras tu solicitud está en trámite. La tarjeta blanca es similar pero para renovaciones.",
    declaracionJuradaTerm: "declaración jurada",
    declaracionJurada: "Es una declaración firmada ante notario en la que afirmas no tener antecedentes penales. Solo se acepta si no puedes obtener el certificado oficial por razones justificadas (guerra, falta de relaciones diplomáticas, etc.).",
  },
  validation: {
    required: "Este campo es obligatorio.",
    invalidDate: "Fecha inválida.",
    invalidEmail: "Dirección de correo electrónico inválida.",
    invalidPhone: "Número de teléfono inválido.",
    invalidNie: "Introduce un NIE válido.",
    invalidPostal: "Código postal inválido (5 dígitos).",
    postalProvinceMismatch: "Las dos primeras cifras del código postal no coinciden con la provincia.",
    invalidAddressStreet:
      "Calle: 1–100 caracteres; solo letras, números, espacios, tildes, puntos y comas.",
    invalidAddressStreetNumber: "Número: 1–10 caracteres; letras, números o S/N (con /).",
    invalidAddressFloorDoor: "Puerta: como máximo 10 caracteres; solo letras y números.",
    invalidAddressCity: "Ciudad: 1–40 caracteres; letras, espacios, guiones y tildes.",
    minLength: "Demasiado corto.",
    invalidMonth: "El mes debe estar entre 01 y 12.",
    invalidDay: "Este día no es válido para ese mes.",
    futureDate: "La fecha no puede ser posterior a hoy.",
    pastExpiryDate: "La caducidad debe ser hoy o una fecha futura.",
    yearOutOfRange: "El año no está en el rango permitido.",
    futureMonthYear: "La fecha no puede ser posterior al mes actual.",
    invalidYear: "El año no es válido.",
  },
  deadline: { label: "Solicitudes hasta el", date: "30 de junio de 2026" },
  optional: "Opcional",
  returning: {
    greeting: "¡Hola,",
    progress: "Progreso de tu solicitud",
    continue: "Continuar donde lo dejaste",
    restart: "Revisar desde el principio",
  },
  landing: {
    greeting: "¡Hola",
    firstVisitMessage: "Empecemos con tu solicitud. Solo toma unos 10 minutos.",
    firstVisitMessageWithAdvisor: "Te ayudaré con tu solicitud. Solo toma unos 10 minutos.",
    thisIs: "soy",
    autoSave: "Puedes salir y volver cuando quieras. Tu trabajo se guarda automáticamente.",
    advisor: "Tu asesor/a:",
    getStarted: "Empezar",
    continue: "Continuar",
    reviewFromStart: "Revisar desde el principio",
    progress: {
      moreThan3: "Vas muy bien — sigamos.",
      exactly3: "Vas muy bien — solo faltan 3 secciones.",
      exactly2: "Ya casi — solo faltan 2 secciones.",
      exactly1: "Ya casi — solo falta 1 sección.",
      allDone: "Todo parece completo — revisemos y terminemos.",
    },
    procedureBadge: "Regularización Extraordinaria España 2026",
    videoLength: "1 min · Subtítulos disponibles",
    skipVideo: "Saltar video",
    familyMenuHint: "Puedes añadir familiares en cualquier momento desde el menú ☰",
    purpose: "Te guiaré en todo lo que necesitas para tu solicitud de residencia. Hemos organizado todo en secciones sencillas, solo avanza a tu ritmo y sube tus documentos en el camino. Yo me encargo del resto. No te preocupes si no tienes todo ahora, tus respuestas se guardan automáticamente, así que puedes volver en cualquier momento y continuar justo donde lo dejaste.",
    purposeShort: "Te guiaré en todo lo que necesitas para tu solicitud de residencia. Tus respuestas se guardan automáticamente, así que puedes volver cuando quieras.",
    purposeExpanded: "Hemos organizado todo en secciones sencillas. Solo avanza a tu ritmo y sube tus documentos en el camino. Yo me encargo del resto. Es posible que no tengas todos tus documentos listos todavía, y eso está perfectamente bien. Muchas personas necesitan solicitar documentos como antecedentes penales en su consulado o un padrón en su ayuntamiento, lo cual puede llevar tiempo. Solo completa lo que puedas ahora y vuelve cuando tengas más. No se pierde nada, puedes continuar justo donde lo dejaste.",
    more: "Más",
    less: "Menos",
    familyInfo: "Si vas a solicitar como familia, cada persona mayor de 18 años necesita su propia solicitud. Los menores se incluyen en la solicitud del padre o tutor legal. Puedes añadir familiares en cualquier momento desde tu perfil en la esquina superior derecha.",
    familyInterestText: "¿Solicitas con familiares? Avísanos y continúa con tu solicitud. Te contactaremos para la parte familiar.",
    familyInterestBtn: "Me interesa una solicitud familiar",
    familyInterestConfirm: "Recibido, te contactaremos",
    familyMenuHintBold: "",
    defaultAdvisorName: "Ana Travieso",
    followInstagram: "@anatraviesoabogada",
    consentIntro: "Antes de empezar, necesito tu permiso para trabajar con tu información.",
    consentLabel: "Acepto la",
    consentPrivacyLink: "Política de Privacidad",
    consentSubtext: "(Es un requisito legal — tus datos están seguros con nosotros.)",
  },
  section1: {
    title: "Confirma tus datos",
    title_rep: "Confirmar los datos del solicitante",
    subtitle_applicant: "Por favor, revisa que tu información es correcta antes de continuar.",
    subtitle_rep: "Por favor, revisa que la información del solicitante es correcta.",
    isCorrect: "¿Es correcto?",
    confirmBtn: "Sí, continuar",
    editLabel: "Editar",
    firstName: "Nombre", firstNamePlaceholder: "María",
    lastName: "Primer apellido", lastNamePlaceholder: "García",
    segundoApellido: "Segundo apellido", segundoApellidoPlaceholder: "López",
    segundoApellidoHelper: "Opcional si no aparece en el documento.",
    email: "Correo electrónico", emailPlaceholder: "email@ejemplo.com",
    phone: "Número de móvil", phonePlaceholder: "600 000 000",
    voiceTitle: "Empecemos con lo básico.",
    voiceTitle_rep: "Empecemos con lo básico del solicitante.",
    fillingForSelf: "¿Estás completando esto para ti?",
    forMyself: "Sí, para mí",
    forSomeoneElse: "No, para otra persona",
    repNote: "Perfecto. Primero confirmamos tus datos, y luego los de la persona que estás ayudando.",
    repRelationship: "Tu relación con el solicitante",
    repRelationshipPlaceholder: "Familiar, amigo, trabajador social...",
    repRelSelectPlaceholder: "Selecciona tu relación",
    repRelFamily: "Familiar",
    repRelSpouse: "Cónyuge / Pareja",
    repRelFriend: "Amigo/a",
    repRelSocialWorker: "Trabajador/a social",
    repRelLawyer: "Abogado/a",
    repRelNGO: "ONG / Asociación",
    repRelOther: "Otro",
    applicantTitle: "Datos del solicitante",
    applicantFirstName: "Nombre del solicitante",
    applicantFirstNamePlaceholder: "Nombre",
    applicantLastName: "Primer apellido del solicitante",
    applicantLastNamePlaceholder: "Primer apellido",
    applicantSegundoApellido: "Segundo apellido",
    applicantSegundoApellidoPlaceholder: "Segundo apellido",
    applicantEmail: "Email del solicitante",
    applicantEmailPlaceholder: "Si tiene uno",
    applicantPhone: "Teléfono del solicitante",
    applicantPhonePlaceholder: "Si tiene uno",
    repSectionLabel: "Tus datos",
    sendPhoneOtp: "Enviar código",
    verifyPhoneOtp: "Verificar",
    otpCodePlaceholder: "6 dígitos",
    phoneVerifiedBadge: "Verificado",
    phoneOtpHelper:
      "Te enviaremos un código por SMS (como en el registro) para confirmar este número y guardarlo en tu perfil.",
    phoneOtpSendError: "No se pudo enviar el código. Inténtalo de nuevo.",
    phoneOtpCodeHint: "Introduce el código que te hemos enviado.",
    phoneOtpSixDigitsHint: "Introduce los 6 dígitos del código.",
    phoneAlreadyRegistered:
      "Este número ya está registrado en otra cuenta. Prueba con otro móvil.",
  },
  section2: {
    title: "Información personal",
    title_rep: "Información personal del solicitante",
    subtitle_applicant: "Completa tus datos personales tal como aparecen en tu documento oficial.",
    subtitle_rep: "Completa los datos personales del solicitante.",
    dob: "Fecha de nacimiento", dobHelper: "DD / MM / AAAA",
    sex: "Sexo",
    sexOptions: { male: "Hombre", female: "Mujer", undetermined: "Indeterminado" },
    nationality: "Nacionalidad", nationalityPlaceholder: "Buscar nacionalidad…",
    countryOfBirth: "País de nacimiento", countryOfBirthPlaceholder: "Buscar país…",
    placeOfBirth: "Lugar de nacimiento", placeOfBirthPlaceholder: "Ej. Casablanca",
    placeOfBirthHelper: "Ciudad o localidad donde naciste.",
    voiceTitle: "Ahora, algunos datos personales tal como aparecen en tu documento.",
    voiceTitle_rep: "Ahora, algunos datos personales tal como aparecen en el documento del solicitante.",
  },
  section2e: {
    stepLabel: "Tu situación",
    voiceTitle: "Cuéntanos sobre tu situación para que podamos orientar tu solicitud.",
    asylumRoutingTitle: "¿Has solicitado asilo en España?",
    asylumRoutingSubtitle: "Nos referimos a la protección internacional, presentada ante la policía española antes del 1 de enero de 2026.",
    asylumRoutingYesTitle: "Sí",
    asylumRoutingYesDescription: "He solicitado asilo en España.",
    asylumRoutingNoTitle: "No",
    asylumRoutingNoDescription: "No he solicitado asilo en España.",
    qualifyingTitle: "¿Cuál describe mejor tu situación actual?",
    qualifyingSubtitle: "Elige la opción que mejor describe tu situación.",
    qualifyingWorkTitle: "Por trabajo",
    qualifyingWorkDescription: "Trabajo en España, tengo una oferta de empleo o quiero trabajar por mi cuenta.",
    qualifyingFamilyTitle: "Por familia",
    qualifyingFamilyDescription: "Vivo con mis hijos o con mis padres en España.",
    qualifyingVulnerabilityTitle: "Por una situación de vulnerabilidad",
    qualifyingVulnerabilityDescription: "Puedo conseguir un certificado de servicios sociales o de una organización de apoyo.",
    yes: "Sí",
    no: "No",
    arrivalDate: "¿Cuándo llegaste a España?",
    arrivalDateHelper: "Fecha aproximada de llegada. No necesita ser exacta.",
    submissionFixed: "Tu solicitud puede presentarse a partir del 1 de abril de 2026.",
    submissionFrom: "Tu solicitud puede presentarse a partir del {{date}}.",
    hardStopArrival: "La regularización extraordinaria requiere presencia en España antes del 1 de enero de 2026. Pero no te preocupes, puede haber otras opciones para ti.",
    hardStopTooLate: "Con esta fecha de llegada, los 5 meses requeridos caerían después del plazo del 30 de junio de 2026. Pero puede haber otros caminos para ti.",
    validPermit: "¿Tienes actualmente un permiso de residencia válido en España?",
    hardStopPermit: "Si ya tienes un permiso de residencia válido, probablemente necesites una renovación o modificación. Puedo ayudarte con eso.",
    asylum: "¿Solicitaste asilo o protección internacional en España antes del 1 de enero de 2026?",
    helpAsylum: "Revisa tu resguardo de solicitud o tarjeta roja: la fecha indica cuándo solicitaste.",
    oarLabel: "Número de expediente de asilo (OAR)",
    oarPlaceholder: "Ej. OAR/2024/12345",
    oarHelper: "Lo encontrarás en el resguardo o en la tarjeta roja. Si no lo tienes ahora, podrás añadirlo más tarde.",
    subRouteLabel: "¿Cuál describe mejor tu situación?",
    subRouteWork: "Tengo trabajo u oferta de empleo",
    subRouteFamily: "Vivo con mi familia en España",
    subRouteVulnerability: "Estoy en una situación de vulnerabilidad",
    subRouteWorkDesc: "He trabajado en España, trabajo ahora o tengo una oferta de empleo.",
    subRouteFamilyDesc: "Vivo en España con mi pareja, hijos u otros familiares.",
    subRouteVulnerabilityDesc: "Estoy en una situación difícil y necesito apoyo de servicios sociales.",
    workHistoryLabel: "¿Qué describe mejor tu situación laboral?",
    workHistoryWorked: "He trabajado antes en España",
    workHistoryCurrent: "Tengo un trabajo actualmente",
    workHistoryOffer: "Tengo una oferta de trabajo",
    workTypeLabel: "¿Por cuenta ajena o propia?",
    workTypeEmployed: "Por cuenta ajena",
    workTypeSelfEmployed: "Por cuenta propia",
    selfEmploymentActivityLabel: "Describe tu actividad",
    selfEmploymentActivityPlaceholder: "ej. Repartidor, cocinera, diseñadora gráfica",
    selfEmploymentActivityHint: "Necesario para la declaración de intención de actividad por cuenta propia del EX-32.",
    criminalRecord: "¿Tienes antecedentes penales en los últimos cinco años?",
    helpCriminal:
      "Incluye condenas o procedimientos penales en curso en cualquier país durante los últimos cinco años, no solo en España. Si respondes «No», te pediremos el certificado de antecedentes en el flujo de documentos.",
    previousApplication: "¿Has tenido una solicitud migratoria anterior en España?",
    helpPreviousApplication:
      "Cuenta permisos de residencia, arraigos u otros trámites de extranjería presentados en España, aunque fueran denegados o archivados.",
    callbackBtn: "Quiero explorar opciones",
    callbackConfirm: "✓ Te contactaremos pronto",
    hasPassport: "¿Tienes pasaporte válido?",
    hasPassportHelp: "Un pasaporte de tu país de origen. No importa si está caducado o próximo a caducar.",
    noPassportTitle: "No hay problema, yo me encargo.",
    noPassportBody:
      "Si no tienes pasaporte, prepararé una declaración oficial (Anexo I-2) y la firmaré en tu nombre como tu representante autorizada. No necesitarás hacer nada adicional.",
    helpPermit:
      "Este programa es para personas sin un permiso de residencia vigente. Si ya tienes uno, podemos ayudarte con renovaciones y modificaciones.",
    permitOtherServices: "Conocer otros servicios",
    permitNotSure: "¿No estás seguro/a? Continúa igualmente y revisaré tu caso.",
    soon: "Próximamente",
    showOtherOptions: "Mostrar otras opciones",
    scenario_1_label: "Es mi primera solicitud",
    scenario_1_h: "",
    scenario_2_label: "Un menor nacido en España",
    scenario_2_h: "",
    scenario_3_label: "Un menor no nacido en España",
    scenario_3_h: "",
    scenario_4_label: "Un familiar",
    scenario_4_h: "",
    scenario_7A_label: "Estoy trabajando o tengo una oferta de empleo",
    scenario_7A_h: "",
    scenario_7B_label: "Vivo con mi familia en España",
    scenario_7B_h: "",
    scenario_7C_label: "Tengo un informe de vulnerabilidad",
    scenario_7C_h: "",
    scenario_8_label: "Un menor nacido en España",
    scenario_8_h: "",
    scenario_9_label: "Un menor no nacido en España",
    scenario_9_h: "",
    scenario_10_label: "Un familiar",
    scenario_10_h: "",
    extensionGroup_da20: "Estoy renovando mi autorización de residencia",
    familyGroup_label: "Solicito para un familiar",
    scenario_ext_jobSearch_label: "Estoy buscando empleo",
    scenario_ext_jobSearch_h_da20: "",
    scenario_ext_integration_label: "Tengo un informe de integración",
    scenario_ext_integration_h_da20: "",
    scenario_ext_illness_label: "Tengo una enfermedad",
    scenario_ext_illness_h_da20: "",
    scenario_ext_disability_label: "Tengo una discapacidad",
    scenario_ext_disability_h_da20: "",
    scenario_ext_retirement_label: "Estoy jubilado/a",
    scenario_ext_retirement_h_da20: "",
  },
  section3: {
    title: "Estado civil y familia",
    title_rep: "Estado civil y familia del solicitante",
    subtitle_applicant: "Un par de preguntas rápidas sobre tu situación familiar.",
    subtitle_rep: "Un par de preguntas rápidas sobre la situación familiar del solicitante.",
    civilStatus: "Estado civil",
    civilOptions: { single: "Soltero/a", married: "Casado/a", widowed: "Viudo/a", divorced: "Divorciado/a", separated: "Separado/a" },
    fatherName: "Nombre del padre", fatherNamePlaceholder: "José",
    fatherNameHelper: "Sin apellidos",
    fatherFirst: "Nombre",
    fatherLast1: "Primer apellido",
    fatherLast2: "Segundo apellido",
    fatherUnknown: "No sé cómo se llama",
    parentNotAvailable: "No disponible",
    motherName: "Nombre de la madre", motherNamePlaceholder: "María",
    motherNameHelper: "Sin apellidos",
    motherFirst: "Nombre",
    motherLast1: "Primer apellido",
    motherLast2: "Segundo apellido",
    motherUnknown: "No sé cómo se llama",
    parentsNote: "Los datos de los padres son opcionales.",
    spouseQuestion: "¿Tienes cónyuge o pareja de hecho?",
    spouseHelp: "Responde sí si estás casado/a o tienes una pareja registrada oficialmente.",
    spouseDetailsLabel: "Datos del cónyuge",
    spouseFirstName: "Nombre",
    spouseLastName: "Primer apellido",
    spouseSecondLastName: "Segundo apellido",
    spouseDob: "Fecha de nacimiento",
    spouseNationality: "Nacionalidad",
    spouseLegalStatus: "¿Tiene situación legal en España?",
    spouseLegalStatusHelp: "Si tu cónyuge tiene un NIE o permiso de residencia legal en España, responde sí.",
    spouseNie: "NIE o número de pasaporte",
    childrenQuestion: "¿Tienes hijos?",
    childrenHelp: "Incluye hijos biológicos, adoptados o bajo tu tutela legal.",
    childHeader: "Hijo/a",
    addChild: "Añadir hijo",
    childFirstName: "Nombre",
    childLastName: "Primer apellido",
    childSecondLastName: "Segundo apellido",
    childDob: "Fecha de nacimiento",
    childNationality: "Nacionalidad",
    childLivesInSpain: "¿Vive contigo en España?",
    childLivesInSpainHelp: "Marca sí si este hijo vive actualmente contigo en España.",
    childInSchool: "¿Está escolarizado en España?",
    childInSchoolHelp: "Marca sí si este hijo está matriculado en un centro escolar en España.",
    removeChild: "Eliminar hijo",
    dependentParentsQuestion: "¿Tienes padres a tu cargo viviendo contigo?",
    dependentParentsHelp: "Padres que dependen de ti económicamente y viven contigo.",
    parentHeader: "Padre/Madre",
    addParent: "Añadir padre/madre",
    parentFirstName: "Nombre",
    parentLastName: "Primer apellido",
    parentDob: "Fecha de nacimiento",
    parentNationality: "Nacionalidad",
    removeParent: "Eliminar padre/madre",
    sexLabel: "Sexo",
    sexPlaceholder: "Seleccionar...",
    sexMale: "Hombre",
    sexFemale: "Mujer",
    sexUndetermined: "X / indeterminado",
    unknownLabel: "No disponible",
    dependentsQuestion: "¿Tienes hijos menores o familiares dependientes viviendo contigo en España?",
    dependentsHelper: "Incluye hijos menores de 18 años, hijos mayores con discapacidad o padres a tu cargo.",
    voiceTitle: "Un par de preguntas rápidas sobre tu situación familiar.",
    voiceTitle_rep: "Un par de preguntas rápidas sobre la situación familiar del solicitante.",
  },
  section4: {
    title: "Dirección",
    title_rep: "Dirección del solicitante",
    subtitle_applicant: "¿Dónde vives en España?",
    subtitle_rep: "¿Dónde vive el solicitante en España?",
    streetType: "Tipo de vía",
    streetTypePlaceholder: "Seleccionar tipo de vía",
    streetTypeOptionStreet: "Calle",
    streetTypeOptionAvenue: "Avenida",
    streetTypeOptionSquare: "Plaza",
    streetTypeOptionRoad: "Carretera",
    streetTypeOptionPassage: "Pasaje",
    streetTypeOptionOther: "Otro",
    street: "Vía",
    streetPlaceholder: "Nombre de la calle",
    streetNumber: "Número",
    streetNumberPlaceholder: "10",
    floorPiso: "Piso",
    floorPisoPlaceholder: "—",
    floorDoor: "Puerta",
    floorDoorPlaceholder: "A, B",
    city: "Localidad",
    cityPlaceholder: "Ciudad o pueblo",
    province: "Provincia",
    provincePlaceholder: "Seleccionar provincia",
    municipality: "Municipio",
    municipalityPlaceholder: "Seleccionar municipio",
    locality: "Localidad",
    localityPlaceholder: "Seleccionar localidad",
    postalCode: "Código Postal",
    postalCodePlaceholder: "28001",
    country: "País",
    countryPlaceholder: "España",
    residenceSince: "¿Desde cuándo vives aquí?",
    residenceSinceHelper: "Mes y año aproximados en que empezaste a vivir en esta dirección.",
    residenceSincePlaceholderMonth: "MM",
    residenceSincePlaceholderYear: "AAAA",
    residenceSinceOptional: "Opcional",
    voiceTitle: "¿Dónde vives en España?",
    voiceTitle_rep: "¿Dónde vive el solicitante en España?",
  },
  section5: {
    title: "Tu situación en España",
    title_rep: "Situación del solicitante en España",
    subtitle_applicant: "Cuéntanos sobre tu situación actual en España.",
    subtitle_rep: "Información sobre la situación actual del solicitante.",
    arrivalDate: "¿Cuándo llegaste a España?", arrivalDateHelper: "Día / Mes / Año de llegada aproximada.",
    wantsToWork: "¿Quieres trabajar en España?",
    wantsToWorkOptions: { yes: "Sí", no: "No" },
    workSituation: "¿Cuál es tu situación?",
    workSituation_rep: "¿Cuál es su situación?",
    workSituationOptions: { employed: "Tengo trabajo", looking: "Busco trabajo", selfemployed: "Autónomo/a" },
    nie: "NIE", niePlaceholder: "X1234567A", nieHelper: "Opcional. Puede estar caducado",
    nieAsk: "¿No lo tienes? Pregúntanos",
    voiceTitle: "Algunas preguntas sobre tu situación.",
    voiceTitle_rep: "Algunas preguntas sobre la situación del solicitante.",
  },
  section6: {
    title: "Tu pasaporte",
    title_rep: "Pasaporte del solicitante",
    subtitle:
      "Haz una foto de la página de datos o sube un PDF. Rellenaremos tus datos automáticamente.",
    subtitle_applicant: "Información sobre tu documento de identidad o pasaporte.",
    subtitle_rep: "Información sobre el documento de identidad del solicitante.",
    photoInstructions:
      "Haz una foto o sube una imagen de la página con tu foto y datos personales. Si ya tienes un PDF completo del pasaporte, súbelo aquí. No intentes fotografiar cada página por separado.",
    photoHint:
      "Podrás corregir los datos después. Más adelante subirás el pasaporte completo (todas las páginas).",
    passportName: "Nombre como aparece en el pasaporte",
    docType: "Tipo de documento",
    docTypeOptions: {
      passport: "Pasaporte",
      nationalId: "Cédula/DNI",
      travelDoc: "Título viaje",
      cedulaInscripcion: "Inscripción",
      other: "Otro",
    },
    docTypeOtherLabel: "Especifica el tipo de documento",

    docTypeOtherPlaceholder: "Ej: documento de refugiado",
    passportNumber: "Número de pasaporte", passportNumberPlaceholder: "ABC123456",
    passportNumberHelper: "Como figura en el pasaporte.",
    docExpiry: "Fecha de caducidad", docExpiryHelper: "Día / Mes / Año",
    docExpiryAsk: "¿No estás seguro? Pregúntanos",
    expiredNote: "Se aceptan documentos caducados.",
    docUpload: "Sube tu documento",
    docUploadHelper: "Haz una foto de la página principal de tu pasaporte (la que tiene tu foto y datos personales). Colócalo sobre una superficie plana con buena iluminación.",
    uploadBtn: "Elegir archivo", cameraBtn: "Usar cámara",
    uploadedLabel: "Archivo adjunto:", changeFile: "Cambiar",
    nie: "NIE",
    niePlaceholder: "X1234567A",
    nieHelper: "Opcional. Puede estar caducado",
    nieAsk: "¿No tienes uno? Pregúntanos",
    docName: "Nombre como aparece en tu documento",
    docNameHelper: "Confirma que coincide con tu documento de identidad.",
    seeExample: "Ver ejemplos",
    photoGuideTitle: "Requisitos de la foto",
    photoGood: "Plano, buena luz, todo el texto visible",
    photoGoodCaption: "Correcto — plano, buena luz, todo el texto legible, las cuatro esquinas visibles",
    photoBad1: "Borroso",
    photoBad2: "Con reflejo o sombra",
    photoBad3: "Cortado o con dedos",
    carouselBad1: "Borroso — el texto no se puede leer. Mantén el móvil quieto y enfoca antes de hacer la foto.",
    carouselBad2: "Reflejo o sombra — parte del texto está tapado. Busca luz natural sin reflejos directos.",
    carouselBad3: "Cortado — falta parte del documento. Asegúrate de que se ven las cuatro esquinas completas.",
    carouselBad4: "Dedos visibles — tus dedos tapan parte del documento. Sujétalo por los bordes o apóyalo sobre una superficie plana.",
    goodExampleTitle: "Ejemplo correcto",
    badExampleTitle: "Ejemplos incorrectos",
    photoGotIt: "Entendido",
    voiceTitle: "Ahora tu documento de identidad.",
    voiceTitle_rep: "Ahora el documento de identidad del solicitante.",
  },
  section7: {
    title: "Prueba de residencia",
    title_rep: "Prueba de residencia del solicitante",
    subtitle_applicant: "¿Qué documentos tienes para demostrar tu tiempo en España?",
    subtitle_rep: "¿Qué documentos tiene el solicitante para demostrar su tiempo en España?",
    checklistTitle: "Selecciona los documentos que tienes",
    checklistHelper: "Puedes seleccionar varios. Todos son opcionales.",
    docTypes: { utility: "Factura de suministros", bank: "Extracto bancario", medical: "Historial médico", rental: "Contrato de alquiler", transport: "Billete de transporte", other: "Otro" },
    uploadTitle: "Sube un documento", uploadHelper: "Puedes añadir más después.",
    uploadBtn: "Elegir archivo", cameraBtn: "Usar cámara",
    uploadedLabel: "Archivo adjunto:", changeFile: "Cambiar",
    allOptional: "Todos los documentos son opcionales en esta etapa.",
    stayUploadAsk: "¿Qué documentos sirven? Pregúntanos",
    passportUploadGuideLabel: "¿Cómo subir el pasaporte completo?",
    passportUploadGuide: "Necesitamos todas las páginas de tu pasaporte en un solo PDF, incluyendo las vacías. Dos opciones fáciles:\n\n📱 Usa una app gratuita como Adobe Scan o Google Drive para escanear todas las páginas en un PDF desde tu móvil.\n\n🏪 Visita tu copistería o locutorio más cercano y pide que escaneen tu pasaporte completo a PDF. Pueden enviártelo por correo o guardarlo en tu móvil.",
    voiceTitle: "Último paso — prueba de que has estado en España.",
    voiceTitle_rep: "Último paso — prueba de que el solicitante ha estado en España.",
    // Persona titles & instructions
    asylumTitle: "Tu documentación de asilo",
    asylumInstructions: "Como solicitante de asilo, no necesitas probar 5 meses de estancia. Solo sube tu documentación de asilo.",
    longTermTitle: "Prueba de que has estado en España",
    longTermInstructions: "Llevas más de 5 años en España, así que solo necesitas algunos documentos recientes que confirmen tu residencia continuada.",
    standardTitle: "Prueba de que has estado en España",
    standardInstructions: "Sube documentos que muestren que has vivido en España. Cualquier documento con tu nombre y una fecha sirve.",
    priorDenialTitle: "Tus documentos",
    priorDenialInstructions: "Como tuviste un proceso anterior, necesitamos algunos documentos específicos.",
    // Asylum doc types
    asylumDocTypeLabel: "Tipo de documento de asilo",
    asylumDocTypes: { receipt: "Resguardo de asilo", resolution: "Resolución de asilo", other: "Otro documento de asilo" },
    asylumUploadLabel: "Sube tu documento de asilo",
    asylumOptionalTitle: "¿Tienes otros documentos que muestren tu tiempo en España?",
    asylumOptionalHelper: "No son obligatorios pero pueden fortalecer tu caso.",
    asylumOptionalExpand: "Añadir documentos adicionales",
    asylumOptionalCollapse: "Ocultar documentos adicionales",
    // Prior denial sections
    denialSectionATitle: "Documentos del proceso anterior",
    denialResolutionLabel: "Resolución de denegación anterior",
    denialCriminalLabel: "Antecedentes penales del proceso anterior",
    denialCriminalNote: "Aunque estén caducados, son válidos si permaneciste en España.",
    denialSectionBTitle: "Prueba de estancia en España",
    denialSectionBHelper: "Necesitamos documentos que cubran al menos 5 meses.",
    // Standard upload list
    addDocBtn: "+ Añadir documento",
    docTypeLabel: "Tipo de documento",
    docTypeOptions: { utility: "Factura de suministros", bank: "Extracto bancario", medical: "Historial médico", rental: "Contrato de alquiler", transport: "Billete de transporte", other: "Otro" },
    monthCoverageLabel: "Meses cubiertos",
    monthCoverageHelper: "Cada documento que subas puede cubrir uno o más meses.",
    uploadDocLabel: "Sube el documento",
    removeDoc: "Eliminar",
    docEntryTitle: "Documento {{n}}",
    monthLabel: "¿De qué mes es?",
    // Tiered doc types
    tierLabel1: "Más fuerte",
    tierLabel2: "Fuerte",
    tierLabel3: "De apoyo",
    tierLabel4: "Complementario",
    tieredDocTypes: {
      padron: "Padrón / Empadronamiento",
      rental: "Contrato de alquiler",
      medical: "Historial médico",
      school: "Matrícula escolar de hijos",
      bank: "Extracto bancario",
      utility: "Factura de suministros (luz, agua, gas, internet)",
      payslip: "Nómina o contrato de trabajo",
      transfer: "Recibo de transferencia",
      course: "Certificado de curso o formación",
      transport: "Billete de transporte",
      police: "Denuncia policial",
      official: "Carta o notificación oficial",
      other: "Otro",
    },
    // Month tracker
    monthsCovered: "Documentos subidos cubriendo {{n}} de 5 meses",
    allMonthsCovered: "✓ Los 5 meses cubiertos",
    // Info note
    infoNote: "Sube los documentos que tengas. No necesitas tenerlos todos ahora, puedes añadir más en cualquier momento. El padrón (empadronamiento) es el documento más importante — si lo tienes, súbelo primero.",
    // Video placeholder
    videoLabel: "1 min · Qué documentos sirven",
    // Flat alias keys (used by StayUploadList and ProofOfStaySection)
    title: "Prueba de que has estado en España",
    voiceTitle: "Último paso — prueba de tu estancia en España.",
    instructions: "Sube documentos que muestren que has vivido en España. Cualquier documento con tu nombre y una fecha sirve.",
    titleLongTerm: "Prueba de que has estado en España",
    instructionsLongTerm: "Llevas más de 5 años en España, así que solo necesitas algunos documentos recientes que confirmen tu residencia continuada.",
    titleAsylum: "Tu documentación de asilo",
    instructionsAsylum: "Como solicitante de asilo, no necesitas probar 5 meses de estancia. Solo sube tu documentación de asilo.",
    asylumOptionalNote: "No son obligatorios pero pueden fortalecer tu caso.",
    asylumReceipt: "Resguardo de asilo",
    asylumResolution: "Resolución de asilo",
    asylumOther: "Otro documento de asilo",
    titlePriorDenial: "Tus documentos",
    instructionsPriorDenial: "Como tuviste un proceso anterior, necesitamos algunos documentos específicos.",
    denialResolution: "Resolución de denegación anterior",
    priorCriminalRecords: "Antecedentes penales del proceso anterior",
    priorCriminalNote: "Aunque estén caducados, son válidos si permaneciste en España.",
    docTypePadron: "Padrón / Empadronamiento",
    docTypeRental: "Contrato de alquiler",
    docTypeMedical: "Historial médico",
    docTypeSchool: "Matrícula escolar de hijos",
    docTypeBank: "Extracto bancario",
    docTypeUtility: "Factura de suministros",
    docTypePayslip: "Nómina o contrato de trabajo",
    docTypeTransfer: "Recibo de transferencia",
    docTypeCourse: "Certificado de curso o formación",
    docTypeTransport: "Billete de transporte",
    docTypePolice: "Denuncia policial",
    docTypeOfficial: "Carta o notificación oficial",
    docTypeOther: "Otro",
    acceptedWidget: {
      title: "¿Qué documentos sirven como prueba de residencia?",
      subtitleOpen: "Asegúrate de que cada documento incluya tu nombre y una fecha. Los documentos sin nombre o fecha no son válidos.",
      tier1Label: "Nivel 1 — Evidencia más fuerte",
      tier2Label: "Nivel 2 — Evidencia fuerte",
      tier3Label: "Nivel 3 — Evidencia de apoyo",
      descPadron: "Certificado de empadronamiento del ayuntamiento donde vives. El empadronamiento histórico es el documento único más fuerte porque muestra todas tus fechas de empadronamiento, no solo el día actual. Por sí solo no siempre es suficiente, pero es la base de casi todas las solicitudes fuertes.",
    descRental: "Contrato de alquiler a tu nombre de una vivienda en España. Más fuerte si está registrado ante la comunidad autónoma o si va acompañado de recibos de pago mensuales que confirmen la continuidad.",
    descMedical: "Registros de visitas al médico, hospital o centro de salud público con fecha y tu nombre. Las tarjetas sanitarias, informes médicos, recetas, o citas en el sistema público cuentan. Evidencia especialmente útil porque es difícil de falsificar.",
    descSchool: "Matrícula, asistencia o certificados escolares tuyos o de tus hijos en un centro educativo español. Incluye guarderías, colegios, institutos, academias oficiales y universidades.",
    descBank: "Extractos de una cuenta bancaria española que muestren tus transacciones a lo largo del tiempo. Cuantos más meses cubran, más fuerte es la prueba. Las cuentas con actividad regular en España son las más convincentes.",
    descUtility: "Facturas de luz, agua, gas, internet o móvil a tu nombre y con una dirección española. Las facturas recurrentes de los últimos meses ayudan a demostrar presencia continua.",
    descPayslip: "Nóminas o contratos de trabajo que muestren al empleador, fecha y tu nombre. Si tienes historial laboral formal en España, también sirve para la vía laboral de la regularización, no solo como prueba de residencia.",
    descTransfer: "Recibos de envíos de dinero enviados o recibidos desde España (Western Union, MoneyGram, Wise, Ria, etc.). Cada recibo debe mostrar tu nombre, la ubicación en España y la fecha.",
    descCourse: "Certificados, matrículas o diplomas de cursos, academias de idiomas o programas de formación en España. Incluye cursos de español, formación profesional y programas privados acreditados.",
    descTransport: "Abono transporte mensual, billetes de Renfe, tarjetas de transporte recargadas, billetes de vuelo o autobús. Cada billete debe incluir tu nombre y fecha para ser válido.",
    descPolice: "Denuncias, atestados o cualquier documento policial que te identifique por nombre y lleve fecha. Incluye denuncias que hayas puesto tú como víctima o testigo, no solo las que te mencionen como investigado.",
    descOfficial: "Cartas o notificaciones oficiales de cualquier administración pública española (Seguridad Social, Hacienda, ayuntamiento, comunidad autónoma, juzgados, etc.) dirigidas a ti. La correspondencia oficial a una dirección española es evidencia sólida de que vives allí.",
    descOther: "Cualquier otro documento con tu nombre y una fecha que demuestre tu presencia en España: recibos de compras, entradas a eventos, certificados religiosos, cartas de asociaciones, etc.",
    },
    addDocument: "+ Añadir documento",
    whatMonth: "¿De qué mes es?",
    uploadNote: "Sube los documentos que tengas. No necesitas tenerlos todos ahora, puedes añadir más en cualquier momento. El padrón (empadronamiento) es el documento más importante — si lo tienes, súbelo primero.",
    coverageLabel: "Documentos subidos cubriendo {{n}} de 5 meses",
    coverageComplete: "✓ Los 5 meses cubiertos",
    criminalDocType: "Documentación de antecedentes",
    // Criminal record section
    criminalTitle: "Documentación sobre antecedentes penales",
    criminalInstructions: "Sube cualquier documentación sobre tus antecedentes — certificados de cancelación, documentos judiciales, o cualquier cosa que muestre el estado de tu caso. Tu asesor lo revisará.",
    criminalUploadLabel: "Documentación de antecedentes penales",
  },
  section7b: {
    title: "Qué documentos necesitarás",
    subtitle: "Necesitas demostrar presencia continua en España durante aproximadamente {{duration}}. Los documentos deben cubrir tantos meses como sea posible.",
    monthsApprox: "{{n}} meses",
    yearsApprox: "{{n}} años",
    primaryTitle: "Empadronamiento (padrón municipal)",
    primaryBadge: "Prueba más fuerte",
    primaryDesc: "Tu certificado de registro municipal. Es el documento más importante — demuestra directamente tu domicilio y presencia en España. Si solo subes un documento, que sea este.",
    primaryHistorical: "Solicita un certificado histórico en tu ayuntamiento mostrando todo tu historial de registro.",
    supportingTitle: "Documentos de apoyo",
    rentalTitle: "Contrato de alquiler / Vivienda",
    rentalDesc: "Contratos de alquiler, recibos o documentos de propiedad que muestren tu dirección en España.",
    rentalNote: "Cubre todo el período de alquiler en un solo documento.",
    medicalTitle: "Documentos médicos",
    medicalDesc: "Tarjeta sanitaria, visitas médicas, informes hospitalarios, recetas.",
    medicalNote: "Cada visita demuestra presencia en esa fecha específica.",
    bankTitle: "Extractos bancarios",
    bankDesc: "Extractos mensuales de una cuenta bancaria española.",
    bankNote: "Cada extracto cubre un mes.",
    utilityTitle: "Facturas de suministros",
    utilityDesc: "Facturas de electricidad, agua, gas, internet en tu dirección.",
    utilityNote: "Cada factura cubre un mes. Reúne varias para cubrir huecos.",
    employmentTitle: "Documentos de empleo",
    employmentDesc: "Contrato de trabajo, nóminas, vida laboral.",
    employmentNote: "La vida laboral muestra todo tu historial de empleo en un solo documento.",
    selfEmploymentTitle: "Documentos de autónomo",
    selfEmploymentDesc: "Alta de autónomo, declaraciones trimestrales (modelo 130/303), facturas.",
    selfEmploymentNote: "Tu alta de autónomo muestra la fecha exacta en que empezaste a trabajar.",
    familyTitle: "Documentos familiares",
    familyDesc: "Certificado de matrimonio, matrícula escolar de hijos, libro de familia.",
    familyNote: "La matrícula escolar demuestra presencia continua durante todo el curso.",
    otherTitle: "Otros documentos aceptables",
    otherDesc: "Transferencias, matrícula de cursos, tarjetas de transporte, denuncias, cualquier documento oficial con tu nombre y fecha.",
    otherNote: "Más débiles individualmente pero ayudan a cubrir huecos.",
    guidanceShort: "Céntrate en tu empadronamiento más 2-3 documentos de apoyo repartidos en diferentes meses.",
    guidanceMedium: "Intenta tener al menos un documento por trimestre. Tu mejor combinación es empadronamiento + contrato de alquiler + documentos de empleo.",
    guidanceLong: "Para estancias largas, un certificado histórico de empadronamiento es esencial. Compleméntalo con vida laboral y extractos bancarios.",
    rentalDetail: "Sube el contrato completo en PDF. Si tienes varios contratos seguidos, súbelos todos. Asegúrate de que aparezca tu nombre, la dirección y las fechas de inicio y fin.",
    medicalDetail: "Cada visita médica, receta o informe hospitalario cuenta. Cuantos más tengas, mejor. Puedes pedirle a tu médico de cabecera un historial de visitas.",
    bankDetail: "Sube los extractos mensuales en PDF. Cada extracto cubre un mes. Si tienes varios años, prioriza los más recientes y los que cubran los meses que te faltan.",
    utilityDetail: "Cada factura cubre el mes en que fue emitida. Reúne facturas de diferentes meses para cubrir el mayor período posible. Sirven facturas de luz, agua, gas o internet.",
    employmentDetail: "La vida laboral es el documento más completo — muestra todo tu historial de empleo en España en un solo PDF. Puedes obtenerla gratis en la Sede Electrónica de la Seguridad Social.",
    selfEmploymentDetail: "El alta de autónomo muestra la fecha exacta en que empezaste. Las declaraciones trimestrales (modelo 130 o 303) demuestran actividad continuada. Súbelas en orden cronológico.",
    familyDetail: "El certificado de matrícula escolar es muy valioso — demuestra presencia continua durante todo el curso. Pídelo directamente en el colegio de tus hijos.",
    otherDetail: "Estos documentos son más débiles por sí solos, pero ayudan a cubrir huecos entre documentos más fuertes. Cuantos más tengas de diferentes meses, mejor.",
  },
  review: {
    title: "Revisa tu solicitud",
    subtitle: "Esto es lo que está completo y lo que aún necesitamos",
    progressLabel: "campos obligatorios completados",
    statusComplete: "Completo",
    statusPartial: "Parcial",
    statusNotStarted: "Sin iniciar",
    notProvided: "No proporcionado",
    addLink: "Añadir",
    editLink: "Editar",
    continueBtn: "Continuar al consentimiento",
    sections: { s1: "Datos personales", s2: "Información personal", s3: "Estado civil y familia", s4: "Dirección en España", s5: "Situación en España", s6: "Documento de identidad", s7: "Prueba de residencia" },
    voiceTitle: "Revisemos todo antes de terminar.",
    voiceTitle_rep: "Revisemos la información del solicitante antes de terminar.",
    saveBtn: "Guardar y enviar",
    completionGreetComplete: "¡Estás registrado, {{name}}!",
    completionBodyComplete: "Hemos recibido tu información. El siguiente paso es subir tus documentos — puedes hacerlo ahora o volver más tarde.",
    completionGreetMissing: "¡Buen trabajo, {{name}}!",
    completionBodyMissing: "He guardado todo. Te enviaré un mensaje con lo que falta — puedes terminarlo cuando quieras.",
    completionTextOrEmail: "Me pondré en contacto por mensaje o correo.",
    completionPortalNote: "Puedes volver en cualquier momento en:",
    completionTextLink: "Te enviaré un enlace por mensaje para que puedas volver cuando quieras.",
    ctaDocs: "Continuar con documentos",
    ctaOrganizer: "Ir a Mi Organizador",
    ctaNote: "Puedes volver en cualquier momento. Te enviaremos un enlace por mensaje.",
    completionHeadline: "¡Listo, {{name}}!",
    completedLabel: "Lo que has completado",
    item1: "Información personal",
    item2: "Situación y elegibilidad",
    item3: "Estado civil y familia",
    item4: "Dirección en España",
    item5: "Acuerdo firmado",
    savedConfirmation: "Todo se ha guardado de forma segura. Puedes cerrar esta página y volver en cualquier momento.",
    whatsNext: "Siguiente paso: reúne tus documentos",
    whatsNextBody: "Necesitamos algunos documentos para completar tu solicitud: tu pasaporte, prueba de residencia y posiblemente un certificado de antecedentes penales. Puedes subirlos ahora o volver más tarde.",
  },
  section9: {
    title: "Consentimiento y guardar",
    subtitle: "Casi listo — por favor confirma antes de guardar tu solicitud.",
    gdprLabel: "He leído y acepto la Política de Privacidad y consiento el tratamiento de mis datos personales para la evaluación de mi caso y para ser contactado en relación con mi solicitud.",
    saveBtn: "Guardar y enviar",
    checkboxRequired: "Debes aceptar la política de privacidad para continuar.",
    successTitleMissing: "¡Progreso guardado!",
    successMissing: "Tu información ha sido guardada. Te enviaremos un resumen de lo que falta con un enlace para volver.",
    successTitleComplete: "¡Todo listo!",
    successComplete: "Tu información será revisada por tu asesor. Te contactaremos si necesitamos algo más.",
    advisorWillReview: "revisará tu solicitud y se pondrá en contacto si necesita algo.",
    teamReview: "Tu solicitud será revisada por nuestro equipo. Nos pondremos en contacto si es necesario.",
    missingFieldsNote: "Algunos campos aún faltan — no pasa nada. Te haremos un seguimiento.",
    privacyLink: "Política de Privacidad",
    voiceTitle: "Una última cosa — tu consentimiento para continuar.",
    voiceTitle_rep: "Una última cosa — el consentimiento del solicitante para continuar.",
    advisorMsgComplete: "Tengo todo lo que necesito. Revisaré tu solicitud y me pondré en contacto si necesito algo.",
    advisorMsgMissing: "He guardado tu avance. Te enviaré un recordatorio de lo que aún falta.",
    teamMsgComplete: "Tenemos todo lo que necesitamos. Revisaremos tu solicitud y nos pondremos en contacto si es necesario.",
    teamMsgMissing: "Hemos guardado tu avance. Te enviaremos un recordatorio de lo que aún falta.",
  },
  advisorProfile: {
    moreAbout: "Más sobre",
    role: "Asesora de inmigración",
    bioLine: "Asesora de inmigración · 🇻🇪",
    bioBlock: "Mamá de 2 · Dog mom de Luna 🐕 · Senderismo en la montaña ⛰️",
    quote: "Mi pasión es ayudar a las personas a construir una nueva vida en España — ¡yo misma lo hice, y he ayudado a muchos otros a hacerlo también!",
    from: "Originaria de Venezuela",
    personal1: "Mamá de dos niños increíbles",
    personal2: "Amante de los perros — tengo una rescatada llamada Luna",
    personal3: "Me encanta hacer senderismo en la montaña los fines de semana",
    passion: "Mi pasión es ayudar a las personas a construir una nueva vida en España — ¡yo misma lo hice!",
    contact: "Te contactaré por mensaje o correo 💬",
    instagram: "Sígueme en Instagram",
  },
  engagement: {
    reviewData: "Revisar mis datos ›",
    title: "Acuerdo de Encargo",
    subtitle: "Requerido antes de comenzar con tu expediente",
    whyTitle: "¿Por qué firmamos esto antes de empezar?",
    whyBody: "Este contrato formaliza la relación profesional entre usted y nuestro despacho. Define los servicios acordados, los honorarios y las obligaciones de ambas partes, garantizando transparencia y seguridad jurídica desde el primer momento.",
    required: "Requerido",
    signatureLabel: "Firma del cliente",
    clearSignature: "Borrar firma",
    signatureBlank: "Dibuja tu firma en el recuadro. No se acepta un lienzo en blanco.",
    signatureHint: "Firme aquí con el ratón o su dedo",
    signatureBottom: "firma del cliente",
    advisorRole: "Asesora Senior, Global Mobility Law",
    assignedBadge: "Su asesora asignada",
    scopeLabel: "Alcance de los servicios",
    feeLabel: "Honorarios acordados",
    feeTitle: "Regularización Extraordinaria 2026",
    feeNote: "Incluye la gestión completa del expediente",
    feeAmount: "€199",
    feeWord: "honorarios",
    feeAmountNote: "más tasa administrativa del gobierno (38,28 €)",
    consentsLabel: "Tu consentimiento",
    consent1Before: "He leído y acepto las ",
    consent1Link: "condiciones generales de encargo profesional",
    consent1After: " y la política de honorarios descrita anteriormente.",
    consent2: "Autorizo a Global Mobility Law a actuar como mi representante profesional ante las autoridades migratorias españolas en relación con los servicios contratados.",
    questionsText: "¿Tienes preguntas sobre lo anterior?",
    questionsBtn: "Tengo dudas, por favor haced seguimiento",
    questionsConfirm: "Recibido. Haremos seguimiento contigo",
    consent3: "Acepto recibir comunicaciones informativas sobre el estado de mi expediente y novedades relevantes en materia de extranjería por correo electrónico.",
    optional: "(opcional)",
    dateLabel: "Fecha de firma:",
    dateAuto: "— generada automáticamente",
    declarationsLabel: "Declaraciones legales",
    validationHint: "Para continuar: acepte los dos consentimientos obligatorios y firme el documento.",
    readyMessage: "Todo listo — puede continuar con el formulario.",
    noReturnCommitment: "Confirmo que no tengo ninguna orden que me obligue a abandonar España.",
    noHealthcareCoverage: "Confirmo que no tengo cobertura sanitaria en España a cargo de otro Estado ni de un tercero.",
    scope: {
      analysis: "Análisis y diagnóstico inicial de su situación migratoria",
      preparation: "Preparación y revisión de toda la documentación necesaria",
      submission: "Presentación de la solicitud ante la autoridad competente",
      followup: "Seguimiento del expediente hasta resolución final",
    },
  },
  docBriefing: {
    residenceTitle: "Prueba de residencia: Descripción general",
    residenceSubtitle: "Necesitas demostrar presencia continua en España durante aproximadamente {{duration}}.",
    reviewThenContinue: "Revisa la guía de abajo y, cuando termines, pulsa Continuar para subir tus documentos.",
    criminalUploadDocsHeading: "Sube tus documentos",
    monthsApprox: "{{n}} meses",
    yearsApprox: "{{n}} años",
    videoLabel: "1 min · Qué documentos sirven",
    primaryTitle: "Empadronamiento (padrón municipal)",
    primaryBadge: "Prueba más fuerte",
    primaryDesc: "Tu certificado de registro municipal. Es el documento más importante.",
    primaryHistorical: "Solicita un certificado histórico en tu ayuntamiento.",
    supportingTitle: "Documentos de apoyo",
    identityTitle: "Documento de identidad",
    identitySubtitle: "Necesitamos una copia de tu pasaporte, DNI u otro documento de identidad oficial.",
    identityTip1: "Se aceptan pasaportes caducados — sube el que tengas.",
    identityTip2: "Necesitas una copia completa de todas las páginas de tu pasaporte.",
    identityTip3: "El formato preferido es un único archivo PDF.",
    identityTip4: "Necesitas TODAS las páginas — incluso las vacías. Los sellos de entrada y salida demuestran que no has salido de España.",
    identityTip5: "Escanea todas las páginas en un solo PDF. Puedes usar una app gratuita como Adobe Scan o Google Drive desde tu móvil.",
    identityTip6: "También puedes ir a una copistería o locutorio y pedir que escaneen tu pasaporte completo a PDF.",
    rejectTitle: "Los documentos de mala calidad retrasan tu solicitud",
    rejectBody: "La administración rechaza pasaportes con fotos borrosas, páginas faltantes o texto ilegible. Usa un escáner o buena iluminación. Cada página debe ser legible.",
    criminalExpiry: "Estos certificados solo son válidos durante 3 meses. No los solicites demasiado pronto — pídelos 2-3 semanas antes de que estemos listos para presentar tu solicitud. Si no estás seguro del momento, pregúntanos.",
    criminalTitle: "Antecedentes penales",
    criminalSubtitle: "Necesitamos certificados de antecedentes penales de cada país donde hayas vivido en los últimos 5 años antes de llegar a España.",
    criminalWhichTitle: "¿Qué certificados necesitas?",
    criminalWhichBody: "Uno por cada país donde viviste en los 5 años antes de entrar en España. Puede ser solo tu país de origen o varios, según tu situación. En el siguiente paso te preguntaremos cuáles.",
    criminalTip1: "Solicita el certificado en el consulado de tu país en España o en el Registro Central de Penados.",
    criminalTip2: "El certificado debe estar apostillado o legalizado si es de otro país.",
    criminalTip3: "Si tienes antecedentes, sube igualmente el certificado — tu asesor lo revisará.",
    criminalDiplomaticNote: "Si no puedes obtener un certificado de un país en el plazo de un mes, no te preocupes. Presentaremos la documentación (Anexo I-1 y I-2) para solicitarlo por vía diplomática en tu nombre.",
    employmentTitle: "Documentos de empleo",
    employmentSubtitle: "Necesitamos documentos que acrediten tu situación laboral actual o pasada en España.",
    employmentTip1: "Sube tu contrato de trabajo, nóminas recientes o vida laboral.",
    employmentTip2: "Si eres autónomo, incluye el alta de autónomo y declaraciones trimestrales.",
    employmentTip3: "Si buscas trabajo, una oferta de empleo o carta de intención también sirve.",
    familyTitle: "Documentos familiares",
    familySubtitle: "Necesitamos documentos que acrediten tu situación familiar, especialmente si tienes cónyuge o hijos en España.",
    familyTip1: "Sube el libro de familia, certificado de matrimonio o partidas de nacimiento de los hijos.",
    familyTip2: "Si tienes hijos escolarizados en España, incluye el certificado de matrícula escolar.",
    familyTip3: "Los documentos extranjeros deben estar apostillados y, si es necesario, traducidos.",
    asylumTitle: "Documentación de asilo",
    asylumSubtitle: "Como solicitante de asilo, necesitamos tu documentación oficial del proceso de protección internacional.",
    asylumTip1: "Sube el resguardo de solicitud de asilo o la resolución de tu expediente.",
    asylumTip2: "Si tienes tarjeta roja o blanca de solicitante de asilo, inclúyela también.",
    asylumTip3: "No necesitas demostrar 5 meses de estancia — tu documentación de asilo es suficiente.",
    denialTitle: "Documentos de denegación previa",
    denialSubtitle: "Como tuviste un proceso migratorio anterior denegado, necesitamos documentación específica de ese expediente.",
    denialTip1: "Sube la resolución de denegación del proceso anterior.",
    denialTip2: "Incluye los antecedentes penales del proceso anterior, aunque estén caducados.",
    denialTip3: "Añade cualquier documento que demuestre que permaneciste en España tras la denegación.",
    continueToUpload: "Continuar",
    back: "Volver",
    rentalTitle: "Contrato de alquiler / Vivienda",
    rentalDesc: "Contratos de alquiler, recibos o documentos de propiedad que muestren tu dirección en España.",
    rentalNote: "Cubre todo el período de alquiler en un solo documento.",
    medicalTitle: "Documentos médicos",
    medicalDesc: "Tarjeta sanitaria, visitas médicas, informes hospitalarios, recetas.",
    medicalNote: "Cada visita demuestra presencia en esa fecha específica.",
    bankTitle: "Extractos bancarios",
    bankDesc: "Extractos mensuales de una cuenta bancaria española.",
    bankNote: "Cada extracto cubre un mes.",
    utilityTitle: "Facturas de suministros",
    utilityDesc: "Facturas de electricidad, agua, gas, internet en tu dirección.",
    utilityNote: "Cada factura cubre un mes. Reúne varias para cubrir huecos.",
    employmentTitle: "Documentos de empleo",
    employmentDesc: "Contrato de trabajo, nóminas, vida laboral.",
    employmentNote: "La vida laboral muestra todo tu historial de empleo en un solo documento.",
    selfEmploymentTitle: "Documentos de autónomo",
    selfEmploymentDesc: "Alta de autónomo, declaraciones trimestrales (modelo 130/303), facturas.",
    selfEmploymentNote: "Tu alta de autónomo muestra la fecha exacta en que empezaste a trabajar.",
    familyTitle: "Documentos familiares",
    familyDesc: "Certificado de matrimonio, matrícula escolar de hijos, libro de familia.",
    familyNote: "La matrícula escolar demuestra presencia continua durante todo el curso.",
    otherTitle: "Otros documentos aceptables",
    otherDesc: "Transferencias, matrícula de cursos, tarjetas de transporte, denuncias, cualquier documento oficial con tu nombre y fecha.",
    otherNote: "Más débiles individualmente pero ayudan a cubrir huecos.",
    guidanceShort: "Céntrate en tu empadronamiento más 2-3 documentos de apoyo repartidos en diferentes meses.",
    guidanceMedium: "Intenta tener al menos un documento por trimestre. Tu mejor combinación es empadronamiento + contrato de alquiler + documentos de empleo.",
    guidanceLong: "Para estancias largas, un certificado histórico de empadronamiento es esencial. Compleméntalo con vida laboral y extractos bancarios.",
    residenceVideoLabel: "2 min · Qué documentos demuestran tu presencia en España",
    noExitTitle: "No puedes haber salido de España",
    noExitBody: "La permanencia debe ser ininterrumpida durante los 5 meses anteriores a tu solicitud. Cualquier salida del país rompe el requisito — no se permiten ni viajes breves. Si has salido, habla con nosotros antes de continuar.",
    whatCountsTitle: "¿Qué documentos sirven?",
    whatCountsBody: "Cualquier documento público o privado con tu nombre y una fecha. No necesitas un documento de cada mes, pero cuantos más tengas, más fuerte será tu expediente. Lo ideal: al menos un documento por mes.",
    rentalDetail: "Sube el contrato completo como PDF. Si tienes recibos de alquiler mensuales, sube los de cada mes por separado. Usa nuestro escáner para documentos de varias páginas.",
    medicalDetail: "Sirven: tarjeta sanitaria, informes médicos, recetas, citas del centro de salud. Basta con una foto clara de cada documento. Cada visita demuestra presencia en una fecha concreta.",
    bankDetail: "Descarga los extractos mensuales desde tu banca online como PDF. Si no tienes acceso online, pide los extractos impresos en tu banco y escanéalos.",
    utilityDetail: "Facturas de luz, agua, gas o internet a tu nombre. Una foto clara de cada factura es suficiente. Si las recibes por email, puedes subirlas directamente como PDF.",
    employmentDetail: "La vida laboral se solicita online en sede.seg-social.gob.es o por SMS al 600 123 456. Es un PDF que muestra todo tu historial. Las nóminas y contratos: una foto clara o PDF.",
    selfEmploymentDetail: "El alta de autónomo y las declaraciones trimestrales (modelo 130/303) se descargan desde la sede electrónica de la Agencia Tributaria.",
    familyDetail: "El libro de familia es un documento de varias páginas — usa nuestro escáner o una copistería. La matrícula escolar: pide una copia al centro educativo.",
    otherDetail: "Envíos de dinero: guarda los resguardos. Abono transporte: los nominativos sirven. Cualquier documento oficial con tu nombre y fecha es válido.",
  },
  docFlow: {
    title: "Lista de documentos",
    subtitle: "Esto es lo que necesitamos. Toca cada categoría para ver instrucciones y subir documentos.",
    catIdentity: "Identidad",
    catResidence: "Prueba de residencia",
    catCriminal: "Antecedentes penales",
    catEmployment: "Documentos de empleo",
    catFamily: "Documentos familiares",
    catAsylum: "Documentación de asilo",
    catVulnerability: "Informe de vulnerabilidad",
    catOther: "Otros",
    catDenial: "Documentos de denegación previa",
    helpSheetTitle: "¿En qué podemos ayudarte?",
    helpSheetSubtitle: "Te responderemos pronto; normalmente en 1 día laborable.",
    helpSheetContextPrefix: "Estás en:",
    helpSheetPlaceholder: "Cuéntanos qué necesitas…",
    helpSheetSend: "Enviar",
    helpSheetSending: "Enviando…",
    helpSheetSentTitle: "Mensaje enviado",
    helpSheetSessionError: "No pudimos conectar con tu sesión. Inténtalo de nuevo.",
    helpSheetSendError: "Algo salió mal. Inténtalo de nuevo.",
    helpFabAria: "Ayuda — enviar una pregunta",
    notStarted: "Sin empezar",
    inProgress: "En progreso",
    complete: "Completado",
    backToChecklist: "Volver a Documentos",
    goToOrganizer: "Ir a Mi Organizador",
    videoLabel: "1 min · Qué documentos sirven",
    qualityTitle: "La calidad de tus documentos importa",
    qualityBody: "Documentos borrosos, oscuros o cortados son la causa principal de retrasos. Las solicitudes con documentos ilegibles se devuelven para corrección, lo que puede añadir semanas a tu proceso. Tómate un momento extra para asegurarte de que cada documento sea claro y legible.",
    photoTipsLink: "Ver consejos para buenas fotos de documentos",
    letsBegin: "Empecemos",
    skip: "Saltar",
    continue: "Continuar",
  },
  sectionExit: {
    title: "¿Has terminado con esta sección?",
    yes: "Sí",
    no: "No",
    stuck: "Estoy atascado/a",
    stuckPlaceholder: "Cuéntale a Ana qué te impide avanzar...",
    stuckSend: "Enviar",
    back: "Volver",
    porMarkTitle: "¿Marcar esta sección como completa?",
    porMarkSubtitle: "Puedes cambiarlo más tarde si lo necesitas.",
    porMarkComplete: "Marcar como completa y continuar",
    porComeBackLater: "Guardar y terminar más tarde",
  },
  docUpload: {
    title: "Sube tus documentos",
    done: "Listo — ir a Mi Organizador",
    backToChecklist: "Volver al Organizador de Documentos",
    continue: "Continuar",
    goToOrganizer: "Ir a Mi Organizador",
    backToRequirements: "Volver a los requisitos",
    empHeading: "Documentos de empleo",
    empHeadingEmployed: "Documentos de empleo",
    empHeadingSelfEmployed: "Documentos de autónomo",
    workTypeGatePrompt: "Dinos cómo trabajas para saber qué necesitamos.",
    workTypeEmployed: "Por cuenta ajena",
    workTypeSelfEmployed: "Por cuenta propia (autónomo)",
    workTypeEmployedHelper: "Has trabajado o tienes un contrato/oferta con un empleador.",
    workTypeSelfEmployedHelper: "Trabajas por tu cuenta o planeas hacerlo como autónomo.",
    empDescriptionLabel: "Describe tu actividad",
    empDescriptionPlaceholder: "ej. Repartidor, cocinera, diseñadora gráfica",
    selfEmployedNote: "Trabajas por tu cuenta o planeas hacerlo como autónomo.",
    empDocContract: "Contrato de trabajo",
    empDocContractHelper: "Contratos firmados de empleadores actuales o anteriores en España. Incluye como mínimo la primera página y la página de firmas.",
    empDocPayslip: "Nómina",
    empDocPayslipHelper: "Nóminas recientes que muestren tu salario y empleador. Los últimos 3 a 6 meses son los más útiles.",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper: "Tu informe oficial de vida laboral de la Seguridad Social. Muestra todos los trabajos que has tenido. Es una prueba muy sólida.",
    empDocJobOffer: "Carta de oferta de trabajo",
    empDocJobOfferHelper: "Una carta de compromiso firmada por un empleador. Debe incluir el puesto, la fecha de inicio, el salario y los datos del empleador.",
    empDocOther: "Otros documentos",
    empDocOtherHelper: "Cualquier otro documento que apoye tu situación laboral.",
    empOtherPlaceholder: "Breve descripción",
    empAddDoc: "Añadir documento",
    empGateContinue: "Continuar a subir documentos",
    empTipsTitle: "Consejos",
    empTipOptional: "Sube solo los documentos que apliquen a tu situación. Nada de aquí es obligatorio.",
    empTipVidaLaboral: "Tu vida laboral se puede solicitar online en sede.seg-social.gob.es con tu certificado digital o Cl@ve.",
    empTipJobOffer: "Si tienes una oferta de trabajo, asegúrate de que esté firmada por el empleador e incluya su CIF o NIF.",
    empTipInformal: "Las nóminas de trabajo informal (sin contrato) también son útiles. Sube lo que tengas.",
    empTipAutonomo: "Los documentos de autónomo demuestran que pagas impuestos y Seguridad Social por tu propia actividad.",
    identityTitleNoPassport: "Documento de identidad",
    identityTitlePassport: "Pasaporte",
    identityGateQuestion: "¿Qué tipo de identificación tienes?",
    identityGateHint: "Usa un pasaporte válido y no caducado si lo tienes. Elige \"Otro documento\" solo si no lo tienes.",
    identityGatePassport: "Pasaporte",
    identityGateOther: "Otro documento",
    identityGateContinue: "Continuar",
    identityTypeSwitchFromPassport: "No tengo pasaporte válido",
    identityTypeSwitchFromOther: "Sí tengo pasaporte válido",
    identityStep1: "Paso 1: Introduce tus datos",
    passportNumber: "Número de pasaporte",
    passportFullName: "Nombre completo tal como aparece en el pasaporte",
    passportExpiry: "Fecha de caducidad",
    nieLabel: "Número de NIE",
    nieHelper: "NIE (si tienes uno, puede estar caducado)",
    nationalIdType: "Tipo de documento",
    nationalIdNumber: "Número de documento",
    nationalIdCountry: "País emisor",
    nationalIdCountryPlaceholder: "Seleccionar país...",
    typeNationalId: "Documento nacional de identidad",
    typeTravelDoc: "Documento de viaje",
    typeCedulaInscripcion: "Cédula de inscripción",
    identityStep2NoPassport: "Paso 2: Sube tu documento de identidad",
    identityStep2BodyNoPassport: "Sube cualquier documento de identidad que tengas de tu país de origen.",
    identityStep2: "Paso 2: Sube tu pasaporte",
    identityStep2Body: "Necesitamos todas las páginas, incluidas las en blanco. Los sellos de entrada y salida ayudan a demostrar tu tiempo en España. Todas las páginas deben subirse en un único archivo PDF.",
    ocrTitle: "Escanea o sube la página de datos de tu pasaporte",
    ocrSubtitle:
      "Para muchas personas el escáner integrado es lo más fácil si no tienen PDF. Intentaremos leer tus datos y rellenar el formulario.",
    ocrUploadZone: "Toma una foto o elige un archivo",
    ocrUploadHint: "PDF o imagen — solo la página con tu foto y datos",
    ocrSuccessTitle: "Listo",
    ocrBody:
      "Solo la página de identificación del pasaporte (la del retrato). Si la lectura funciona, pasarás directamente al siguiente paso para revisar o corregir tus datos. Si no, puedes reintentar o saltar y escribir tú mismo — no es obligatorio. Más adelante subirás el pasaporte completo en un único PDF.",
    ocrFreedomHint: "No estás obligado a hacerlo ahora — puedes saltar en cualquier momento.",
    ocrSkipManual: "Saltar — introduciré mis datos yo",
    ocrUploadFileLabel: "Subir un archivo",
    ocrUseCameraHint: "Opcional — foto rápida si aún no tienes escaneado",
    ocrUseCamera: "Usar cámara",
    ocrUseScanner: "Escanear (varias páginas)",
    ocrSkip: "Saltar",
    ocrContinue: "Continuar",
    passportAddTitle: "Añade tu pasaporte",
    passportAddBody1:
      "Necesitamos un PDF escaneado de tu pasaporte que incluya todas las páginas.",
    passportAddBody2:
      "Las imágenes con marcos excesivos, dedos visibles o reflejos serán rechazadas.",
    passportAddExamplesLink: "Ver ejemplos de escaneos aceptables",
    passportAddScannerCardTitle: "Usar nuestro escáner",
    passportAddScannerCardDesc:
      "Captura cada página con la cámara. Las uniremos en un único PDF por ti.",
    passportAddPdfCardTitle: "Subir un PDF",
    passportAddPdfCardDesc:
      "Sube un PDF que ya incluya todas las páginas de tu pasaporte.",
    passportScanExamplesCloseAria: "Cerrar",
    passportScanExamplesTitle: "Ejemplos de escaneo",
    passportScanExamplesIntro:
      "Busca un escaneo parecido al ejemplo aceptable. Todo lo que se parezca a los rechazados tendrá que volverse a tomar.",
    passportScanExamplesAcceptableLabel: "ACEPTABLE",
    passportScanExamplesAcceptableCaption:
      "De borde a borde, foco nítido, sin dedos ni sombras en encuadre.",
    passportScanExamplesRejectedLabel: "SERÁ RECHAZADO",
    passportScanExamplesRejectDarkTitle: "Demasiado oscuro",
    passportScanExamplesRejectDarkDesc:
      "Muévete a mejor luz. Evita sombras sobre el pasaporte.",
    passportScanExamplesRejectBlurTitle: "Borroso o fuera de foco",
    passportScanExamplesRejectBlurDesc:
      "Mantén la cámara firme. Toca el pasaporte en pantalla para enfocar.",
    passportScanExamplesRejectFingersTitle: "Dedos en el encuadre",
    passportScanExamplesRejectFingersDesc:
      "Usa un objeto pequeño para mantener el pasaporte abierto.",
    passportScanExamplesGotIt: "Entendido",
    passportScanSavedTitle: "Pasaporte guardado",
    passportScanSavedSubtitle: "{{filename}} · {{pages}}",
    passportScanSavedSubtitleFilenameOnly: "{{filename}}",
    passportScanSavedPagesOne: "1 página",
    passportScanSavedPagesMany: "{{count}} páginas",
    passportScanSavedView: "Ver",
    passportScanSavedReplace: "Reemplazar",
    identityReplaceConfirmTitle: "¿Reemplazar este documento?",
    identityReplaceConfirmBody:
      "Se eliminará tu PDF actual. Podrás subir un nuevo PDF o volver a escanear tu pasaporte.",
    identityReplaceConfirmCancel: "Cancelar",
    identityReplaceConfirmReplace: "Reemplazar documento",
    passportViewOpening: "Abriendo…",
    passportViewUnavailable:
      "No pudimos abrir el archivo del pasaporte. Inténtalo de nuevo o usa Reemplazar para subirlo otra vez.",
    passportViewPopupBlocked: "Permite ventanas emergentes en este sitio para ver el PDF del pasaporte.",
    passportViewerSheetTitle: "Tu documento",
    passportViewerClose: "Cerrar",
    identityAiUploadingSecure: "Guardando tu documento y leyendo los datos…",
    identityAiWaitHint:
      "En cuanto terminemos, pasarás al siguiente paso para revisar tus datos. Si algo falla, podrás reintentar o saltar y escribirlos tú.",
    passportPdfProcessingTitle: "Procesando tu PDF…",
    passportPdfPhaseUpload: "Subiendo el archivo de forma segura…",
    passportPdfPhaseAnalyze: "Leyendo los datos del pasaporte…",
    passportPdfPhaseApply: "Rellenando el formulario…",
    identityUploadRetry: "Reintentar subida",
    identityUploadPickAnother: "Elegir otro archivo",
    passportAiBannerTitle: "Documento guardado — no pudimos leerlo automáticamente.",
    passportAiBannerBody:
      "Tu archivo está guardado. Revisa o escribe los datos abajo, prueba de nuevo con escaneo o subida, o salta y sigue sin insistir — podemos ayudarte más adelante con un PDF más claro. Si los datos no coinciden con este escaneo, pueden ser de una sesión anterior: corrígelos o inténtalo de nuevo.",
    passportAiBannerShopHint: "Una copistería local suele conseguir un escaneo nítido si la cámara del móvil se atranca.",
    passportAiBannerRetry: "Intentar escaneo o subida otra vez",
    passportAiSavedManualTitle: "Guardado — confirma o introduce tus datos",
    identityTitle: "Sube tu documento de identidad",
    criminalTitle: "Sube tu certificado de antecedentes penales",
    asylumTitle: "Sube tu documentación de asilo",
    denialTitle: "Sube la resolución de denegación",
    employmentTitle: "Sube tus documentos de empleo",
    familyTitle: "Sube tus documentos familiares",
    vulnHeading: "Informe de vulnerabilidad",
    vulnIntroBody: "Un formulario oficial que confirma tu situación de vulnerabilidad. Necesitarás que un trabajador social o una ONG registrada lo complete y firme.",
    vulnStep1: "Paso 1: Obtén el formulario",
    vulnStep1Body: "Lleva este formulario a un trabajador social o a una ONG registrada para que completen la evaluación, lo firmen y lo sellen.",
    vulnDownloadBtn: "Descargar",
    vulnEmailBtn: "Email",
    vulnStep2: "Paso 2: Súbelo aquí",
    vulnStep2Body: "Cuando esté firmado y sellado, súbelo aquí.",
    vulnScannerBtn: "Usar escáner",
    vulnTipsTitle: "Consejos",
    vulnTipSocial: "Cualquier oficina municipal de servicios sociales puede completar este formulario.",
    vulnTipNGO: "ONG registradas como Cáritas, Cruz Roja, ACCEM o CEAR también pueden completarlo.",
    vulnTipForward: "A veces puedes enviarlo por email a servicios sociales o a una ONG, pero depende de cada oficina.",
    vulnTipSigned: "Asegúrate de que el informe tenga firma, sello y fecha. Los formularios sin firma serán rechazados.",
    vulnTipID: "Lleva tu pasaporte y cualquier otro documento de identidad cuando vayas a pedir la firma.",
    otherHeading: "Otros",
    otherBody: "Usa esta sección para subir cualquier documento adicional que tu asesor te pida y que no encaje en las categorías anteriores. Por ejemplo, un poder notarial, una traducción jurada o una apostilla.",
    otherAddDoc: "Subir documento",
    documentTypeLabel: "Tipo de documento",
    documentTypePlaceholder: "Selecciona…",
    otrosDescribeLabel: "Describe el documento",
    otrosDescribePlaceholder: "Breve descripción (obligatorio para «Otros»)",
    uploadRulesHint:
      "Formatos: PDF, DOC, JPG, TIF o GIF. Máx. 6 MB por archivo. Nombre: letras, números, espacios, _ - . ( )",
    multipleFilesHint: "Puedes subir varios documentos.",
    residenceHeading: "Prueba de residencia",
    residenceDesc: "Sube documentos que demuestren que has estado en España de forma continua. Cuantos más documentos tengas, mejor. Puedes subirlos ahora o volver más tarde.",
    residencePickerTitle: "Añadir prueba de residencia",
    residencePickerSubtitle: "Elige el documento que quieres subir. Si no estás seguro, elige la opción más cercana o selecciona \"Otro\".",
    residenceFeatured: "Más fuerte",
    residenceGroupOfficial: "Documentos oficiales",
    residenceGroupPersonal: "Otros registros personales",
    residenceGroupOther: "Otro",
    residenceGuidance: "Asegúrate de que este documento muestre claramente tu nombre y una fecha. Los documentos sin estos datos pueden no ser aceptados.",
    residenceOtherLabel: "¿Qué tipo de documento es?",
    residenceOtherPlaceholder: "Ejemplo: gimnasio, recibo de alquiler, cita confirmada",
    residenceOtherHelper: "Opcional. Una breve descripción ayuda a Ana a entender qué es.",
    residenceChange: "Cambiar tipo de documento",
    monthsCovered: "Meses cubiertos",
    monthsTip: "Toca los meses que cubre cada documento que subas para llevar un registro.",
    addDocument: "+ Añadir documento",
    chooseFile: "Elegir archivo",
    uploadedDocs: "Documentos subidos",
    modalTitle: "Añadir documento",
    modalDocType: "Tipo de documento",
    modalSelectType: "Seleccionar tipo...",
    modalMonthRange: "Meses que cubre",
    modalFrom: "Desde",
    modalTo: "Hasta",
    modalFile: "Archivo",
    modalAdd: "Añadir",
    acceptedTypes: "Tipos de documentos aceptados",
    typeEmpadronamiento: "Empadronamiento",
    typeRental: "Contrato de alquiler",
    typeMedical: "Informe médico",
    typeBank: "Extracto bancario",
    typeUtility: "Factura de suministros",
    typeEmployment: "Documento de empleo",
    typeOther: "Otro",
    monthsAutoTip: "Dic 2025 a Abr 2026. Se actualizan automáticamente al añadir documentos.",
    submissionMonthNote: "Dependiendo de la fecha de presentación, es posible que necesitemos volver y añadir los documentos de mayo.",
    to: "a",
    openScanner: "Abrir escáner",
    helpTitle: "Consejos rápidos",
    helpTip1: "El empadronamiento es el documento más importante. Súbelo primero si lo tienes.",
    helpTip2: "Cada documento puede cubrir varios meses. Un contrato de alquiler puede cubrir todo el período.",
    helpTip3: "Los extractos bancarios, facturas y visitas médicas también sirven: cualquier documento con tu nombre y fecha.",
    helpTip4: "No necesitas tenerlos todos ahora. Puedes volver y añadir más en cualquier momento.",
    seeFullRequirements: "Volver al Organizador de Documentos",
    backToOverview: "Volver a la vista general",
    backToGuidance: "Volver al Organizador de Documentos",
    modalScanBtn: "Abrir escáner",
    modalChangeFile: "Cambiar",
    removeDoc: "Eliminar",
    deleteFileConfirm: "¿Seguro que quieres eliminar este archivo?",
    criminalDesc: "Necesitas un certificado de antecedentes penales de tu país de origen y de cualquier país donde hayas vivido en los últimos 5 años.",
    criminalReq1: "Solicítalos en el consulado de tu país en España o pide a un familiar que los tramite en tu país",
    criminalReq2: "Debe estar apostillado o legalizado",
    criminalReq2Pre: "Deben estar ",
    criminalReq2Term: "apostillado o legalizado",
    criminalReq2Help: "La apostilla es un sello oficial que valida tu documento para uso internacional. Se obtiene en el ministerio correspondiente de tu país de origen. Si tu país no está en el Convenio de La Haya, necesitas legalización consular. Si no sabes qué necesitas, pregúntanos.",
    criminalReq3: "Si no está en castellano, necesita traducción jurada",
    criminalReq3Pre: "Si no están en castellano, necesitan ",
    criminalReq3Term: "traducción jurada",
    criminalReq3Help: "Un traductor jurado es un profesional nombrado oficialmente por el Ministerio de Asuntos Exteriores de España. Sus traducciones tienen validez legal. Puedes buscar uno en la web del MAEC (maec.gob.es) o preguntarnos — te podemos recomendar uno.",
    criminalDeclaracion: "¿No puedes obtenerlo? En algunos casos se acepta una declaración jurada. Habla con nosotros.",
    criminalDeclaracionPre: "¿No puedes obtener alguno? En algunos casos se acepta una ",
    criminalDeclaracionPost: ". Habla con nosotros.",
    criminalHelpTitle: "Consejos",
    criminalHelp1: "Algunos consulados tardan semanas — solicítalo cuanto antes",
    criminalHelp2: "Si el certificado tiene más de una página, usa nuestro escáner para crear un PDF",
    criminalVideoLabel: "1 min · Cómo obtener tu certificado de antecedentes",
    empHeading: "Documentos de empleo",
    empHeadingEmployed: "Documentos de empleo",
    empHeadingSelfEmployed: "Documentos de autónomo",
    empDesc:
      "Sube documentos que demuestren tu historial laboral o tu oferta de empleo en España. Sube solo lo que aplique a tu situación.",
    empWhatTitle: "¿Qué demuestra tu trabajo?",
    empWhatBody:
      "No necesitas todos estos documentos. Cualquier combinación de contratos, nóminas o vida laboral demuestra tu historial laboral. Una oferta de empleo por sí sola puede ser suficiente.",
    workTypeGatePrompt: "Dinos cómo trabajas para saber qué necesitamos.",
    workTypeEmployed: "Por cuenta ajena",
    workTypeSelfEmployed: "Por cuenta propia (autónomo)",
    workTypeEmployedHelper: "Has trabajado o tienes un contrato/oferta con un empleador.",
    workTypeSelfEmployedHelper: "Trabajas por tu cuenta o planeas hacerlo como autónomo.",
    empDescriptionLabel: "Describe tu actividad",
    empDescriptionPlaceholder: "ej. Repartidor, cocinera, diseñadora gráfica",
    selfEmployedNote: "Trabajas por tu cuenta o planeas hacerlo como autónomo.",
    empDocContract: "Contrato de trabajo",
    empDocContractHelper:
      "Contratos firmados de empleadores actuales o anteriores en España. Incluye como mínimo la primera página y la página de firmas.",
    empDocPayslip: "Nómina",
    empDocPayslipHelper:
      "Nóminas recientes que muestren tu salario y empleador. Los últimos 3 a 6 meses son los más útiles.",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper:
      "Tu informe oficial de vida laboral de la Seguridad Social. Muestra todos los trabajos que has tenido. Es una prueba muy sólida.",
    empDocJobOffer: "Carta de oferta de trabajo",
    empDocJobOfferHelper:
      "Una carta de compromiso firmada por un empleador. Debe incluir el puesto, la fecha de inicio, el salario y los datos del empleador.",
    empDocAutonomo: "Alta de autónomo",
    empDocAutonomoHelper:
      "Alta de autónomo, registro del IAE o declaraciones tributarias (modelo 130, 303) que demuestren que trabajas por cuenta propia.",
    empDocOther: "Otros documentos",
    empDocOtherHelper: "Cualquier otro documento que apoye tu situación laboral.",
    empOtherPlaceholder: "Breve descripción",
    empAddDoc: "Añadir documento",
    empGateContinue: "Continuar a subir documentos",
    empTipsTitle: "Consejos",
    empTipOptional: "Sube solo los documentos que apliquen a tu situación. Nada de aquí es obligatorio.",
    empTipVidaLaboral:
      "Tu vida laboral se puede solicitar online en sede.seg-social.gob.es con tu certificado digital o Cl@ve.",
    empTipJobOffer:
      "Si tienes una oferta de trabajo, asegúrate de que esté firmada por el empleador e incluya su CIF o NIF.",
    empTipInformal:
      "Las nóminas de trabajo informal (sin contrato) también son útiles. Sube lo que tengas.",
    empTipAutonomo:
      "Los documentos de autónomo demuestran que pagas impuestos y Seguridad Social por tu propia actividad.",
    familyHeading: "Documentos familiares",
    familyDesc:
      "Sube cualquier documento que aplique a tu situación. Ninguno es obligatorio para todas las personas. Sube solo lo que tengas.",
    familyDocMarriage: "Certificado de matrimonio",
    familyDocMarriageHelper:
      "Certificado oficial. Apostillado y traducido si fue emitido fuera de España.",
    familyDocLibro: "Libro de familia",
    familyDocLibroHelper: "Sube todas las páginas relevantes.",
    familyDocBirth: "Certificados de nacimiento de los hijos",
    familyDocBirthHelper: "Uno por hijo. Apostillado y traducido si no está en español.",
    familyDocCohabitation: "Prueba de convivencia",
    familyDocCohabitationHelper:
      "Empadronamiento en la misma dirección, contrato de alquiler compartido, etc.",
    familyDocSchool: "Certificado de escolarización",
    familyDocSchoolHelper: "Certificados de matrícula del colegio de tus hijos.",
    familyDocOther: "Otros documentos",
    familyDocOtherHelper: "Cualquier otro documento de apoyo.",
    familyOtherPlaceholder: "Breve descripción",
    familyScannerBtn: "Usar escáner",
    familyAddDoc: "Añadir documento",
    familyTipsTitle: "Consejos",
    familyTipOptional: "Sube solo los documentos que apliquen a tu situación.",
    familyTipApostille:
      "Los documentos extranjeros deben estar apostillados y traducidos al español.",
    familyTipSkip: "Si no tienes un documento todavía, sáltalo y vuelve más tarde.",
    asylumHeading: "Documentación de asilo",
    asylumDesc:
      "Introduce tu número de expediente OAR y sube tu tarjeta roja o el resguardo de solicitud. Si tu solicitud de asilo fue denegada, sube también la resolución denegatoria.",
    asylumWhatTitle: "¿Por qué es importante?",
    asylumWhatBody:
      "Tus documentos de asilo demuestran que solicitaste protección internacional antes del 1 de enero de 2026. Esto es lo que te califica para la vía EX-31, que tiene requisitos distintos a la regularización general.",
    asylumDocReceipt: "Resguardo de solicitud",
    asylumDocReceiptHelper:
      "Incluye todas las páginas",
    asylumDocCard: "Tarjeta roja",
    asylumDocCardHelper:
      "Sube ambas caras.",
    asylumDocResolution: "Resolución denegatoria",
    asylumDocResolutionHelper:
      "Si tu asilo fue denegado",
    asylumDocOther: "Otros documentos de asilo",
    asylumDocOtherHelper:
      "Cualquier otro documento relacionado con tu solicitud de asilo, como recursos, informes médicos o información sobre tu país de origen.",
    asylumOtherPlaceholder: "Breve descripción",
    asylumAddDoc: "Añadir documento",
    asylumTipsTitle: "Consejos",
    asylumTipOARMatch:
      "Tu número de expediente OAR debe coincidir con el que introdujiste en el Paso 2. Lo encontrarás en tu resguardo de asilo.",
    asylumTipCardBoth:
      "Sube ambos lados de tu tarjeta roja o blanca. El reverso tiene información importante sobre renovaciones.",
    asylumTipDenied:
      "Si tu solicitud de asilo fue denegada pero permaneciste en España, aún puedes usar la vía EX-31. Sube también la resolución denegatoria.",
    asylumTipStay:
      "No necesitas demostrar 5 meses de estancia. Tu documentación de asilo cubre tu tiempo en España.",
    asylumTipLanguage:
      "Todos los documentos deben estar en español o traducidos. Los originales de la OAR ya están en español.",
    or: "o",
  },
  criminal: {
    multiCountryTitle: "¿Qué certificados necesitas?",
    multiCountryBody: "Uno por cada país donde hayas vivido en los 5 años antes de entrar en España. Puede ser solo tu país de origen, o varios — depende de tu caso. En el siguiente paso te preguntaremos cuáles.",
    diplomaticNote: "Si no puedes conseguir un certificado de un país concreto en un mes, no te preocupes. Presentaremos la documentación (Anexo I-1 e I-2) para solicitarlo por vía diplomática en tu nombre.",
    uploadPageDesc: "Necesitamos certificados de antecedentes penales de cada país donde hayas residido durante los 5 años anteriores a tu entrada en España. Incluye tu país de origen si vivías allí en ese periodo. No incluyas España.",
    countriesQuestion: "¿En qué países residiste durante los 5 años anteriores a tu entrada en España?",
    countriesHint: "Añade todos los países donde residiste en los 5 años anteriores a tu entrada en España. Incluye tu país de origen si vivías allí en ese periodo.",
    selectCountryPlaceholder: "Seleccionar país…",
    countrySearchPlaceholder: "Buscar país…",
    countrySearchNoResults: "Sin resultados",
    addCountry: "Añadir",
    uploadSectionTitle: "Sube los certificados",
    noSpainNote: "No incluyas España. El certificado español lo gestiona el sistema automáticamente.",
    or: "o",
    requestedNotReceivedLabel: "Solicitado pero no recibido.",
    requestedNotReceivedDesc: "Han pasado más de 30 días desde que solicité el certificado. Subiré una prueba de solicitud (correo electrónico, carta o recibo).",
    requestedNotReceivedBadge: "Solicitado, no recibido en 30 días",
    requestProofPending: "Prueba de solicitud pendiente",
    uploadedBadge: "Certificado subido",
    uploadReceiptBtn: "Subir justificante",
    scanReceiptBtn: "Escanear justificante",
    uploadCertificateBtn: "Subir certificado",
    scanCertificateBtn: "Escanear certificado",
    awaitingCertificate: "Han pasado mÃ¡s de 30 dÃ­as desde que solicitÃ© el certificado",
    briefingTip1: "Solicita el certificado en el consulado de tu país en España o pide a un familiar que lo tramite en tu país.",
    briefingTip2: "Si solo viviste en tu país de origen antes de venir a España, solo necesitas un certificado.",
    briefingTip3: "Si tienes antecedentes, sube igualmente el certificado — tu asesor lo revisará.",
  },
  docPhotoGuide: {
    title: "Consejos para fotos de documentos",
    link: "Ver consejos para fotos de documentos",
    good: "Correcto — plano, buena luz, todo el texto legible",
    goodTitle: "Buen ejemplo",
    badTitle: "Ejemplos incorrectos",
    bad1: "Sombra — el texto queda oculto. Cambia la posición de la luz.",
    bad2: "Cortado o inclinado — asegúrate de que todo el documento esté dentro del marco.",
    bad3: "Demasiado oscuro — el texto no se puede leer. Usa más luz.",
  },
  organizer: {
    title: "Mi Organizador",
    needItems: "Todavía necesitas {{n}} cosas",
    allDone: "¡Todo completo!",
    residence: "Prueba de residencia",
    criminal: "Certificado de antecedentes",
    asylum: "Documentación de asilo",
    notStarted: "Sin empezar",
    inProgress: "En progreso",
    complete: "Completado",
    upload: "Subir",
    viewCompleted: "Ver completados",
    uploadDocuments: "Subir documentos",
    greeting: "Hola de nuevo, {{name}}",
    greetingSub: "Aquí tienes el estado de tu solicitud.",
    caseProgress: "Progreso de tu caso",
    phaseInfo: "Información",
    phaseDocs: "Documentos",
    phaseSubmit: "Envío",
    remaining: "pendientes",
    deadline: "Plazo de presentación: 30 de junio de 2026",
    detailsLabel: "Tus datos",
    docsLabel: "Documentos",
    viewChecklist: "Ver Lista de Documentos",
    contactTitle: "¿Necesitas ayuda?",
    contactSub: "Escríbenos por WhatsApp",
    submitInlineBannerTitle: "¿Estás listo para enviar tu solicitud?",
    submitInlineBannerBody:
      "Si has terminado de completar todas las secciones, avísanos y revisaremos y nos pondremos en contacto contigo pronto.",
    submitInlineBannerCta: "Enviar a {{advisor}}",
    submitInlineSending: "Enviando…",
    submitDoneHeading: "Enviado a {{advisor}} el {{date}}",
    submitDoneHeadingNoDate: "Enviado a {{advisor}}",
    submitDoneMessagePara1:
      "{{advisor}} está revisando tu solicitud y te enviará un mensaje si tiene preguntas o necesita alguna actualización. También te enviaremos una copia por correo electrónico.",
    submitDoneMessagePara2:
      "Por ahora has terminado. Si tienes preguntas, puedes responder al correo o volver aquí cuando quieras.",
    incomplete: "Sin completar",
    stepConfirm: "Confirmar datos",
    stepAsylum: "Pregunta de asilo",
    stepQualifying: "¿Cuál describe mejor tu situación actual?",
    stepSituation: "Tu situación",
    stepDocument: "Documento de identidad",
    stepPassportPersonal: "Pasaporte y datos personales",
    stepPersonal: "Información personal",
    stepCivil: "Estado civil y familia",
    stepAddress: "Dirección en España",
    stepConsent: "Acuerdo y firma",
    signSubmitLabel: "Firmar y enviar",
    signSubmitStatusPending: "Pendiente",
    signSubmitStatusComplete: "Completado",
    deadlineLabel: "Plazo límite",
    nextAction: "Continúa con tu solicitud",
    nextActionSub: "Completa la información que falta",
    nextUpload: "Sube tus documentos",
    nextUploadSub: "Tienes documentos pendientes de subir",
    progress: "Progreso",
    of: "de",
    contextBody: "Completa cada sección a tu ritmo — tu progreso se guarda automáticamente.",
    finishItems: "Por favor, completa los apartados que te faltan. Cuando termines, revisaré todo y lo prepararé para su presentación ante la oficina de inmigración española. Si tienes alguna pregunta, envíame una nota al final de esta página y te responderé pronto.",
    signAvailableLater: "Disponible al final",
    helpStuckTitle: "Estoy atascado/a",
    helpStuckSub: "Cuéntanos qué pasa y te responderemos pronto.",
    helpStuckSubExpanded: "{lawyer} te responderá pronto.",
    helpPlaceholder: "Escribe aquí...",
    helpSendButton: "Enviar",
    helpSentTitle: "Mensaje enviado",
    helpSentSub: "{lawyer} lo verá y te responderá pronto.",
    helpDefaultLawyer: "tu abogada",
  },
  upload: {
    scanTitle: "Escanear con tu móvil",
    scanSubtitle: "Detecta bordes y crea un PDF automáticamente",
    scanBtn: "Abrir escáner",
    appTitle: "Usar una app de escáner",
    appSubtitle: "Escanea las páginas y súbelas como PDF",
    appGoogle: "Google Drive — Escanear documento",
    appAdobe: "Adobe Scan — Gratis en App Store y Google Play",
    appCopyShop: "O ve a una copistería / locutorio cercano",
    fileTitle: "Subir archivo",
    fileSubtitle: "Ya tengo un PDF o foto lista",
    scanHeroTitle: "Escanea tu pasaporte aquí",
    scanHeroSubtitle: "Te guiamos página por página. Detectamos bordes y creamos el PDF por ti.",
    alternativesLabel: "Otras opciones",
    altApp: "Usar una app de escáner",
    altAppHint: "Adobe Scan o Google Drive (gratis)",
    altShop: "Ir a una copistería",
    altShopHint: "Pide que escaneen tu pasaporte completo a PDF",
    altUpload: "Ya tengo un archivo",
    altUploadHint: "Sube un PDF o foto que ya tengas",
    videoLabel: "30 seg · Cómo escanear tu pasaporte",
    browseFiles: "Buscar archivos",
    scanCriminalNote: "Escanea cada página del certificado. Incluye sellos y firmas.",
    scanDocTitle: "¿Necesitas escanear un documento?",
    scanDocSubtitle: "Usa nuestro escáner para documentos de varias páginas",
  },
  auth: {
    signIn: "Inicia sesión",
    subtitle: "Introduce tu número de teléfono para continuar.",
    phone: "Número de teléfono",
    sendCode: "Enviar código",
    otpIntroTitle: "Introduce el código que te enviamos",
    otpIntroBody: "Hemos enviado un código de un solo uso a tu teléfono.",
    enterCode: "Introduce el código",
    sixDigitCodePlaceholder: "Introduce el código de 6 dígitos",
    verify: "Verificar",
    resend: "Reenviar código",
    or: "o",
    google: "Continuar con Google",
    apple: "Continuar con Apple",
    privacyNote: "Al continuar, aceptas nuestra",
  },
  faq: {
    title: "¿Cómo puedo ayudarte?",
    about: "Sobre:",
    other: "Tengo otra pregunta",
    otherPlaceholder: "Escribe tu pregunta…",
    otherHelper: "Te responderemos por mensaje o correo.",
    send: "Enviar",
    sent: "¡Enviado! Tu asesor/a se pondrá en contacto contigo.",
    sentTo: "Enviado a",
    sentContact: "Te responderé por mensaje o correo.",
    sentTeam: "Enviado a nuestro equipo. Te responderemos pronto.",
    s1: {
      q1: "¿Puedo cambiar esta información más adelante?",
      a1: "Sí, puedes editar cualquier campo en cualquier momento antes de la presentación final. Nada queda bloqueado.",
      q2: "¿Debo incluir tildes en mi nombre?",
      a2: "Usa tu nombre exactamente como aparece en tu pasaporte o documento, incluyendo tildes y caracteres especiales.",
      q3: "¿Están seguros mis datos personales?",
      a3: "Tu información está cifrada y solo pueden acceder a ella tú y tu asesor/a asignado/a.",
    },
    s2: {
      q1: "¿Y si no sé mi fecha de nacimiento exacta?",
      a1: "Usa la fecha que figura en tu pasaporte o partida de nacimiento. Tu asesor/a puede ayudarte si solo tienes una fecha aproximada.",
      q2: "¿Qué nacionalidad debo introducir?",
      a2: "Introduce tu nacionalidad actual tal como aparece en tu pasaporte. Si tienes varias, usa la del documento que vas a presentar.",
      q3: "¿Qué significa el sexo 'Indeterminado'?",
      a3: "Elige esta opción si tu documento no especifica hombre o mujer — algunos pasaportes usan 'X' o dejan el campo en blanco.",
    },
    s3: {
      q1: "¿Por qué necesitan los nombres de mis padres?",
      a1: "El formulario oficial del gobierno requiere los nombres de pila de los padres. Puedes omitirlo por ahora — tu asesor/a puede ayudarte más tarde.",
      q2: "¿Y si no sé el nombre de alguno de mis padres?",
      a2: "Deja esos campos en blanco. Tu asesor/a te ayudará cuando revise tu expediente.",
    },
    s4: {
      q1: "¿Y si no tengo una dirección fija?",
      a1: "Introduce la dirección donde estás alojado/a actualmente, aunque sea temporal. Puedes actualizarla más adelante si cambia.",
      q2: "¿Necesito un padrón municipal?",
      a2: "No, el padrón no es obligatorio para esta solicitud. Se aceptan muchos otros tipos de documentos.",
      q3: "¿Qué significa 'Residente desde'?",
      a3: "Introduce el mes y año aproximados en que empezaste a vivir en tu dirección actual. Una estimación es perfectamente válida.",
    },
    s5: {
      q1: "¿Necesito un contrato de trabajo para solicitar?",
      a1: "No. La solicitud cubre tres vías: trabajo, reagrupación familiar o vulnerabilidad. Un contrato de trabajo es solo una opción.",
      q2: "¿Qué es un número NIE?",
      a2: "El NIE es el número de identificación de extranjero en España. Si no tienes uno, deja este campo vacío — es opcional.",
      q3: "¿Cuenta ser autónomo/a?",
      a3: "Sí. Una declaración de intención de trabajar como autónomo/a es suficiente en esta etapa.",
    },
    s6: {
      q1: "¿Se acepta un pasaporte caducado?",
      a1: "Sí. La ley española permite usar pasaportes caducados para esta solicitud. Sube el documento que tengas.",
      q2: "¿Y si perdí mi pasaporte?",
      a2: "Obtener un pasaporte nuevo es la mejor forma de acreditar tu identidad. Para la residencia, puedes usar facturas o extractos bancarios.",
      q3: "¿Y si no tengo ningún documento de identidad?",
      a3: "Contacta con el consulado de tu país para obtener documentos de viaje de emergencia. Tu asesor/a te orientará sobre las opciones disponibles.",
    },
    s7: {
      q1: "¿Qué documentos sirven como prueba?",
      a1: "Facturas, extractos bancarios, historial médico, contratos de alquiler, billetes de transporte — cualquier documento con tu nombre y una fecha en España.",
      q2: "¿Tengo que demostrar cada mes por separado?",
      a2: "No. Una combinación de documentos que cubra diferentes periodos a lo largo de 5 meses es suficiente.",
      q3: "¿Y si tengo muy pocos documentos?",
      a3: "No te rindas — se aceptan muchos tipos y tu asesor/a te ayudará a encontrar la mejor combinación. Sube lo que tengas.",
    },
    s8: {
      q1: "¿Puedo cambiar mis respuestas después de esto?",
      a1: "Sí. Puedes volver y editar cualquier sección en cualquier momento antes de la presentación oficial final.",
      q2: "¿Qué pasa después de guardar?",
      a2: "Tu asesor/a revisará tu expediente y te contactará si falta algo o necesita aclaración.",
    },
    s9: {
      q1: "¿A qué exactamente estoy dando mi consentimiento?",
      a1: "Consientes que tus datos se utilicen para evaluar tu caso y para que tu asesor/a se ponga en contacto contigo. Tus datos no se comparten con terceros.",
      q2: "¿Puedo retirar mi consentimiento más adelante?",
      a2: "Sí. Contáctanos en cualquier momento y dejaremos de procesar tus datos de inmediato.",
    },
  },
};

/* ─────────────────────────── ENGLISH ─────────────────────────── */
const EN: Translations = {
  header: {
    firm: "Global Mobility", help: "Need help?", saved: "Saved ✓", saving: "Saving…",
    close: "Close",
    deleteTitle: "Are you sure?",
    deleteBody1: "All your personal data, documents, and case history will be deleted. This action cannot be undone.",
    deleteBody2: "Your account will be deactivated immediately and your data will be deleted within 15 days.",
    deleteBody3: "If you change your mind, contact hola@globalmobility.law to reactivate your account before the deletion is completed.",
    deleteConfirm: "Yes, delete my account",
    deleteCountdown: "Yes, delete my account ({{n}})",
    cancel: "Cancel",
    addFamily: "Add family member",
    addFamilySoon: "Coming soon — contact your lawyer via WhatsApp to add family members.",
    privacy: "Privacy Policy",
    logout: "Log out",
    deleteAccount: "Delete my account",
  },
  exitModal: {
    title: "Your progress is saved!",
    body: "You can come back anytime and continue where you left off.",
    stay: "Continue with my application",
    leave: "Exit",
  },
  progress: {
    of: "of",
    steps: ["Confirm details", "Your situation", "Address", "Review & submit"],
    applyingFor: "Applying for",
  },
  help: {
    title: "Need help?",
    placeholder: "Describe your question or issue…",
    helper: "We'll reply by message or email.",
    send: "Send message",
    sent: "Message sent! We'll get back to you soon.",
    section: "Current section",
  },
  nav: {
    back: "Back",
    continue: "Continue",
    submit: "Continue",
    backToOrganizer: "Back to My Organizer",
    breadcrumbOrganizer: "Organizer",
    breadcrumbDocuments: "Documents",
  },
  footer: {
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    cookiePolicy: "Cookie Policy",
    legalNotice: "Legal Notice",
    copyright: "© 2026 Global Mobility Group LLC",
  },
  documentScanner: {
    title: "Scan document",
    desktopOnly: "To capture multiple pages and build one PDF, open this screen on your phone’s browser.",
    scannerBtn: "Scan",
    addPages: "Add pages",
    addAnotherPage: "Add another page",
    fromGallery: "From gallery",
    buildPdf: "Create PDF",
    processing: "Processing…",
    scanMergeSaving: "Saving your scan…",
    connectionProblemTitle: "Connection problem",
    connectionProblemBody:
      "We can't reach our server, likely because of a weak cellular signal. If you cancel now, your scans will be lost.",
    connectionProblemRetry: "Try again",
    connectionProblemCancel: "Cancel and lose scans",
    devicePdfFailureKicker: "PDF generation",
    devicePdfFailureTitle: "We couldn't create your PDF",
    devicePdfFailureBody:
      "Something went wrong while generating the file on your device. Your photos are still here. Try again, or close other browser tabs first if your phone is low on memory. Our team has been notified.",
    devicePdfFailureRetry: "Try again",
    devicePdfFailureCancel: "Cancel",
    pageLabel: "Page {{n}}",
    remove: "Remove",
    moveUp: "Move up",
    moveDown: "Move down",
    close: "Close",
    needOnePage: "Add at least one page photo.",
    errorGeneric: "We couldn't process those images. Try again with clearer photos.",
    multiPageHint: "Capture multiple pages and combine them into one PDF as framed.",
    cameraTitle: "Scan",
    cameraLoadingLibs: "Loading scanner…",
    cameraStarting: "Starting camera…",
    cameraTapToCapture: "Point at your document, hold steady, then tap the white button below.",
    cameraVideoNotReady: "Camera preview isn't ready. Close and try again.",
    cameraAlignHint: "Position the document inside the green frame",
    cameraAlignSubtitle:
      "Fill the green frame tightly with minimal empty margin. Keep fingers out; slight bends only—not heavily folded.",
    cameraAlignSubtitleSpread:
      "Multi-page mode: fit the document inside the green frame with an even margin on every side.",
    cameraFrameModeAria: "Guide shape",
    cameraFrameModeSingle: "One page",
    cameraFrameModeSpread: "Open spread",
    cameraDocDetected: "Document detected — tap to capture",
    cameraPermissionDenied: "Camera access denied. Enable it in your browser settings.",
    cameraStreamEnded:
      "The camera stopped — for example after the phone was idle or this tab was in the background. Tap Try again.",
    cameraPlayBlocked: "Couldn’t resume the camera preview. Tap Try again.",
    cameraRetryButton: "Try again",
    cameraNotFound: "No camera found on this device.",
    cameraInitError: "Could not start the camera.",
    captureError: "Could not capture the page.",
    cameraShutter: "Capture page",
    cameraPageCaptured: "Page saved — take another shot or tap Close.",
    flattenedBadge: "Flattened",
    flattenedBadgeHint: "Auto-cropped and flattened on this device.",
    previewThumbnailAria: "Enlarge preview",
    previewHint: "Check the photo is sharp and shows the full page.",
    previewRotate: "Rotate 90°",
    previewRotateAria: "Rotate this page 90 degrees clockwise",
    rotateError: "Couldn't rotate the image. Try another photo or pick one from your gallery.",
    previewRemovePage: "Remove this page",
    cornerAdjust: "Adjust corners & straighten",
    cornerAdjustAria: "Open corner adjustment to straighten and crop",
    cornerEditorTitle: "Straighten & crop",
    cornerEditorHint:
      "Drag each corner onto your document edges. We flatten perspective and trim empty background.",
    cornerEditorApply: "Apply",
    cornerEditorReset: "Reset corners",
    cornerEditorCancel: "Back",
    cornerEditorBusy: "Processing…",
    cornerEditorError: "Couldn't straighten this image. Try different corners or reset.",
    cornerEditorInvalidQuad: "Corners must form a valid quadrilateral inside the photo.",
    cornerHandleAria: "Corner",
    cornerEditorApplyContinue: "Apply & continue",
    cornerEditorDone: "Done",
    cornerEditorAddPage: "+ Add page",
    cornerEditorResetHeader: "Reset",
    passportCornerDragHint: "Drag the corners to match your passport edges.",
    passportCornerRetake: "Retake",
    passportIntroTitle: "Let's take a picture of your first page",
    passportIntroHelp:
      "Open your passport to the first page. If necessary, use a pointed object to help hold it open, and take a picture of both pages while open. Do not use your fingers.",
    passportTakePhoto: "Take photo",
    passportContinue: "Continue",
    passportSubmitPhotos: "Submit photo(s)",
    passportRetakePhoto: "Retake photo",
    passportReviewPrev: "Previous page",
    passportReviewNext: "Next page",
    passportReviewPageStatus: "{{current}} / {{total}}",
    passportPagesCapturedSummary: "{{n}} page(s) captured.",
    passportSheetReviewHint:
      "After each shot you’ll straighten corners, then you can take another. When you’re done with all pages, tap Review / adjust, then Create PDF below.",
    passportReviewAdjust: "Review / adjust",
    passportSheetMinimalHint:
      "Your photos are still here. Create PDF to finish, or Cancel to go back and choose upload or scan again.",
    passportPreviewReviewHint:
      "Tap Edit to straighten and crop. If the photo is wrong, use Retake (top left) on that screen. Tap ? (top right) for photo examples. Drag thumbnails to reorder. Create PDF when finished.",
    reviewScreenTitle: "Review",
    reviewPageOf: "Page {{current}} of {{total}}",
    reviewPagesSectionLabel: "PAGES",
    reviewReorderHint:
      "Swipe the row to scroll thumbnails. Touch and drag the ⠿ handle on a thumbnail to reorder.",
    reviewReorderHoldA11y: "Drag handle — press and drag to reorder pages.",
    reviewInsertPage: "Add page",
    reviewEdit: "Edit",
    passportReviewView: "View",
    reviewDeletePageAria: "Delete page",
    reviewEditMenuTitle: "Page actions",
    reviewEditRemove: "Remove page",
    reviewDeleteConfirmTitle: "Delete this page?",
    reviewDeleteConfirmBody:
      "This page will be removed from your scan. You can capture it again afterward.",
    reviewDeleteConfirmDelete: "Delete",
    reviewDeleteConfirmCancel: "Cancel",
    reviewLeaveConfirmTitle: "Discard your photos?",
    reviewLeaveConfirmBody: "You'll lose all the pages you've captured so far.",
    reviewLeaveConfirmStay: "Keep scanning",
    reviewLeaveConfirmDiscard: "Discard photos",
    passportLowPageWarnTitle: "Are all pages included?",
    passportLowPageWarnBody:
      "We usually need photos of every page in your passport, including blank pages. It looks like some pages may be missing.",
    passportLowPageWarnReview: "Review pages",
    passportLowPageWarnAnyway: "Create PDF anyway",
    passportCameraHeader: "Passport",
    passportCameraHintNext: "Fit the open spread inside the green frame, then take another photo when ready.",
    passportCaptureToast: "Photo saved — review the crop.",
    passportCancel: "Cancel",
    passportPageTitle: "PAGE {{n}}",
    passportViewfinderPill:
      "Open the passport flat and capture both pages.",
    passportViewfinderPillSubsequent:
      "Capture the next spread in one shot.",
    passportCaptureFooterHint:
      "Hold the pages flat against a surface. You can adjust the edges in the next step.",
    passportExamplesAria: "Photo examples",
    passportViewExamplesLink: "View photo examples",
    passportCameraHelpAria: "Help capturing passport",
    passportFlashAria: "Toggle flash",
    passportHelpGotIt: "Got it",
  },
  help: {
    empadronamientoTerm: "empadronamiento",
    empadronamiento: "This is your municipal registration certificate. It proves you live in a Spanish municipality. Request it at your town hall, usually with an appointment. Ask for the historical certificate — it shows all your addresses and dates.",
    vidaLaboralTerm: "vida laboral",
    vidaLaboral: "This is an official Social Security document showing your entire employment history in Spain. It\'s free and you can get it at sede.seg-social.gob.es, by SMS to 600 123 456, or in person at a Social Security office.",
    altaAutonomoTerm: "autónomo registration",
    altaAutonomo: "This is the document proving your registration as a self-employed worker in Spain. It\'s obtained through the Tax Agency and shows the exact date you started your activity.",
    modelo130Term: "modelo 130/303",
    modelo130: "These are quarterly tax filings for self-employed workers. Modelo 130 is for income tax (IRPF) and 303 is for VAT (IVA). They can be downloaded from the Tax Agency\'s electronic office.",
    libroFamiliaTerm: "family book",
    libroFamilia: "This is an official document issued by the Civil Registry proving marriage and children. If you married or had children in another country, you\'ll need the equivalent apostilled and translated.",
    apostilladosTerm: "apostilled",
    apostillados: "An apostille is an official stamp that validates foreign documents for international use, under the Hague Convention. It\'s obtained from the competent ministry or authority in the country that issued the document. If your country is not part of the Hague Convention, you need consular legalization instead.",
    tarjetaRojaTerm: "red card",
    tarjetaRoja: "This is the document proving you\'ve applied for international protection (asylum) in Spain. It\'s renewed every 6 months while your application is pending. The white card is similar but for renewals.",
    declaracionJuradaTerm: "sworn declaration",
    declaracionJurada: "This is a signed declaration before a notary stating you have no criminal record. It\'s only accepted if you can\'t obtain the official certificate for justified reasons (war, lack of diplomatic relations, etc.).",
  },
  validation: {
    required: "This field is required.",
    invalidDate: "Invalid date.",
    invalidEmail: "Invalid email address.",
    invalidPhone: "Invalid phone number.",
    invalidNie: "Enter a valid NIE.",
    invalidPostal: "Invalid postal code (5 digits).",
    postalProvinceMismatch: "The first two digits of the postal code do not match the province.",
    invalidAddressStreet: "Street: 1–100 characters; letters, digits, spaces, accents, periods, and commas only.",
    invalidAddressStreetNumber: "Number: 1–10 characters; letters, digits, or S/N (with /).",
    invalidAddressFloorDoor: "Door: up to 10 characters; letters and digits only.",
    invalidAddressCity: "City: 1–40 characters; letters, spaces, hyphens, and accents only.",
    minLength: "Too short.",
    invalidMonth: "Month must be between 01 and 12.",
    invalidDay: "This day is not valid for that month.",
    futureDate: "The date cannot be in the future.",
    pastExpiryDate: "Expiry must be today or a future date.",
    yearOutOfRange: "The year is outside the allowed range.",
    futureMonthYear: "This date cannot be after the current month.",
    invalidYear: "The year is not valid.",
  },
  deadline: { label: "Application deadline", date: "June 30, 2026" },
  optional: "Optional",
  returning: {
    greeting: "Welcome back,",
    progress: "Your application progress",
    continue: "Continue where you left off",
    restart: "Review from the beginning",
  },
  landing: {
    greeting: "Hi",
    firstVisitMessage: "Let\'s get your application started. It only takes about 10 minutes.",
    firstVisitMessageWithAdvisor: "I\'ll be helping you with your application. It only takes about 10 minutes.",
    thisIs: "this is",
    autoSave: "You can leave and come back anytime. Your work is saved automatically.",
    advisor: "Your advisor:",
    getStarted: "Get started",
    continue: "Continue",
    reviewFromStart: "Review from the beginning",
    progress: {
      moreThan3: "You\'re making good progress — let\'s keep going.",
      exactly3: "You\'re well on your way — just 3 sections left.",
      exactly2: "Almost there — just 2 sections left.",
      exactly1: "Almost there — just 1 section left.",
      allDone: "Everything looks complete — let\'s review and finish up.",
    },
    procedureBadge: "Extraordinary Regularization Spain 2026",
    videoLength: "1 min · Subtitles available",
    skipVideo: "Skip video",
    familyMenuHint: "You can add family members anytime from the ☰ menu",
    purpose: "I\'ll walk you through everything you need for your residence application. We\'ve organized everything into simple sections, just work through them at your own pace and upload your documents along the way. I\'ll take care of the rest. Don\'t worry if you don\'t have everything right now, your answers are saved automatically so you can come back anytime and pick up exactly where you left off.",
    purposeShort: "I\'ll guide you through everything you need for your residency application. Your answers are saved automatically, so you can come back to them whenever you want.",
    purposeExpanded: "We\'ve organized everything into simple sections. Just go at your own pace and upload your documents along the way. I\'ll take care of the rest. You may not have all your documents ready yet, and that\'s completely fine. Many people need to request documents like criminal records from their consulate or a padrón from their town hall, which can take time. Just fill in what you can now and come back when you have more. Nothing is lost, you can pick up right where you left off.",
    more: "More",
    less: "Less",
    familyInfo: "If you are applying as a family, each person over 18 needs their own application. Minors are included on the application of their parent or legal guardian. You can add family members anytime from your profile in the upper right corner.",
    familyInterestText: "Applying with family members? Let us know and keep going with your application. We'll reach out about the family part.",
    familyInterestBtn: "I'm interested in a family application",
    familyInterestConfirm: "Got it, we will be in touch",
    familyMenuHintBold: "",
    defaultAdvisorName: "Ana Travieso",
    followInstagram: "@anatraviesoabogada",
    consentIntro: "Before we start, I need your OK to work with your information.",
    consentLabel: "I agree to the",
    consentPrivacyLink: "Privacy Policy",
    consentSubtext: "(It\'s a legal thing — your data is safe with us.)",
  },
  section1: {
    title: "Confirm your details",
    title_rep: "Confirm the applicant's details",
    subtitle_applicant: "Please review that your information is correct before continuing.",
    subtitle_rep: "Please review that the applicant's information is correct.",
    isCorrect: "Is this correct?",
    confirmBtn: "Yes, continue",
    editLabel: "Edit",
    firstName: "First name", firstNamePlaceholder: "Mary",
    lastName: "First last name", lastNamePlaceholder: "Smith",
    segundoApellido: "Second last name", segundoApellidoPlaceholder: "Johnson",
    segundoApellidoHelper: "Optional if not on the document.",
    email: "Email", emailPlaceholder: "email@example.com",
    phone: "Mobile number", phonePlaceholder: "600 000 000",
    voiceTitle: "Let\'s start with the basics.",
    voiceTitle_rep: "Let\'s start with the applicant\'s basics.",
    fillingForSelf: "Are you filling this out for yourself?",
    forMyself: "Yes, for myself",
    forSomeoneElse: "No, for someone else",
    repNote: "Perfect. First we\'ll confirm your details, then those of the person you\'re helping.",
    repRelationship: "Your relationship to the applicant",
    repRelationshipPlaceholder: "Family member, friend, social worker...",
    repRelSelectPlaceholder: "Select your relationship",
    repRelFamily: "Family member",
    repRelSpouse: "Spouse / Partner",
    repRelFriend: "Friend",
    repRelSocialWorker: "Social worker",
    repRelLawyer: "Lawyer",
    repRelNGO: "NGO / Organization",
    repRelOther: "Other",
    applicantTitle: "Applicant\'s details",
    applicantFirstName: "Applicant\'s first name",
    applicantFirstNamePlaceholder: "First name",
    applicantLastName: "Applicant\'s first last name",
    applicantLastNamePlaceholder: "First last name",
    applicantSegundoApellido: "Second surname",
    applicantSegundoApellidoPlaceholder: "Second surname",
    applicantEmail: "Applicant\'s email",
    applicantEmailPlaceholder: "If they have one",
    applicantPhone: "Applicant\'s phone",
    applicantPhonePlaceholder: "If they have one",
    repSectionLabel: "Your details",
    sendPhoneOtp: "Send code",
    verifyPhoneOtp: "Verify",
    otpCodePlaceholder: "6-digit code",
    phoneVerifiedBadge: "Verified",
    phoneOtpHelper:
      "We’ll send a code by SMS (same as sign-up) to confirm this number and save it to your profile.",
    phoneOtpSendError: "Could not send the code. Please try again.",
    phoneOtpCodeHint: "Enter the code we sent you.",
    phoneOtpSixDigitsHint: "Enter all 6 digits of the code.",
    phoneAlreadyRegistered:
      "This number is already registered to another account. Use a different mobile number.",
  },
  section2: {
    title: "Personal information",
    title_rep: "Applicant's personal information",
    subtitle_applicant: "Complete your personal details as they appear on your official document.",
    subtitle_rep: "Complete the applicant's personal details.",
    dob: "Date of birth", dobHelper: "DD / MM / AAAA",
    sex: "Sex",
    sexOptions: { male: "Male", female: "Female", undetermined: "Undetermined" },
    nationality: "Nationality", nationalityPlaceholder: "Search nationality…",
    countryOfBirth: "Country of birth", countryOfBirthPlaceholder: "Search country…",
    placeOfBirth: "Place of birth", placeOfBirthPlaceholder: "e.g. Casablanca",
    placeOfBirthHelper: "City or town where you were born.",
    voiceTitle: "Now, some personal details as they appear on your document.",
    voiceTitle_rep: "Now, some personal details as they appear on the applicant\'s document.",
  },
  section2e: {
    stepLabel: "Your situation",
    voiceTitle: "Tell us about your situation so we can guide your application.",
    asylumRoutingTitle: "Have you applied for asylum in Spain?",
    asylumRoutingSubtitle: "We mean international protection, filed with the Spanish police before January 1, 2026.",
    asylumRoutingYesTitle: "Yes",
    asylumRoutingYesDescription: "I have applied for asylum in Spain.",
    asylumRoutingNoTitle: "No",
    asylumRoutingNoDescription: "I have not applied for asylum in Spain.",
    qualifyingTitle: "Which best describes your current situation?",
    qualifyingSubtitle: "Pick the option that fits your situation best.",
    qualifyingWorkTitle: "Through work",
    qualifyingWorkDescription: "I work in Spain, have a job offer, or plan to work on my own.",
    qualifyingFamilyTitle: "Through family",
    qualifyingFamilyDescription: "I live with my children or my parents in Spain.",
    qualifyingVulnerabilityTitle: "Through a vulnerability situation",
    qualifyingVulnerabilityDescription: "I can get a certificate from social services or a support organization.",
    yes: "Yes",
    no: "No",
    arrivalDate: "When did you arrive in Spain?",
    arrivalDateHelper: "Approximate arrival date. Doesn\'t need to be exact.",
    submissionFixed: "Your application can be submitted starting April 1, 2026.",
    submissionFrom: "Your application can be submitted starting {{date}}.",
    hardStopArrival: "The extraordinary regularization requires presence in Spain before January 1, 2026. But don\'t worry, there may be other options for you.",
    hardStopTooLate: "With this arrival date, the required 5 months would fall after the June 30, 2026 deadline. But there may be other paths available to you.",
    validPermit: "Do you currently hold a valid residence permit in Spain?",
    hardStopPermit: "If you already have a valid residence permit, you probably need a renewal or modification instead. I can help you figure that out.",
    asylum: "Did you apply for asylum or international protection in Spain before January 1, 2026?",
    helpAsylum: "Check your asylum receipt or tarjeta roja. The date on it tells you when you applied.",
    oarLabel: "Asylum case number (OAR)",
    oarPlaceholder: "e.g. OAR/2024/12345",
    oarHelper: "You'll find this on your asylum receipt or tarjeta roja. If you don't have it right now, you can add it later.",
    subRouteLabel: "Which best describes your situation?",
    subRouteWork: "I have work or a job offer",
    subRouteFamily: "I live with my family in Spain",
    subRouteVulnerability: "I'm in a vulnerable situation",
    subRouteWorkDesc: "I've worked in Spain, I'm currently working, or I have a job offer.",
    subRouteFamilyDesc: "I live in Spain with my spouse, children, or other family members.",
    subRouteVulnerabilityDesc: "I'm in a difficult situation and need support from social services.",
    workHistoryLabel: "What best describes your work situation?",
    workHistoryWorked: "I've worked before in Spain",
    workHistoryCurrent: "I have a current job",
    workHistoryOffer: "I have a job offer",
    workTypeLabel: "Employed or self-employed?",
    workTypeEmployed: "Employed",
    workTypeSelfEmployed: "Self-employed",
    selfEmploymentActivityLabel: "Describe your activity",
    selfEmploymentActivityPlaceholder: "e.g., delivery rider, cook, graphic designer",
    selfEmploymentActivityHint: "Required for the EX-32 sworn declaration of intent to engage in self-employed activity.",
    criminalRecord: "Do you have a criminal record in the last five years?",
    helpCriminal:
      "Include convictions or pending criminal matters in any country in the last five years, not only Spain. If you answer No, we will ask for a criminal record certificate in the document flow.",
    previousApplication: "Have you had a previous immigration application in Spain?",
    helpPreviousApplication:
      "Include residence permits, arraigo, or other immigration applications filed in Spain, even if they were denied or closed.",
    callbackBtn: "I\'d like to explore options",
    callbackConfirm: "✓ We\'ll be in touch soon",
    hasPassport: "Do you have a valid passport?",
    hasPassportHelp: "A passport from your country of origin. It doesn\'t matter if it\'s expired or expires soon.",
    noPassportTitle: "No problem, I\'ll handle it.",
    noPassportBody:
      "If you don\'t have a passport, I\'ll prepare an official declaration (Anexo I-2) and sign it on your behalf as your authorized representative. You won\'t need to do anything extra.",
    helpPermit:
      "This program is for people without a current residence permit. If you have one, we can help with renewals and modifications instead.",
    permitOtherServices: "Learn about other services",
    permitNotSure: "Not sure? Continue anyway and I\'ll review your case.",
    soon: "Soon",
    showOtherOptions: "Show other options",
    scenario_1_label: "This is my first application",
    scenario_1_h: "",
    scenario_2_label: "A child born in Spain",
    scenario_2_h: "",
    scenario_3_label: "A child not born in Spain",
    scenario_3_h: "",
    scenario_4_label: "A family member",
    scenario_4_h: "",
    scenario_7A_label: "I'm working or I have a job offer",
    scenario_7A_h: "",
    scenario_7B_label: "I live with my family in Spain",
    scenario_7B_h: "",
    scenario_7C_label: "I have a vulnerability report",
    scenario_7C_h: "",
    scenario_8_label: "A child born in Spain",
    scenario_8_h: "",
    scenario_9_label: "A child not born in Spain",
    scenario_9_h: "",
    scenario_10_label: "A family member",
    scenario_10_h: "",
    extensionGroup_da20: "I'm renewing my residence authorization",
    familyGroup_label: "I'm applying for a family member",
    scenario_ext_jobSearch_label: "I'm looking for work",
    scenario_ext_jobSearch_h_da20: "",
    scenario_ext_integration_label: "I have an integration report",
    scenario_ext_integration_h_da20: "",
    scenario_ext_illness_label: "I have an illness",
    scenario_ext_illness_h_da20: "",
    scenario_ext_disability_label: "I have a disability",
    scenario_ext_disability_h_da20: "",
    scenario_ext_retirement_label: "I'm retired",
    scenario_ext_retirement_h_da20: "",
  },
  section3: {
    title: "Civil & family",
    title_rep: "Applicant's civil & family",
    subtitle_applicant: "A couple of quick questions about your family status.",
    subtitle_rep: "A couple of quick questions about the applicant's family status.",
    civilStatus: "Civil status",
    civilOptions: { single: "Single", married: "Married", widowed: "Widowed", divorced: "Divorced", separated: "Separated" },
    fatherName: "Father's first name", fatherNamePlaceholder: "John",
    fatherNameHelper: "No surnames",
    fatherFirst: "First name",
    fatherLast1: "Last name",
    fatherLast2: "Second last name",
    fatherUnknown: "I don't know his name",
    parentNotAvailable: "Not available",
    motherName: "Mother's first name", motherNamePlaceholder: "Mary",
    motherNameHelper: "No surnames",
    motherFirst: "First name",
    motherLast1: "Last name",
    motherLast2: "Second last name",
    motherUnknown: "I don't know her name",
    parentsNote: "Parents' details are optional.",
    spouseQuestion: "Do you have a spouse or registered partner?",
    spouseHelp: "Answer yes if you are married or have a legally registered partner.",
    spouseDetailsLabel: "Spouse details",
    spouseFirstName: "First name",
    spouseLastName: "Last name",
    spouseSecondLastName: "Second last name",
    spouseDob: "Date of birth",
    spouseNationality: "Nationality",
    spouseLegalStatus: "Do they have legal status in Spain?",
    spouseLegalStatusHelp: "If your spouse has an NIE or legal residency permit in Spain, answer yes.",
    spouseNie: "NIE or passport number",
    childrenQuestion: "Do you have children?",
    childrenHelp: "Include biological, adopted, or legally dependent children.",
    childHeader: "Child",
    addChild: "Add child",
    childFirstName: "First name",
    childLastName: "Last name",
    childSecondLastName: "Second last name",
    childDob: "Date of birth",
    childNationality: "Nationality",
    childLivesInSpain: "Lives in Spain with you?",
    childLivesInSpainHelp: "Check yes if this child currently lives in Spain with you.",
    childInSchool: "Enrolled in school in Spain?",
    childInSchoolHelp: "Check yes if this child is enrolled in a school in Spain.",
    removeChild: "Remove child",
    dependentParentsQuestion: "Do you have parents in your care living with you?",
    dependentParentsHelp: "Parents who depend on you financially and live with you.",
    parentHeader: "Parent",
    addParent: "Add parent",
    parentFirstName: "First name",
    parentLastName: "Last name",
    parentDob: "Date of birth",
    parentNationality: "Nationality",
    removeParent: "Remove parent",
    sexLabel: "Sex",
    sexPlaceholder: "Select...",
    sexMale: "Male",
    sexFemale: "Female",
    sexUndetermined: "X / undetermined",
    unknownLabel: "Not available",
    dependentsQuestion: "Do you have minor children or dependent family members living with you in Spain?",
    dependentsHelper: "Includes children under 18, adult children with disabilities, or parents in your care.",
    voiceTitle: "A couple of quick questions about your family status.",
    voiceTitle_rep: "A couple of quick questions about the applicant's family status.",
  },
  section4: {
    title: "Address",
    title_rep: "Applicant's address",
    subtitle_applicant: "Where do you live in Spain?",
    subtitle_rep: "Where does the applicant live in Spain?",
    streetType: "Street type",
    streetTypePlaceholder: "Select street type",
    streetTypeOptionStreet: "Street",
    streetTypeOptionAvenue: "Avenue",
    streetTypeOptionSquare: "Square",
    streetTypeOptionRoad: "Road",
    streetTypeOptionPassage: "Passage",
    streetTypeOptionOther: "Other",
    street: "Address",
    streetPlaceholder: "Street name",
    streetNumber: "No.",
    streetNumberPlaceholder: "12",
    floorPiso: "Floor",
    floorPisoPlaceholder: "—",
    floorDoor: "Door",
    floorDoorPlaceholder: "A, B",
    city: "Locality",
    cityPlaceholder: "City or town",
    province: "Province",
    provincePlaceholder: "Select province",
    municipality: "Municipality",
    municipalityPlaceholder: "Select municipality",
    locality: "Locality",
    localityPlaceholder: "Select locality",
    postalCode: "Postcode",
    postalCodePlaceholder: "08001",
    country: "Country",
    countryPlaceholder: "Spain",
    residenceSince: "Since when have you lived here?",
    residenceSinceHelper: "Approximate month and year you started living at this address.",
    residenceSincePlaceholderMonth: "MM",
    residenceSincePlaceholderYear: "YYYY",
    residenceSinceOptional: "Optional",
    voiceTitle: "Where are you living in Spain?",
    voiceTitle_rep: "Where is the applicant living in Spain?",
  },
  section5: {
    title: "Your situation in Spain",
    title_rep: "Applicant's situation in Spain",
    subtitle_applicant: "Tell us about your current situation in Spain.",
    subtitle_rep: "Information about the applicant's current situation.",
    arrivalDate: "When did you arrive in Spain?", arrivalDateHelper: "Day / Month / Year of approximate arrival.",
    wantsToWork: "Do you want to work in Spain?",
    wantsToWorkOptions: { yes: "Yes", no: "No" },
    workSituation: "What is your situation?",
    workSituation_rep: "What is their situation?",
    workSituationOptions: { employed: "Have a job", looking: "Looking for work", selfemployed: "Self-employed" },
    nie: "NIE", niePlaceholder: "X1234567A", nieHelper: "Optional. Expired is fine",
    nieAsk: "Don't have one? Ask us",
    voiceTitle: "A few questions about your situation.",
    voiceTitle_rep: "A few questions about the applicant's situation.",
  },
  section6: {
    title: "Your passport",
    title_rep: "The applicant's passport",
    subtitle:
      "Take a photo of the data page or upload a PDF. We'll fill in your details automatically.",
    subtitle_applicant: "Information about your identity document or passport.",
    subtitle_rep: "Information about the applicant's identity document.",
    photoInstructions:
      "Take a photo or upload an image of the page with your photo and personal details. If you already have a full PDF of your passport, you can upload that here too. Don't try to photograph each page separately.",
    photoHint:
      "You can correct any details afterwards. You'll upload the full passport (all pages) later.",
    passportName: "Name as it appears on your passport",
    docType: "Document type",
    docTypeOptions: {
      passport: "Passport",
      nationalId: "ID",
      travelDoc: "Travel doc",
      cedulaInscripcion: "Registration",
      other: "Other",
    },
    docTypeOtherLabel: "Specify document type",

    docTypeOtherPlaceholder: "e.g. refugee document",
    passportNumber: "Passport number", passportNumberPlaceholder: "ABC123456",
    passportNumberHelper: "As shown on your passport.",
    docExpiry: "Expiry date", docExpiryHelper: "Day / Month / Year",
    docExpiryAsk: "Not sure? Ask us",
    expiredNote: "Expired documents are accepted.",
    docUpload: "Upload your document",
    docUploadHelper: "Take a photo of the main page of your passport (the one with your photo and personal details). Place it on a flat surface in good light.",
    uploadBtn: "Choose file", cameraBtn: "Use camera",
    uploadedLabel: "Attached file:", changeFile: "Change",
    nie: "NIE number",
    niePlaceholder: "X1234567A",
    nieHelper: "Optional. Expired is fine",
    nieAsk: "Don\'t have one? Ask us",
    docName: "Name as it appears on your document",
    docNameHelper: "Confirm this matches your identity document.",
    seeExample: "See examples",
    photoGuideTitle: "Photo requirements",
    photoGood: "Flat, good light, all text visible",
    photoGoodCaption: "Correct — flat, good light, all text readable, all four corners visible",
    photoBad1: "Blurry",
    photoBad2: "Glare or shadow",
    photoBad3: "Cropped or fingers visible",
    carouselBad1: "Blurry — text can\'t be read. Hold your phone steady and focus before taking the photo.",
    carouselBad2: "Glare or shadow — part of the text is covered. Use natural light without direct reflections.",
    carouselBad3: "Cropped — part of the document is missing. Make sure all four corners are fully visible.",
    carouselBad4: "Fingers visible — your fingers are covering part of the document. Hold it by the edges or lay it flat on a surface.",
    goodExampleTitle: "Good example",
    badExampleTitle: "Bad examples",
    photoGotIt: "Got it",
    voiceTitle: "Now your identity document.",
    voiceTitle_rep: "Now the applicant\'s identity document.",
  },
  section7: {
    title: "Proof of stay",
    title_rep: "Applicant's proof of stay",
    subtitle_applicant: "Which documents do you have to prove your time in Spain?",
    subtitle_rep: "Which documents does the applicant have to prove their time in Spain?",
    checklistTitle: "Select the documents you have",
    checklistHelper: "You can select multiple. All are optional.",
    docTypes: { utility: "Utility bill", bank: "Bank statement", medical: "Medical record", rental: "Rental agreement", transport: "Transport ticket", other: "Other" },
    uploadTitle: "Upload one document", uploadHelper: "You can add more later.",
    uploadBtn: "Choose file", cameraBtn: "Use camera",
    uploadedLabel: "Attached file:", changeFile: "Change",
    allOptional: "All documents are optional at this stage.",
    stayUploadAsk: "What documents work? Ask us",
    passportUploadGuideLabel: "How to upload your full passport?",
    passportUploadGuide: "We need all pages of your passport in a single PDF, including blank ones. Two easy options:\n\n📱 Use a free app like Adobe Scan or Google Drive to scan all pages into a PDF from your phone.\n\n🏪 Visit your nearest copistería or locutorio and ask them to scan your full passport to PDF. They can email it to you or save it to your phone.",
    voiceTitle: "Last step — proof you\'ve been in Spain.",
    voiceTitle_rep: "Last step — proof the applicant has been in Spain.",
    asylumTitle: "Your asylum documentation",
    asylumInstructions: "As an asylum applicant, you don\'t need to prove 5 months of stay. Just upload your asylum documentation.",
    longTermTitle: "Proof you\'ve been in Spain",
    longTermInstructions: "You\'ve been in Spain for over 5 years, so you just need a few recent documents to confirm your continued residence.",
    standardTitle: "Proof you\'ve been in Spain",
    standardInstructions: "Upload documents that show you\'ve been living in Spain. Any document with your name and a date works.",
    priorDenialTitle: "Your documents",
    priorDenialInstructions: "Since you had a previous process, we need a few specific documents.",
    asylumDocTypeLabel: "Asylum document type",
    asylumDocTypes: { receipt: "Asylum receipt", resolution: "Asylum resolution", other: "Other asylum document" },
    asylumUploadLabel: "Upload your asylum document",
    asylumOptionalTitle: "Do you have other documents showing your time in Spain?",
    asylumOptionalHelper: "Not required but can strengthen your case.",
    asylumOptionalExpand: "Add additional documents",
    asylumOptionalCollapse: "Hide additional documents",
    denialSectionATitle: "Documents from previous process",
    denialResolutionLabel: "Previous denial resolution",
    denialCriminalLabel: "Criminal records from prior process",
    denialCriminalNote: "Even if expired, these are valid since you remained in Spain.",
    denialSectionBTitle: "Proof of stay in Spain",
    denialSectionBHelper: "We need documents covering at least 5 months.",
    addDocBtn: "+ Add document",
    docTypeLabel: "Document type",
    docTypeOptions: { utility: "Utility bill", bank: "Bank statement", medical: "Medical record", rental: "Rental agreement", transport: "Transport ticket", other: "Other" },
    monthCoverageLabel: "Months covered",
    monthCoverageHelper: "Each document you upload can cover one or more months.",
    uploadDocLabel: "Upload document",
    removeDoc: "Remove",
    docEntryTitle: "Document {{n}}",
    monthLabel: "What month is this from?",
    tierLabel1: "Strongest",
    tierLabel2: "Strong",
    tierLabel3: "Supporting",
    tierLabel4: "Supplementary",
    tieredDocTypes: {
      padron: "Padrón / Municipal registration",
      rental: "Rental contract",
      medical: "Medical record",
      school: "Children\'s school enrollment",
      bank: "Bank statement",
      utility: "Utility bill (electricity, water, gas, internet)",
      payslip: "Payslip or work contract",
      transfer: "Money transfer receipt",
      course: "Course or training certificate",
      transport: "Transport ticket",
      police: "Police report",
      official: "Official letter or notification",
      other: "Other",
    },
    monthsCovered: "Documents uploaded covering {{n}} of 5 months",
    allMonthsCovered: "✓ All 5 months covered",
    infoNote: "Upload whatever documents you have. You don\'t need them all now, you can add more anytime. The padrón (municipal registration) is the most important document — if you have it, upload it first.",
    videoLabel: "1 min · What documents count",
    // Flat alias keys
    title: "Proof you\'ve been in Spain",
    voiceTitle: "Last step — proof of your time in Spain.",
    instructions: "Upload documents that show you\'ve been living in Spain. Any document with your name and a date works.",
    titleLongTerm: "Proof you\'ve been in Spain",
    instructionsLongTerm: "You\'ve been in Spain for over 5 years, so you just need a few recent documents to confirm your continued residence.",
    titleAsylum: "Your asylum documentation",
    instructionsAsylum: "As an asylum applicant, you don\'t need to prove 5 months of stay. Just upload your asylum documentation.",
    asylumOptionalNote: "Not required but can strengthen your case.",
    asylumReceipt: "Asylum receipt",
    asylumResolution: "Asylum resolution",
    asylumOther: "Other asylum document",
    titlePriorDenial: "Your documents",
    instructionsPriorDenial: "Since you had a previous process, we need a few specific documents.",
    denialResolution: "Previous denial resolution",
    priorCriminalRecords: "Criminal records from prior process",
    priorCriminalNote: "Even if expired, these are valid since you remained in Spain.",
    docTypePadron: "Padrón / Municipal registration",
    docTypeRental: "Rental contract",
    docTypeMedical: "Medical record",
    docTypeSchool: "Children\'s school enrollment",
    docTypeBank: "Bank statement",
    docTypeUtility: "Utility bill (electricity, water, gas, internet)",
    docTypePayslip: "Payslip or work contract",
    docTypeTransfer: "Money transfer receipt",
    docTypeCourse: "Course or training certificate",
    docTypeTransport: "Transport ticket",
    docTypePolice: "Police report",
    docTypeOfficial: "Official letter or notification",
    docTypeOther: "Other",
    acceptedWidget: {
      title: "What documents count as proof of residence?",
      subtitleOpen: "Make sure every document includes your name and a date. Documents without a name or date are not valid.",
      tier1Label: "Tier 1 — Strongest evidence",
      tier2Label: "Tier 2 — Strong evidence",
      tier3Label: "Tier 3 — Supporting evidence",
      descPadron: "Empadronamiento certificate from the town hall where you live. The historic empadronamiento is the strongest single document because it shows your full registration dates, not just the current day. On its own it is not always sufficient, but it is the foundation of almost every strong application.",
    descRental: "Rental contract in your name for a home in Spain. Stronger if registered with the autonomous community's housing registry, or if accompanied by monthly payment receipts that confirm continuity.",
    descMedical: "Records of visits to a doctor, hospital, or public health center with a date and your name. Health cards, medical reports, prescriptions, and public-system appointments all count. Especially useful evidence because it is hard to fabricate.",
    descSchool: "Enrollment, attendance, or school certificates for you or your children at a Spanish educational institution. Includes nurseries, primary and secondary schools, accredited academies, and universities.",
    descBank: "Statements from a Spanish bank account showing your transactions over time. The more months covered, the stronger the evidence. Accounts with regular Spanish activity are the most convincing.",
    descUtility: "Bills for electricity, water, gas, internet, or mobile service in your name at a Spanish address. Recurring bills across recent months help demonstrate continuous presence.",
    descPayslip: "Payslips or employment contracts showing the employer, date, and your name. If you have a formal work history in Spain, these documents also support the work pathway of the regularización, not just residence proof.",
    descTransfer: "Receipts for money transfers sent or received from Spain (Western Union, MoneyGram, Wise, Ria, etc.). Each receipt should show your name, the Spanish location, and the date.",
    descCourse: "Certificates, enrollments, or diplomas from courses, language academies, or training programs in Spain. Includes Spanish-language courses, vocational training, and accredited private programs.",
    descTransport: "Monthly transport pass, Renfe tickets, reloaded transit cards, flight tickets, or bus tickets. Each ticket must include your name and a date to be valid.",
    descPolice: "Police reports or any police document that identifies you by name and carries a date. Includes reports you filed as a victim or witness, not only ones that name you as a subject of investigation.",
    descOfficial: "Letters or notifications from any Spanish public administration (Social Security, tax office, town hall, autonomous community, courts, etc.) addressed to you. Official correspondence sent to a Spanish address is solid evidence that you live there.",
    descOther: "Any other document with your name and a date that demonstrates your presence in Spain: purchase receipts, event tickets, religious certificates, letters from associations, etc.",
    },
    addDocument: "+ Add document",
    whatMonth: "What month is this from?",
    uploadNote: "Upload whatever documents you have. You don\'t need them all now, you can add more anytime. The padrón (municipal registration) is the most important document — if you have it, upload it first.",
    coverageLabel: "Documents uploaded covering {{n}} of 5 months",
    coverageComplete: "✓ All 5 months covered",
    criminalDocType: "Criminal record documentation",
    criminalTitle: "Criminal record documentation",
    criminalInstructions: "Upload any documentation about your record — cancellation certificates, court documents, or anything showing the status of your case. Your advisor will review this.",
    criminalUploadLabel: "Criminal record documentation",
  },
  section7b: {
    title: "What documents you'll need",
    subtitle: "You need to show continuous presence in Spain for approximately {{duration}}. Documents should cover as many months as possible.",
    monthsApprox: "{{n}} months",
    yearsApprox: "{{n}} years",
    primaryTitle: "Empadronamiento (municipal registration)",
    primaryBadge: "Strongest proof",
    primaryDesc: "Your municipal registration certificate. This is the single most important document — it directly proves your address and presence in Spain. If you only upload one thing, make it this.",
    primaryHistorical: "Request a historical certificate from your town hall showing your full registration history.",
    supportingTitle: "Supporting documents",
    rentalTitle: "Rental contract / Housing",
    rentalDesc: "Lease agreements, rental receipts, or property documents showing your address in Spain.",
    rentalNote: "Covers your entire lease period in one document.",
    medicalTitle: "Medical records",
    medicalDesc: "Health card (tarjeta sanitaria), doctor visits, hospital records, prescriptions.",
    medicalNote: "Each visit proves presence on that specific date.",
    bankTitle: "Bank statements",
    bankDesc: "Monthly statements from a Spanish bank account showing transactions in Spain.",
    bankNote: "Good for covering multiple months — each statement covers one month.",
    utilityTitle: "Utility bills",
    utilityDesc: "Electricity, water, gas, internet bills at your Spanish address.",
    utilityNote: "Each bill covers one month. Collect several to fill gaps.",
    employmentTitle: "Employment documents",
    employmentDesc: "Work contract, payslips (nóminas), Social Security registration (vida laboral).",
    employmentNote: "Vida laboral is excellent — shows your entire employment history in one document.",
    selfEmploymentTitle: "Self-employment documents",
    selfEmploymentDesc: "Autónomo registration, quarterly tax filings (modelo 130/303), invoices.",
    selfEmploymentNote: "Your alta de autónomo shows the exact date you started working.",
    familyTitle: "Family documents",
    familyDesc: "Marriage certificate, children\'s school enrollment, family book (libro de familia).",
    familyNote: "School enrollment is very strong — proves continuous presence for the full school year.",
    otherTitle: "Other acceptable documents",
    otherDesc: "Money transfers, course enrollment, transport cards, police reports, any official Spanish document with your name and a date.",
    otherNote: "Weaker individually but help fill gaps between stronger documents.",
    guidanceShort: "Focus on your empadronamiento plus 2-3 supporting documents spread across different months.",
    guidanceMedium: "Try to have at least one document per quarter. Your strongest combination is empadronamiento + rental contract + employment records.",
    guidanceLong: "For longer stays, a historical empadronamiento certificate is essential. Supplement with vida laboral and bank statements showing continuous activity.",
    rentalDetail: "Upload the full contract as a PDF. If you have multiple consecutive contracts, upload them all. Make sure your name, address, and start/end dates are visible.",
    medicalDetail: "Every doctor visit, prescription, or hospital report counts. The more you have, the better. You can ask your GP for a visit history printout.",
    bankDetail: "Upload monthly statements as PDFs. Each statement covers one month. If you have several years, prioritise the most recent ones and those covering any gaps.",
    utilityDetail: "Each bill covers the month it was issued. Collect bills from different months to cover as long a period as possible. Electricity, water, gas, or internet bills all work.",
    employmentDetail: "The vida laboral is the most complete document — it shows your entire employment history in Spain in one PDF. You can get it free from the Social Security online portal.",
    selfEmploymentDetail: "Your alta de autónomo shows the exact start date. Quarterly filings (modelo 130 or 303) prove ongoing activity. Upload them in chronological order.",
    familyDetail: "School enrollment certificates are very strong — they prove continuous presence for the full school year. Request them directly from your children\'s school.",
    otherDetail: "These documents are weaker on their own but help fill gaps between stronger ones. The more you have from different months, the better.",
  },
  review: {
    title: "Review your application",
    subtitle: "Here's what's done and what we still need",
    progressLabel: "required fields completed",
    statusComplete: "Complete",
    statusPartial: "Partial",
    statusNotStarted: "Not started",
    notProvided: "Not provided",
    addLink: "Add",
    editLink: "Edit",
    continueBtn: "Continue to consent",
    sections: { s1: "Personal details", s2: "Personal information", s3: "Civil & family", s4: "Address in Spain", s5: "Situation in Spain", s6: "Identity document", s7: "Proof of stay" },
    voiceTitle: "Let\'s review everything before finishing.",
    voiceTitle_rep: "Let\'s review the applicant\'s information before finishing.",
    saveBtn: "Save & Send",
    completionGreetComplete: "You\'re signed up, {{name}}!",
    completionBodyComplete: "We\'ve received your information. Next step is uploading your documents — you can do it now or come back later.",
    completionGreetMissing: "Great work, {{name}}!",
    completionBodyMissing: "I\'ve saved everything. I\'ll send you a message with what\'s still needed — you can finish whenever you\'re ready.",
    completionTextOrEmail: "You\'ll hear from me by text or email.",
    completionPortalNote: "You can come back anytime at:",
    completionTextLink: "I\'ll send you a link by text so you can come back anytime.",
    ctaDocs: "Continue to documents",
    ctaOrganizer: "Go to My Organizer",
    ctaNote: "You can come back anytime. We\'ll send you a link by text.",
    completionHeadline: "You\'re all set, {{name}}!",
    completedLabel: "What you\'ve completed",
    item1: "Personal information",
    item2: "Situation & eligibility",
    item3: "Civil & family details",
    item4: "Address in Spain",
    item5: "Agreement signed",
    savedConfirmation: "Everything has been saved securely. You can close this page and come back anytime.",
    whatsNext: "Next step: gather your documents",
    whatsNextBody: "We need a few documents to complete your application: your passport, proof of residence, and possibly a criminal record certificate. You can upload them now or come back later.",
  },
  section9: {
    title: "Consent & Save",
    subtitle: "Almost done — please confirm before we save your application.",
    gdprLabel: "I have read and accept the Privacy Policy and I consent to the processing of my personal data for the evaluation of my case and to be contacted about my request.",
    saveBtn: "Save & Send",
    checkboxRequired: "You must accept the privacy policy to continue.",
    successTitleMissing: "Progress saved!",
    successMissing: "Your progress has been saved. We'll send you a summary of what's still needed with a link to come back.",
    successTitleComplete: "You're all set!",
    successComplete: "Your information will be reviewed by your advisor. We'll be in touch if anything else is needed.",
    advisorWillReview: "will review your application and reach out if anything is needed.",
    teamReview: "Your application will be reviewed by our team. We'll reach out if anything is needed.",
    missingFieldsNote: "Some fields are still missing — that's okay. We'll follow up with a list.",
    privacyLink: "Privacy Policy",
    voiceTitle: "One last thing — your consent to proceed.",
    voiceTitle_rep: "One last thing — the applicant\'s consent to proceed.",
    advisorMsgComplete: "I\'ve got everything I need. I\'ll review your application and reach out if anything is needed.",
    advisorMsgMissing: "I\'ve saved your progress. I\'ll send you a reminder of what\'s still needed.",
    teamMsgComplete: "We\'ve got everything we need. We\'ll review your application and reach out if anything is needed.",
    teamMsgMissing: "We\'ve saved your progress. We\'ll send you a reminder of what\'s still needed.",
  },
  advisorProfile: {
    moreAbout: "More about",
    role: "Immigration Advisor",
    bioLine: "Immigration Advisor · 🇻🇪",
    bioBlock: "Mom of 2 · Dog mom to Luna 🐕 · Weekend hiker ⛰️",
    quote: "My passion is helping people build a new life in Spain — I've done it myself, and I've helped many others do it too!",
    from: "Originally from Venezuela",
    personal1: "Mom of two amazing kids",
    personal2: "Dog lover — I have a rescue named Luna",
    personal3: "I love hiking in the mountains on weekends",
    passion: "My passion is helping people build a new life in Spain — I've done it myself!",
    contact: "I'll reach you by text or email 💬",
    instagram: "Follow me on Instagram",
  },
  engagement: {
    reviewData: "Review my information ›",
    title: "Engagement Agreement",
    subtitle: "Required before we begin your case",
    whyTitle: "Why do we sign this before starting?",
    whyBody: "This agreement formalizes the professional relationship between you and our firm. It defines the agreed services, fees, and obligations of both parties, ensuring transparency and legal certainty from the start.",
    required: "Required",
    signatureLabel: "Client signature",
    clearSignature: "Clear signature",
    signatureBlank: "Draw your signature in the box. A blank canvas is not accepted.",
    signatureHint: "Sign here with your mouse or finger",
    signatureBottom: "client signature",
    advisorRole: "Senior Advisor, Global Mobility Law",
    assignedBadge: "Your assigned advisor",
    scopeLabel: "Scope of services",
    feeLabel: "Agreed fees",
    feeTitle: "Extraordinary Regularization 2026",
    feeNote: "Includes full case management",
    feeAmount: "€199",
    feeWord: "fees",
    feeAmountNote: "plus government filing fee (€38.28)",
    consentsLabel: "Your consent",
    consent1Before: "I have read and accept the ",
    consent1Link: "general terms of professional engagement",
    consent1After: " and the fee policy described above.",
    consent2: "I authorize Global Mobility Law to act as my professional representative before Spanish immigration authorities.",
    questionsText: "Have questions about any of the above?",
    questionsBtn: "I have questions, please follow up",
    questionsConfirm: "Received. We will follow up with you",
    consent3: "I agree to receive informational communications about my case status and relevant immigration updates by email.",
    optional: "(optional)",
    dateLabel: "Date of signature:",
    dateAuto: "— generated automatically",
    declarationsLabel: "Legal declarations",
    validationHint: "To continue: accept both mandatory consents and sign the document.",
    readyMessage: "All set — you can continue.",
    noReturnCommitment: "I confirm there is no order requiring me to leave Spain.",
    noHealthcareCoverage: "I confirm I do not have healthcare coverage in Spain from another state or third party.",
    scope: {
      analysis: "Initial analysis and assessment of your immigration situation",
      preparation: "Preparation and review of all required documentation",
      submission: "Filing the application with the competent authority",
      followup: "Case monitoring through to final resolution",
    },
  },
  docBriefing: {
    residenceTitle: "Proof of residence: Overview",
    residenceSubtitle: "You need to show continuous presence in Spain for approximately {{duration}}.",
    reviewThenContinue: "Review the guidance below, then tap Continue to upload your documents.",
    criminalUploadDocsHeading: "Upload documents",
    monthsApprox: "{{n}} months",
    yearsApprox: "{{n}} years",
    videoLabel: "1 min · What documents count",
    primaryTitle: "Empadronamiento (municipal registration)",
    primaryBadge: "Strongest proof",
    primaryDesc: "Your municipal registration certificate. This is the single most important document.",
    primaryHistorical: "Request a historical certificate from your town hall showing your full registration history.",
    supportingTitle: "Supporting documents",
    identityTitle: "Identity document",
    identitySubtitle: "We need a copy of your passport, national ID, or other official identity document.",
    identityTip1: "Expired passports are accepted — upload whatever you have.",
    identityTip2: "You need a complete copy of all pages of your passport.",
    identityTip3: "The preferred format is a single PDF file.",
    identityTip4: "You need ALL pages — including blank ones. Entry/exit stamps prove you haven't left Spain.",
    identityTip5: "Scan all pages into a single PDF. Use a free app like Adobe Scan or Google Drive on your phone.",
    identityTip6: "You can also visit a copy shop and ask them to scan your full passport to PDF.",
    rejectTitle: "Poor quality documents delay your application",
    rejectBody: "The administration rejects passports with blurry photos, missing pages, or unreadable text. Use a scanner or good lighting. Every page must be readable.",
    criminalExpiry: "These certificates are only valid for 3 months. Don't request them too early. Get them 2-3 weeks before we're ready to submit your application. If you're not sure about timing, just ask me.",
    criminalTitle: "Criminal record: Overview",
    criminalSubtitle: "We need criminal record certificates from each country where you lived in the 5 years before coming to Spain.",
    criminalWhichTitle: "Which certificates do you need?",
    criminalWhichBody: "One for each country where you lived in the 5 years before entering Spain. It could be just your home country, or several — it depends on your situation. We'll ask you which ones in the next step.",
    criminalTip1: "Request the certificate from your country's consulate in Spain or the Central Criminal Records Registry.",
    criminalTip2: "The certificate must be apostilled or legalised if it comes from another country.",
    criminalTip3: "If you have a record, upload the certificate anyway — your advisor will review it.",
    criminalDiplomaticNote: "If you can't get a certificate from a particular country within a month, don't worry. We'll file the paperwork (Anexo I-1 and I-2) to request it through diplomatic channels on your behalf.",
    employmentTitle: "Employment documents",
    employmentSubtitle: "We need documents that prove your current or past employment situation in Spain.",
    employmentTip1: "Upload your work contract, recent payslips, or Social Security history (vida laboral).",
    employmentTip2: "If self-employed, include your autónomo registration and quarterly tax filings.",
    employmentTip3: "If looking for work, a job offer or letter of intent also works.",
    familyTitle: "Family documents",
    familySubtitle: "We need documents that prove your family situation, especially if you have a spouse or children in Spain.",
    familyTip1: "Upload the family book, marriage certificate, or children's birth certificates.",
    familyTip2: "If your children are enrolled in school in Spain, include the school enrollment certificate.",
    familyTip3: "Foreign documents must be apostilled and, if necessary, translated.",
    asylumTitle: "Asylum documentation",
    asylumSubtitle: "As an asylum applicant, we need your official documentation from the international protection process.",
    asylumTip1: "Upload your asylum application receipt or the resolution of your case.",
    asylumTip2: "If you have a red or white asylum applicant card, include that too.",
    asylumTip3: "You don't need to prove 5 months of stay — your asylum documentation is sufficient.",
    denialTitle: "Prior denial documents",
    denialSubtitle: "Since you had a previous immigration process that was denied, we need specific documentation from that case.",
    denialTip1: "Upload the denial resolution from the previous process.",
    denialTip2: "Include the criminal records from the prior process, even if expired.",
    denialTip3: "Add any document that shows you remained in Spain after the denial.",
    continueToUpload: "Continue",
    back: "Back",
    rentalTitle: "Rental contract / Housing",
    rentalDesc: "Lease agreements, rental receipts, or property documents showing your address in Spain.",
    rentalNote: "Covers your entire lease period in one document.",
    medicalTitle: "Medical records",
    medicalDesc: "Health card (tarjeta sanitaria), doctor visits, hospital records, prescriptions.",
    medicalNote: "Each visit proves presence on that specific date.",
    bankTitle: "Bank statements",
    bankDesc: "Monthly statements from a Spanish bank account showing transactions in Spain.",
    bankNote: "Each statement covers one month.",
    utilityTitle: "Utility bills",
    utilityDesc: "Electricity, water, gas, internet bills at your Spanish address.",
    utilityNote: "Each bill covers one month. Collect several to fill gaps.",
    employmentTitle: "Employment documents",
    employmentDesc: "Work contract, payslips (nóminas), Social Security registration (vida laboral).",
    employmentNote: "Vida laboral shows your entire employment history in one document.",
    selfEmploymentTitle: "Self-employment documents",
    selfEmploymentDesc: "Autónomo registration, quarterly tax filings (modelo 130/303), invoices.",
    selfEmploymentNote: "Your alta de autónomo shows the exact date you started working.",
    familyTitle: "Family documents",
    familyDesc: "Marriage certificate, children\'s school enrollment, family book (libro de familia).",
    familyNote: "School enrollment proves continuous presence for the full school year.",
    otherTitle: "Other acceptable documents",
    otherDesc: "Money transfers, course enrollment, transport cards, police reports, any official Spanish document with your name and a date.",
    otherNote: "Weaker individually but help fill gaps between stronger documents.",
    guidanceShort: "Focus on your empadronamiento plus 2-3 supporting documents spread across different months.",
    guidanceMedium: "Try to have at least one document per quarter. Your strongest combination is empadronamiento + rental contract + employment records.",
    guidanceLong: "For longer stays, a historical empadronamiento certificate is essential. Supplement with vida laboral and bank statements.",
    residenceVideoLabel: "2 min · What documents prove your presence in Spain",
    noExitTitle: "You cannot have left Spain",
    noExitBody: "Your stay must be uninterrupted for the 5 months before your application. Any exit from the country breaks the requirement — even brief trips are not allowed. If you have left, talk to us before continuing.",
    whatCountsTitle: "What documents count?",
    whatCountsBody: "Any public or private document with your name and a date. You don't need one for every single month, but the more you have, the stronger your case. Aim for at least one document per month.",
    rentalDetail: "Upload the full contract as a PDF. If you have monthly rent receipts, upload each month separately. Use our scanner for multi-page documents.",
    medicalDetail: "Accepted: health card, medical reports, prescriptions, clinic appointments. A clear photo of each document is enough. Each visit proves presence on a specific date.",
    bankDetail: "Download monthly statements from your online banking as PDF. If you don't have online access, request printed statements from your bank and scan them.",
    utilityDetail: "Electricity, water, gas, or internet bills in your name. A clear photo of each bill is enough. If you receive them by email, you can upload them directly as PDF.",
    employmentDetail: "Request your vida laboral online at sede.seg-social.gob.es or by SMS to 600 123 456. It's a single PDF showing your full history. Payslips and contracts: a clear photo or PDF.",
    selfEmploymentDetail: "Your autónomo registration and quarterly tax filings (modelo 130/303) can be downloaded from the Tax Agency's electronic office.",
    familyDetail: "The family book is multi-page — use our scanner or a copy shop. School enrollment: request a copy from the school.",
    otherDetail: "Money transfers: save the receipts. Transport pass: nominative cards count. Any official document with your name and a date is valid.",
  },
  docFlow: {
    title: "Document checklist",
    subtitle: "Here's what we need. Tap each category for instructions and to upload.",
    catIdentity: "Identity",
    catResidence: "Proof of residence",
    catCriminal: "Criminal record",
    catEmployment: "Employment documents",
    catFamily: "Family documents",
    catAsylum: "Asylum documentation",
    catVulnerability: "Vulnerability report",
    catOther: "Other",
    catDenial: "Prior denial documents",
    helpSheetTitle: "How can we help?",
    helpSheetSubtitle: "We'll get back to you soon. Usually within 1 business day.",
    helpSheetContextPrefix: "You're on:",
    helpSheetPlaceholder: "Tell us what you need...",
    helpSheetSend: "Send",
    helpSheetSending: "Sending…",
    helpSheetSentTitle: "Message sent",
    helpSheetSessionError: "Could not reach your session. Please try again.",
    helpSheetSendError: "Something went wrong. Please try again.",
    helpFabAria: "Help — ask a question",
    notStarted: "Not started",
    inProgress: "In progress",
    complete: "Complete",
    backToChecklist: "Back to Documents",
    goToOrganizer: "Go to My Organizer",
    videoLabel: "1 min · What documents count",
    qualityTitle: "Document quality matters",
    qualityBody: "Blurry, dark, or cropped documents are the #1 cause of delays. Applications with unreadable documents get sent back for corrections, which can add weeks to your process. Take an extra moment to make sure each document is clear and readable.",
    photoTipsLink: "See tips for good document photos",
    letsBegin: "Let's begin",
    skip: "Skip",
    continue: "Continue",
  },
  sectionExit: {
    title: "Are you done with this section?",
    yes: "Yes",
    no: "No",
    stuck: "I'm stuck",
    stuckPlaceholder: "Tell us what's holding you up...",
    stuckSend: "Send",
    back: "Back",
    porMarkTitle: "Mark this section as complete?",
    porMarkSubtitle: "You can change this later if you need to.",
    porMarkComplete: "Mark complete and continue",
    porComeBackLater: "Save and finish later",
  },
  docUpload: {
    title: "Upload your documents",
    done: "Done — go to My Organizer",
    backToChecklist: "Back to Document Organizer",
    continue: "Continue",
    goToOrganizer: "Go to My Organizer",
    backToRequirements: "Back to requirements",
    empHeading: "Employment documents",
    empHeadingEmployed: "Employment documents",
    empHeadingSelfEmployed: "Self-employment documents",
    workTypeGatePrompt: "Tell us how you work so we know what to request.",
    workTypeEmployed: "Employed",
    workTypeSelfEmployed: "Self-employed",
    workTypeEmployedHelper: "You work for an employer or have a contract or offer.",
    workTypeSelfEmployedHelper: "You work for yourself or plan to as autónomo.",
    empDescriptionLabel: "Describe your activity",
    empDescriptionPlaceholder: "e.g. Delivery rider, cook, graphic designer",
    selfEmployedNote: "You work for yourself or plan to as autónomo.",
    empDocContract: "Work contract",
    empDocContractHelper: "Signed contracts from current or past employers in Spain. Include the first page and signature page at minimum.",
    empDocPayslip: "Payslip",
    empDocPayslipHelper: "Recent payslips showing your salary and employer. The last 3 to 6 months are most useful.",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper: "Your official work history report from Seguridad Social. Shows every job you've had. Very strong evidence.",
    empDocJobOffer: "Job offer letter",
    empDocJobOfferHelper: "A signed commitment letter from an employer. Must include the role, start date, salary, and employer details.",
    empDocOther: "Other documents",
    empDocOtherHelper: "Any other documents that support your work situation.",
    empOtherPlaceholder: "Brief description",
    empAddDoc: "Add document",
    empGateContinue: "Continue to document upload",
    empTipsTitle: "Tips",
    empTipOptional: "Only upload documents that apply to your situation. Nothing here is required.",
    empTipVidaLaboral: "Your vida laboral can be requested online at sede.seg-social.gob.es with your digital certificate or cl@ve.",
    empTipJobOffer: "If you have a job offer, make sure it's signed by the employer and includes their tax ID (CIF or NIF).",
    empTipInformal: "Payslips from informal work (without contract) are still useful. Upload whatever you have.",
    empTipAutonomo: "Self-employment documents prove you pay taxes and social security on your own activity.",
    identityTitleNoPassport: "Identity document",
    identityTitlePassport: "Passport",
    identityGateQuestion: "What kind of ID do you have?",
    identityGateHint: "Use a valid, unexpired passport if you have one. Select \"Other ID\" only if you don't.",
    identityGatePassport: "Passport",
    identityGateOther: "Other ID",
    identityGateContinue: "Continue",
    identityTypeSwitchFromPassport: "I don't have a valid passport",
    identityTypeSwitchFromOther: "I have a valid passport",
    identityStep1: "Step 1: Enter your details",
    passportNumber: "Passport number",
    passportFullName: "Full name as shown on passport",
    passportExpiry: "Expiry date",
    nieLabel: "NIE number",
    nieHelper: "If you have one",
    nationalIdType: "Document type",
    nationalIdNumber: "Document number",
    nationalIdCountry: "Issuing country",
    nationalIdCountryPlaceholder: "Select country...",
    typeNationalId: "National ID card",
    typeTravelDoc: "Travel document",
    typeCedulaInscripcion: "Registration certificate",
    identityStep2NoPassport: "Step 2: Upload your identity document",
    identityStep2BodyNoPassport: "Upload any identity document you have from your home country.",
    identityStep2: "Step 2: Upload your passport",
    identityStep2Body: "We need all pages including blank ones. Entry and exit stamps help prove your time in Spain. All pages must be uploaded as a single PDF file.",
    ocrTitle: "Scan or upload your passport ID page",
    ocrSubtitle:
      "Built-in scanning works well for many people who don't already have a PDF. We'll try to read your details and fill in the form.",
    ocrUploadZone: "Take a photo or choose a file",
    ocrUploadHint: "PDF or image — ID page only (photo page)",
    ocrSuccessTitle: "Got it",
    ocrBody:
      "Just the identity page of your passport (the one with your photo). If reading works, we'll take you straight to the next step to review or edit your details. If not, you can retry or skip and type everything yourself — nothing here is required. Later you'll upload your full passport in one PDF, all pages.",
    ocrFreedomHint: "You're not required to do this now — you can skip anytime.",
    ocrSkipManual: "Skip — I'll enter my details",
    ocrUploadFileLabel: "Upload a file",
    ocrUseCameraHint: "Optional — quick photo if you don't have a scan yet",
    ocrUseCamera: "Use camera",
    ocrUseScanner: "Scan (multiple pages)",
    ocrSkip: "Skip",
    ocrContinue: "Continue",
    passportAddTitle: "Add your passport",
    passportAddBody1: "We need a scanned PDF of your passport with every page included.",
    passportAddBody2: "Images with excessive borders, visible fingers, or glare will be rejected.",
    passportAddExamplesLink: "See examples of acceptable scans",
    passportAddScannerCardTitle: "Use our scanner",
    passportAddScannerCardDesc:
      "Capture every page with your camera. We'll combine them into a single PDF for you.",
    passportAddPdfCardTitle: "Upload a PDF",
    passportAddPdfCardDesc: "Upload an existing PDF that includes every page of your passport.",
    passportScanExamplesCloseAria: "Close",
    passportScanExamplesTitle: "Scan examples",
    passportScanExamplesIntro:
      "Aim for a scan that looks like the acceptable example. Anything that resembles the rejected examples will need to be retaken.",
    passportScanExamplesAcceptableLabel: "ACCEPTABLE",
    passportScanExamplesAcceptableCaption:
      "Edge to edge, sharp focus, no fingers or shadows in frame.",
    passportScanExamplesRejectedLabel: "WILL BE REJECTED",
    passportScanExamplesRejectDarkTitle: "Too dark",
    passportScanExamplesRejectDarkDesc: "Move to better lighting. Avoid shadows over the passport.",
    passportScanExamplesRejectBlurTitle: "Blurry or out of focus",
    passportScanExamplesRejectBlurDesc: "Hold the camera steady. Tap the passport on screen to focus.",
    passportScanExamplesRejectFingersTitle: "Fingers in frame",
    passportScanExamplesRejectFingersDesc: "Use a small object to hold the passport open instead.",
    passportScanExamplesGotIt: "Got it",
    passportScanSavedTitle: "Passport saved",
    passportScanSavedSubtitle: "{{filename}} · {{pages}}",
    passportScanSavedSubtitleFilenameOnly: "{{filename}}",
    passportScanSavedPagesOne: "1 page",
    passportScanSavedPagesMany: "{{count}} pages",
    passportScanSavedView: "View",
    passportScanSavedReplace: "Replace",
    identityReplaceConfirmTitle: "Replace this document?",
    identityReplaceConfirmBody:
      "Your current PDF will be removed. You can upload a new PDF or scan your passport again.",
    identityReplaceConfirmCancel: "Cancel",
    identityReplaceConfirmReplace: "Replace document",
    passportViewOpening: "Opening…",
    passportViewUnavailable:
      "We couldn't open your passport file. Try again or use Replace to upload again.",
    passportViewPopupBlocked: "Allow pop-ups for this site to view your passport PDF.",
    passportViewerSheetTitle: "Your document",
    passportViewerClose: "Done",
    identityAiUploadingSecure: "Saving your document and reading your details…",
    identityAiWaitHint:
      "When we're done, you'll land on the next step to review your details. If something goes wrong, you can retry or skip and enter them manually.",
    passportPdfProcessingTitle: "Processing your PDF…",
    passportPdfPhaseUpload: "Uploading your file securely…",
    passportPdfPhaseAnalyze: "Reading passport details…",
    passportPdfPhaseApply: "Applying details to your form…",
    identityUploadRetry: "Retry upload",
    identityUploadPickAnother: "Choose another file",
    passportAiBannerTitle: "Document saved — we couldn't read it automatically.",
    passportAiBannerBody:
      "Your file is saved. Review or enter your details below, try scan or upload again, or skip — you don't have to keep trying. We can help with a clearer PDF later. If the fields below don't match this scan, they may be left over from an earlier session: update them or try again.",
    passportAiBannerShopHint: "A local copy shop can often make a sharp scan if using your phone camera is difficult.",
    passportAiBannerRetry: "Try scan or upload again",
    passportAiSavedManualTitle: "Saved — confirm or enter your details",
    identityTitle: "Upload your identity document",
    criminalTitle: "Criminal record",
    asylumTitle: "Upload your asylum documentation",
    denialTitle: "Upload the denial resolution",
    employmentTitle: "Upload your employment documents",
    familyTitle: "Upload your family documents",
    vulnHeading: "Vulnerability report",
    vulnIntroBody: "An official form that confirms your vulnerability situation. You will need a social worker or registered NGO to complete it.",
    vulnStep1: "Step 1: Get the form",
    vulnStep1Body: "Bring this form to a social worker or registered NGO so they can complete the assessment, sign it, and stamp it.",
    vulnDownloadBtn: "Download",
    vulnEmailBtn: "Email",
    vulnStep2: "Step 2: Upload it back",
    vulnStep2Body: "Once it is signed and stamped, upload it here.",
    vulnScannerBtn: "Use scanner",
    vulnTipsTitle: "Tips",
    vulnTipSocial: "Any servicios sociales municipales office can complete this form for you.",
    vulnTipNGO: "Registered NGOs such as Caritas, Red Cross, ACCEM, or CEAR can also complete it.",
    vulnTipForward: "You might be able to email it directly to social services or an NGO. Some offices accept this, others prefer you bring it in person.",
    vulnTipSigned: "Make sure the report is signed, stamped, and dated. Unsigned forms will be rejected.",
    vulnTipID: "Bring your passport and any other ID when you go to get it signed.",
    otherHeading: "Other",
    otherBody: "Use this section to upload any additional document your advisor asks for that doesn't fit the categories above. For example, a power of attorney, a sworn translation, or an apostille.",
    otherAddDoc: "Upload document",
    documentTypeLabel: "Document type",
    documentTypePlaceholder: "Select…",
    otrosDescribeLabel: "Describe the document",
    otrosDescribePlaceholder: "Short description (required for «Otros»)",
    uploadRulesHint:
      "PDF, DOC, JPG, TIF, or GIF. Max 6 MB per file. File name: letters, numbers, spaces, _ - . ( )",
    multipleFilesHint: "You can upload multiple documents.",
    residenceHeading: "Proof of residence",
    residenceDesc: "Upload documents showing you've been in Spain continuously. The more documents you have, the better. You can upload now or come back later.",
    residencePickerTitle: "Add proof of residence",
    residencePickerSubtitle: "Choose the document you want to upload. If you are not sure, choose the closest option or select \"Other\".",
    residenceFeatured: "Strongest",
    residenceGroupOfficial: "Official documents",
    residenceGroupPersonal: "Other personal records",
    residenceGroupOther: "Other",
    residenceGuidance: "Make sure this document clearly shows your name and a date. Documents without these may not be accepted.",
    residenceOtherLabel: "What type of document is this?",
    residenceOtherPlaceholder: "Example: gym membership, rental receipt, appointment confirmation",
    residenceOtherHelper: "Optional. A short description helps Ana understand what it is.",
    residenceChange: "Change document type",
    monthsCovered: "Months covered",
    monthsTip: "Tap the months each document you upload covers to help you remember.",
    addDocument: "+ Add document",
    chooseFile: "Choose file",
    uploadedDocs: "Uploaded documents",
    modalTitle: "Add document",
    modalDocType: "Document type",
    modalSelectType: "Select type...",
    modalMonthRange: "Months covered",
    modalFrom: "From",
    modalTo: "To",
    modalFile: "File",
    modalAdd: "Add",
    acceptedTypes: "Accepted document types",
    typeEmpadronamiento: "Empadronamiento",
    typeRental: "Rental contract",
    typeMedical: "Medical record",
    typeBank: "Bank statement",
    typeUtility: "Utility bill",
    typeEmployment: "Employment document",
    typeOther: "Other",
    monthsAutoTip: "Dec 2025 to Apr 2026. These update automatically as you add documents.",
    submissionMonthNote: "Depending on the submission date, we may need to come back and add May's documents.",
    to: "to",
    openScanner: "Open scanner",
    helpTitle: "Quick tips",
    helpTip1: "The Empadronamiento is the most important document. Upload it first if you have it.",
    helpTip2: "Each document can cover multiple months. A rental contract can cover the whole period.",
    helpTip3: "Bank statements, utility bills, and medical visits also count: any document with your name and a date.",
    helpTip4: "You don't need everything now. You can come back and add more at any time.",
    seeFullRequirements: "Back to Document Organizer",
    backToOverview: "Back to overview",
    backToGuidance: "Back to Document Organizer",
    modalScanBtn: "Open scanner",
    modalChangeFile: "Change",
    removeDoc: "Remove",
    deleteFileConfirm: "Are you sure you want to delete this file?",
    criminalDesc: "You need a criminal record certificate from your country of origin and any country you\'ve lived in for the past 5 years.",
    criminalReq1: "Request them from your country\'s consulate in Spain or have a family member get them from your home country",
    criminalReq2: "It must be apostilled or legalized",
    criminalReq2Pre: "They must be ",
    criminalReq2Term: "apostilled or legalized",
    criminalReq2Help: "An apostille is an official stamp that validates your document for international use. It\'s obtained from the relevant ministry in your home country. If your country isn\'t part of the Hague Convention, you need consular legalization. If you\'re not sure what you need, ask us.",
    criminalReq3: "If not in Spanish, it needs a sworn translation",
    criminalReq3Pre: "If not in Spanish, they need a ",
    criminalReq3Term: "sworn translation",
    criminalReq3Help: "A sworn translator is a professional officially appointed by Spain\'s Ministry of Foreign Affairs. Their translations have legal validity. You can search for one at maec.gob.es or ask us — we can recommend one.",
    criminalDeclaracion: "Can\'t get it? In some cases a sworn declaration is accepted. Talk to us.",
    criminalDeclaracionPre: "Can\'t get one? In some cases a ",
    criminalDeclaracionPost: " is accepted. Talk to us.",
    criminalHelpTitle: "Tips",
    criminalHelp1: "Some consulates take weeks — request it as soon as possible",
    criminalHelp2: "If the certificate is more than one page, use our scanner to create a PDF",
    criminalVideoLabel: "1 min · How to get your criminal record certificate",
    empHeading: "Employment documents",
    empHeadingEmployed: "Employment documents",
    empHeadingSelfEmployed: "Self-employment documents",
    empDesc:
      "Upload documents that prove your work history or job offer in Spain. Only upload what applies to your situation.",
    empWhatTitle: "What proves your work?",
    empWhatBody:
      "You don't need all of these. Any combination of contracts, payslips, or vida laboral shows your work history. A job offer alone can qualify you.",
    workTypeGatePrompt: "Tell us how you work so we know what to request.",
    workTypeEmployed: "Employed",
    workTypeSelfEmployed: "Self-employed",
    workTypeEmployedHelper: "You work for an employer or have a contract or offer.",
    workTypeSelfEmployedHelper: "You work for yourself or plan to as autónomo.",
    empDescriptionLabel: "Describe your activity",
    empDescriptionPlaceholder: "e.g. Delivery rider, cook, graphic designer",
    selfEmployedNote: "You work for yourself or plan to as autónomo.",
    empDocContract: "Work contract",
    empDocContractHelper:
      "Signed contracts from current or past employers in Spain. Include the first page and signature page at minimum.",
    empDocPayslip: "Payslip",
    empDocPayslipHelper:
      "Recent payslips showing your salary and employer. The last 3 to 6 months are most useful.",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper:
      "Your official work history report from Seguridad Social. Shows every job you've had. Very strong evidence.",
    empDocJobOffer: "Job offer letter",
    empDocJobOfferHelper:
      "A signed commitment letter from an employer. Must include the role, start date, salary, and employer details.",
    empDocAutonomo: "Self-employment registration",
    empDocAutonomoHelper:
      "Alta de autonomo, IAE registration, or tax declarations (modelo 130, 303) showing you work for yourself.",
    empDocOther: "Other documents",
    empDocOtherHelper: "Any other documents that support your work situation.",
    empOtherPlaceholder: "Brief description",
    empAddDoc: "Add document",
    empGateContinue: "Continue to document upload",
    empTipsTitle: "Tips",
    empTipOptional: "Only upload documents that apply to your situation. Nothing here is required.",
    empTipVidaLaboral:
      "Your vida laboral can be requested online at sede.seg-social.gob.es with your digital certificate or cl@ve.",
    empTipJobOffer:
      "If you have a job offer, make sure it's signed by the employer and includes their tax ID (CIF or NIF).",
    empTipInformal:
      "Payslips from informal work (without contract) are still useful. Upload whatever you have.",
    empTipAutonomo:
      "Self-employment documents prove you pay taxes and social security on your own activity.",
    familyHeading: "Family documents",
    familyDesc:
      "Upload any documents that apply to your situation. None of these are required for every applicant. Just upload what you have.",
    familyDocMarriage: "Marriage certificate",
    familyDocMarriageHelper:
      "Official certificate. Apostilled and translated if issued outside Spain.",
    familyDocLibro: "Libro de familia",
    familyDocLibroHelper: "Upload all relevant pages.",
    familyDocBirth: "Children's birth certificates",
    familyDocBirthHelper: "One per child. Apostilled and translated if not in Spanish.",
    familyDocCohabitation: "Proof of cohabitation",
    familyDocCohabitationHelper:
      "Empadronamiento at the same address, shared rental contract, etc.",
    familyDocSchool: "School enrollment",
    familyDocSchoolHelper: "Enrollment certificates from your children's school.",
    familyDocOther: "Other documents",
    familyDocOtherHelper: "Any other supporting documents.",
    familyOtherPlaceholder: "Brief description",
    familyScannerBtn: "Use scanner",
    familyAddDoc: "Add document",
    familyTipsTitle: "Tips",
    familyTipOptional: "Only upload documents that apply to your situation.",
    familyTipApostille: "Foreign documents must be apostilled and translated into Spanish.",
    familyTipSkip: "If you don't have a document yet, skip it and come back later.",
    asylumHeading: "Asylum documentation",
    asylumDesc:
      "Enter your OAR case number and upload your red card or application receipt. If your asylum application was denied, also upload the denial resolution.",
    asylumWhatTitle: "Why this matters",
    asylumWhatBody:
      "Your asylum documents prove you applied for international protection before January 1, 2026. This is what qualifies you for the EX-31 route, which has different requirements than the general regularization.",
    asylumDocReceipt: "Application receipt",
    asylumDocReceiptHelper:
      "Provide all pages",
    asylumDocCard: "Red card",
    asylumDocCardHelper:
      "Upload both sides.",
    asylumDocResolution: "Denial resolution",
    asylumDocResolutionHelper:
      "If your asylum was denied",
    asylumDocOther: "Other asylum documents",
    asylumDocOtherHelper:
      "Any other documents related to your asylum application, such as appeals, medical reports, or country-of-origin information.",
    asylumOtherPlaceholder: "Brief description",
    asylumAddDoc: "Add document",
    asylumTipsTitle: "Tips",
    asylumTipOARMatch:
      "Your OAR case number should match what you entered on Step 2. You'll find it on your asylum receipt.",
    asylumTipCardBoth:
      "Upload both sides of your tarjeta roja or blanca. The back has important renewal information.",
    asylumTipDenied:
      "If your asylum application was denied but you remained in Spain, you can still use the EX-31 route. Upload the denial resolution too.",
    asylumTipStay:
      "You don't need to prove 5 months of stay. Your asylum documentation covers your time in Spain.",
    asylumTipLanguage:
      "All documents must be in Spanish or translated. Originals from the OAR are already in Spanish.",
    or: "or",
  },
  criminal: {
    multiCountryTitle: "Which certificates do you need?",
    multiCountryBody: "One for each country where you lived in the 5 years before entering Spain. It could be just your home country, or several — it depends on your situation. We'll ask you which ones in the next step.",
    diplomaticNote: "If you can't get a certificate from a particular country within a month, don't worry. We'll file the paperwork (Anexo I-1 and I-2) to request it through diplomatic channels on your behalf.",
    uploadPageDesc: "We need criminal record certificates from every country where you lived during the 5 years before entering Spain. Include your country of origin if you lived there during that period. Do not include Spain.",
    countriesQuestion: "Which countries did you live in during the 5 years before entering Spain?",
    countriesHint: "Add every country where you lived in the 5 years before entering Spain. Include your country of origin if you lived there during that period.",
    selectCountryPlaceholder: "Select country…",
    countrySearchPlaceholder: "Search country…",
    countrySearchNoResults: "No results",
    addCountry: "Add",
    uploadSectionTitle: "Upload certificates",
    noSpainNote: "Don't include Spain. The Spanish certificate is handled automatically by the system.",
    or: "or",
    requestedNotReceivedLabel: "Requested but not received.",
    requestedNotReceivedDesc: "More than 30 days have passed since I requested the certificate. I'll upload proof of request (email, letter, or receipt).",
    requestedNotReceivedBadge: "Requested, not received in 30 days",
    requestProofPending: "Proof of request pending",
    uploadedBadge: "Certificate uploaded",
    uploadReceiptBtn: "Upload receipt",
    scanReceiptBtn: "Scan receipt",
    uploadCertificateBtn: "Upload certificate",
    scanCertificateBtn: "Scan certificate",
    awaitingCertificate: "Over 30 days since certificate was ordered",
    briefingTip1: "Request the certificate from your country's consulate in Spain, or ask a family member to get it in your home country.",
    briefingTip2: "The Spain certificate can be requested online at sede.mjusticia.gob.es. I'll guide you through the process.",
    briefingTip3: "If you have a criminal record, upload the certificate anyway. I'll review it.",
  },
  docPhotoGuide: {
    title: "Document photo tips",
    link: "See document photo tips",
    good: "Correct — flat, good lighting, all text readable",
    goodTitle: "Good example",
    badTitle: "Incorrect examples",
    bad1: "Shadow — text is hidden. Move your light source.",
    bad2: "Cut off or angled — make sure the entire document is in the frame.",
    bad3: "Too dark — text can\'t be read. Use more light.",
  },
  organizer: {
    title: "My Organizer",
    needItems: "You still need {{n}} items",
    allDone: "All complete!",
    residence: "Proof of residence",
    criminal: "Criminal record certificate",
    asylum: "Asylum documentation",
    notStarted: "Not started",
    inProgress: "In progress",
    complete: "Complete",
    upload: "Upload",
    viewCompleted: "View completed",
    uploadDocuments: "Upload documents",
    greeting: "Welcome back, {{name}}",
    greetingSub: "Here's where your application stands.",
    caseProgress: "Your case progress",
    phaseInfo: "Information",
    phaseDocs: "Documents",
    phaseSubmit: "Submission",
    remaining: "remaining",
    deadline: "Submission deadline: June 30, 2026",
    detailsLabel: "Your details",
    docsLabel: "Documents",
    viewChecklist: "View Document Checklist",
    contactTitle: "Need help?",
    contactSub: "Message us on WhatsApp",
    submitInlineBannerTitle: "Are you ready to submit your application?",
    submitInlineBannerBody:
      "If you're finished completing all of the sections, then let us know, and we will review and get back to you soon.",
    submitInlineBannerCta: "Send to {{advisor}}",
    submitInlineSending: "Sending…",
    submitDoneHeading: "Submitted to {{advisor}} on {{date}}",
    submitDoneHeadingNoDate: "Submitted to {{advisor}}",
    submitDoneMessagePara1:
      "{{advisor}} is reviewing your application and will text you if she has questions or needs anything updated. We'll send an email copy too.",
    submitDoneMessagePara2:
      "You're all done for now. Feel free to reply to the email or come back here if you have any questions.",
    incomplete: "Not completed",
    stepConfirm: "Confirm details",
    stepAsylum: "Asylum question",
    stepQualifying: "Which best describes your current situation?",
    stepSituation: "Your situation",
    stepDocument: "Identity document",
    stepPassportPersonal: "Passport & personal details",
    stepPersonal: "Personal information",
    stepCivil: "Civil status & family",
    stepAddress: "Address in Spain",
    stepConsent: "Agreement & signature",
    signSubmitLabel: "Sign & submit",
    signSubmitStatusPending: "Pending",
    signSubmitStatusComplete: "Complete",
    deadlineLabel: "Deadline",
    nextAction: "Continue your application",
    nextActionSub: "Complete your missing information",
    nextUpload: "Upload your documents",
    nextUploadSub: "You have documents waiting to be uploaded",
    progress: "Progress",
    of: "of",
    contextBody: "Complete each section at your own pace — your progress saves automatically.",
    finishItems: "Please finish your remaining items. Once you're done, I'll review everything and prepare it for submission to the Spanish immigration office. If you have any questions, send me a note at the bottom of this page and I'll get back to you shortly.",
    signAvailableLater: "Available at the end",
    helpStuckTitle: "I'm stuck",
    helpStuckSub: "Tell us what's going on and we will get back to you shortly.",
    helpStuckSubExpanded: "{lawyer} will respond shortly.",
    helpPlaceholder: "Type here...",
    helpSendButton: "Send",
    helpSentTitle: "Message sent",
    helpSentSub: "{lawyer} will see it and respond soon.",
    helpDefaultLawyer: "your lawyer",
  },
  upload: {
    scanTitle: "Scan with your phone",
    scanSubtitle: "Auto-detects edges and creates a PDF",
    scanBtn: "Open scanner",
    appTitle: "Use a scanner app",
    appSubtitle: "Scan the pages and upload as PDF",
    appGoogle: "Google Drive — Scan document",
    appAdobe: "Adobe Scan — Free on App Store and Google Play",
    appCopyShop: "Or visit a nearby copy shop",
    fileTitle: "Upload a file",
    fileSubtitle: "I already have a PDF or photo ready",
    scanHeroTitle: "Scan your passport right here",
    scanHeroSubtitle: "We guide you page by page. We detect edges and create the PDF for you.",
    alternativesLabel: "Other options",
    altApp: "Use a scanner app",
    altAppHint: "Adobe Scan or Google Drive (free)",
    altShop: "Visit a copy shop",
    altShopHint: "Ask them to scan your full passport to PDF",
    altUpload: "I already have a file",
    altUploadHint: "Upload a PDF or photo you already have",
    videoLabel: "30 sec · How to scan your passport",
    browseFiles: "Browse files",
    scanCriminalNote: "Scan each page of the certificate. Include stamps and signatures.",
    scanDocTitle: "Need to scan a document?",
    scanDocSubtitle: "Use our scanner for multi-page documents",
  },
  auth: {
    signIn: "Sign in",
    subtitle: "Enter your phone number to continue.",
    phone: "Phone number",
    sendCode: "Send code",
    otpIntroTitle: "Enter the code we sent",
    otpIntroBody: "We sent a one-time code to your phone.",
    enterCode: "Enter the code",
    sixDigitCodePlaceholder: "Enter 6-digit code",
    verify: "Verify",
    resend: "Resend code",
    or: "or",
    google: "Continue with Google",
    apple: "Continue with Apple",
    privacyNote: "By continuing, you agree to our",
  },
  faq: {
    title: "How can I help?",
    about: "About:",
    other: "I have a different question",
    otherPlaceholder: "Type your question…",
    otherHelper: "We'll reply by text or email.",
    send: "Send",
    sent: "Sent! Your advisor will get back to you.",
    sentTo: "Sent to",
    sentContact: "I\'ll get back to you by text or email.",
    sentTeam: "Sent to our team. We'll get back to you soon.",
    s1: {
      q1: "Can I change this information later?",
      a1: "Yes, you can edit any field at any time before the final submission. Nothing is locked in.",
      q2: "Should I include accents in my name?",
      a2: "Use your name exactly as it appears on your passport or ID, including any accents or special characters.",
      q3: "Is my personal data secure?",
      a3: "Your information is encrypted and only accessible by you and your assigned advisor.",
    },
    s2: {
      q1: "What if I don't know my exact date of birth?",
      a1: "Use the date shown on your passport or birth certificate. Your advisor can help if you only have an approximate date.",
      q2: "Which nationality should I enter?",
      a2: "Enter your current nationality as it appears on your passport. If you have multiple, use the one on the document you'll submit.",
      q3: "What does 'Undetermined' sex mean?",
      a3: "Choose this if your document doesn't specify male or female — some passports use 'X' or leave the field blank.",
    },
    s3: {
      q1: "Why do you need my parents' names?",
      a1: "The official government form requires parents' first names. It's fine to skip for now — your advisor can help fill it in later.",
      q2: "What if I don't know a parent's name?",
      a2: "Just leave those fields blank. Your advisor will assist you when reviewing your file.",
    },
    s4: {
      q1: "What if I don't have a fixed address?",
      a1: "Enter the address where you are currently staying, even if it's temporary. You can update it later if anything changes.",
      q2: "Do I need a padrón (census registration)?",
      a2: "No, a padrón is not required for this application. Many other types of documents are accepted.",
      q3: "What does 'Living here since' mean?",
      a3: "Enter the approximate month and year you started living at your current address. An estimate is perfectly fine.",
    },
    s5: {
      q1: "Do I need a work contract to apply?",
      a1: "No. The application covers three pathways: work, family reunification, or vulnerability. A work contract is just one option.",
      q2: "What is an NIE number?",
      a2: "An NIE is a foreign ID number in Spain. If you don't have one, leave this field empty — it's optional.",
      q3: "Does being self-employed count?",
      a3: "Yes. A declaration of intent to work as self-employed is enough at this stage.",
    },
    s6: {
      q1: "Is an expired passport accepted?",
      a1: "Yes. Spanish law allows expired passports for this application. Upload whatever document you have.",
      q2: "What if I lost my passport?",
      a2: "Getting a new passport is the best way to prove your identity. For proving residence, you can use bills or bank statements.",
      q3: "What if I have no ID document at all?",
      a3: "Contact your country's consulate about emergency travel documents. Your advisor can guide you through the options.",
    },
    s7: {
      q1: "What types of documents count as proof?",
      a1: "Utility bills, bank statements, medical records, rental contracts, transport tickets — anything with your name and a date in Spain.",
      q2: "Do I need to prove every single month?",
      a2: "No. A combination of documents covering different periods across 5 months is enough.",
      q3: "What if I have very few documents?",
      a3: "Don't give up — many types are accepted and your advisor will help find the best combination. Upload what you have.",
    },
    s8: {
      q1: "Can I change my answers after this?",
      a1: "Yes. You can go back and edit any section at any time before the final official submission.",
      q2: "What happens after I save?",
      a2: "Your advisor will review your file and contact you if anything is missing or needs clarification.",
    },
    s9: {
      q1: "What exactly am I consenting to?",
      a1: "You're consenting to your data being used to evaluate your case and to be contacted by your advisor. Your data is not shared with third parties.",
      q2: "Can I withdraw my consent later?",
      a2: "Yes. Contact us at any time and we'll stop processing your data upon request.",
    },
  },
};

/* ─────────────────────────── FRENCH ─────────────────────────── */
const FR: Translations = {
  header: {
    firm: "Global Mobility", help: "Besoin d'aide ?", saved: "Enregistré ✓", saving: "Enregistrement…",
    close: "Fermer",
    deleteTitle: "Êtes-vous sûr(e) ?",
    deleteBody1: "Toutes vos données personnelles, documents et l'historique de votre dossier seront supprimés. Cette action est irréversible.",
    deleteBody2: "Votre compte sera désactivé immédiatement et vos données seront supprimées dans un délai de 15 jours.",
    deleteBody3: "Si vous changez d'avis, contactez hola@globalmobility.law pour réactiver votre compte avant la suppression.",
    deleteConfirm: "Oui, supprimer mon compte",
    deleteCountdown: "Oui, supprimer mon compte ({{n}})",
    cancel: "Annuler",
    addFamily: "Ajouter un membre de la famille",
    addFamilySoon: "Bientôt disponible — contactez votre avocat via WhatsApp pour ajouter des membres de la famille.",
    privacy: "Politique de confidentialité",
    logout: "Se déconnecter",
    deleteAccount: "Supprimer mon compte",
  },
  exitModal: {
    title: "Votre progression est sauvegardée !",
    body: "Vous pouvez revenir à tout moment et reprendre là où vous en étiez.",
    stay: "Continuer ma demande",
    leave: "Quitter",
  },
  progress: {
    of: "sur",
    steps: ["Confirmer données", "Votre situation", "Adresse", "Révision et envoi"],
    applyingFor: "Demande pour",
  },
  help: {
    title: "Besoin d'aide ?",
    placeholder: "Décrivez votre question ou problème…",
    helper: "Nous vous répondrons par message ou e-mail.",
    send: "Envoyer le message",
    sent: "Message envoyé ! Nous vous répondrons bientôt.",
    section: "Section actuelle",
  },
  nav: {
    back: "Retour",
    continue: "Continuer",
    submit: "Continuer",
    backToOrganizer: "Retour à Mon Organisateur",
    breadcrumbOrganizer: "Organisateur",
    breadcrumbDocuments: "Documents",
  },
  footer: {
    privacyPolicy: "Politique de confidentialité",
    termsOfService: "Conditions d'utilisation",
    cookiePolicy: "Politique relative aux cookies",
    legalNotice: "Mentions légales",
    copyright: "© 2026 Global Mobility Group LLC",
  },
  documentScanner: {
    title: "Scanner un document",
    desktopOnly: "Pour photographier plusieurs pages et obtenir un seul PDF, ouvre cet écran dans le navigateur de ton téléphone.",
    scannerBtn: "Scanner",
    addPages: "Ajouter des pages",
    addAnotherPage: "Ajouter une autre page",
    fromGallery: "Depuis la galerie",
    buildPdf: "Créer le PDF",
    processing: "Traitement…",
    scanMergeSaving: "Enregistrement de votre scan…",
    connectionProblemTitle: "Problème de connexion",
    connectionProblemBody:
      "Nous ne parvenons pas à joindre notre serveur, probablement à cause d’un signal cellulaire faible. Si vous annulez maintenant, vos scans seront perdus.",
    connectionProblemRetry: "Réessayer",
    connectionProblemCancel: "Annuler et perdre les scans",
    devicePdfFailureKicker: "Génération du PDF",
    devicePdfFailureTitle: "Impossible de créer votre PDF",
    devicePdfFailureBody:
      "Un problème est survenu lors de la génération du fichier sur votre appareil. Vos photos sont toujours enregistrées. Réessayez, ou fermez d’autres onglets du navigateur si la mémoire de votre téléphone est saturée. Notre équipe en a été informée.",
    devicePdfFailureRetry: "Réessayer",
    devicePdfFailureCancel: "Annuler",
    pageLabel: "Page {{n}}",
    remove: "Retirer",
    moveUp: "Monter",
    moveDown: "Descendre",
    close: "Fermer",
    needOnePage: "Ajoute au moins une photo de page.",
    errorGeneric: "Impossible de traiter ces images. Réessaie avec des photos plus nettes.",
    multiPageHint: "Capturez plusieurs pages et assemblez-les en un seul PDF tel quel.",
    cameraTitle: "Scanner",
    cameraLoadingLibs: "Chargement du scanner…",
    cameraStarting: "Ouverture de la caméra…",
    cameraTapToCapture: "Cadrez le document, puis appuyez sur le bouton blanc en bas.",
    cameraVideoNotReady: "L’aperçu de la caméra n’est pas prêt. Fermez et réessayez.",
    cameraAlignHint: "Place le document dans le cadre vert",
    cameraAlignSubtitle:
      "Cadrez serré : document qui remplit le cadre vert, peu de marge vide. Pas de doigts ; léger pli acceptable.",
    cameraAlignSubtitleSpread:
      "Mode plusieurs pages : placez le document dans le cadre vert avec une marge fine et régulière.",
    cameraFrameModeAria: "Forme du guide",
    cameraFrameModeSingle: "Une page",
    cameraFrameModeSpread: "Ouvert",
    cameraDocDetected: "Document détecté — touche pour capturer",
    cameraPermissionDenied: "Accès à la caméra refusé. Active-le dans les réglages du navigateur.",
    cameraStreamEnded:
      "La caméra s’est arrêtée (par ex. téléphone inactif ou onglet en arrière-plan). Appuyez sur Réessayer.",
    cameraPlayBlocked: "Impossible de reprendre l’aperçu. Appuyez sur Réessayer.",
    cameraRetryButton: "Réessayer",
    cameraNotFound: "Aucune caméra trouvée sur cet appareil.",
    cameraInitError: "Impossible de démarrer la caméra.",
    captureError: "Impossible de capturer la page.",
    cameraShutter: "Capturer la page",
    cameraPageCaptured: "Page enregistrée — une autre photo ou Fermer.",
    flattenedBadge: "Recadrée",
    flattenedBadgeHint: "Recadrée et redressée automatiquement sur cet appareil.",
    previewThumbnailAria: "Agrandir l’aperçu",
    previewHint: "Vérifie que la photo est nette et montre la page entière.",
    previewRotate: "Pivoter 90°",
    previewRotateAria: "Pivoter cette page de 90° dans le sens des aiguilles d’une montre",
    rotateError: "Impossible de faire pivoter l’image. Réessaie avec une autre photo ou depuis la galerie.",
    previewRemovePage: "Supprimer cette page",
    cornerAdjust: "Ajuster les coins et redresser",
    cornerAdjustAria: "Ouvrir l’ajustement des coins pour redresser et recadrer",
    cornerEditorTitle: "Redresser et recadrer",
    cornerEditorHint:
      "Fais glisser chaque coin jusqu’aux bords du document. On redresse la page et on réduit le fond vide.",
    cornerEditorApply: "Appliquer",
    cornerEditorReset: "Réinitialiser les coins",
    cornerEditorCancel: "Retour",
    cornerEditorBusy: "Traitement…",
    cornerEditorError: "Impossible de redresser l’image. Réessaie avec d’autres coins ou réinitialise.",
    cornerEditorInvalidQuad: "Les coins doivent former un quadrilatère valide dans la photo.",
    cornerHandleAria: "Coin",
    cornerEditorApplyContinue: "Appliquer et continuer",
    cornerEditorDone: "Terminé",
    cornerEditorAddPage: "+ Ajouter une page",
    cornerEditorResetHeader: "Réinitialiser",
    passportCornerDragHint:
      "Faites glisser les coins pour les aligner sur les bords du passeport.",
    passportCornerRetake: "Reprendre",
    passportIntroTitle: "Prenons une photo de votre première page",
    passportIntroHelp:
      "Ouvrez votre passeport sur la première page. Si besoin, utilisez un objet pointu pour le maintenir ouvert et photographiez les deux pages ouvertes. N'utilisez pas vos doigts.",
    passportTakePhoto: "Prendre une photo",
    passportContinue: "Continuer",
    passportSubmitPhotos: "Soumettre la ou les photo(s)",
    passportRetakePhoto: "Reprendre la photo",
    passportReviewPrev: "Page précédente",
    passportReviewNext: "Page suivante",
    passportReviewPageStatus: "{{current}} / {{total}}",
    passportPagesCapturedSummary: "{{n}} page(s) capturée(s).",
    passportSheetReviewHint:
      "Après chaque photo vous redressez les coins, puis vous pouvez en prendre une autre. Quand tout est prêt, appuyez sur « Revoir / ajuster », puis « Créer le PDF » en bas.",
    passportReviewAdjust: "Revoir / ajuster",
    passportSheetMinimalHint:
      "Vos photos sont encore ici. Créez le PDF, ou annulez pour revenir au choix import ou scanner.",
    passportPreviewReviewHint:
      "Appuyez sur « Modifier » pour redresser et recadrer. Si la photo ne convient pas, utilisez « Reprendre » en haut à gauche sur cet écran. Appuyez sur « ? » en haut à droite pour des exemples de photos. Faites glisser les vignettes pour réordonner. « Créer le PDF » une fois terminé.",
    reviewScreenTitle: "Revoir",
    reviewPageOf: "Page {{current}} sur {{total}}",
    reviewPagesSectionLabel: "PAGES",
    reviewReorderHint:
      "Faites défiler la ligne pour voir les vignettes. Touchez et faites glisser la poignée ⠿ d’une vignette pour réorganiser.",
    reviewReorderHoldA11y: "Poignée — maintenez et faites glisser pour réorganiser les pages.",
    reviewInsertPage: "Ajouter une page",
    reviewEdit: "Modifier",
    passportReviewView: "Voir",
    reviewDeletePageAria: "Supprimer la page",
    reviewEditMenuTitle: "Actions",
    reviewEditRemove: "Retirer la page",
    reviewDeleteConfirmTitle: "Supprimer cette page ?",
    reviewDeleteConfirmBody:
      "Cette page sera retirée du scan. Vous pourrez la capturer à nouveau ensuite.",
    reviewDeleteConfirmDelete: "Supprimer",
    reviewDeleteConfirmCancel: "Annuler",
    reviewLeaveConfirmTitle: "Abandonner vos photos ?",
    reviewLeaveConfirmBody: "Vous perdrez toutes les pages capturées jusqu’ici.",
    reviewLeaveConfirmStay: "Continuer à scanner",
    reviewLeaveConfirmDiscard: "Abandonner les photos",
    passportLowPageWarnTitle: "Toutes les pages sont incluses ?",
    passportLowPageWarnBody:
      "Nous avons en principe besoin d’une photo de chaque page de votre passeport, y compris les pages vierges. Il semble qu’il puisse manquer des pages.",
    passportLowPageWarnReview: "Revoir les pages",
    passportLowPageWarnAnyway: "Créer le PDF quand même",
    passportCameraHeader: "Passeport",
    passportCameraHintNext:
      "Placez le passeport ouvert dans le cadre vert, puis prenez une autre photo quand vous êtes prêt.",
    passportCaptureToast: "Photo enregistrée — vérifiez le recadrage.",
    passportCancel: "Annuler",
    passportPageTitle: "PAGE {{n}}",
    passportViewfinderPill:
      "Ouvrez le passeport à plat et capturez les deux pages.",
    passportViewfinderPillSubsequent:
      "Capturez la page suivante ouverte en une seule photo.",
    passportCaptureFooterHint:
      "Maintenez les pages bien à plat sur une surface. Vous pourrez ajuster les bords à l’étape suivante.",
    passportExamplesAria: "Exemples de photos",
    passportViewExamplesLink: "Voir des exemples de photos",
    passportCameraHelpAria: "Aide pour photographier le passeport",
    passportFlashAria: "Activer ou désactiver le flash",
    passportHelpGotIt: "Compris",
  },
  help: {
    empadronamientoTerm: "empadronamiento",
    empadronamiento: "C\'est votre certificat d\'inscription municipale. Il prouve que vous vivez dans une commune espagnole. Demandez-le à votre mairie, généralement sur rendez-vous. Demandez le certificat historique — il indique toutes vos adresses et dates.",
    vidaLaboralTerm: "vida laboral",
    vidaLaboral: "C\'est un document officiel de la Sécurité sociale qui montre tout votre historique d\'emploi en Espagne. Il est gratuit et vous pouvez l\'obtenir sur sede.seg-social.gob.es, par SMS au 600 123 456, ou en personne dans un bureau de la Sécurité sociale.",
    altaAutonomoTerm: "inscription d\'indépendant",
    altaAutonomo: "C\'est le document prouvant votre inscription en tant que travailleur indépendant en Espagne. Il s\'obtient auprès de l\'Agence fiscale et indique la date exacte à laquelle vous avez commencé votre activité.",
    modelo130Term: "modèle 130/303",
    modelo130: "Ce sont les déclarations fiscales trimestrielles pour les travailleurs indépendants. Le modèle 130 concerne l\'impôt sur le revenu (IRPF) et le 303 la TVA (IVA). Ils peuvent être téléchargés depuis le bureau électronique de l\'Agence fiscale.",
    libroFamiliaTerm: "livret de famille",
    libroFamilia: "C\'est un document officiel délivré par le Registre civil prouvant le mariage et les enfants. Si vous vous êtes marié(e) ou avez eu des enfants dans un autre pays, vous aurez besoin de l\'équivalent apostillé et traduit.",
    apostilladosTerm: "apostillés",
    apostillados: "L\'apostille est un cachet officiel qui valide les documents étrangers pour une utilisation internationale, selon la Convention de La Haye. Elle s\'obtient auprès du ministère ou de l\'autorité compétente du pays qui a émis le document. Si votre pays ne fait pas partie de la Convention de La Haye, vous avez besoin d\'une légalisation consulaire à la place.",
    tarjetaRojaTerm: "carte rouge",
    tarjetaRoja: "C\'est le document prouvant que vous avez demandé la protection internationale (asile) en Espagne. Il est renouvelé tous les 6 mois pendant que votre demande est en cours. La carte blanche est similaire mais pour les renouvellements.",
    declaracionJuradaTerm: "déclaration sous serment",
    declaracionJurada: "C\'est une déclaration signée devant notaire attestant que vous n\'avez pas de casier judiciaire. Elle n\'est acceptée que si vous ne pouvez pas obtenir le certificat officiel pour des raisons justifiées (guerre, absence de relations diplomatiques, etc.).",
  },
  validation: {
    required: "Ce champ est obligatoire.",
    invalidDate: "Date invalide.",
    invalidEmail: "Adresse e-mail invalide.",
    invalidPhone: "Numéro de téléphone invalide.",
    invalidNie: "Saisissez un NIE valide.",
    invalidPostal: "Code postal invalide (5 chiffres).",
    postalProvinceMismatch: "Les deux premiers chiffres du code postal ne correspondent pas à la province.",
    invalidAddressStreet:
      "Rue : 1 à 100 caractères ; lettres, chiffres, espaces, accents, points et virgules uniquement.",
    invalidAddressStreetNumber: "Numéro : 1 à 10 caractères ; lettres, chiffres ou S/N (avec /).",
    invalidAddressFloorDoor: "Porte : jusqu’à 10 caractères ; lettres et chiffres uniquement.",
    invalidAddressCity: "Ville : 1 à 40 caractères ; lettres, espaces, traits d’union et accents uniquement.",
    minLength: "Trop court.",
    invalidMonth: "Le mois doit être entre 01 et 12.",
    invalidDay: "Ce jour n'est pas valide pour ce mois.",
    futureDate: "La date ne peut pas être postérieure à aujourd'hui.",
    pastExpiryDate: "La date d'expiration doit être aujourd'hui ou dans le futur.",
    yearOutOfRange: "L'année est en dehors de la plage autorisée.",
    futureMonthYear: "Cette date ne peut pas être après le mois en cours.",
    invalidYear: "L'année n'est pas valide.",
  },
  deadline: { label: "Date limite de dépôt", date: "30 juin 2026" },
  optional: "Facultatif",
  returning: {
    greeting: "Bienvenue,",
    progress: "Progression de votre dossier",
    continue: "Reprendre où vous en étiez",
    restart: "Revoir depuis le début",
  },
  landing: {
    greeting: "Bonjour",
    firstVisitMessage: "Commençons votre demande. Cela ne prend qu\'environ 10 minutes.",
    firstVisitMessageWithAdvisor: "Je vais vous aider avec votre demande. Cela ne prend qu\'environ 10 minutes.",
    thisIs: "je suis",
    autoSave: "Vous pouvez partir et revenir à tout moment. Votre travail est sauvegardé automatiquement.",
    advisor: "Votre conseiller(ère) :",
    getStarted: "Commencer",
    continue: "Continuer",
    reviewFromStart: "Revoir depuis le début",
    progress: {
      moreThan3: "Vous progressez bien — continuons.",
      exactly3: "Vous êtes sur la bonne voie — il ne reste que 3 sections.",
      exactly2: "Presque là — il ne reste que 2 sections.",
      exactly1: "Presque là — il ne reste qu\'une section.",
      allDone: "Tout semble complet — revoyons et finalisons.",
    },
    procedureBadge: "Régularisation Extraordinaire Espagne 2026",
    videoLength: "1 min · Sous-titres disponibles",
    skipVideo: "Passer la vidéo",
    familyMenuHint: "Vous pouvez ajouter des membres de la famille à tout moment depuis le menu ☰",
    purpose: "Je vous guiderai dans tout ce dont vous avez besoin pour votre demande de résidence. Nous avons organisé tout en sections simples, avancez à votre rythme et téléchargez vos documents en cours de route. Je m\'occupe du reste. Ne vous inquiétez pas si vous n\'avez pas tout maintenant, vos réponses sont sauvegardées automatiquement, vous pouvez revenir à tout moment et reprendre là où vous en étiez.",
    purposeShort: "Je vous guiderai dans tout ce dont vous avez besoin pour votre demande de résidence. Vos réponses sont sauvegardées automatiquement, vous pouvez revenir quand vous voulez.",
    purposeExpanded: "Nous avons organisé tout en sections simples. Avancez à votre rythme et téléchargez vos documents en cours de route. Je m\'occupe du reste. Ne vous inquiétez pas si vous n\'avez pas tout maintenant, vous pouvez reprendre là où vous en étiez.",
    more: "Plus",
    less: "Moins",
    familyInfo: "Si vous postulez en famille, chaque personne de plus de 18 ans a besoin de sa propre demande. Les mineurs sont inclus dans la demande du parent ou tuteur légal.",
    familyInterestText: "Vous postulez avec des membres de votre famille ? Dites-le-nous et continuez votre demande. Nous vous contacterons pour la partie familiale.",
    familyInterestBtn: "Je suis intéressé(e) par une demande familiale",
    familyInterestConfirm: "Bien reçu, nous vous contacterons",
    defaultAdvisorName: "Ana Travieso",
    followInstagram: "Suivez-moi sur Instagram",
    consentIntro: "Avant de commencer, j'ai besoin de votre accord pour travailler avec vos informations.",
    consentLabel: "J\'accepte la",
    consentPrivacyLink: "Politique de confidentialité",
    consentSubtext: "(C\'est une obligation légale — vos données sont en sécurité avec nous.)",
  },
  section1: {
    title: "Confirmez vos données",
    title_rep: "Confirmer les données du demandeur",
    subtitle_applicant: "Veuillez vérifier que vos informations sont correctes avant de continuer.",
    subtitle_rep: "Veuillez vérifier que les informations du demandeur sont correctes.",
    isCorrect: "Est-ce correct ?",
    confirmBtn: "Oui, continuer",
    editLabel: "Modifier",
    firstName: "Prénom", firstNamePlaceholder: "Fatima",
    lastName: "Premier nom de famille", lastNamePlaceholder: "Benali",
    segundoApellido: "Deuxième nom de famille", segundoApellidoPlaceholder: "Diallo",
    segundoApellidoHelper: "Facultatif si non indiqué sur le document.",
    email: "E-mail", emailPlaceholder: "email@exemple.com",
    phone: "Numéro de mobile", phonePlaceholder: "600 000 000",
    voiceTitle: "Commençons par les bases.",
    voiceTitle_rep: "Commençons par les bases du demandeur.",
    fillingForSelf: "Remplissez-vous ceci pour vous-même ?",
    forMyself: "Oui, pour moi",
    forSomeoneElse: "Non, pour quelqu\'un d\'autre",
    repNote: "Parfait. D\'abord nous confirmons vos coordonnées, puis celles de la personne que vous aidez.",
    repRelationship: "Votre relation avec le demandeur",
    repRelationshipPlaceholder: "Membre de la famille, ami, travailleur social...",
    repRelSelectPlaceholder: "Sélectionnez votre relation",
    repRelFamily: "Membre de la famille",
    repRelSpouse: "Conjoint(e) / Partenaire",
    repRelFriend: "Ami(e)",
    repRelSocialWorker: "Travailleur/euse social(e)",
    repRelLawyer: "Avocat(e)",
    repRelNGO: "ONG / Organisation",
    repRelOther: "Autre",
    applicantTitle: "Informations du demandeur",
    applicantFirstName: "Prénom du demandeur",
    applicantFirstNamePlaceholder: "Prénom",
    applicantLastName: "Premier nom de famille du demandeur",
    applicantLastNamePlaceholder: "Premier nom de famille",
    applicantSegundoApellido: "Deuxième nom de famille",
    applicantSegundoApellidoPlaceholder: "Deuxième nom de famille",
    applicantEmail: "E-mail du demandeur",
    applicantEmailPlaceholder: "S\'il en a un",
    applicantPhone: "Téléphone du demandeur",
    applicantPhonePlaceholder: "S\'il en a un",
    repSectionLabel: "Vos coordonnées",
    sendPhoneOtp: "Envoyer le code",
    verifyPhoneOtp: "Vérifier",
    otpCodePlaceholder: "6 chiffres",
    phoneVerifiedBadge: "Vérifié",
    phoneOtpHelper:
      "Nous enverrons un code par SMS (comme à l’inscription) pour confirmer ce numéro et l’enregistrer sur votre profil.",
    phoneOtpSendError: "Impossible d’envoyer le code. Réessayez.",
    phoneOtpCodeHint: "Saisissez le code que nous vous avons envoyé.",
    phoneOtpSixDigitsHint: "Saisissez les 6 chiffres du code.",
    phoneAlreadyRegistered:
      "Ce numéro est déjà enregistré sur un autre compte. Utilisez un autre numéro.",
  },
  section2: {
    title: "Informations personnelles",
    title_rep: "Informations personnelles du demandeur",
    subtitle_applicant: "Complétez vos données personnelles telles qu'elles apparaissent sur votre document officiel.",
    subtitle_rep: "Complétez les données personnelles du demandeur.",
    dob: "Date de naissance", dobHelper: "DD / MM / AAAA",
    sex: "Sexe",
    sexOptions: { male: "Homme", female: "Femme", undetermined: "Indéterminé" },
    nationality: "Nationalité", nationalityPlaceholder: "Rechercher une nationalité…",
    countryOfBirth: "Pays de naissance", countryOfBirthPlaceholder: "Rechercher un pays…",
    placeOfBirth: "Lieu de naissance", placeOfBirthPlaceholder: "ex. Casablanca",
    placeOfBirthHelper: "Ville ou localité où vous êtes né(e).",
    voiceTitle: "Quelques informations personnelles, telles qu\'elles apparaissent sur votre document.",
    voiceTitle_rep: "Quelques informations personnelles, telles qu\'elles apparaissent sur le document du demandeur.",
  },
  section2e: {
    stepLabel: "Votre situation",
    voiceTitle: "Parlez-nous de votre situation pour que nous puissions orienter votre demande.",
    asylumRoutingTitle: "Avez-vous demandé l'asile en Espagne ?",
    asylumRoutingSubtitle: "Nous entendons par là la protection internationale, déposée auprès de la police espagnole avant le 1er janvier 2026.",
    asylumRoutingYesTitle: "Oui",
    asylumRoutingYesDescription: "J'ai demandé l'asile en Espagne.",
    asylumRoutingNoTitle: "Non",
    asylumRoutingNoDescription: "Je n'ai pas demandé l'asile en Espagne.",
    qualifyingTitle: "Laquelle décrit le mieux votre situation actuelle ?",
    qualifyingSubtitle: "Choisissez l'option qui correspond le mieux à votre situation.",
    qualifyingWorkTitle: "Par le travail",
    qualifyingWorkDescription: "Je travaille en Espagne, j'ai une offre d'emploi ou je prévois de travailler à mon compte.",
    qualifyingFamilyTitle: "Par la famille",
    qualifyingFamilyDescription: "Je vis avec mes enfants ou mes parents en Espagne.",
    qualifyingVulnerabilityTitle: "Par une situation de vulnérabilité",
    qualifyingVulnerabilityDescription: "Je peux obtenir un certificat des services sociaux ou d'une organisation d'accompagnement.",
    yes: "Oui",
    no: "Non",
    arrivalDate: "Quand êtes-vous arrivé(e) en Espagne ?",
    arrivalDateHelper: "Date d\'arrivée approximative. Pas besoin d\'être exact.",
    submissionFixed: "Votre demande peut être soumise à partir du 1er avril 2026.",
    submissionFrom: "Votre demande peut être soumise à partir du {{date}}.",
    hardStopArrival: "La régularisation extraordinaire exige une présence en Espagne avant le 1er janvier 2026. Mais ne vous inquiétez pas, il peut y avoir d\'autres options pour vous.",
    hardStopTooLate: "Avec cette date d\'arrivée, les 5 mois requis tomberaient après la date limite du 30 juin 2026. Mais il peut y avoir d\'autres voies disponibles.",
    validPermit: "Avez-vous actuellement un permis de résidence valide en Espagne ?",
    hardStopPermit: "Si vous avez déjà un permis de résidence valide, vous avez probablement besoin d\'un renouvellement ou d\'une modification. Je peux vous aider.",
    asylum: "Avez-vous demandé l\'asile ou la protection internationale en Espagne avant le 1er janvier 2026 ?",
    helpAsylum: "Vérifiez votre récépissé de demande d’asile ou tarjeta roja : la date indique quand vous avez déposé la demande.",
    oarLabel: "Numéro de dossier d’asile (OAR)",
    oarPlaceholder: "ex. OAR/2024/12345",
    oarHelper: "Vous le trouverez sur le récépissé ou la tarjeta roja. Si vous ne l’avez pas maintenant, vous pourrez l’ajouter plus tard.",
    subRouteLabel: "Quelle situation vous correspond le mieux ?",
    subRouteWork: "J’ai un emploi ou une offre d’emploi",
    subRouteFamily: "Je vis avec ma famille en Espagne",
    subRouteVulnerability: "Je suis dans une situation de vulnérabilité",
    subRouteWorkDesc: "J’ai travaillé en Espagne, j’y travaille actuellement ou j’ai une offre d’emploi.",
    subRouteFamilyDesc: "Je vis en Espagne avec mon conjoint, mes enfants ou d’autres membres de ma famille.",
    subRouteVulnerabilityDesc: "Je traverse une période difficile et j’ai besoin du soutien des services sociaux.",
    workHistoryLabel: "Quelle proposition décrit le mieux votre situation professionnelle ?",
    workHistoryWorked: "J’ai déjà travaillé en Espagne",
    workHistoryCurrent: "J’ai actuellement un emploi",
    workHistoryOffer: "J’ai une offre d’emploi",
    workTypeLabel: "Salarié ou indépendant ?",
    workTypeEmployed: "Salarié",
    workTypeSelfEmployed: "Indépendant",
    selfEmploymentActivityLabel: "Décrivez votre activité",
    selfEmploymentActivityPlaceholder: "ex. livreur, cuisinière, graphiste",
    selfEmploymentActivityHint: "Requis pour la déclaration sur l'honneur EX-32 d'intention d'exercer une activité indépendante.",
    empHeading: "Documents de travail",
    empHeadingEmployed: "Documents de travail",
    empHeadingSelfEmployed: "Documents de travail indépendant",
    workTypeGatePrompt: "Dites-nous comment vous travaillez pour savoir ce qu’il faut demander.",
    workTypeEmployedHelper: "Vous travaillez pour un employeur ou avez un contrat / une offre.",
    workTypeSelfEmployedHelper: "Vous travaillez pour vous-même ou prévoyez de le faire en indépendant.",
    empDescriptionLabel: "Décrivez votre activité",
    empDescriptionPlaceholder: "ex. livreur, cuisinière, graphiste",
    selfEmployedNote: "Vous travaillez pour vous-même ou prévoyez de le faire en indépendant.",
    empDocContract: "Contrat de travail",
    empDocContractHelper: "Contrats signés d’employeurs actuels ou passés en Espagne. Inclure au minimum la première page et la page de signature.",
    empDocPayslip: "Bulletin de paie",
    empDocPayslipHelper: "Bulletins récents montrant votre salaire et votre employeur. Les 3 à 6 derniers mois sont les plus utiles.",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper: "Votre relevé officiel d’activité de la Sécurité sociale. Il montre tous les emplois que vous avez eus. Très solide.",
    empDocJobOffer: "Lettre d’offre d’emploi",
    empDocJobOfferHelper: "Une lettre signée par un employeur. Elle doit indiquer le poste, la date de début, le salaire et les coordonnées de l’employeur.",
    empDocOther: "Autres documents",
    empDocOtherHelper: "Tout autre document qui appuie votre situation professionnelle.",
    empOtherPlaceholder: "Brève description",
    empAddDoc: "Ajouter un document",
    empGateContinue: "Continuer vers le téléchargement des documents",
    empTipsTitle: "Conseils",
    empTipOptional: "Téléchargez uniquement les documents qui s’appliquent à votre situation.",
    empTipVidaLaboral: "La vida laboral est souvent le document le plus solide pour un salarié.",
    empTipJobOffer: "Une offre d’emploi peut suffire si vous n’avez pas encore de contrat.",
    empTipInformal: "Si vous n’avez qu’une preuve informelle, téléchargez ce que vous avez.",
    empTipAutonomo: "Pour l’indépendant, ajoutez l’inscription et les déclarations trimestrielles.",
    criminalRecord: "Avez-vous un casier judiciaire au cours des cinq dernières années ?",
    helpCriminal:
      "Incluez condamnations ou procédures pénales en cours dans tout pays au cours des cinq dernières années, pas seulement en Espagne. Si vous répondez Non, nous demanderons un extrait de casier judiciaire dans le parcours documents.",
    previousApplication: "Avez-vous déjà déposé une demande d’immigration en Espagne ?",
    helpPreviousApplication:
      "Incluez titres de séjour, régularisations ou autres demandes d’immigration en Espagne, même refusées ou classées.",
    callbackBtn: "Je souhaite explorer les options",
    callbackConfirm: "✓ Nous vous contacterons bientôt",
    hasPassport: "Avez-vous un passeport valide ?",
    hasPassportHelp: "Un passeport de votre pays d’origine. Peu importe s’il est expiré ou sur le point d’expirer.",
    noPassportTitle: "Pas de problème, je m’en occupe.",
    noPassportBody:
      "Si vous n’avez pas de passeport, je préparerai une déclaration officielle (Anexo I-2) et la signerai en votre nom en tant que représentante autorisée. Vous n’aurez rien d’autre à faire.",
    helpPermit:
      "Ce programme s'adresse aux personnes sans titre de séjour en cours de validité. Si vous en avez un, nous pouvons vous aider pour les renouvellements et les modifications.",
    permitOtherServices: "Découvrir nos autres services",
    permitNotSure: "Pas sûr(e) ? Continuez quand même et j'examinerai votre dossier.",
    soon: "Bientôt",
    showOtherOptions: "Afficher les autres options",
    scenario_1_label: "C'est ma première demande",
    scenario_1_h: "",
    scenario_2_label: "Un enfant né en Espagne",
    scenario_2_h: "",
    scenario_3_label: "Un enfant non né en Espagne",
    scenario_3_h: "",
    scenario_4_label: "Un membre de la famille",
    scenario_4_h: "",
    scenario_7A_label: "Je travaille ou j'ai une offre d'emploi",
    scenario_7A_h: "",
    scenario_7B_label: "Je vis avec ma famille en Espagne",
    scenario_7B_h: "",
    scenario_7C_label: "J'ai un rapport de vulnérabilité",
    scenario_7C_h: "",
    scenario_8_label: "Un enfant né en Espagne",
    scenario_8_h: "",
    scenario_9_label: "Un enfant non né en Espagne",
    scenario_9_h: "",
    scenario_10_label: "Un membre de la famille",
    scenario_10_h: "",
    extensionGroup_da20: "Je renouvelle mon autorisation de séjour",
    familyGroup_label: "Je fais la demande pour un membre de ma famille",
    scenario_ext_jobSearch_label: "Je cherche un emploi",
    scenario_ext_jobSearch_h_da20: "",
    scenario_ext_integration_label: "J'ai un rapport d'intégration",
    scenario_ext_integration_h_da20: "",
    scenario_ext_illness_label: "J'ai une maladie",
    scenario_ext_illness_h_da20: "",
    scenario_ext_disability_label: "J'ai un handicap",
    scenario_ext_disability_h_da20: "",
    scenario_ext_retirement_label: "Je suis à la retraite",
    scenario_ext_retirement_h_da20: "",
  },
  section3: {
    title: "État civil et famille",
    title_rep: "État civil et famille du demandeur",
    subtitle_applicant: "Quelques questions rapides sur votre situation familiale.",
    subtitle_rep: "Quelques questions rapides sur la situation familiale du demandeur.",
    civilStatus: "État civil",
    civilOptions: { single: "Célibataire", married: "Marié(e)", widowed: "Veuf/Veuve", divorced: "Divorcé(e)", separated: "Séparé(e)" },
    fatherName: "Prénom du père", fatherNamePlaceholder: "Mohamed",
    fatherNameHelper: "Sans nom de famille",
    fatherFirst: "Prénom",
    fatherLast1: "Nom",
    fatherLast2: "Deuxième nom",
    fatherUnknown: "Je ne connais pas son prénom",
    parentNotAvailable: "Non disponible",
    motherName: "Prénom de la mère", motherNamePlaceholder: "Fatima",
    motherNameHelper: "Sans nom de famille",
    motherFirst: "Prénom",
    motherLast1: "Nom",
    motherLast2: "Deuxième nom",
    motherUnknown: "Je ne connais pas son prénom",
    parentsNote: "Les données des parents sont facultatives.",
    spouseQuestion: "Avez-vous un conjoint ou un partenaire enregistré ?",
    spouseHelp: "Répondez oui si vous êtes marié(e) ou avez un partenariat officiellement enregistré.",
    spouseDetailsLabel: "Détails du conjoint",
    spouseFirstName: "Prénom",
    spouseLastName: "Nom",
    spouseSecondLastName: "Deuxième nom",
    spouseDob: "Date de naissance",
    spouseNationality: "Nationalité",
    spouseLegalStatus: "A-t-il/elle une situation légale en Espagne ?",
    spouseLegalStatusHelp: "Si votre conjoint possède un NIE ou un titre de séjour légal en Espagne, répondez oui.",
    spouseNie: "NIE ou numéro de passeport",
    childrenQuestion: "Avez-vous des enfants ?",
    childrenHelp: "Incluez les enfants biologiques, adoptés ou légalement à votre charge.",
    childHeader: "Enfant",
    addChild: "Ajouter un enfant",
    childFirstName: "Prénom",
    childLastName: "Nom",
    childSecondLastName: "Deuxième nom",
    childDob: "Date de naissance",
    childNationality: "Nationalité",
    childLivesInSpain: "Vit avec vous en Espagne ?",
    childLivesInSpainHelp: "Répondez oui si cet enfant vit actuellement avec vous en Espagne.",
    childInSchool: "Est-il scolarisé en Espagne ?",
    childInSchoolHelp: "Répondez oui si cet enfant est inscrit dans un établissement scolaire en Espagne.",
    removeChild: "Supprimer l'enfant",
    dependentParentsQuestion: "Avez-vous des parents à votre charge qui vivent avec vous ?",
    dependentParentsHelp: "Parents qui dépendent financièrement de vous et vivent avec vous.",
    parentHeader: "Parent",
    addParent: "Ajouter un parent",
    parentFirstName: "Prénom",
    parentLastName: "Nom",
    parentDob: "Date de naissance",
    parentNationality: "Nationalité",
    removeParent: "Supprimer le parent",
    sexLabel: "Sexe",
    sexPlaceholder: "Sélectionner...",
    sexMale: "Homme",
    sexFemale: "Femme",
    sexUndetermined: "X / indéterminé",
    unknownLabel: "Non disponible",
    dependentsQuestion: "Avez-vous des enfants mineurs ou des membres de la famille à charge vivant avec vous en Espagne ?",
    dependentsHelper: "Inclut les enfants de moins de 18 ans, les enfants adultes handicapés ou les parents à votre charge.",
    voiceTitle: "Quelques questions rapides sur votre situation familiale.",
    voiceTitle_rep: "Quelques questions rapides sur la situation familiale du demandeur.",
  },
  section4: {
    title: "Adresse",
    title_rep: "Adresse du demandeur",
    subtitle_applicant: "Où habitez-vous en Espagne ?",
    subtitle_rep: "Où le demandeur habite-t-il en Espagne ?",
    streetType: "Type de voie",
    streetTypePlaceholder: "Sélectionner le type de voie",
    streetTypeOptionStreet: "Rue",
    streetTypeOptionAvenue: "Avenue",
    streetTypeOptionSquare: "Place",
    streetTypeOptionRoad: "Route",
    streetTypeOptionPassage: "Passage",
    streetTypeOptionOther: "Autre",
    street: "Adresse",
    streetPlaceholder: "Nom de la rue",
    streetNumber: "Nº",
    streetNumberPlaceholder: "Nº",
    floorPiso: "Étage",
    floorPisoPlaceholder: "—",
    floorDoor: "Porte",
    floorDoorPlaceholder: "A, B",
    city: "Localité",
    cityPlaceholder: "Ville ou commune",
    province: "Province",
    provincePlaceholder: "Sélectionner une province",
    municipality: "Municipalité",
    municipalityPlaceholder: "Sélectionner une municipalité",
    locality: "Localité",
    localityPlaceholder: "Sélectionner une localité",
    postalCode: "C.P.",
    postalCodePlaceholder: "08001",
    country: "Pays",
    countryPlaceholder: "Espagne",
    residenceSince: "Depuis quand vivez-vous ici ?",
    residenceSinceHelper: "Mois et année approximatifs où vous avez commencé à vivre à cette adresse.",
    residenceSincePlaceholderMonth: "MM",
    residenceSincePlaceholderYear: "AAAA",
    residenceSinceOptional: "Facultatif",
    voiceTitle: "Où habitez-vous en Espagne ?",
    voiceTitle_rep: "Où le demandeur habite-t-il en Espagne ?",
  },
  section5: {
    title: "Votre situation en Espagne",
    title_rep: "Situation du demandeur en Espagne",
    subtitle_applicant: "Parlez-nous de votre situation actuelle en Espagne.",
    subtitle_rep: "Informations sur la situation actuelle du demandeur.",
    arrivalDate: "Quand êtes-vous arrivé(e) en Espagne ?", arrivalDateHelper: "Jour / Mois / Année approximatifs.",
    wantsToWork: "Voulez-vous travailler en Espagne ?",
    wantsToWorkOptions: { yes: "Oui", no: "Non" },
    workSituation: "Quelle est votre situation ?",
    workSituation_rep: "Quelle est leur situation ?",
    workSituationOptions: { employed: "J'ai un emploi", looking: "Je cherche du travail", selfemployed: "Indépendant(e)" },
    nie: "NIE", niePlaceholder: "X1234567A", nieHelper: "Optionnel. Expiré accepté",
    nieAsk: "Vous n'en avez pas ? Demandez-nous",
    voiceTitle: "Quelques questions sur votre situation.",
    voiceTitle_rep: "Quelques questions sur la situation du demandeur.",
  },
  section6: {
    title: "Votre passeport",
    title_rep: "Passeport du demandeur",
    subtitle:
      "Prenez en photo la page de données ou téléchargez un PDF. Nous remplirons vos informations automatiquement.",
    subtitle_applicant: "Informations sur votre document d'identité ou passeport.",
    subtitle_rep: "Informations sur le document d'identité du demandeur.",
    photoInstructions:
      "Prenez une photo ou téléchargez l'image de la page avec votre photo et vos données. Si vous avez déjà un PDF complet du passeport, téléchargez-le ici. N'essayez pas de photographier chaque page séparément.",
    photoHint:
      "Vous pourrez corriger les détails ensuite. Vous téléchargerez le passeport complet (toutes les pages) plus tard.",
    passportName: "Nom tel qu'il apparaît sur le passeport",
    docType: "Type de document",
    docTypeOptions: {
      passport: "Passeport",
      nationalId: "Carte d'identité",
      travelDoc: "Titre voyage",
      cedulaInscripcion: "Inscription",
      other: "Autre",
    },
    docTypeOtherLabel: "Précisez le type de document",
    docTypeOtherPlaceholder: "ex. document de réfugié",
    passportNumber: "Numéro de passeport", passportNumberPlaceholder: "Ex. ABC123456",
    passportNumberHelper: "Tel qu'il figure sur le passeport.",
    docExpiry: "Date d'expiration", docExpiryHelper: "Jour / Mois / Année",
    docExpiryAsk: "Pas sûr(e) ? Demandez-nous",
    expiredNote: "Les documents expirés sont acceptés.",
    docUpload: "Télécharger votre document",
    docUploadHelper: "Prenez une photo de la page principale de votre passeport (celle avec votre photo et vos données personnelles). Posez-le sur une surface plane avec un bon éclairage.",
    uploadBtn: "Choisir un fichier", cameraBtn: "Utiliser la caméra",
    uploadedLabel: "Fichier joint :", changeFile: "Changer",
    nie: "NIE",
    niePlaceholder: "X1234567A",
    nieHelper: "Optionnel. Expiré accepté",
    nieAsk: "Vous n'en avez pas ? Demandez-nous",
    docName: "Nom tel qu'il apparaît sur votre document",
    docNameHelper: "Confirmez que cela correspond à votre document d'identité.",
    seeExample: "Voir des exemples",
    photoGuideTitle: "Exigences photo",
    photoGood: "À plat, bonne lumière, tout le texte visible",
    photoGoodCaption: "Correct — à plat, bonne lumière, tout le texte lisible, les quatre coins visibles",
    photoBad1: "Floue",
    photoBad2: "Reflet ou ombre",
    photoBad3: "Coupée ou avec des doigts",
    carouselBad1: "Floue — le texte ne peut pas être lu. Tenez votre téléphone stable et faites la mise au point avant de prendre la photo.",
    carouselBad2: "Reflet ou ombre — une partie du texte est masquée. Utilisez la lumière naturelle sans reflets directs.",
    carouselBad3: "Coupée — une partie du document est manquante. Assurez-vous que les quatre coins sont entièrement visibles.",
    carouselBad4: "Doigts visibles — vos doigts couvrent une partie du document. Tenez-le par les bords ou posez-le à plat sur une surface.",
    goodExampleTitle: "Bon exemple",
    badExampleTitle: "Mauvais exemples",
    photoGotIt: "Compris",
    voiceTitle: "Maintenant votre pièce d'identité.",
    voiceTitle_rep: "Maintenant la pièce d'identité du demandeur.",
  },
  section7: {
    title: "Preuve de séjour",
    title_rep: "Preuve de séjour du demandeur",
    subtitle_applicant: "Quels documents avez-vous pour prouver votre séjour en Espagne ?",
    subtitle_rep: "Quels documents le demandeur a-t-il pour prouver son séjour en Espagne ?",
    checklistTitle: "Sélectionnez les documents que vous avez",
    checklistHelper: "Vous pouvez en sélectionner plusieurs. Tous sont facultatifs.",
    docTypes: { utility: "Facture de services", bank: "Relevé bancaire", medical: "Dossier médical", rental: "Contrat de location", transport: "Billet de transport", other: "Autre" },
    uploadTitle: "Télécharger un document", uploadHelper: "Vous pourrez en ajouter d'autres plus tard.",
    uploadBtn: "Choisir un fichier", cameraBtn: "Utiliser la caméra",
    uploadedLabel: "Fichier joint :", changeFile: "Changer",
    allOptional: "Tous les documents sont facultatifs à ce stade.",
    stayUploadAsk: "Quels documents conviennent ? Demandez-nous",
    passportUploadGuideLabel: "Comment télécharger votre passeport complet ?",
    passportUploadGuide: "Nous avons besoin de toutes les pages de votre passeport dans un seul PDF, y compris les pages vierges. Deux options faciles :\n\n📱 Utilisez une application gratuite comme Adobe Scan ou Google Drive pour scanner toutes les pages en un PDF depuis votre téléphone.\n\n🏪 Rendez-vous dans la copistería ou le locutorio le plus proche et demandez-leur de scanner votre passeport complet en PDF. Ils peuvent vous l'envoyer par e-mail ou le sauvegarder sur votre téléphone.",
    voiceTitle: "Dernière étape — preuve que vous avez séjourné en Espagne.",
    voiceTitle_rep: "Dernière étape — preuve que le demandeur a séjourné en Espagne.",
    asylumTitle: "Votre documentation d\'asile",
    asylumInstructions: "En tant que demandeur d\'asile, vous n\'avez pas besoin de prouver 5 mois de séjour. Téléchargez simplement votre documentation d\'asile.",
    longTermTitle: "Preuve de votre séjour en Espagne",
    longTermInstructions: "Vous êtes en Espagne depuis plus de 5 ans, vous avez donc juste besoin de quelques documents récents pour confirmer votre résidence continue.",
    standardTitle: "Preuve de votre séjour en Espagne",
    standardInstructions: "Téléchargez des documents montrant que vous avez vécu en Espagne. Tout document avec votre nom et une date convient.",
    priorDenialTitle: "Vos documents",
    priorDenialInstructions: "Comme vous avez eu un processus antérieur, nous avons besoin de quelques documents spécifiques.",
    asylumDocTypeLabel: "Type de document d\'asile",
    asylumDocTypes: { receipt: "Reçu d\'asile", resolution: "Résolution d\'asile", other: "Autre document d\'asile" },
    asylumUploadLabel: "Téléchargez votre document d\'asile",
    asylumOptionalTitle: "Avez-vous d\'autres documents montrant votre séjour en Espagne ?",
    asylumOptionalHelper: "Non obligatoires mais peuvent renforcer votre dossier.",
    asylumOptionalExpand: "Ajouter des documents supplémentaires",
    asylumOptionalCollapse: "Masquer les documents supplémentaires",
    denialSectionATitle: "Documents du processus antérieur",
    denialResolutionLabel: "Résolution de refus antérieure",
    denialCriminalLabel: "Casier judiciaire du processus antérieur",
    denialCriminalNote: "Même expirés, ils sont valides puisque vous êtes resté(e) en Espagne.",
    denialSectionBTitle: "Preuve de séjour en Espagne",
    denialSectionBHelper: "Nous avons besoin de documents couvrant au moins 5 mois.",
    addDocBtn: "+ Ajouter un document",
    docTypeLabel: "Type de document",
    docTypeOptions: { utility: "Facture de services", bank: "Relevé bancaire", medical: "Dossier médical", rental: "Contrat de location", transport: "Billet de transport", other: "Autre" },
    monthCoverageLabel: "Mois couverts",
    monthCoverageHelper: "Chaque document que vous téléchargez peut couvrir un ou plusieurs mois.",
    uploadDocLabel: "Télécharger le document",
    removeDoc: "Supprimer",
    docEntryTitle: "Document {{n}}",
    monthLabel: "De quel mois est ce document ?",
    tierLabel1: "Plus fort",
    tierLabel2: "Fort",
    tierLabel3: "Soutien",
    tierLabel4: "Complémentaire",
    tieredDocTypes: {
      padron: "Padrón / Inscription municipale",
      rental: "Contrat de location",
      medical: "Dossier médical",
      school: "Inscription scolaire des enfants",
      bank: "Relevé bancaire",
      utility: "Facture de services (électricité, eau, gaz, internet)",
      payslip: "Fiche de paie ou contrat de travail",
      transfer: "Reçu de virement",
      course: "Certificat de cours ou formation",
      transport: "Billet de transport",
      police: "Rapport de police",
      official: "Lettre ou notification officielle",
      other: "Autre",
    },
    monthsCovered: "Documents téléchargés couvrant {{n}} sur 5 mois",
    allMonthsCovered: "✓ Les 5 mois couverts",
    infoNote: "Téléchargez les documents que vous avez. Vous n\'en avez pas besoin de tous maintenant, vous pouvez en ajouter à tout moment. Le padrón (inscription municipale) est le document le plus important — si vous l\'avez, téléchargez-le en premier.",
    videoLabel: "1 min · Quels documents sont acceptés",
    // Flat alias keys
    title: "Preuve de votre séjour en Espagne",
    voiceTitle: "Dernière étape — preuve de votre séjour en Espagne.",
    instructions: "Téléchargez des documents montrant que vous avez vécu en Espagne. Tout document avec votre nom et une date convient.",
    titleLongTerm: "Preuve de votre séjour en Espagne",
    instructionsLongTerm: "Vous êtes en Espagne depuis plus de 5 ans, vous avez donc juste besoin de quelques documents récents pour confirmer votre résidence continue.",
    titleAsylum: "Votre documentation d\'asile",
    instructionsAsylum: "En tant que demandeur d\'asile, vous n\'avez pas besoin de prouver 5 mois de séjour. Téléchargez simplement votre documentation d\'asile.",
    asylumOptionalNote: "Non obligatoires mais peuvent renforcer votre dossier.",
    asylumReceipt: "Reçu d\'asile",
    asylumResolution: "Résolution d\'asile",
    asylumOther: "Autre document d\'asile",
    titlePriorDenial: "Vos documents",
    instructionsPriorDenial: "Comme vous avez eu un processus antérieur, nous avons besoin de quelques documents spécifiques.",
    denialResolution: "Résolution de refus antérieure",
    priorCriminalRecords: "Casier judiciaire du processus antérieur",
    priorCriminalNote: "Même expirés, ils sont valides puisque vous êtes resté(e) en Espagne.",
    docTypePadron: "Padrón / Inscription municipale",
    docTypeRental: "Contrat de location",
    docTypeMedical: "Dossier médical",
    docTypeSchool: "Inscription scolaire des enfants",
    docTypeBank: "Relevé bancaire",
    docTypeUtility: "Facture de services (électricité, eau, gaz, internet)",
    docTypePayslip: "Fiche de paie ou contrat de travail",
    docTypeTransfer: "Reçu de virement",
    docTypeCourse: "Certificat de cours ou formation",
    docTypeTransport: "Billet de transport",
    docTypePolice: "Rapport de police",
    docTypeOfficial: "Lettre ou notification officielle",
    docTypeOther: "Autre",
    acceptedWidget: {
      title: "Quels documents comptent comme preuve de résidence ?",
      subtitleOpen: "Assurez-vous que chaque document inclut votre nom et une date. Les documents sans nom ou sans date ne sont pas valides.",
      tier1Label: "Niveau 1 — Preuve la plus forte",
      tier2Label: "Niveau 2 — Preuve forte",
      tier3Label: "Niveau 3 — Preuve d’appui",
      descPadron: "Certificat d’empadronamiento (inscription municipale) délivré par la mairie où vous vivez. L’empadronamiento historique est le document unique le plus fort, car il montre toutes vos dates d’inscription, pas seulement la date actuelle. À lui seul, il n’est pas toujours suffisant, mais il constitue la base de presque toutes les demandes solides.",
    descRental: "Contrat de location à votre nom pour un logement en Espagne. Plus solide s’il est enregistré auprès du registre du logement de la communauté autonome, ou accompagné de reçus mensuels prouvant la continuité.",
    descMedical: "Preuves de visites chez le médecin, à l’hôpital ou dans un centre de santé public avec une date et votre nom. Carte de santé, rapports médicaux, ordonnances et rendez-vous du système public comptent aussi. C’est une preuve particulièrement utile car difficile à falsifier.",
    descSchool: "Certificats d’inscription, d’assiduité ou scolaires pour vous ou vos enfants dans un établissement éducatif espagnol. Cela inclut crèches, écoles, collèges, lycées, académies reconnues et universités.",
    descBank: "Relevés d’un compte bancaire espagnol montrant vos transactions au fil du temps. Plus ils couvrent de mois, plus la preuve est forte. Les comptes avec une activité régulière en Espagne sont les plus convaincants.",
    descUtility: "Factures d’électricité, d’eau, de gaz, d’internet ou de téléphone mobile à votre nom et à une adresse en Espagne. Des factures récurrentes sur plusieurs mois récents aident à démontrer une présence continue.",
    descPayslip: "Bulletins de salaire ou contrats de travail indiquant l’employeur, la date et votre nom. Si vous avez un historique de travail formel en Espagne, ces documents soutiennent aussi la voie professionnelle de la régularisation, pas seulement la preuve de résidence.",
    descTransfer: "Reçus de transferts d’argent envoyés ou reçus depuis l’Espagne (Western Union, MoneyGram, Wise, Ria, etc.). Chaque reçu doit mentionner votre nom, le lieu en Espagne et la date.",
    descCourse: "Certificats, inscriptions ou diplômes de cours, d’académies de langues ou de programmes de formation en Espagne. Cela inclut cours d’espagnol, formation professionnelle et programmes privés accrédités.",
    descTransport: "Abonnement mensuel de transport, billets Renfe, cartes de transport rechargées, billets d’avion ou de bus. Chaque billet doit comporter votre nom et une date pour être valable.",
    descPolice: "Plaintes, procès-verbaux ou tout document de police qui vous identifie par votre nom et comporte une date. Cela inclut les plaintes que vous avez déposées comme victime ou témoin, pas seulement celles où vous êtes mentionné comme mis en cause.",
    descOfficial: "Lettres ou notifications officielles de toute administration publique espagnole (Sécurité sociale, impôts, mairie, communauté autonome, tribunaux, etc.) adressées à votre nom. Un courrier officiel envoyé à une adresse espagnole est une preuve solide que vous y vivez.",
    descOther: "Tout autre document avec votre nom et une date prouvant votre présence en Espagne : tickets d’achat, billets d’événements, certificats religieux, lettres d’associations, etc.",
    },
    addDocument: "+ Ajouter un document",
    whatMonth: "De quel mois s\'agit-il ?",
    uploadNote: "Téléchargez les documents que vous avez. Vous n\'avez pas besoin de tout avoir maintenant, vous pouvez en ajouter à tout moment. Le padrón (inscription municipale) est le document le plus important — si vous l\'avez, téléchargez-le en premier.",
    coverageLabel: "Documents couvrant {{n}} sur 5 mois",
    coverageComplete: "✓ Les 5 mois sont couverts",
    criminalDocType: "Documentation du casier judiciaire",
    criminalTitle: "Documentation sur le casier judiciaire",
    criminalInstructions: "Téléchargez toute documentation concernant votre casier — certificats d\'annulation, documents judiciaires, ou tout ce qui montre l\'état de votre dossier. Votre conseiller l\'examinera.",
    criminalUploadLabel: "Documentation du casier judiciaire",
    docEntryTitle: "Document {{n}}",
    standardInstructions: "Téléchargez des documents montrant que vous avez vécu en Espagne. Tout document avec votre nom et une date convient.",
    asylumUploadLabel: "Téléchargez votre document d\'asile",
    asylumOptionalExpand: "Ajouter des documents supplémentaires",
    addDocBtn: "+ Ajouter un document",
    uploadDocLabel: "Télécharger le document",
    monthLabel: "De quel mois s\'agit-il ?",
    removeDoc: "Supprimer",
    monthsCovered: "Documents couvrant {{n}} sur 5 mois",
    allMonthsCovered: "✓ Les 5 mois sont couverts",
    infoNote: "Téléchargez les documents que vous avez. Vous n\'avez pas besoin de tout avoir maintenant, vous pouvez en ajouter à tout moment. Le padrón (inscription municipale) est le document le plus important — si vous l\'avez, téléchargez-le en premier.",
  },
  section7b: {
    title: "Quels documents vous aurez besoin",
    subtitle: "Vous devez prouver une présence continue en Espagne pendant environ {{duration}}. Les documents doivent couvrir autant de mois que possible.",
    monthsApprox: "{{n}} mois",
    yearsApprox: "{{n}} ans",
    primaryTitle: "Empadronamiento (inscription municipale)",
    primaryBadge: "Preuve la plus forte",
    primaryDesc: "Votre certificat d\'inscription municipale. C\'est le document le plus important — il prouve directement votre adresse et votre présence en Espagne. Si vous ne téléchargez qu\'un seul document, que ce soit celui-ci.",
    primaryHistorical: "Demandez un certificat historique à votre mairie montrant tout votre historique d\'inscription.",
    supportingTitle: "Documents justificatifs",
    rentalTitle: "Contrat de location / Logement",
    rentalDesc: "Contrats de location, reçus de loyer ou documents de propriété indiquant votre adresse en Espagne.",
    rentalNote: "Couvre toute la période de location en un seul document.",
    medicalTitle: "Documents médicaux",
    medicalDesc: "Carte de santé (tarjeta sanitaria), visites médicales, dossiers hospitaliers, ordonnances.",
    medicalNote: "Chaque visite prouve votre présence à cette date précise.",
    bankTitle: "Relevés bancaires",
    bankDesc: "Relevés mensuels d\'un compte bancaire espagnol montrant des transactions en Espagne.",
    bankNote: "Idéal pour couvrir plusieurs mois — chaque relevé couvre un mois.",
    utilityTitle: "Factures de services",
    utilityDesc: "Factures d\'électricité, d\'eau, de gaz, d\'internet à votre adresse espagnole.",
    utilityNote: "Chaque facture couvre un mois. Collectez-en plusieurs pour combler les lacunes.",
    employmentTitle: "Documents d\'emploi",
    employmentDesc: "Contrat de travail, fiches de paie (nóminas), inscription à la Sécurité sociale (vida laboral).",
    employmentNote: "La vida laboral est excellente — elle montre tout votre historique d\'emploi en un seul document.",
    selfEmploymentTitle: "Documents d\'indépendant",
    selfEmploymentDesc: "Inscription en tant qu\'indépendant, déclarations fiscales trimestrielles (modèle 130/303), factures.",
    selfEmploymentNote: "Votre alta de autónomo indique la date exacte à laquelle vous avez commencé à travailler.",
    familyTitle: "Documents familiaux",
    familyDesc: "Acte de mariage, inscription scolaire des enfants, livret de famille (libro de familia).",
    familyNote: "L\'inscription scolaire est très solide — elle prouve une présence continue pendant toute l\'année scolaire.",
    otherTitle: "Autres documents acceptables",
    otherDesc: "Transferts d\'argent, inscriptions à des cours, cartes de transport, rapports de police, tout document officiel espagnol avec votre nom et une date.",
    otherNote: "Plus faibles individuellement mais aident à combler les lacunes entre les documents plus solides.",
    guidanceShort: "Concentrez-vous sur votre empadronamiento plus 2-3 documents justificatifs répartis sur différents mois.",
    guidanceMedium: "Essayez d\'avoir au moins un document par trimestre. Votre combinaison la plus solide est empadronamiento + contrat de location + documents d\'emploi.",
    guidanceLong: "Pour les séjours plus longs, un certificat historique d\'empadronamiento est essentiel. Complétez avec la vida laboral et des relevés bancaires montrant une activité continue.",
    rentalDetail: "Téléchargez le contrat complet en PDF. Si vous avez plusieurs contrats consécutifs, téléchargez-les tous. Assurez-vous que votre nom, l\'adresse et les dates de début et de fin sont visibles.",
    medicalDetail: "Chaque visite médicale, ordonnance ou rapport hospitalier compte. Plus vous en avez, mieux c\'est. Vous pouvez demander à votre médecin un historique de visites.",
    bankDetail: "Téléchargez les relevés mensuels en PDF. Chaque relevé couvre un mois. Si vous en avez plusieurs années, priorisez les plus récents et ceux couvrant les mois manquants.",
    utilityDetail: "Chaque facture couvre le mois où elle a été émise. Collectez des factures de différents mois pour couvrir la période la plus longue possible.",
    employmentDetail: "La vida laboral est le document le plus complet — elle montre tout votre historique d\'emploi en Espagne dans un seul PDF. Obtenez-la gratuitement sur le portail de la Sécurité sociale.",
    selfEmploymentDetail: "Votre alta de autónomo indique la date de début exacte. Les déclarations trimestrielles (modèle 130 ou 303) prouvent une activité continue. Téléchargez-les dans l\'ordre chronologique.",
    familyDetail: "Les certificats d\'inscription scolaire sont très solides — ils prouvent une présence continue pendant toute l\'année scolaire. Demandez-les directement à l\'école de vos enfants.",
    otherDetail: "Ces documents sont plus faibles seuls mais aident à combler les lacunes. Plus vous en avez de différents mois, mieux c\'est.",
  },
  review: {
    progressLabel: "champs obligatoires complétés",
    statusComplete: "Complet",
    statusPartial: "Partiel",
    statusNotStarted: "Non commencé",
    notProvided: "Non fourni",
    addLink: "Ajouter",
    editLink: "Modifier",
    continueBtn: "Continuer vers le consentement",
    sections: { s1: "Données personnelles", s2: "Informations personnelles", s3: "État civil et famille", s4: "Adresse en Espagne", s5: "Situation en Espagne", s6: "Document d'identité", s7: "Preuve de séjour" },
    voiceTitle: "Revoyons tout avant de terminer.",
    voiceTitle_rep: "Revoyons les informations du demandeur avant de terminer.",
    saveBtn: "Sauvegarder et envoyer",
    completionGreetComplete: "Vous êtes inscrit, {{name}} !",
    completionBodyComplete: "Nous avons reçu vos informations. La prochaine étape est de télécharger vos documents — vous pouvez le faire maintenant ou revenir plus tard.",
    completionGreetMissing: "Bon travail, {{name}} !",
    completionBodyMissing: "J\'ai tout sauvegardé. Je vous enverrai un message avec ce qu\'il manque — vous pourrez terminer quand vous le souhaitez.",
    completionTextOrEmail: "Je vous contacterai par message ou e-mail.",
    completionPortalNote: "Vous pouvez revenir à tout moment sur :",
    completionTextLink: "Je vous enverrai un lien par message pour que vous puissiez revenir quand vous le souhaitez.",
    ctaDocs: "Continuer avec les documents",
    ctaOrganizer: "Aller à Mon Organisateur",
    ctaNote: "Vous pouvez revenir à tout moment. Nous vous enverrons un lien par message.",
    completionHeadline: "C\'est bon, {{name}} !",
    completedLabel: "Ce que vous avez complété",
    item1: "Informations personnelles",
    item2: "Situation et éligibilité",
    item3: "État civil et famille",
    item4: "Adresse en Espagne",
    item5: "Accord signé",
    savedConfirmation: "Tout a été sauvegardé en toute sécurité. Vous pouvez fermer cette page et revenir à tout moment.",
    whatsNext: "Prochaine étape : rassemblez vos documents",
    whatsNextBody: "Nous avons besoin de quelques documents pour compléter votre demande : votre passeport, une preuve de résidence et éventuellement un extrait de casier judiciaire. Vous pouvez les télécharger maintenant ou revenir plus tard.",
  },
  section9: {
    title: "Consentement et enregistrement",
    subtitle: "Presque terminé — veuillez confirmer avant de sauvegarder votre dossier.",
    gdprLabel: "J'ai lu et j'accepte la Politique de confidentialité et je consens au traitement de mes données personnelles pour l'évaluation de mon dossier et pour être contacté(e) au sujet de ma demande.",
    saveBtn: "Sauvegarder et envoyer",
    checkboxRequired: "Vous devez accepter la politique de confidentialité pour continuer.",
    successTitleMissing: "Dossier sauvegardé !",
    successMissing: "Votre dossier a été sauvegardé. Nous vous enverrons un résumé des éléments manquants avec un lien pour revenir compléter votre dossier.",
    successTitleComplete: "Tout est prêt !",
    successComplete: "Votre dossier sera examiné par votre conseiller. Nous vous contacterons si nous avons besoin d'autres informations.",
    advisorWillReview: "examinera votre dossier et vous contactera si nécessaire.",
    teamReview: "Votre dossier sera examiné par notre équipe. Nous vous contacterons si nécessaire.",
    missingFieldsNote: "Certains champs sont encore manquants — ce n'est pas grave. Nous assurerons le suivi.",
    privacyLink: "Politique de confidentialité",
    voiceTitle: "Une dernière chose — votre consentement pour continuer.",
    voiceTitle_rep: "Une dernière chose — le consentement du demandeur pour continuer.",
    advisorMsgComplete: "J'ai tout ce qu'il me faut. Je vais examiner votre dossier et vous contacter si nécessaire.",
    advisorMsgMissing: "J'ai sauvegardé votre progression. Je vous enverrai un rappel de ce qu'il manque encore.",
    teamMsgComplete: "Nous avons tout ce qu'il nous faut. Nous examinerons votre dossier et vous contacterez si nécessaire.",
    teamMsgMissing: "Nous avons sauvegardé votre progression. Nous vous enverrons un rappel de ce qu'il manque encore.",
  },
  advisorProfile: {
    moreAbout: "Plus sur",
    role: "Conseillère en immigration",
    bioLine: "Conseillère en immigration · 🇻🇪",
    bioBlock: "Maman de 2 · Dog mom de Luna 🐕 · Randonneuse du week-end ⛰️",
    quote: "Ma passion est d'aider les gens à construire une nouvelle vie en Espagne — je l'ai fait moi-même et j'ai aidé beaucoup d'autres à le faire aussi !",
    from: "Originaire du Venezuela",
    personal1: "Maman de deux enfants merveilleux",
    personal2: "Amoureuse des chiens — j'ai une chienne adoptée appelée Luna",
    personal3: "J'adore la randonnée en montagne le week-end",
    passion: "Ma passion est d'aider les gens à construire une nouvelle vie en Espagne — je l'ai fait moi-même !",
    contact: "Je vous contacterai par message ou e-mail 💬",
    instagram: "Suivez-moi sur Instagram",
  },
  engagement: {
    reviewData: "Revoir mes données ›",
    title: "Accord de mission",
    subtitle: "Obligatoire avant le début de votre dossier",
    whyTitle: "Pourquoi signons-nous ceci avant de commencer ?",
    whyBody: "Ce contrat formalise la relation professionnelle entre vous et notre cabinet. Il définit les services convenus, les honoraires et les obligations des deux parties, garantissant transparence et sécurité juridique dès le départ.",
    required: "Obligatoire",
    signatureLabel: "Signature du client",
    clearSignature: "Effacer la signature",
    signatureBlank: "Tracez votre signature dans l’encadré. Un canevas vide n’est pas accepté.",
    signatureHint: "Signez ici avec votre souris ou votre doigt",
    signatureBottom: "signature du client",
    advisorRole: "Conseillère Senior, Global Mobility Law",
    assignedBadge: "Votre conseillère assignée",
    scopeLabel: "Étendue des services",
    feeLabel: "Honoraires convenus",
    feeTitle: "Régularisation extraordinaire 2026",
    feeNote: "Inclut la gestion complète du dossier",
    feeAmount: "€199",
    feeWord: "honoraires",
    feeAmountNote: "plus taxe administrative de l'État (38,28 €)",
    consentsLabel: "Votre consentement",
    consent1Before: "J\'ai lu et j\'accepte les ",
    consent1Link: "conditions générales de mission professionnelle",
    consent1After: " et la politique d\'honoraires décrite ci-dessus.",
    consent2: "J\'autorise Global Mobility Law à agir en tant que mon représentant professionnel auprès des autorités espagnoles de l\'immigration.",
    questionsText: "Avez-vous des questions sur ce qui précède ?",
    questionsBtn: "J'ai des questions, merci de me recontacter",
    questionsConfirm: "Bien reçu. Nous reviendrons vers vous",
    consent3: "J\'accepte de recevoir des communications informatives sur l\'état de mon dossier et les actualités pertinentes en matière d\'immigration par e-mail.",
    optional: "(facultatif)",
    dateLabel: "Date de signature :",
    dateAuto: "— générée automatiquement",
    declarationsLabel: "Déclarations légales",
    validationHint: "Pour continuer : acceptez les deux consentements obligatoires et signez le document.",
    readyMessage: "Tout est prêt — vous pouvez continuer.",
    noReturnCommitment: "Je confirme qu'aucune décision ne m'oblige à quitter l'Espagne.",
    noHealthcareCoverage: "Je confirme que je ne bénéficie pas en Espagne d'une couverture de santé prise en charge par un autre État ou un tiers.",
    scope: {
      analysis: "Analyse et diagnostic initial de votre situation migratoire",
      preparation: "Préparation et révision de toute la documentation nécessaire",
      submission: "Dépôt de la demande auprès de l\'autorité compétente",
      followup: "Suivi du dossier jusqu\'à la résolution finale",
    },
  },
  docBriefing: {
    residenceTitle: "Preuve de résidence: Aperçu",
    residenceSubtitle: "Vous devez prouver une présence continue en Espagne pendant environ {{duration}}.",
    reviewThenContinue: "Consultez les instructions ci-dessous, puis appuyez sur Continuer pour téléverser vos documents.",
    criminalUploadDocsHeading: "Téléchargez les documents",
    monthsApprox: "{{n}} mois",
    yearsApprox: "{{n}} ans",
    videoLabel: "1 min · Quels documents sont acceptés",
    primaryTitle: "Empadronamiento (inscription municipale)",
    primaryBadge: "Preuve la plus forte",
    primaryDesc: "Votre certificat d\'inscription municipale. C\'est le document le plus important.",
    primaryHistorical: "Demandez un certificat historique à votre mairie.",
    supportingTitle: "Documents justificatifs",
    identityTitle: "Document d'identité",
    identitySubtitle: "Nous avons besoin d'une copie de votre passeport, carte d'identité ou autre document d'identité officiel.",
    identityTip1: "Les passeports expirés sont acceptés — téléchargez ce que vous avez.",
    identityTip2: "Vous avez besoin d'une copie complète de toutes les pages de votre passeport.",
    identityTip3: "Le format préféré est un seul fichier PDF.",
    identityTip4: "Vous avez besoin de TOUTES les pages — y compris les pages vierges. Les tampons d'entrée/sortie prouvent que vous n'avez pas quitté l'Espagne.",
    identityTip5: "Scannez toutes les pages dans un seul PDF. Utilisez une application gratuite comme Adobe Scan ou Google Drive sur votre téléphone.",
    identityTip6: "Vous pouvez aussi vous rendre dans une copistería ou un locutorio et leur demander de scanner votre passeport complet en PDF.",
    rejectTitle: "Les documents de mauvaise qualité retardent votre demande",
    rejectBody: "L\'administration rejette les passeports avec des photos floues, des pages manquantes ou du texte illisible. Utilisez un scanner ou un bon éclairage. Chaque page doit être lisible.",
    criminalExpiry: "Ces certificats ne sont valables que 3 mois. Ne les demandez pas trop tôt — obtenez-les 2 à 3 semaines avant que nous soyons prêts à soumettre votre dossier. En cas de doute sur le moment, contactez-nous.",
    criminalTitle: "Extrait de casier judiciaire",
    criminalSubtitle: "Nous avons besoin de certificats de casier judiciaire de chaque pays où vous avez vécu pendant les 5 années précédant votre arrivée en Espagne.",
    criminalWhichTitle: "De quels certificats avez-vous besoin ?",
    criminalWhichBody: "Un certificat pour chaque pays où vous avez vécu pendant les 5 années avant votre entrée en Espagne. Il peut s'agir uniquement de votre pays d'origine, ou de plusieurs selon votre situation. Nous vous demanderons lesquels à l'étape suivante.",
    criminalTip1: "Demandez le certificat au consulat de votre pays en Espagne ou au Registre Central des Condamnés.",
    criminalTip2: "Le certificat doit être apostillé ou légalisé s'il provient d'un autre pays.",
    criminalTip3: "Si vous avez un casier, téléchargez quand même le certificat — votre conseiller l'examinera.",
    criminalDiplomaticNote: "Si vous ne pouvez pas obtenir un certificat d’un pays dans un délai d’un mois, ne vous inquiétez pas. Nous déposerons les documents (Annexe I-1 et I-2) pour le demander par voie diplomatique en votre nom.",
    employmentTitle: "Documents d'emploi",
    employmentSubtitle: "Nous avons besoin de documents prouvant votre situation professionnelle actuelle ou passée en Espagne.",
    employmentTip1: "Téléchargez votre contrat de travail, vos fiches de paie récentes ou votre historique de Sécurité sociale.",
    employmentTip2: "Si vous êtes indépendant, incluez votre inscription et vos déclarations fiscales trimestrielles.",
    employmentTip3: "Si vous cherchez du travail, une offre d'emploi ou une lettre d'intention convient également.",
    familyTitle: "Documents familiaux",
    familySubtitle: "Nous avons besoin de documents prouvant votre situation familiale, surtout si vous avez un conjoint ou des enfants en Espagne.",
    familyTip1: "Téléchargez le livret de famille, l'acte de mariage ou les actes de naissance des enfants.",
    familyTip2: "Si vos enfants sont scolarisés en Espagne, incluez le certificat d'inscription scolaire.",
    familyTip3: "Les documents étrangers doivent être apostillés et, si nécessaire, traduits.",
    asylumTitle: "Documentation d'asile",
    asylumSubtitle: "En tant que demandeur d'asile, nous avons besoin de votre documentation officielle du processus de protection internationale.",
    asylumTip1: "Téléchargez votre reçu de demande d'asile ou la résolution de votre dossier.",
    asylumTip2: "Si vous avez une carte rouge ou blanche de demandeur d'asile, incluez-la également.",
    asylumTip3: "Vous n'avez pas besoin de prouver 5 mois de séjour — votre documentation d'asile est suffisante.",
    denialTitle: "Documents de refus antérieur",
    denialSubtitle: "Comme vous avez eu un processus d'immigration antérieur refusé, nous avons besoin de documentation spécifique de ce dossier.",
    denialTip1: "Téléchargez la décision de refus du processus antérieur.",
    denialTip2: "Incluez le casier judiciaire du processus antérieur, même s'il est expiré.",
    denialTip3: "Ajoutez tout document montrant que vous êtes resté(e) en Espagne après le refus.",
    continueToUpload: "Continuer",
    back: "Retour",
    rentalTitle: "Contrat de location / Logement",
    rentalDesc: "Contrats de location, reçus de loyer ou documents de propriété indiquant votre adresse en Espagne.",
    rentalNote: "Couvre toute la période de location en un seul document.",
    medicalTitle: "Documents médicaux",
    medicalDesc: "Carte de santé, visites médicales, dossiers hospitaliers, ordonnances.",
    medicalNote: "Chaque visite prouve votre présence à cette date précise.",
    bankTitle: "Relevés bancaires",
    bankDesc: "Relevés mensuels d\'un compte bancaire espagnol.",
    bankNote: "Chaque relevé couvre un mois.",
    utilityTitle: "Factures de services",
    utilityDesc: "Factures d\'électricité, d\'eau, de gaz, d\'internet à votre adresse espagnole.",
    utilityNote: "Chaque facture couvre un mois. Collectez-en plusieurs pour combler les lacunes.",
    employmentTitle: "Documents d\'emploi",
    employmentDesc: "Contrat de travail, fiches de paie, inscription à la Sécurité sociale (vida laboral).",
    employmentNote: "La vida laboral montre tout votre historique d\'emploi en un seul document.",
    selfEmploymentTitle: "Documents d\'indépendant",
    selfEmploymentDesc: "Inscription en tant qu\'indépendant, déclarations fiscales trimestrielles, factures.",
    selfEmploymentNote: "Votre alta de autónomo indique la date exacte à laquelle vous avez commencé à travailler.",
    familyTitle: "Documents familiaux",
    familyDesc: "Acte de mariage, inscription scolaire des enfants, livret de famille.",
    familyNote: "L\'inscription scolaire prouve une présence continue pendant toute l\'année scolaire.",
    otherTitle: "Autres documents acceptables",
    otherDesc: "Transferts d\'argent, inscriptions à des cours, cartes de transport, rapports de police, tout document officiel espagnol avec votre nom et une date.",
    otherNote: "Plus faibles individuellement mais aident à combler les lacunes.",
    guidanceShort: "Concentrez-vous sur votre empadronamiento plus 2-3 documents justificatifs répartis sur différents mois.",
    guidanceMedium: "Essayez d\'avoir au moins un document par trimestre. Votre combinaison la plus solide est empadronamiento + contrat de location + documents d\'emploi.",
    guidanceLong: "Pour les séjours plus longs, un certificat historique d\'empadronamiento est essentiel. Complétez avec la vida laboral et des relevés bancaires.",
    residenceVideoLabel: "2 min · Quels documents prouvent votre présence en Espagne",
    noExitTitle: "Vous ne pouvez pas avoir quitté l\'Espagne",
    noExitBody: "Votre séjour doit être ininterrompu pendant les 5 mois précédant votre demande. Toute sortie du pays rompt l\'exigence — même les courts voyages ne sont pas autorisés. Si vous êtes parti(e), contactez-nous avant de continuer.",
    whatCountsTitle: "Quels documents sont acceptés ?",
    whatCountsBody: "Tout document public ou privé avec votre nom et une date. Vous n\'avez pas besoin d\'un document pour chaque mois, mais plus vous en avez, plus votre dossier sera solide. Idéalement : au moins un document par mois.",
    rentalDetail: "Téléchargez le contrat complet en PDF. Si vous avez des reçus de loyer mensuels, téléchargez chaque mois séparément. Utilisez notre scanner pour les documents de plusieurs pages.",
    medicalDetail: "Acceptés : carte de santé, rapports médicaux, ordonnances, rendez-vous au centre de santé. Une photo claire de chaque document suffit. Chaque visite prouve votre présence à une date précise.",
    bankDetail: "Téléchargez les relevés mensuels depuis votre banque en ligne en PDF. Si vous n\'avez pas accès en ligne, demandez des relevés imprimés à votre banque et scannez-les.",
    utilityDetail: "Factures d\'électricité, d\'eau, de gaz ou d\'internet à votre nom. Une photo claire de chaque facture suffit. Si vous les recevez par e-mail, vous pouvez les télécharger directement en PDF.",
    employmentDetail: "Demandez votre vida laboral en ligne sur sede.seg-social.gob.es ou par SMS au 600 123 456. C\'est un PDF unique montrant tout votre historique. Fiches de paie et contrats : une photo claire ou PDF.",
    selfEmploymentDetail: "Votre inscription en tant qu\'indépendant et les déclarations fiscales trimestrielles (modèle 130/303) peuvent être téléchargées depuis le bureau électronique de l\'Agence fiscale.",
    familyDetail: "Le livret de famille est un document de plusieurs pages — utilisez notre scanner ou une copistería. Inscription scolaire : demandez une copie à l\'école.",
    otherDetail: "Transferts d\'argent : conservez les reçus. Abonnement transport : les cartes nominatives comptent. Tout document officiel avec votre nom et une date est valide.",
  },
  docFlow: {
    title: "Liste de documents",
    subtitle: "Voici ce dont nous avons besoin. Appuyez sur chaque catégorie pour les instructions et le téléchargement.",
    catIdentity: "Identité",
    catResidence: "Justificatif de résidence",
    catCriminal: "Casier judiciaire",
    catEmployment: "Documents d'emploi",
    catFamily: "Documents familiaux",
    catAsylum: "Documentation d'asile",
    catVulnerability: "Rapport de vulnérabilité",
    catOther: "Autres",
    catDenial: "Documents de refus antérieur",
    helpSheetTitle: "Comment pouvons-nous vous aider ?",
    helpSheetSubtitle: "Nous vous répondrons bientôt, en général sous 1 jour ouvré.",
    helpSheetContextPrefix: "Vous êtes sur :",
    helpSheetPlaceholder: "Dites-nous ce dont vous avez besoin…",
    helpSheetSend: "Envoyer",
    helpSheetSending: "Envoi…",
    helpSheetSentTitle: "Message envoyé",
    helpSheetSessionError: "Impossible d’atteindre votre session. Réessayez.",
    helpSheetSendError: "Une erreur s’est produite. Réessayez.",
    helpFabAria: "Aide — poser une question",
    notStarted: "Non commencé",
    inProgress: "En cours",
    complete: "Complet",
    backToChecklist: "Retour aux documents",
    goToOrganizer: "Aller à Mon Organisateur",
    videoLabel: "1 min · Quels documents comptent",
    qualityTitle: "La qualité de vos documents est importante",
    qualityBody: "Les documents flous, sombres ou coupés sont la principale cause de retards. Les demandes avec des documents illisibles sont renvoyées pour correction, ce qui peut ajouter des semaines à votre processus. Prenez un moment supplémentaire pour vous assurer que chaque document est clair et lisible.",
    photoTipsLink: "Voir les conseils pour de bonnes photos de documents",
    letsBegin: "Commençons",
    skip: "Passer",
    continue: "Continuer",
  },
  sectionExit: {
    title: "Avez-vous terminé cette section ?",
    yes: "Oui",
    no: "Non",
    stuck: "Je suis bloqué(e)",
    stuckPlaceholder: "Dites à Ana ce qui vous empêche d'avancer...",
    stuckSend: "Envoyer",
    back: "Retour",
    porMarkTitle: "Marquer cette section comme complète ?",
    porMarkSubtitle: "Vous pourrez modifier cela plus tard si besoin.",
    porMarkComplete: "Marquer comme complète et continuer",
    porComeBackLater: "Enregistrer et terminer plus tard",
  },
  docUpload: {
    title: "Téléchargez vos documents",
    done: "Terminé — aller à Mon Organisateur",
    backToChecklist: "Retour à la Liste des Documents",
    continue: "Continuer",
    goToOrganizer: "Aller à Mon Organisateur",
    backToRequirements: "Retour aux exigences",
    empHeading: "Documents de travail",
    empHeadingEmployed: "Documents de travail",
    empHeadingSelfEmployed: "Documents de travail indépendant",
    workTypeGatePrompt: "Dites-nous comment vous travaillez pour savoir ce qu’il faut demander.",
    workTypeEmployed: "Salarié",
    workTypeSelfEmployed: "Indépendant",
    workTypeEmployedHelper: "Vous travaillez pour un employeur ou avez un contrat / une offre.",
    workTypeSelfEmployedHelper: "Vous travaillez pour vous-même ou prévoyez de le faire en indépendant.",
    empDescriptionLabel: "Décrivez votre activité",
    empDescriptionPlaceholder: "ex. livreur, cuisinière, graphiste",
    selfEmployedNote: "Vous travaillez pour vous-même ou prévoyez de le faire en indépendant.",
    empDocContract: "Contrat de travail",
    empDocContractHelper: "Contrats signés d’employeurs actuels ou passés en Espagne. Inclure au minimum la première page et la page de signature.",
    empDocPayslip: "Bulletin de paie",
    empDocPayslipHelper: "Bulletins récents montrant votre salaire et votre employeur. Les 3 à 6 derniers mois sont les plus utiles.",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper: "Votre relevé officiel d’activité de la Sécurité sociale. Il montre tous les emplois que vous avez eus. Très solide.",
    empDocJobOffer: "Lettre d’offre d’emploi",
    empDocJobOfferHelper: "Une lettre signée par un employeur. Elle doit indiquer le poste, la date de début, le salaire et les coordonnées de l’employeur.",
    empDocOther: "Autres documents",
    empDocOtherHelper: "Tout autre document qui appuie votre situation professionnelle.",
    empOtherPlaceholder: "Brève description",
    empAddDoc: "Ajouter un document",
    empGateContinue: "Continuer vers le téléchargement des documents",
    empTipsTitle: "Conseils",
    empTipOptional: "Téléchargez uniquement les documents qui s’appliquent à votre situation.",
    empTipVidaLaboral: "La vida laboral est souvent le document le plus solide pour un salarié.",
    empTipJobOffer: "Une offre d’emploi peut suffire si vous n’avez pas encore de contrat.",
    empTipInformal: "Si vous n’avez qu’une preuve informelle, téléchargez ce que vous avez.",
    empTipAutonomo: "Pour l’indépendant, ajoutez l’inscription et les déclarations trimestrielles.",
    identityTitleNoPassport: "Document d'identité",
    identityTitlePassport: "Passeport",
    identityGateQuestion: "Quel type de pièce d'identité avez-vous ?",
    identityGateHint: "Utilisez un passeport valide et non expiré si vous en avez un. Sélectionnez « Autre pièce d'identité » seulement si vous n'en avez pas.",
    identityGatePassport: "Passeport",
    identityGateOther: "Autre document",
    identityGateContinue: "Continuer",
    identityTypeSwitchFromPassport: "Je n'ai pas de passeport valide",
    identityTypeSwitchFromOther: "J'ai un passeport valide",
    identityStep1: "Étape 1 : Saisissez vos informations",
    passportNumber: "Numéro de passeport",
    passportFullName: "Nom complet tel qu'il apparaît sur le passeport",
    passportExpiry: "Date d'expiration",
    nieLabel: "Numéro NIE",
    nieHelper: "Si vous en avez un",
    nationalIdType: "Type de document",
    nationalIdNumber: "Numéro du document",
    nationalIdCountry: "Pays émetteur",
    nationalIdCountryPlaceholder: "Sélectionner un pays...",
    typeNationalId: "Carte nationale d'identité",
    typeTravelDoc: "Document de voyage",
    typeCedulaInscripcion: "Certificat d'inscription",
    identityStep2NoPassport: "Étape 2 : Téléchargez votre document d'identité",
    identityStep2BodyNoPassport: "Téléchargez tout document d'identité que vous avez de votre pays d'origine.",
    identityStep2: "Étape 2 : Téléchargez votre passeport",
    identityStep2Body: "Nous avons besoin de toutes les pages, y compris les pages vierges. Les tampons d'entrée et de sortie aident à prouver votre temps en Espagne. Toutes les pages doivent être téléversées dans un seul fichier PDF.",
    ocrTitle: "Scannez ou téléchargez la page d'identité de votre passeport",
    ocrSubtitle:
      "Pour beaucoup de monde, le scanner intégré est le plus simple sans PDF. Nous essaierons de lire vos informations et de remplir le formulaire.",
    ocrUploadZone: "Prenez une photo ou choisissez un fichier",
    ocrUploadHint: "PDF ou image — uniquement la page avec votre photo",
    ocrSuccessTitle: "Reçu",
    ocrBody:
      "Uniquement la page d'identité de votre passeport (avec votre photo). Si la lecture réussit, vous passerez directement à l'étape suivante pour vérifier ou corriger vos informations. Sinon, vous pouvez réessayer ou passer et tout saisir vous-même — rien n'est obligatoire. Plus tard, vous téléverserez le passeport complet en un seul PDF.",
    ocrFreedomHint: "Ce n'est pas obligatoire — vous pouvez passer cette étape à tout moment.",
    ocrSkipManual: "Passer — je saisirai mes informations",
    ocrUploadFileLabel: "Télécharger un fichier",
    ocrUseCameraHint: "Facultatif — photo rapide si vous n'avez pas encore de scan",
    ocrUseCamera: "Utiliser l'appareil photo",
    ocrUseScanner: "Scanner (plusieurs pages)",
    ocrSkip: "Passer",
    ocrContinue: "Continuer",
    passportAddTitle: "Ajoutez votre passeport",
    passportAddBody1:
      "Nous avons besoin d'un PDF numérisé de votre passeport incluant toutes les pages.",
    passportAddBody2:
      "Les images avec des bordures trop prononcées, des doigts visibles ou des reflets seront refusées.",
    passportAddExamplesLink: "Voir des exemples de scans acceptables",
    passportAddScannerCardTitle: "Utiliser notre scanner",
    passportAddScannerCardDesc:
      "Photographiez chaque page avec votre téléphone. Nous les regroupons en un seul PDF.",
    passportAddPdfCardTitle: "Télécharger un PDF",
    passportAddPdfCardDesc:
      "Téléchargez un PDF existant qui contient toutes les pages de votre passeport.",
    passportScanExamplesCloseAria: "Fermer",
    passportScanExamplesTitle: "Exemples de scan",
    passportScanExamplesIntro:
      "Visez un scan proche de l'exemple acceptable. Tout ce qui ressemble aux exemples refusés devra être refait.",
    passportScanExamplesAcceptableLabel: "ACCEPTABLE",
    passportScanExamplesAcceptableCaption:
      "Bords aux bords, net sur tout le document, sans doigts ni ombres dans le cadre.",
    passportScanExamplesRejectedLabel: "SERA REFUSÉ",
    passportScanExamplesRejectDarkTitle: "Trop sombre",
    passportScanExamplesRejectDarkDesc:
      "Placez-vous dans une meilleure lumière. Évitez les ombres sur le passeport.",
    passportScanExamplesRejectBlurTitle: "Flou ou pas net",
    passportScanExamplesRejectBlurDesc:
      "Tenez l'appareil stable. Touchez le passeport à l'écran pour faire la mise au point.",
    passportScanExamplesRejectFingersTitle: "Doigts dans le cadre",
    passportScanExamplesRejectFingersDesc:
      "Utilisez un petit objet pour maintenir le passeport ouvert.",
    passportScanExamplesGotIt: "Compris",
    passportScanSavedTitle: "Passeport enregistré",
    passportScanSavedSubtitle: "{{filename}} · {{pages}}",
    passportScanSavedSubtitleFilenameOnly: "{{filename}}",
    passportScanSavedPagesOne: "1 page",
    passportScanSavedPagesMany: "{{count}} pages",
    passportScanSavedView: "Voir",
    passportScanSavedReplace: "Remplacer",
    identityReplaceConfirmTitle: "Remplacer ce document ?",
    identityReplaceConfirmBody:
      "Votre PDF actuel sera supprimé. Vous pourrez téléverser un nouveau PDF ou numériser à nouveau votre passeport.",
    identityReplaceConfirmCancel: "Annuler",
    identityReplaceConfirmReplace: "Remplacer le document",
    passportViewOpening: "Ouverture…",
    passportViewUnavailable:
      "Impossible d’ouvrir le fichier passeport. Réessayez ou utilisez Remplacer pour le téléverser à nouveau.",
    passportViewPopupBlocked: "Autorisez les fenêtres pop-up pour ce site afin d’afficher le PDF.",
    passportViewerSheetTitle: "Votre document",
    passportViewerClose: "Fermer",
    identityAiUploadingSecure: "Enregistrement de votre document et lecture des informations…",
    identityAiWaitHint:
      "Une fois terminé, vous arriverez à l'étape suivante pour vérifier vos informations. En cas de problème, vous pourrez réessayer ou passer et les saisir à la main.",
    passportPdfProcessingTitle: "Traitement de votre PDF…",
    passportPdfPhaseUpload: "Envoi sécurisé du fichier…",
    passportPdfPhaseAnalyze: "Lecture des données du passeport…",
    passportPdfPhaseApply: "Application au formulaire…",
    identityUploadRetry: "Réessayer l'envoi",
    identityUploadPickAnother: "Choisir un autre fichier",
    passportAiBannerTitle: "Document enregistré — nous n'avons pas pu le lire automatiquement.",
    passportAiBannerBody:
      "Votre fichier est enregistré. Vérifiez ou saisissez vos informations ci-dessous, réessayez par scan ou envoi, ou passez — vous n'êtes pas obligé d'insister. Nous pourrons vous aider avec un PDF plus lisible plus tard. Si les champs ne correspondent pas à ce scan, ils peuvent provenir d'une session précédente : mettez-les à jour ou réessayez.",
    passportAiBannerShopHint: "Une photocopie locale peut souvent produire une numérisation nette si la photo au téléphone est difficile.",
    passportAiBannerRetry: "Réessayer scan ou envoi",
    passportAiSavedManualTitle: "Enregistré — confirmez ou saisissez vos informations",
    identityTitle: "Téléchargez votre document d'identité",
    criminalTitle: "Téléchargez votre extrait de casier judiciaire",
    asylumTitle: "Téléchargez votre documentation d'asile",
    denialTitle: "Téléchargez la décision de refus",
    employmentTitle: "Téléchargez vos documents d'emploi",
    familyTitle: "Téléchargez vos documents familiaux",
    vulnHeading: "Rapport de vulnérabilité",
    vulnIntroBody: "Un formulaire officiel qui confirme votre situation de vulnérabilité. Vous aurez besoin d'un travailleur social ou d'une ONG enregistrée pour le remplir.",
    vulnStep1: "Étape 1 : Obtenez le formulaire",
    vulnStep1Body: "Apportez ce formulaire à un travailleur social ou à une ONG enregistrée afin qu'ils complètent l'évaluation, le signent et le tamponnent.",
    vulnDownloadBtn: "Télécharger",
    vulnEmailBtn: "E-mail",
    vulnStep2: "Étape 2 : Téléversez-le ici",
    vulnStep2Body: "Une fois signé et tamponné, téléversez-le ici.",
    vulnScannerBtn: "Utiliser le scanner",
    vulnTipsTitle: "Conseils",
    vulnTipSocial: "Tout bureau municipal des services sociaux peut compléter ce formulaire.",
    vulnTipNGO: "Des ONG enregistrées comme Caritas, Croix-Rouge, ACCEM ou CEAR peuvent aussi le compléter.",
    vulnTipForward: "Vous pouvez parfois l'envoyer par e-mail aux services sociaux ou à une ONG, selon le bureau concerné.",
    vulnTipSigned: "Assurez-vous que le rapport soit signé, tamponné et daté. Les formulaires non signés seront refusés.",
    vulnTipID: "Apportez votre passeport et toute autre pièce d'identité lorsque vous allez demander la signature.",
    otherHeading: "Autres",
    otherBody: "Utilisez cette section pour téléverser tout document supplémentaire demandé par votre conseiller et qui ne correspond pas aux catégories ci-dessus. Par exemple, une procuration, une traduction assermentée ou une apostille.",
    otherAddDoc: "Téléverser un document",
    documentTypeLabel: "Type de document",
    documentTypePlaceholder: "Sélectionnez…",
    otrosDescribeLabel: "Décrivez le document",
    otrosDescribePlaceholder: "Brève description (obligatoire pour « Otros »)",
    uploadRulesHint:
      "PDF, DOC, JPG, TIF ou GIF. 6 Mo max par fichier. Nom : lettres, chiffres, espaces, _ - . ( )",
    multipleFilesHint: "Vous pouvez télécharger plusieurs documents.",
    residenceHeading: "Preuve de résidence",
    residenceDesc: "Téléchargez des documents prouvant que vous avez été en Espagne de manière continue. Plus vous avez de documents, mieux c'est. Vous pouvez les télécharger maintenant ou revenir plus tard.",
    residencePickerTitle: "Ajouter une preuve de résidence",
    residencePickerSubtitle: "Choisissez le document que vous souhaitez téléverser. Si vous n'êtes pas sûr, choisissez l'option la plus proche ou sélectionnez \"Autre\".",
    residenceFeatured: "Le plus fort",
    residenceGroupOfficial: "Documents officiels",
    residenceGroupPersonal: "Autres justificatifs personnels",
    residenceGroupOther: "Autre",
    residenceGuidance: "Assurez-vous que ce document montre clairement votre nom et une date. Les documents sans ces informations peuvent ne pas être acceptés.",
    residenceOtherLabel: "De quel type de document s'agit-il ?",
    residenceOtherPlaceholder: "Exemple : abonnement à une salle de sport, reçu de loyer, confirmation de rendez-vous",
    residenceOtherHelper: "Optionnel. Une courte description aide Ana à comprendre de quoi il s'agit.",
    residenceChange: "Changer le type de document",
    monthsCovered: "Mois couverts",
    monthsTip: "Appuyez sur les mois couverts par chaque document que vous téléchargez pour garder une trace.",
    addDocument: "+ Ajouter un document",
    chooseFile: "Choisir un fichier",
    uploadedDocs: "Documents téléchargés",
    modalTitle: "Ajouter un document",
    modalDocType: "Type de document",
    modalSelectType: "Sélectionner un type...",
    modalMonthRange: "Mois couverts",
    modalFrom: "De",
    modalTo: "À",
    modalFile: "Fichier",
    modalAdd: "Ajouter",
    typeEmpadronamiento: "Empadronamiento",
    typeRental: "Contrat de location",
    typeMedical: "Dossier médical",
    typeBank: "Relevé bancaire",
    typeUtility: "Facture de services",
    typeEmployment: "Document d\'emploi",
    typeOther: "Autre",
    monthsAutoTip: "Déc 2025 à Avr 2026. Ils se mettent à jour automatiquement lorsque vous ajoutez des documents.",
    submissionMonthNote: "Selon la date de dépôt, il se peut que nous devions revenir et ajouter les documents de mai.",
    to: "à",
    openScanner: "Ouvrir le scanner",
    helpTitle: "Conseils rapides",
    helpTip1: "L\'empadronamiento est le document le plus important — téléchargez-le en premier si vous l\'avez.",
    helpTip2: "Chaque document peut couvrir plusieurs mois — un contrat de location peut couvrir toute la période.",
    helpTip3: "Les relevés bancaires, factures et visites médicales comptent aussi — tout document avec votre nom et une date.",
    helpTip4: "Vous n\'avez pas besoin de tout maintenant — vous pouvez revenir et en ajouter d\'autres à tout moment.",
    seeFullRequirements: "Retour à l’aperçu",
    backToOverview: "Retour à l’aperçu",
    backToGuidance: "Retour à l'aperçu",
    modalScanBtn: "Ouvrir le scanner",
    modalChangeFile: "Changer",
    removeDoc: "Supprimer",
    deleteFileConfirm: "Voulez-vous vraiment supprimer ce fichier ?",
    criminalDesc: "Vous avez besoin d\'un extrait de casier judiciaire de votre pays d\'origine et de tout pays où vous avez vécu au cours des 5 dernières années.",
    criminalReq1: "Demandez-les au consulat de votre pays en Espagne ou demandez à un membre de votre famille de les obtenir dans votre pays d\'origine",
    criminalReq2: "Il doit être apostillé ou légalisé",
    criminalReq2Pre: "Ils doivent être ",
    criminalReq2Term: "apostillé ou légalisé",
    criminalReq2Help: "L\'apostille est un cachet officiel qui valide votre document pour une utilisation internationale. Elle s\'obtient auprès du ministère compétent de votre pays d\'origine. Si votre pays ne fait pas partie de la Convention de La Haye, vous avez besoin d\'une légalisation consulaire. Si vous n\'êtes pas sûr de ce dont vous avez besoin, contactez-nous.",
    criminalReq3: "S\'il n\'est pas en espagnol, il nécessite une traduction assermentée",
    criminalReq3Pre: "S\'ils ne sont pas en espagnol, ils nécessitent une ",
    criminalReq3Term: "traduction assermentée",
    criminalReq3Help: "Un traducteur assermenté est un professionnel officiellement nommé par le Ministère des Affaires étrangères d\'Espagne. Ses traductions ont une valeur juridique. Vous pouvez en chercher un sur maec.gob.es ou nous demander — nous pouvons vous en recommander un.",
    criminalDeclaracion: "Vous ne pouvez pas l\'obtenir ? Dans certains cas, une déclaration sous serment est acceptée. Parlez-nous-en.",
    criminalDeclaracionPre: "Vous ne pouvez pas en obtenir un ? Dans certains cas, une ",
    criminalDeclaracionPost: " est acceptée. Parlez-nous-en.",
    criminalHelpTitle: "Conseils",
    criminalHelp1: "Certains consulats prennent des semaines — faites la demande dès que possible",
    criminalHelp2: "Si le certificat fait plus d\'une page, utilisez notre scanner pour créer un PDF",
    criminalVideoLabel: "1 min · Comment obtenir votre extrait de casier judiciaire",
    asylumHeading: "Documentation d'asile",
    asylumDesc:
      "Saisissez votre numéro de dossier OAR et téléversez votre carte rouge ou votre récépissé de demande. Si votre demande d'asile a été refusée, téléversez aussi la décision de refus.",
    asylumDocReceipt: "Récépissé de demande",
    asylumDocReceiptHelper: "Joignez toutes les pages",
    asylumDocCard: "Carte rouge",
    asylumDocCardHelper: "Téléversez les deux faces.",
    asylumDocResolution: "Décision de refus",
    asylumDocResolutionHelper: "Si votre demande d'asile a été refusée",
    or: "ou",
  },
  criminal: {
    multiCountryTitle: "Quels certificats vous faut-il ?",
    multiCountryBody: "Un pour chaque pays où vous avez vécu dans les 5 ans avant votre entrée en Espagne. Il peut s'agir de votre pays d'origine uniquement, ou de plusieurs — cela dépend de votre situation. Nous vous demanderons lesquels à l'étape suivante.",
    diplomaticNote: "Si vous ne pouvez pas obtenir un certificat d’un pays précis dans un délai d’un mois, ne vous inquiétez pas. Nous déposerons les documents (Annexe I-1 et I-2) pour le demander par voie diplomatique en votre nom.",
    uploadPageDesc: "Nous avons besoin de certificats de casier judiciaire de chaque pays où vous avez résidé pendant les 5 ans précédant votre entrée en Espagne. Incluez votre pays d'origine si vous y viviez pendant cette période. N'incluez pas l'Espagne.",
    countriesQuestion: "Dans quels pays avez-vous résidé pendant les 5 années précédant votre entrée en Espagne ?",
    countriesHint: "Ajoutez tous les pays où vous avez résidé dans les 5 ans précédant votre entrée en Espagne. Incluez votre pays d'origine si vous y viviez durant cette période.",
    selectCountryPlaceholder: "Sélectionner un pays…",
    countrySearchPlaceholder: "Rechercher un pays…",
    countrySearchNoResults: "Aucun résultat",
    addCountry: "Ajouter",
    uploadSectionTitle: "Télécharger les certificats",
    noSpainNote: "N'incluez pas l'Espagne. Le certificat espagnol est géré automatiquement par le système.",
    or: "ou",
    requestedNotReceivedLabel: "Demandé mais non reçu.",
    requestedNotReceivedDesc: "Plus de 30 jours se sont écoulés depuis que j'ai demandé le certificat. Je téléchargerai une preuve de la demande (e-mail, lettre ou reçu).",
    requestedNotReceivedBadge: "Demandé, non reçu après 30 jours",
    requestProofPending: "Preuve de demande en attente",
    uploadedBadge: "Certificat téléversé",
    uploadReceiptBtn: "Téléverser le reçu",
    scanReceiptBtn: "Scanner le reçu",
    uploadCertificateBtn: "Téléverser le certificat",
    scanCertificateBtn: "Scanner le certificat",
    awaitingCertificate: "Plus de 30 jours se sont écoulés depuis la demande du certificat",
    briefingTip1: "Demandez le certificat au consulat de votre pays en Espagne ou demandez à un membre de votre famille de l'obtenir dans votre pays d'origine.",
    briefingTip2: "Si vous n'avez vécu que dans votre pays d'origine avant de venir en Espagne, vous n'avez besoin que d'un seul certificat.",
    briefingTip3: "Si vous avez un casier judiciaire, téléchargez quand même le certificat — votre conseiller l'examinera.",
  },
  docPhotoGuide: {
    title: "Conseils pour les photos de documents",
    link: "Voir les conseils pour les photos de documents",
    good: "Correct — à plat, bonne lumière, tout le texte lisible",
    goodTitle: "Bon exemple",
    badTitle: "Exemples incorrects",
    bad1: "Ombre — le texte est caché. Déplacez votre source de lumière.",
    bad2: "Coupé ou incliné — assurez-vous que tout le document est dans le cadre.",
    bad3: "Trop sombre — le texte ne peut pas être lu. Utilisez plus de lumière.",
  },
  organizer: {
    title: "Mon Organisateur",
    needItems: "Il vous manque encore {{n}} éléments",
    allDone: "Tout est complet !",
    residence: "Preuve de résidence",
    criminal: "Certificat de casier judiciaire",
    asylum: "Documentation d\'asile",
    notStarted: "Non commencé",
    inProgress: "En cours",
    complete: "Complet",
    upload: "Télécharger",
    viewCompleted: "Voir les complétés",
    uploadDocuments: "Télécharger des documents",
    greeting: "Bon retour, {{name}}",
    greetingSub: "Voici où en est votre demande.",
    caseProgress: "Progrès de votre dossier",
    phaseInfo: "Informations",
    phaseDocs: "Documents",
    phaseSubmit: "Soumission",
    remaining: "restant",
    deadline: "Date limite de dépôt : 30 juin 2026",
    detailsLabel: "Vos informations",
    docsLabel: "Documents",
    viewChecklist: "Voir la Liste des Documents",
    contactTitle: "Besoin d\'aide ?",
    contactSub: "Contactez-nous sur WhatsApp",
    submitInlineBannerTitle: "Êtes-vous prêt à envoyer votre demande ?",
    submitInlineBannerBody:
      "Si vous avez terminé toutes les sections, prévenez-nous : nous examinerons votre dossier et vous répondrons rapidement.",
    submitInlineBannerCta: "Envoyer à {{advisor}}",
    submitInlineSending: "Envoi en cours…",
    submitDoneHeading: "Envoyé à {{advisor}} le {{date}}",
    submitDoneHeadingNoDate: "Envoyé à {{advisor}}",
    submitDoneMessagePara1:
      "{{advisor}} examine votre dossier et vous enverra un message si elle a des questions ou si quelque chose doit être mis à jour. Nous vous enverrons aussi une copie par e-mail.",
    submitDoneMessagePara2:
      "Pour l'instant c'est tout. Vous pouvez répondre à l'e-mail ou revenir ici si vous avez des questions.",
    incomplete: "Non complété",
    stepConfirm: "Confirmer les données",
    stepSituation: "Votre situation",
    stepDocument: "Document d\'identité",
    stepPassportPersonal: "Passeport et informations personnelles",
    stepPersonal: "Informations personnelles",
    stepCivil: "État civil et famille",
    stepAsylum: "Question d'asile",
    stepQualifying: "Laquelle décrit le mieux votre situation actuelle ?",
    stepAddress: "Adresse en Espagne",
    stepConsent: "Accord et signature",
    signSubmitLabel: "Signer et envoyer",
    signSubmitStatusPending: "En attente",
    signSubmitStatusComplete: "TerminÃ©",
    deadlineLabel: "Date limite",
    nextAction: "Continuez votre demande",
    nextActionSub: "Complétez les informations manquantes",
    nextUpload: "Téléchargez vos documents",
    nextUploadSub: "Vous avez des documents en attente de téléchargement",
    progress: "Progrès",
    of: "sur",
    contextBody: "Complétez chaque section à votre rythme — votre progression est sauvegardée automatiquement.",
    finishItems: "Veuillez terminer vos éléments restants. Une fois terminé, je passerai tout en revue et préparerai votre dossier pour le soumettre au bureau d'immigration espagnol. Si vous avez des questions, envoyez-moi une note en bas de cette page et je vous répondrai rapidement.",
    signAvailableLater: "Disponible à la fin",
    helpStuckTitle: "Je suis bloqué(e)",
    helpStuckSub: "Dites-nous ce qui se passe et nous vous répondrons rapidement.",
    helpStuckSubExpanded: "{lawyer} vous répondra bientôt.",
    helpPlaceholder: "Écrivez ici...",
    helpSendButton: "Envoyer",
    helpSentTitle: "Message envoyé",
    helpSentSub: "{lawyer} le verra et vous répondra bientôt.",
    helpDefaultLawyer: "votre avocate",
  },
  upload: {
    scanTitle: "Scanner avec votre téléphone",
    scanSubtitle: "Détecte automatiquement les bords et crée un PDF",
    scanBtn: "Ouvrir le scanner",
    appTitle: "Utiliser une application de scanner",
    appSubtitle: "Scannez les pages et téléchargez-les en PDF",
    appGoogle: "Google Drive — Scanner un document",
    appAdobe: "Adobe Scan — Gratuit sur App Store et Google Play",
    appCopyShop: "Ou rendez-vous dans une copistería / locutorio proche",
    fileTitle: "Télécharger un fichier",
    fileSubtitle: "J\'ai déjà un PDF ou une photo prête",
    scanHeroTitle: "Scannez votre passeport ici",
    scanHeroSubtitle: "Nous vous guidons page par page. Nous détectons les bords et créons le PDF pour vous.",
    alternativesLabel: "Autres options",
    altApp: "Utiliser une application de scanner",
    altAppHint: "Adobe Scan ou Google Drive (gratuit)",
    altShop: "Aller dans une copistería",
    altShopHint: "Demandez-leur de scanner votre passeport complet en PDF",
    altUpload: "J\'ai déjà un fichier",
    altUploadHint: "Téléchargez un PDF ou une photo que vous avez déjà",
    videoLabel: "30 sec · Comment scanner votre passeport",
    browseFiles: "Parcourir les fichiers",
    scanCriminalNote: "Scannez chaque page du certificat. Incluez les tampons et les signatures.",
    scanDocTitle: "Besoin de scanner un document ?",
    scanDocSubtitle: "Utilisez notre scanner pour les documents de plusieurs pages",
  },
  auth: {
    signIn: "Se connecter",
    subtitle: "Entrez votre numéro de téléphone pour continuer.",
    phone: "Numéro de téléphone",
    sendCode: "Envoyer le code",
    otpIntroTitle: "Saisissez le code que nous avons envoyé",
    otpIntroBody: "Nous avons envoyé un code à usage unique sur votre téléphone.",
    enterCode: "Entrez le code",
    sixDigitCodePlaceholder: "Entrez le code à 6 chiffres",
    verify: "Vérifier",
    resend: "Renvoyer le code",
    or: "ou",
    google: "Continuer avec Google",
    apple: "Continuer avec Apple",
    privacyNote: "En continuant, vous acceptez notre",
  },
  faq: {
    title: "Comment puis-je vous aider ?",
    about: "À propos de :",
    other: "J'ai une autre question",
    otherPlaceholder: "Écrivez votre question…",
    otherHelper: "Nous vous répondrons par message ou e-mail.",
    send: "Envoyer",
    sent: "Envoyé ! Votre conseiller(ère) vous contactera bientôt.",
    sentTo: "Envoyé à",
    sentContact: "Je vous répondrai par message ou e-mail.",
    sentTeam: "Envoyé à notre équipe. Nous vous répondrons bientôt.",
    s1: {
      q1: "Puis-je modifier ces informations plus tard ?",
      a1: "Oui, vous pouvez modifier n'importe quel champ avant la soumission finale. Rien n'est verrouillé.",
      q2: "Dois-je inclure les accents dans mon nom ?",
      a2: "Utilisez votre nom exactement comme il apparaît sur votre passeport, avec les accents et caractères spéciaux.",
      q3: "Mes données personnelles sont-elles sécurisées ?",
      a3: "Vos informations sont chiffrées et accessibles uniquement par vous et votre conseiller(ère).",
    },
    s2: {
      q1: "Que faire si je ne connais pas ma date de naissance exacte ?",
      a1: "Utilisez la date figurant sur votre passeport ou acte de naissance. Votre conseiller(ère) peut vous aider si vous n'avez qu'une date approximative.",
      q2: "Quelle nationalité dois-je indiquer ?",
      a2: "Indiquez votre nationalité actuelle telle qu'elle apparaît sur votre passeport.",
      q3: "Que signifie le sexe 'Indéterminé' ?",
      a3: "Choisissez cette option si votre document ne précise pas homme ou femme — certains passeports utilisent 'X'.",
    },
    s3: {
      q1: "Pourquoi avez-vous besoin des noms de mes parents ?",
      a1: "Le formulaire officiel requiert les prénoms des parents. Vous pouvez ignorer cette section — votre conseiller(ère) peut vous aider plus tard.",
      q2: "Que faire si je ne connais pas le nom d'un parent ?",
      a2: "Laissez ces champs vides. Votre conseiller(ère) vous assistera lors de l'examen de votre dossier.",
    },
    s4: {
      q1: "Que faire si je n'ai pas d'adresse fixe ?",
      a1: "Indiquez l'adresse où vous séjournez actuellement, même si c'est temporaire. Vous pourrez la mettre à jour plus tard.",
      q2: "Ai-je besoin d'un padrón ?",
      a2: "Non, le padrón n'est pas obligatoire. De nombreux autres types de documents sont acceptés.",
      q3: "Que signifie 'Résidant(e) depuis' ?",
      a3: "Indiquez le mois et l'année approximatifs où vous avez commencé à vivre à cette adresse. Une estimation est tout à fait acceptable.",
    },
    s5: {
      q1: "Ai-je besoin d'un contrat de travail pour postuler ?",
      a1: "Non. La demande couvre trois voies : travail, regroupement familial ou vulnérabilité. Un contrat de travail n'est qu'une option.",
      q2: "Qu'est-ce qu'un numéro NIE ?",
      a2: "Le NIE est le numéro d'identification des étrangers en Espagne. Laissez ce champ vide si vous n'en avez pas.",
      q3: "Le travail indépendant est-il pris en compte ?",
      a3: "Oui. Une déclaration d'intention de travailler à son compte est suffisante à ce stade.",
    },
    s6: {
      q1: "Un passeport expiré est-il accepté ?",
      a1: "Oui. La loi espagnole autorise les passeports expirés pour cette demande. Téléchargez le document que vous avez.",
      q2: "Que faire si j'ai perdu mon passeport ?",
      a2: "Obtenir un nouveau passeport est la meilleure façon de prouver votre identité. Pour la résidence, vous pouvez utiliser des factures ou relevés bancaires.",
      q3: "Que faire si je n'ai aucun document d'identité ?",
      a3: "Contactez le consulat de votre pays pour obtenir des documents de voyage d'urgence. Votre conseiller(ère) vous guidera.",
    },
    s7: {
      q1: "Quels types de documents sont acceptés comme preuve ?",
      a1: "Factures, relevés bancaires, dossiers médicaux, contrats de location, tickets de transport — tout document avec votre nom et une date en Espagne.",
      q2: "Dois-je prouver chaque mois séparément ?",
      a2: "Non. Une combinaison de documents couvrant différentes périodes sur 5 mois est suffisante.",
      q3: "Que faire si j'ai très peu de documents ?",
      a3: "Ne vous découragez pas — de nombreux types sont acceptés et votre conseiller(ère) vous aidera à trouver la meilleure combinaison.",
    },
    s8: {
      q1: "Puis-je modifier mes réponses après cela ?",
      a1: "Oui. Vous pouvez revenir en arrière et modifier n'importe quelle section avant la soumission officielle finale.",
      q2: "Que se passe-t-il après l'enregistrement ?",
      a2: "Votre conseiller(ère) examinera votre dossier et vous contactera si quelque chose manque ou nécessite une clarification.",
    },
    s9: {
      q1: "À quoi exactément estoy dando mi consentimiento?",
      a1: "Consientes que tus datos se utilicen para evaluar tu caso y para que tu asesor/a se ponga en contacto contigo. Tus datos no se comparten con terceros.",
      q2: "¿Puedo retirar mi consentimiento más adelante?",
      a2: "Sí. Contáctanos en cualquier momento y dejaremos de procesar tus datos de inmediato.",
    },
  },
};

/* ─────────────────────────── ARABIC ─────────────────────────── */
const AR: Translations = {
  header: {
    firm: "Global Mobility", help: "هل تحتاج مساعدة؟", saved: "تم الحفظ ✓", saving: "جاري الحفظ…",
    close: "إغلاق",
    deleteTitle: "هل أنت متأكد؟",
    deleteBody1: "سيتم حذف جميع بياناتك الشخصية والوثائق وسجل قضيتك. لا يمكن التراجع عن هذا الإجراء.",
    deleteBody2: "سيتم تعطيل حسابك فوراً وسيتم حذف بياناتك خلال 15 يوماً.",
    deleteBody3: "إذا غيرت رأيك، تواصل مع hola@globalmobility.law لإعادة تفعيل حسابك قبل اكتمال الحذف.",
    deleteConfirm: "نعم، احذف حسابي",
    deleteCountdown: "نعم، احذف حسابي ({{n}})",
    cancel: "إلغاء",
    addFamily: "إضافة فرد من العائلة",
    addFamilySoon: "قريباً — تواصل مع محاميك عبر واتساب لإضافة أفراد العائلة.",
    privacy: "سياسة الخصوصية",
    logout: "تسجيل الخروج",
    deleteAccount: "حذف حسابي",
  },
  exitModal: {
    title: "تم حفظ تقدمك!",
    body: "يمكنك العودة في أي وقت ومتابعة من حيث توقفت.",
    stay: "متابعة طلبي",
    leave: "خروج",
  },
  progress: {
    of: "من",
    steps: ["تأكيد البيانات", "وضعك", "العنوان", "المراجعة والإرسال"],
    applyingFor: "تقديم طلب لـ",
  },
  help: {
    title: "هل تحتاج مساعدة؟",
    placeholder: "صف سؤالك أو مشكلتك…",
    helper: "سنرد عليك برسالة أو بريد إلكتروني.",
    send: "إرسال الرسالة",
    sent: "تم إرسال الرسالة! سنرد عليك قريباً.",
    section: "القسم الحالي",
  },
  nav: {
    back: "رجوع",
    continue: "متابعة",
    submit: "متابعة",
    backToOrganizer: "العودة إلى المنظم",
    breadcrumbOrganizer: "المنظّم",
    breadcrumbDocuments: "المستندات",
  },
  footer: {
    privacyPolicy: "سياسة الخصوصية",
    termsOfService: "شروط الخدمة",
    cookiePolicy: "سياسة ملفات تعريف الارتباط",
    legalNotice: "إشعار قانوني",
    copyright: "© 2026 Global Mobility Group LLC",
  },
  documentScanner: {
    title: "مسح المستند ضوئياً",
    desktopOnly: "لالتقاط عدة صفحات وإنشاء ملف PDF واحد، افتح هذه الشاشة من متصفح هاتفك.",
    scannerBtn: "مسح",
    addPages: "إضافة صفحات",
    addAnotherPage: "إضافة صفحة أخرى",
    fromGallery: "من المعرض",
    buildPdf: "إنشاء PDF",
    processing: "جاري المعالجة…",
    scanMergeSaving: "جاري حفظ المسح…",
    connectionProblemTitle: "مشكلة في الاتصال",
    connectionProblemBody:
      "لا يمكننا الوصول إلى خادمنا، غالبًا بسبب ضعف إشارة الشبكة. إذا ألغيت الآن، ستفقد عمليات المسح.",
    connectionProblemRetry: "إعادة المحاولة",
    connectionProblemCancel: "إلغاء وفقدان عمليات المسح",
    devicePdfFailureKicker: "إنشاء PDF",
    devicePdfFailureTitle: "تعذر إنشاء ملف PDF",
    devicePdfFailureBody:
      "حدث خطأ أثناء إنشاء الملف على جهازك. صورك ما زالت محفوظة هنا. أعد المحاولة، أو أغلق تبويبات أخرى في المتصفح إذا كان هاتفك منخفض الذاكرة. تم إخطار فريقنا تلقائياً.",
    devicePdfFailureRetry: "إعادة المحاولة",
    devicePdfFailureCancel: "إلغاء",
    pageLabel: "الصفحة {{n}}",
    remove: "إزالة",
    moveUp: "أعلى",
    moveDown: "أسفل",
    close: "إغلاق",
    needOnePage: "أضف صورةً لصفحة واحدة على الأقل.",
    errorGeneric: "تعذرت معالجة الصور. أعد المحاولة بصور أوضح.",
    multiPageHint: "التقط عدة صفحات ونجمعها في ملف PDF واحد كما هي في الإطار.",
    cameraTitle: "مسح",
    cameraLoadingLibs: "جاري تحميل الماسح…",
    cameraStarting: "جاري تشغيل الكاميرا…",
    cameraTapToCapture: "وجّه الكاميرا نحو المستند، ثم اضغط الزر الأبيض بالأسفل للالتقاط.",
    cameraVideoNotReady: "معاينة الكاميرا غير جاهزة. أغلق وحاول مرة أخرى.",
    cameraAlignHint: "ضع المستند داخل الإطار الأخضر",
    cameraAlignSubtitle:
      "لأفضل نتيجة: املأ الإطار الأخضر بإحكام مع هامش صغير. بدون أصابع في الصورة؛ ثني طفيف فقط.",
    cameraAlignSubtitleSpread:
      "وضع عدة صفحات: ضع المستند داخل الإطار الأخضر مع هامش رفيع ومنتظم من كل الجهات.",
    cameraFrameModeAria: "شكل دليل المحاذاة",
    cameraFrameModeSingle: "صفحة واحدة",
    cameraFrameModeSpread: "مفتوحة",
    cameraDocDetected: "تم اكتشاف المستند — انقر للالتقاط",
    cameraPermissionDenied: "تم رفض الوصول إلى الكاميرا. فعّله من إعدادات المتصفح.",
    cameraStreamEnded:
      "توقفت الكاميرا (مثلًا بعد خمول الشاشة أو وضع التبويب في الخلفية). اضغط إعادة المحاولة.",
    cameraPlayBlocked: "تعذّر استئناف المعاينة. اضغط إعادة المحاولة.",
    cameraRetryButton: "إعادة المحاولة",
    cameraNotFound: "لم يتم العثور على كاميرا في هذا الجهاز.",
    cameraInitError: "تعذّر تشغيل الكاميرا.",
    captureError: "تعذّر التقاط الصفحة.",
    cameraShutter: "التقاط الصفحة",
    cameraPageCaptured: "تم حفظ الصفحة — التقط صورة أخرى أو اضغط إغلاق.",
    flattenedBadge: "مُقتصّة",
    flattenedBadgeHint: "تم اقتصاصها وتعديلها تلقائياً على هذا الجهاز.",
    previewThumbnailAria: "تكبير المعاينة",
    previewHint: "تأكد أن الصورة واضحة وتعرض الصفحة كاملة.",
    previewRotate: "تدوير 90°",
    previewRotateAria: "تدوير هذه الصفحة 90 درجة باتجاه عقارب الساعة",
    rotateError: "تعذر تدوير الصورة. جرّب صورة أخرى أو اخترها من المعرض.",
    previewRemovePage: "إزالة هذه الصفحة",
    cornerAdjust: "ضبط الزوايا وتسوية الصورة",
    cornerAdjustAria: "فتح ضبط الزوايا لتسوية المنظور والاقتصاص",
    cornerEditorTitle: "تسوية واقتصاص",
    cornerEditorHint:
      "اسحب كل زاوية لتطابق حواف المستند. نُسوّي المنظور ونقلّل الخلفية الفارغة.",
    cornerEditorApply: "تطبيق",
    cornerEditorReset: "إعادة ضبط الزوايا",
    cornerEditorCancel: "رجوع",
    cornerEditorBusy: "جاري المعالجة…",
    cornerEditorError: "تعذّر تسوية الصورة. جرّب زوايا مختلفة أو أعد الضبط.",
    cornerEditorInvalidQuad: "يجب أن تشكّل الزوايا رباعيًا صالحًا داخل الصورة.",
    cornerHandleAria: "زاوية",
    cornerEditorApplyContinue: "تطبيق ومتابعة",
    cornerEditorDone: "تم",
    cornerEditorAddPage: "+ إضافة صفحة",
    cornerEditorResetHeader: "إعادة ضبط",
    passportCornerDragHint: "اسحب الزوايا لتطابق حواف جواز السفر.",
    passportCornerRetake: "إعادة التقاط",
    passportIntroTitle: "لنتخذ صورة للصفحة الأولى",
    passportIntroHelp:
      "افتح جواز السفر على الصفحة الأولى. إن لزم الأمر استخدم شيئًا مدببًا لإبقائه مفتوحًا والتقط صورة للصفحتين وهما مفتوحتان. لا تستخدم أصابعك.",
    passportTakePhoto: "التقاط صورة",
    passportContinue: "متابعة",
    passportSubmitPhotos: "إرسال الصورة/الصور",
    passportRetakePhoto: "إعادة التقاط الصورة",
    passportReviewPrev: "الصفحة السابقة",
    passportReviewNext: "الصفحة التالية",
    passportReviewPageStatus: "{{current}} / {{total}}",
    passportPagesCapturedSummary: "تم التقاط {{n}} صفحة/صفحات.",
    passportSheetReviewHint:
      "بعد كل صورة ستسوّي الزوايا ثم يمكنك التقاط أخرى. عند الانتهاء من كل الصفحات اضغط «مراجعة / ضبط» ثم «إنشاء PDF» في الأسفل.",
    passportReviewAdjust: "مراجعة / ضبط",
    passportSheetMinimalHint:
      "الصور ما زالت هنا. أنشئ ملف PDF، أو ألغِ للعودة واختيار الرفع أو المسح مرة أخرى.",
    passportPreviewReviewHint:
      "اضغط «تحرير» لتسوية الزوايا والاقتصاص. إذا كانت الصورة غير مناسبة استخدم «إعادة التقاط» أعلى اليسار في تلك الشاشة. اضغط «?» أعلى اليمين لعرض أمثلة الصور. اسحب المصغرات لإعادة الترتيب. «إنشاء PDF» عند الانتهاء.",
    reviewScreenTitle: "مراجعة",
    reviewPageOf: "صفحة {{current}} من {{total}}",
    reviewPagesSectionLabel: "الصفحات",
    reviewReorderHint:
      "مرّر الشريط لعرض كل المصغرات. المس مقبض ⠿ على المصغّرة واسحبه لإعادة الترتيب.",
    reviewReorderHoldA11y: "مقبض السحب — اضغط واسحب لإعادة ترتيب الصفحات.",
    reviewInsertPage: "إضافة صفحة",
    reviewEdit: "تحرير",
    passportReviewView: "عرض",
    reviewDeletePageAria: "حذف الصفحة",
    reviewEditMenuTitle: "إجراءات",
    reviewEditRemove: "إزالة الصفحة",
    reviewDeleteConfirmTitle: "حذف هذه الصفحة؟",
    reviewDeleteConfirmBody:
      "ستُزال هذه الصفحة من المسح. يمكنك التقاطها مجددًا لاحقًا.",
    reviewDeleteConfirmDelete: "حذف",
    reviewDeleteConfirmCancel: "إلغاء",
    reviewLeaveConfirmTitle: "تجاهل الصور؟",
    reviewLeaveConfirmBody: "ستفقد كل الصفحات التي التقطتها حتى الآن.",
    reviewLeaveConfirmStay: "مواصلة المسح",
    reviewLeaveConfirmDiscard: "تجاهل الصور",
    passportLowPageWarnTitle: "هل كل الصفحات مضمّنة؟",
    passportLowPageWarnBody:
      "نحتاج عادةً إلى صور لكل صفحة في جواز السفر، بما فيها الصفحات الفارغة. يبدو أن بعض الصفحات غير مكتملة.",
    passportLowPageWarnReview: "مراجعة الصفحات",
    passportLowPageWarnAnyway: "إنشاء PDF على أي حال",
    passportCameraHeader: "جواز السفر",
    passportCameraHintNext: "ضع الصفحات المفتوحة داخل الإطار الأخضر ثم التقط صورة أخرى عند الجاهزية.",
    passportCaptureToast: "تم حفظ الصورة — راجع القص.",
    passportCancel: "إلغاء",
    passportPageTitle: "صفحة {{n}}",
    passportViewfinderPill:
      "افتح جواز السفر مسطحًا والتقط الصفحتين معًا.",
    passportViewfinderPillSubsequent:
      "التقط الصفحة التالية المفتوحة في صورة واحدة.",
    passportCaptureFooterHint:
      "اجعل الصفحات مسطحة على سطح ما. يمكنك ضبط الحواف في الخطوة التالية.",
    passportExamplesAria: "أمثلة للصور",
    passportViewExamplesLink: "عرض أمثلة الصور",
    passportCameraHelpAria: "مساعدة التقاط جواز السفر",
    passportFlashAria: "تبديل الفلاش",
    passportHelpGotIt: "حسنًا",
  },
  help: {
    empadronamientoTerm: "الإمبادرونامينتو",
    empadronamiento: "هذه شهادة تسجيلك البلدي. تُثبت أنك تعيش في بلدية إسبانية. اطلبها من بلديتك، عادةً بموعد مسبق. اطلب الشهادة التاريخية — فهي تُظهر جميع عناوينك وتواريخها.",
    vidaLaboralTerm: "vida laboral",
    vidaLaboral: "هذه وثيقة رسمية من الضمان الاجتماعي تُظهر كامل تاريخ عملك في إسبانيا. إنها مجانية ويمكنك الحصول عليها على sede.seg-social.gob.es، أو عبر رسالة نصية إلى 600 123 456، أو شخصياً في مكتب الضمان الاجتماعي.",
    altaAutonomoTerm: "تسجيل العامل المستقل",
    altaAutonomo: "هذه الوثيقة تُثبت تسجيلك كعامل مستقل في إسبانيا. تُحصل عليها من خلال وكالة الضرائب وتُظهر التاريخ الدقيق الذي بدأت فيه نشاطك.",
    modelo130Term: "نموذج 130/303",
    modelo130: "هذه إقرارات ضريبية فصلية للعمال المستقلين. النموذج 130 لضريبة الدخل (IRPF) والنموذج 303 لضريبة القيمة المضافة (IVA). يمكن تنزيلهما من المكتب الإلكتروني لوكالة الضرائب.",
    libroFamiliaTerm: "دفتر العائلة",
    libroFamilia: "هذه وثيقة رسمية صادرة عن سجل الأحوال المدنية تُثبت الزواج والأطفال. إذا تزوجت أو أنجبت أطفالاً في بلد آخر، ستحتاج إلى ما يعادلها مُصادقاً عليه بالأبوستيل ومُترجماً.",
    apostilladosTerm: "مصادق عليها بالأبوستيل",
    apostillados: "الأبوستيل هو ختم رسمي يُصادق على الوثائق الأجنبية للاستخدام الدولي، وفقاً لاتفاقية لاهاي. يُحصل عليه من الوزارة أو السلطة المختصة في البلد الذي أصدر الوثيقة. إذا لم يكن بلدك طرفاً في اتفاقية لاهاي، فأنت بحاجة إلى توثيق قنصلي بدلاً من ذلك.",
    tarjetaRojaTerm: "البطاقة الحمراء",
    tarjetaRoja: "هذه الوثيقة تُثبت أنك تقدمت بطلب الحماية الدولية (اللجوء) في إسبانيا. تُجدَّد كل 6 أشهر طالما طلبك قيد المعالجة. البطاقة البيضاء مشابهة لكنها للتجديدات.",
    declaracionJuradaTerm: "إقرار بالقسم",
    declaracionJurada: "هذا إقرار موقَّع أمام كاتب عدل يُفيد بأنه لا توجد لديك سوابق جنائية. لا يُقبل إلا إذا تعذّر عليك الحصول على الشهادة الرسمية لأسباب مبررة (حرب، انعدام العلاقات الدبلوماسية، إلخ).",
  },
  validation: {
    required: "هذا الحقل مطلوب.",
    invalidDate: "تاريخ غير صالح.",
    invalidEmail: "عنوان بريد إلكتروني غير صالح.",
    invalidPhone: "رقم هاتف غير صالح.",
    invalidNie: "أدخل رقم NIE صالحًا.",
    invalidPostal: "الرمز البريدي غير صالح (5 أرقام).",
    postalProvinceMismatch: "الرقمان الأولان من الرمز البريدي لا يطابقان المقاطعة.",
    invalidAddressStreet: "الشارع: 1–100 حرفًا؛ أحرف وأرقام ومسافات وعلامات ترقيم محددة فقط.",
    invalidAddressStreetNumber: "الرقم: 1–10 أحرف؛ أحرف أو أرقام أو S/N (مع /).",
    invalidAddressFloorDoor: "الباب: حتى 10 أحرف؛ أحرف وأرقام فقط.",
    invalidAddressCity: "المدينة: 1–40 حرفًا؛ أحرف ومسافات وشرطات فقط.",
    minLength: "قصير جداً.",
    invalidMonth: "يجب أن يكون الشهر بين 01 و 12.",
    invalidDay: "هذا اليوم غير صالح لهذا الشهر.",
    futureDate: "لا يمكن أن يكون التاريخ بعد اليوم.",
    pastExpiryDate: "يجب أن تكون انتهاء الصلاحية اليوم أو في المستقبل.",
    yearOutOfRange: "السنة خارج النطاق المسموح.",
    futureMonthYear: "لا يمكن أن يكون التاريخ بعد الشهر الحالي.",
    invalidYear: "السنة غير صالحة.",
  },
  deadline: { label: "الموعد النهائي للتقديم", date: "30 يونيو 2026" },
  optional: "اختياري",
  returning: {
    greeting: "مرحباً،",
    progress: "تقدم طلبك",
    continue: "متابعة من حيث توقفت",
    restart: "مراجعة من البداية",
  },
  landing: {
    greeting: "مرحباً",
    firstVisitMessage: "لنبدأ طلبك. لن يستغرق الأمر سوى حوالي 10 دقائق.",
    firstVisitMessageWithAdvisor: "سأساعدك في طلبك. لن يستغرق الأمر سوى حوالي 10 دقائق.",
    thisIs: "أنا",
    autoSave: "يمكنك المغادرة والعودة في أي وقت. عملك يُحفظ تلقائياً.",
    advisor: "مستشارك:",
    getStarted: "ابدأ الآن",
    continue: "متابعة",
    reviewFromStart: "مراجعة من البداية",
    progress: {
      moreThan3: "أنت تتقدم جيداً — هيا نكمل.",
      exactly3: "أنت في الطريق الصحيح — تبقى فقط 3 أقسام.",
      exactly2: "اقتربنا — تبقى فقط قسمان.",
      exactly1: "اقتربنا — يتبقى قسم واحد فقط.",
      allDone: "يبدو أن كل شيء مكتمل — لنراجع وننهي.",
    },
    procedureBadge: "التسوية الاستثنائية إسبانيا 2026",
    videoLength: "1 دقيقة · ترجمة متاحة",
    skipVideo: "تخطي الفيديو",
    familyMenuHint: "يمكنك إضافة أفراد العائلة في أي وقت من القائمة ☰",
    purpose: "سأرشدك في كل ما تحتاجه لطلب إقامتك. نظمنا كل شيء في أقسام بسيطة، تقدم بوتيرتك وارفع وثائقك أثناء التقدم. سأتولى الباقي. لا تقلق إن لم يكن لديك كل شيء الآن، يتم حفظ إجاباتك تلقائياً، يمكنك العودة في أي وقت والمتابعة من حيث توقفت.",
    purposeShort: "سأرشدك في كل ما تحتاجه لطلب إقامتك. يتم حفظ إجاباتك تلقائياً، يمكنك العودة متى شئت.",
    purposeExpanded: "نظمنا كل شيء في أقسام بسيطة. تقدم بوتيرتك وارفع وثائقك أثناء التقدم. سأتولى الباقي. لا تقلق إن لم يكن لديك كل شيء الآن، يمكنك المتابعة من حيث توقفت.",
    more: "المزيد",
    less: "أقل",
    familyInfo: "إذا كنت تتقدم كعائلة، كل شخص فوق 18 عاماً يحتاج طلباً خاصاً. يُدرج القاصرون في طلب الوالد أو الوصي القانوني.",
    familyInterestText: "هل تتقدم مع أفراد من عائلتك؟ أخبرنا وتابع طلبك. سنتواصل معك بخصوص الجزء العائلي.",
    familyInterestBtn: "أنا مهتم بطلب عائلي",
    familyInterestConfirm: "تم الاستلام، سنتواصل معك",
    familyMenuHintBold: "يمكنك إضافة أفراد العائلة في أي وقت من القائمة ☰",
    defaultAdvisorName: "Ana Travieso",
    followInstagram: "تابعني على انستغرام",
    consentIntro: "قبل أن نبدأ، أحتاج إلى موافقتك للعمل مع معلوماتك.",
    consentLabel: "أوافق على",
    consentPrivacyLink: "سياسة الخصوصية",
    consentSubtext: "(هذا متطلب قانوني — بياناتك آمنة معنا.)",
  },
  section1: {
    title: "تأكيد بياناتك",
    title_rep: "تأكيد بيانات مقدم الطلب",
    subtitle_applicant: "يرجى التحقق من صحة معلوماتك قبل المتابعة.",
    subtitle_rep: "يرجى التحقق من صحة معلومات مقدم الطلب.",
    isCorrect: "هل هذا صحيح؟",
    confirmBtn: "نعم، متابعة",
    editLabel: "تعديل",
    firstName: "الاسم الأول", firstNamePlaceholder: "فاطمة",
    lastName: "اللقب الأول", lastNamePlaceholder: "أحمدي",
    segundoApellido: "اللقب الثاني", segundoApellidoPlaceholder: "حسيني",
    segundoApellidoHelper: "اختياري إذا لم يكن مدرجاً في الوثيقة.",
    email: "البريد الإلكتروني", emailPlaceholder: "email@example.com",
    phone: "رقم الهاتف المحمول", phonePlaceholder: "600 000 000",
    voiceTitle: "لنبدأ بالأساسيات.",
    voiceTitle_rep: "لنبدأ بأساسيات مقدم الطلب.",
    fillingForSelf: "هل تملأ هذا النموذج لنفسك؟",
    forMyself: "نعم، لنفسي",
    forSomeoneElse: "لا، لشخص آخر",
    repNote: "ممتاز. أولاً نؤكد بياناتك، ثم بيانات الشخص الذي تساعده.",
    repRelationship: "علاقتك بمقدم الطلب",
    repRelationshipPlaceholder: "فرد من العائلة، صديق، أخصائي اجتماعي...",
    repRelSelectPlaceholder: "اختر علاقتك",
    repRelFamily: "فرد من العائلة",
    repRelSpouse: "زوج/ة أو شريك/ة",
    repRelFriend: "صديق/ة",
    repRelSocialWorker: "أخصائي/ة اجتماعي/ة",
    repRelLawyer: "محامي/ة",
    repRelNGO: "منظمة غير حكومية / جمعية",
    repRelOther: "أخرى",
    applicantTitle: "بيانات مقدم الطلب",
    applicantFirstName: "الاسم الأول لمقدم الطلب",
    applicantFirstNamePlaceholder: "الاسم الأول",
    applicantLastName: "اللقب الأول لمقدم الطلب",
    applicantLastNamePlaceholder: "اللقب الأول",
    applicantSegundoApellido: "اللقب الثاني",
    applicantSegundoApellidoPlaceholder: "اللقب الثاني",
    applicantEmail: "البريد الإلكتروني لمقدم الطلب",
    applicantEmailPlaceholder: "إذا كان لديه واحد",
    applicantPhone: "هاتف مقدم الطلب",
    applicantPhonePlaceholder: "إذا كان لديه واحد",
    repSectionLabel: "بياناتك",
    sendPhoneOtp: "إرسال الرمز",
    verifyPhoneOtp: "تحقق",
    otpCodePlaceholder: "6 أرقام",
    phoneVerifiedBadge: "تم التحقق",
    phoneOtpHelper: "سنرسل رمزاً عبر الرسائل القصيرة (SMS) مثل التسجيل لتأكيد هذا الرقم وحفظه في ملفك.",
    phoneOtpSendError: "تعذر إرسال الرمز. حاول مرة أخرى.",
    phoneOtpCodeHint: "أدخل الرمز الذي أرسلناه لك.",
    phoneOtpSixDigitsHint: "أدخل الـ 6 أرقام للرمز.",
    phoneAlreadyRegistered: "هذا الرقم مسجل بحساب آخر. جرّب رقم هاتف مختلف.",
  },
  section2: {
    title: "المعلومات الشخصية",
    title_rep: "المعلومات الشخصية لمقدم الطلب",
    subtitle_applicant: "أكمل بياناتك الشخصية كما تظهر في وثيقتك الرسمية.",
    subtitle_rep: "أكمل البيانات الشخصية لمقدم الطلب.",
    dob: "تاريخ الميلاد", dobHelper: "DD / MM / AAAA",
    sex: "الجنس",
    sexOptions: { male: "ذكر", female: "أنثى", undetermined: "غير محدد" },
    nationality: "الجنسية", nationalityPlaceholder: "ابحث عن الجنسية…",
    countryOfBirth: "بلد الميلاد", countryOfBirthPlaceholder: "ابحث عن البلد…",
    placeOfBirth: "مكان الميلاد", placeOfBirthPlaceholder: "مثال: الدار البيضاء",
    placeOfBirthHelper: "المدينة أو البلدة التي وُلدت فيها.",
    voiceTitle: "الآن، بعض البيانات الشخصية كما تظهر في وثيقتك.",
    voiceTitle_rep: "الآن، بعض البيانات الشخصية كما تظهر في وثيقة مقدم الطلب.",
  },
  section2e: {
    stepLabel: "وضعك",
    voiceTitle: "أخبرنا عن وضعك حتى نتمكن من توجيه طلبك.",
    asylumRoutingTitle: "هل تقدمت بطلب لجوء في إسبانيا؟",
    asylumRoutingSubtitle: "نقصد بذلك الحماية الدولية المقدمة لدى الشرطة الإسبانية قبل 1 يناير 2026.",
    asylumRoutingYesTitle: "نعم",
    asylumRoutingYesDescription: "لقد تقدمت بطلب لجوء في إسبانيا.",
    asylumRoutingNoTitle: "لا",
    asylumRoutingNoDescription: "لم أتقدم بطلب لجوء في إسبانيا.",
    qualifyingTitle: "أيٌّ مما يلي يصف وضعك الحالي بشكل أفضل؟",
    qualifyingSubtitle: "اختر الخيار الذي يطابق وضعك بشكل أفضل.",
    qualifyingWorkTitle: "من خلال العمل",
    qualifyingWorkDescription: "أنا أعمل في إسبانيا، أو لدي عرض عمل، أو أخطط للعمل لحسابي الخاص.",
    qualifyingFamilyTitle: "من خلال العائلة",
    qualifyingFamilyDescription: "أعيش مع أطفالي أو والديّ في إسبانيا.",
    qualifyingVulnerabilityTitle: "من خلال وضع هشاشة",
    qualifyingVulnerabilityDescription: "يمكنني الحصول على شهادة من الخدمات الاجتماعية أو من منظمة دعم.",
    yes: "نعم",
    no: "لا",
    arrivalDate: "متى وصلت إلى إسبانيا؟",
    arrivalDateHelper: "تاريخ وصول تقريبي. لا يحتاج أن يكون دقيقاً.",
    submissionFixed: "يمكن تقديم طلبك اعتباراً من 1 أبريل 2026.",
    submissionFrom: "يمكن تقديم طلبك اعتباراً من {{date}}.",
    hardStopArrival: "تشترط التسوية الاستثنائية الوجود في إسبانيا قبل 1 يناير 2026. لكن لا تقلق، قد تكون هناك خيارات أخرى لك.",
    hardStopTooLate: "مع هذا التاريخ، ستقع الأشهر الخمسة المطلوبة بعد الموعد النهائي 30 يونيو 2026. لكن قد تكون هناك مسارات أخرى متاحة.",
    validPermit: "هل تمتلك حالياً تصريح إقامة ساري المفعول في إسبانيا؟",
    hardStopPermit: "إذا كان لديك بالفعل تصريح إقامة ساري، فأنت على الأرجح بحاجة إلى تجديد أو تعديل. يمكنني مساعدتك في ذلك.",
    asylum: "هل تقدمت بطلب لجوء أو حماية دولية في إسبانيا قبل 1 يناير 2026؟",
    helpAsylum: "تحقق من إيصال طلب اللجوء أو البطاقة الحمراء (tarjeta roja): التاريخ يوضح متى قدمت الطلب.",
    oarLabel: "رقم ملف اللجوء (OAR)",
    oarPlaceholder: "مثال: OAR/2024/12345",
    oarHelper: "ستجده على إيصال الطلب أو البطاقة الحمراء. إن لم يكن لديك الآن، يمكنك إضافته لاحقاً.",
    subRouteLabel: "أيُّها يصف وضعك بشكل أفضل؟",
    subRouteWork: "لدي عمل أو عرض عمل",
    subRouteFamily: "أعيش مع عائلتي في إسبانيا",
    subRouteVulnerability: "أنا في وضعية هشاشة",
    subRouteWorkDesc: "عملت في إسبانيا، أعمل حالياً، أو لدي عرض عمل.",
    subRouteFamilyDesc: "أعيش في إسبانيا مع زوجي/زوجتي أو أطفالي أو أفراد آخرين من العائلة.",
    subRouteVulnerabilityDesc: "أواجه وضعاً صعباً وأحتاج دعم الخدمات الاجتماعية.",
    workHistoryLabel: "ما الذي يصف وضعك المهني بشكل أفضل؟",
    workHistoryWorked: "لقد عملت سابقاً في إسبانيا",
    workHistoryCurrent: "لدي عمل حالياً",
    workHistoryOffer: "لدي عرض عمل",
    workTypeLabel: "موظف أم تعمل لحسابك الخاص؟",
    workTypeEmployed: "موظف",
    workTypeSelfEmployed: "تعمل لحسابك الخاص",
    selfEmploymentActivityLabel: "صِف نشاطك",
    selfEmploymentActivityPlaceholder: "مثال: عامل توصيل، طاهٍ، مصمم جرافيك",
    selfEmploymentActivityHint: "مطلوب لإقرار EX-32 المحلف بنية مزاولة نشاط العمل الحر.",
    empHeading: "وثائق العمل",
    empHeadingEmployed: "وثائق العمل",
    empHeadingSelfEmployed: "وثائق العمل الحر",
    workTypeGatePrompt: "أخبرنا كيف تعمل حتى نعرف ما الذي نطلبه.",
    workTypeEmployedHelper: "أنت تعمل لدى صاحب عمل أو لديك عقد / عرض.",
    workTypeSelfEmployedHelper: "أنت تعمل لحسابك أو تخطط لذلك كمستقل.",
    empDescriptionLabel: "صف نشاطك",
    empDescriptionPlaceholder: "مثال: عامل توصيل، طاهٍ، مصمم جرافيك",
    selfEmployedNote: "أنت تعمل لحسابك أو تخطط لذلك كمستقل.",
    empDocContract: "عقد عمل",
    empDocContractHelper: "عقود موقعة من أصحاب عمل حاليين أو سابقين في إسبانيا. أرفق الصفحة الأولى وصفحة التوقيع على الأقل.",
    empDocPayslip: "قسيمة راتب",
    empDocPayslipHelper: "قسائم راتب حديثة تظهر الراتب وصاحب العمل. آخر 3 إلى 6 أشهر هي الأكثر فائدة.",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper: "تقريرك الرسمي عن سجل العمل من الضمان الاجتماعي. يوضح جميع الوظائف التي شغلتها. دليل قوي جداً.",
    empDocJobOffer: "خطاب عرض عمل",
    empDocJobOfferHelper: "خطاب موقّع من صاحب عمل. يجب أن يتضمن الوظيفة وتاريخ البدء والراتب وبيانات صاحب العمل.",
    empDocOther: "مستندات أخرى",
    empDocOtherHelper: "أي مستندات أخرى تدعم وضعك المهني.",
    empOtherPlaceholder: "وصف قصير",
    empAddDoc: "إضافة مستند",
    empGateContinue: "المتابعة إلى رفع المستندات",
    empTipsTitle: "نصائح",
    empTipOptional: "حمّل فقط المستندات التي تنطبق على وضعك.",
    empTipVidaLaboral: "غالباً ما تكون vida laboral أقوى مستند للموظف.",
    empTipJobOffer: "قد يكفي عرض عمل إذا لم يكن لديك عقد بعد.",
    empTipInformal: "إذا كانت لديك فقط أدلة غير رسمية، فحمّل ما لديك.",
    empTipAutonomo: "للمستقل، أضف التسجيل والإقرارات الفصلية.",
    criminalRecord: "هل لديك سجل جنائي خلال السنوات الخمس الماضية؟",
    helpCriminal:
      "يشمل الأحكام أو الإجراءات الجنائية الجارية في أي بلد خلال السنوات الخمس الماضية، وليس في إسبانيا فقط. إذا أجبت «لا»، سنطلب شهادة السجل الجنائي لاحقاً ضمن رفع المستندات.",
    previousApplication: "هل سبق أن قدمت طلب هجرة أو إقامة في إسبانيا؟",
    helpPreviousApplication:
      "يشمل تصاريح الإقامة أو تصاريح التسوية أو أي طلب آخر لدى الهجرة في إسبانيا، حتى لو رُفض أو أُغلق.",
    callbackBtn: "أود استكشاف الخيارات",
    callbackConfirm: "✓ سنتواصل معك قريباً",
    hasPassport: "هل لديك جواز سفر صالح؟",
    hasPassportHelp: "جواز سفر من بلدك الأصلي. لا يهم إن كان منتهياً أو على وشك الانتهاء.",
    noPassportTitle: "لا مشكلة، سأتولى الأمر.",
    noPassportBody:
      "إذا لم يكن لديك جواز سفر، فسأعد تصريحاً رسمياً (Anexo I-2) وأوقعه نيابةً عنك بصفتك ممثلتك المعتمدة. لن تحتاج إلى القيام بأي شيء إضافي.",
    helpPermit:
      "هذا البرنامج مخصص لمن لا يملكون إقامة سارية. إن كان لديك إقامة، يمكننا مساعدتك في التجديد أو التعديل.",
    permitOtherServices: "تعرّف على خدماتنا الأخرى",
    permitNotSure: "لست متأكداً؟ يمكنك المتابعة على أي حال وسأراجع حالتك.",
    soon: "قريباً",
    showOtherOptions: "عرض الخيارات الأخرى",
    scenario_1_label: "هذا أول طلب لي",
    scenario_1_h: "",
    scenario_2_label: "طفل مولود في إسبانيا",
    scenario_2_h: "",
    scenario_3_label: "طفل غير مولود في إسبانيا",
    scenario_3_h: "",
    scenario_4_label: "أحد أفراد العائلة",
    scenario_4_h: "",
    scenario_7A_label: "أنا أعمل أو لدي عرض عمل",
    scenario_7A_h: "",
    scenario_7B_label: "أعيش مع عائلتي في إسبانيا",
    scenario_7B_h: "",
    scenario_7C_label: "لدي تقرير هشاشة",
    scenario_7C_h: "",
    scenario_8_label: "طفل مولود في إسبانيا",
    scenario_8_h: "",
    scenario_9_label: "طفل غير مولود في إسبانيا",
    scenario_9_h: "",
    scenario_10_label: "أحد أفراد العائلة",
    scenario_10_h: "",
    extensionGroup_da20: "أقوم بتجديد تصريح الإقامة الخاص بي",
    familyGroup_label: "أتقدم بالطلب لأحد أفراد العائلة",
    scenario_ext_jobSearch_label: "أبحث عن عمل",
    scenario_ext_jobSearch_h_da20: "",
    scenario_ext_integration_label: "لدي تقرير اندماج",
    scenario_ext_integration_h_da20: "",
    scenario_ext_illness_label: "لدي مرض",
    scenario_ext_illness_h_da20: "",
    scenario_ext_disability_label: "لدي إعاقة",
    scenario_ext_disability_h_da20: "",
    scenario_ext_retirement_label: "أنا متقاعد/ة",
    scenario_ext_retirement_h_da20: "",
  },
  section3: {
    title: "الحالة المدنية والعائلة",
    title_rep: "الحالة المدنية وعائلة مقدم الطلب",
    subtitle_applicant: "بعض الأسئلة السريعة عن وضعك الأسري.",
    subtitle_rep: "بعض الأسئلة السريعة عن الوضع الأسري لمقدم الطلب.",
    civilStatus: "الحالة المدنية",
    civilOptions: { single: "أعزب/عزباء", married: "متزوج/ة", widowed: "أرمل/ة", divorced: "مطلق/ة", separated: "منفصل/ة" },
    fatherName: "اسم الأب الأول", fatherNamePlaceholder: "أحمد",
    fatherNameHelper: "بدون ألقاب",
    fatherFirst: "الاسم",
    fatherLast1: "اسم العائلة",
    fatherLast2: "اسم العائلة الثاني",
    fatherUnknown: "لا أعرف اسمه",
    parentNotAvailable: "غير متوفر",
    motherName: "اسم الأم الأول", motherNamePlaceholder: "فاطمة",
    motherNameHelper: "بدون ألقاب",
    motherFirst: "الاسم",
    motherLast1: "اسم العائلة",
    motherLast2: "اسم العائلة الثاني",
    motherUnknown: "لا أعرف اسمها",
    parentsNote: "بيانات الوالدين اختيارية.",
    spouseQuestion: "هل لديك زوج/زوجة أو شريك/شريكة مسجل/ة؟",
    spouseHelp: "أجب بنعم إذا كنت متزوجاً/ة أو لديك شراكة مسجلة قانونياً.",
    spouseDetailsLabel: "بيانات الزوج/الزوجة",
    spouseFirstName: "الاسم",
    spouseLastName: "اسم العائلة الأول",
    spouseSecondLastName: "اسم العائلة الثاني",
    spouseDob: "تاريخ الميلاد",
    spouseNationality: "الجنسية",
    spouseLegalStatus: "هل لديه/لديها وضع قانوني في إسبانيا؟",
    spouseLegalStatusHelp: "إذا كان لدى زوجك/زوجتك NIE أو تصريح إقامة قانوني في إسبانيا، فأجب بنعم.",
    spouseNie: "رقم NIE أو رقم جواز السفر",
    childrenQuestion: "هل لديك أطفال؟",
    childrenHelp: "يشمل الأطفال البيولوجيين أو المتبنين أو من هم تحت وصايتك القانونية.",
    childHeader: "طفل",
    addChild: "إضافة طفل",
    childFirstName: "الاسم",
    childLastName: "اسم العائلة الأول",
    childSecondLastName: "اسم العائلة الثاني",
    childDob: "تاريخ الميلاد",
    childNationality: "الجنسية",
    childLivesInSpain: "هل يعيش معك في إسبانيا؟",
    childLivesInSpainHelp: "اختر نعم إذا كان هذا الطفل يعيش حالياً معك في إسبانيا.",
    childInSchool: "هل هو/هي مسجل/ة في مدرسة في إسبانيا؟",
    childInSchoolHelp: "اختر نعم إذا كان هذا الطفل مسجلاً في مؤسسة تعليمية في إسبانيا.",
    removeChild: "حذف الطفل",
    dependentParentsQuestion: "هل لديك والدان في رعايتك يعيشان معك؟",
    dependentParentsHelp: "والدان يعتمدان عليك مالياً ويعيشان معك.",
    parentHeader: "والد/والدة",
    addParent: "إضافة والد/والدة",
    parentFirstName: "الاسم",
    parentLastName: "اسم العائلة الأول",
    parentDob: "تاريخ الميلاد",
    parentNationality: "الجنسية",
    removeParent: "حذف الوالد/الوالدة",
    sexLabel: "الجنس",
    sexPlaceholder: "اختر...",
    sexMale: "ذكر",
    sexFemale: "أنثى",
    sexUndetermined: "X / غير محدد",
    unknownLabel: "غير متاح",
    dependentsQuestion: "هل لديك أطفال قاصرون أو أفراد عائلة معالون يعيشون معك في إسبانيا؟",
    dependentsHelper: "يشمل الأطفال دون سن 18، والأطفال البالغين ذوي الإعاقة، أو الوالدين في رعايتك.",
    voiceTitle: "بعض الأسئلة السريعة عن وضعك الأسري.",
    voiceTitle_rep: "بعض الأسئلة السريعة عن الوضع الأسري لمقدم الطلب.",
  },
  section4: {
    title: "العنوان",
    title_rep: "عنوان مقدم الطلب",
    subtitle_applicant: "أين تسكن في إسبانيا؟",
    subtitle_rep: "أين يسكن مقدم الطلب في إسبانيا؟",
    streetType: "نوع الطريق",
    streetTypePlaceholder: "اختر نوع الطريق",
    streetTypeOptionStreet: "شارع",
    streetTypeOptionAvenue: "جادة",
    streetTypeOptionSquare: "ساحة",
    streetTypeOptionRoad: "طريق",
    streetTypeOptionPassage: "ممر",
    streetTypeOptionOther: "أخرى",
    street: "العنوان",
    streetPlaceholder: "اسم الشارع",
    streetNumber: "رقم",
    streetNumberPlaceholder: "رقم",
    floorPiso: "الطابق",
    floorPisoPlaceholder: "—",
    floorDoor: "الباب",
    floorDoorPlaceholder: "أ، ب",
    city: "المنطقة",
    cityPlaceholder: "المدينة أو البلدة",
    province: "المقاطعة",
    provincePlaceholder: "اختر المقاطعة",
    municipality: "البلدية",
    municipalityPlaceholder: "اختر البلدية",
    locality: "المحلية",
    localityPlaceholder: "اختر المحلية",
    postalCode: "الرمز البريدي",
    postalCodePlaceholder: "08001",
    country: "البلد",
    countryPlaceholder: "إسبانيا",
    residenceSince: "منذ متى تقيم هنا؟",
    residenceSinceHelper: "الشهر والسنة التقريبيان لبدء إقامتك في هذا العنوان.",
    residenceSincePlaceholderMonth: "شهر",
    residenceSincePlaceholderYear: "سنة",
    residenceSinceOptional: "اختياري",
    voiceTitle: "أين تسكن في إسبانيا؟",
    voiceTitle_rep: "أين يسكن مقدم الطلب في إسبانيا؟",
  },
  section5: {
    title: "وضعك في إسبانيا",
    title_rep: "وضع مقدم الطلب في إسبانيا",
    subtitle_applicant: "أخبرنا عن وضعك الحالي في إسبانيا.",
    subtitle_rep: "معلومات حول الوضع الحالي لمقدم الطلب.",
    arrivalDate: "متى وصلت إلى إسبانيا؟", arrivalDateHelper: "يوم / شهر / سنة الوصول التقريبية.",
    wantsToWork: "هل تريد العمل في إسبانيا؟",
    wantsToWorkOptions: { yes: "نعم", no: "لا" },
    workSituation: "ما هو وضعك؟",
    workSituation_rep: "ما هو وضعهم؟",
    workSituationOptions: { employed: "لدي عمل", looking: "أبحث عن عمل", selfemployed: "عامل مستقل" },
    nie: "NIE", niePlaceholder: "X1234567A", nieHelper: "اختياري. إذا كان منتهيًا فلا بأس",
    nieAsk: "ليس لديك واحد؟ اسألنا",
    voiceTitle: "بعض الأسئلة عن وضعك.",
    voiceTitle_rep: "بعض الأسئلة عن وضع مقدم الطلب.",
  },
  section6: {
    title: "جواز سفرك",
    title_rep: "جواز سفر مقدم الطلب",
    subtitle:
      "التقط صورة لصفحة البيانات أو ارفع ملف PDF. سنملأ بياناتك تلقائياً.",
    subtitle_applicant: "معلومات حول وثيقة هويتك أو جواز سفرك.",
    subtitle_rep: "معلومات حول وثيقة هوية مقدم الطلب.",
    photoInstructions:
      "التقط صورة أو ارفع صورة للصفحة التي تحتوي على صورتك وبياناتك. إذا كان لديك ملف PDF كامل لجواز السفر، ارفعه هنا. لا تحاول تصوير كل صفحة على حدة.",
    photoHint:
      "يمكنك تصحيح التفاصيل لاحقاً. ستقوم لاحقاً برفع جواز السفر كاملاً (جميع الصفحات).",
    passportName: "الاسم كما يظهر في جواز السفر",
    docType: "نوع الوثيقة",
    docTypeOptions: {
      passport: "جواز سفر",
      nationalId: "بطاقة هوية",
      travelDoc: "وثيقة سفر",
      cedulaInscripcion: "شهادة تسجيل",
      other: "آخر",
    },
    docTypeOtherLabel: "حدد نوع الوثيقة",
    docTypeOtherPlaceholder: "مثال: وثيقة لاجئ",
    passportNumber: "رقم جواز السفر", passportNumberPlaceholder: "مثال: ABC123456",
    passportNumberHelper: "كما يظهر في جواز السفر.",
    docExpiry: "تاريخ الانتهاء", docExpiryHelper: "يوم / شهر / سنة",
    docExpiryAsk: "غير متأكد؟ اسألنا",
    expiredNote: "الوثائق المنتهية الصلاحية مقبولة.",
    docUpload: "ارفع وثيقتك",
    docUploadHelper: "التقط صورة للصفحة الرئيسية من جواز سفرك (التي تحتوي على صورتك وبياناتك الشخصية). ضعه على سطح مستوٍ مع إضاءة جيدة.",
    uploadBtn: "اختر ملفاً", cameraBtn: "استخدام الكاميرا",
    uploadedLabel: "الملف المرفق:", changeFile: "تغيير",
    nie: "NIE",
    niePlaceholder: "X1234567A",
    nieHelper: "اختياري. إذا كان منتهيًا فلا بأس",
    nieAsk: "ليس لديك واحد؟ اسألنا",
    docName: "الاسم كما يظهر على وثيقتك",
    docNameHelper: "تأكد من أنه يتطابق مع وثيقة هويتك.",
    seeExample: "عرض أمثلة",
    photoGuideTitle: "متطلبات الصورة",
    photoGood: "مسطحة، إضاءة جيدة، كل النص مرئي",
    photoGoodCaption: "صحيح — مسطحة، إضاءة جيدة، كل النص مقروء، الزوايا الأربع مرئية",
    photoBad1: "ضبابية",
    photoBad2: "انعكاس أو ظل",
    photoBad3: "مقطوعة أو بها أصابع",
    carouselBad1: "ضبابية — لا يمكن قراءة النص. أمسك هاتفك بثبات وركّز قبل التقاط الصورة.",
    carouselBad2: "انعكاس أو ظل — جزء من النص مغطى. استخدم الضوء الطبيعي بدون انعكاسات مباشرة.",
    carouselBad3: "مقطوعة — جزء من الوثيقة مفقود. تأكد من أن الزوايا الأربع مرئية بالكامل.",
    carouselBad4: "أصابع ظاهرة — أصابعك تغطي جزءًا من الوثيقة. أمسكها من الحواف أو ضعها على سطح مستوٍ.",
    goodExampleTitle: "مثال صحيح",
    badExampleTitle: "أمثلة خاطئة",
    photoGotIt: "فهمت",
    voiceTitle: "الآن وثيقة هويتك.",
    voiceTitle_rep: "الآن وثيقة هوية مقدم الطلب.",
  },
  section7: {
    title: "إثبات الإقامة",
    title_rep: "إثبات إقامة مقدم الطلب",
    subtitle_applicant: "ما الوثائق التي لديك لإثبات إقامتك في إسبانيا؟",
    subtitle_rep: "ما الوثائق التي يملكها مقدم الطلب لإثبات إقامته في إسبانيا؟",
    checklistTitle: "اختر الوثائق التي لديك",
    checklistHelper: "يمكنك اختيار عدة وثائق. جميعها اختيارية.",
    docTypes: { utility: "فاتورة خدمات", bank: "كشف حساب بنكي", medical: "سجل طبي", rental: "عقد إيجار", transport: "تذكرة مواصلات", other: "أخرى" },
    uploadTitle: "ارفع وثيقة واحدة", uploadHelper: "يمكنك إضافة المزيد لاحقاً.",
    uploadBtn: "اختر ملفاً", cameraBtn: "استخدام الكاميرا",
    uploadedLabel: "الملف المرفق:", changeFile: "تغيير",
    allOptional: "جميع الوثائق اختيارية في هذه المرحلة.",
    stayUploadAsk: "ما الوثائق المناسبة؟ اسألنا",
    passportUploadGuideLabel: "كيفية رفع جواز السفر كاملاً؟",
    passportUploadGuide: "نحتاج إلى جميع صفحات جواز سفرك في ملف PDF واحد، بما في ذلك الصفحات الفارغة. خياران سهلان:\n\n📱 استخدم تطبيقاً مجانياً مثل Adobe Scan أو Google Drive لمسح جميع الصفحات ضوئياً في ملف PDF من هاتفك.\n\n🏪 قم بزيارة أقرب مركز نسخ أو محل اتصالات واطلب منهم مسح جواز سفرك كاملاً إلى PDF. يمكنهم إرساله إليك بالبريد الإلكتروني أو حفظه على هاتفك.",
    voiceTitle: "الخطوة الأخيرة — إثبات وجودك في إسبانيا.",
    voiceTitle_rep: "الخطوة الأخيرة — إثبات وجود مقدم الطلب في إسبانيا.",
    asylumTitle: "وثائق اللجوء الخاصة بك",
    asylumInstructions: "بصفتك طالب لجوء، لا تحتاج إلى إثبات 5 أشهر من الإقامة. فقط ارفع وثائق اللجوء الخاصة بك.",
    longTermTitle: "إثبات وجودك في إسبانيا",
    longTermInstructions: "لقد كنت في إسبانيا لأكثر من 5 سنوات، لذا تحتاج فقط إلى بعض الوثائق الحديثة لتأكيد إقامتك المستمرة.",
    standardTitle: "إثبات وجودك في إسبانيا",
    standardInstructions: "ارفع وثائق تُظهر أنك كنت تعيش في إسبانيا. أي وثيقة تحمل اسمك وتاريخاً تصلح.",
    priorDenialTitle: "وثائقك",
    priorDenialInstructions: "نظراً لوجود إجراء سابق، نحتاج إلى بعض الوثائق المحددة.",
    asylumDocTypeLabel: "نوع وثيقة اللجوء",
    asylumDocTypes: { receipt: "إيصال اللجوء", resolution: "قرار اللجوء", other: "وثيقة لجوء أخرى" },
    asylumUploadLabel: "ارفع وثيقة اللجوء الخاصة بك",
    asylumOptionalTitle: "هل لديك وثائق أخرى تُظهر وقتك في إسبانيا؟",
    asylumOptionalHelper: "غير مطلوبة لكنها يمكن أن تقوي قضيتك.",
    asylumOptionalExpand: "إضافة وثائق إضافية",
    asylumOptionalCollapse: "إخفاء الوثائق الإضافية",
    denialSectionATitle: "وثائق الإجراء السابق",
    denialResolutionLabel: "قرار الرفض السابق",
    denialCriminalLabel: "السجل الجنائي من الإجراء السابق",
    denialCriminalNote: "حتى لو كانت منتهية الصلاحية، فهي صالحة لأنك بقيت في إسبانيا.",
    denialSectionBTitle: "إثبات الإقامة في إسبانيا",
    denialSectionBHelper: "نحتاج إلى وثائق تغطي 5 أشهر على الأقل.",
    addDocBtn: "+ إضافة وثيقة",
    docTypeLabel: "نوع الوثيقة",
    docTypeOptions: { utility: "فاتورة خدمات", bank: "كشف حساب بنكي", medical: "سجل طبي", rental: "عقد إيجار", transport: "تذكرة مواصلات", other: "أخرى" },
    monthCoverageLabel: "الأشهر المغطاة",
    monthCoverageHelper: "كل وثيقة ترفعها يمكن أن تغطي شهراً أو أكثر.",
    uploadDocLabel: "ارفع الوثيقة",
    removeDoc: "حذف",
    docEntryTitle: "وثيقة {{n}}",
    monthLabel: "من أي شهر هذه الوثيقة؟",
    tierLabel1: "الأقوى",
    tierLabel2: "قوي",
    tierLabel3: "داعم",
    tierLabel4: "تكميلي",
    tieredDocTypes: {
      padron: "بطاقة التسجيل البلدي",
      rental: "عقد الإيجار",
      medical: "السجل الطبي",
      school: "تسجيل الأطفال في المدرسة",
      bank: "كشف الحساب البنكي",
      utility: "فاتورة الخدمات (كهرباء، ماء، غاز، إنترنت)",
      payslip: "كشف الراتب أو عقد العمل",
      transfer: "إيصال التحويل المالي",
      course: "شهادة دورة أو تدريب",
      transport: "تذكرة مواصلات",
      police: "تقرير شرطي",
      official: "رسالة أو إشعار رسمي",
      other: "أخرى",
    },
    monthsCovered: "وثائق مرفوعة تغطي {{n}} من 5 أشهر",
    allMonthsCovered: "✓ تم تغطية الأشهر الخمسة",
    infoNote: "ارفع الوثائق التي لديك. لا تحتاج إلى جميعها الآن، يمكنك إضافة المزيد في أي وقت. بطاقة التسجيل البلدي هي الوثيقة الأهم — إذا كانت لديك، ارفعها أولاً.",
    videoLabel: "1 دقيقة · ما الوثائق المقبولة",
    // Flat alias keys
    title: "إثبات وجودك في إسبانيا",
    voiceTitle: "الخطوة الأخيرة — إثبات إقامتك في إسبانيا.",
    instructions: "ارفع وثائق تُظهر أنك عشت في إسبانيا. أي وثيقة تحمل اسمك وتاريخاً تكفي.",
    titleLongTerm: "إثبات وجودك في إسبانيا",
    instructionsLongTerm: "لقد كنت في إسبانيا لأكثر من 5 سنوات، لذا تحتاج فقط إلى بعض الوثائق الحديثة لتأكيد إقامتك المستمرة.",
    titleAsylum: "وثائق اللجوء الخاصة بك",
    instructionsAsylum: "بصفتك طالب لجوء، لا تحتاج إلى إثبات 5 أشهر من الإقامة. فقط ارفع وثائق اللجوء الخاصة بك.",
    asylumOptionalNote: "غير مطلوبة لكنها يمكن أن تقوي قضيتك.",
    asylumReceipt: "إيصال اللجوء",
    asylumResolution: "قرار اللجوء",
    asylumOther: "وثيقة لجوء أخرى",
    titlePriorDenial: "وثائقك",
    instructionsPriorDenial: "نظراً لوجود إجراء سابق، نحتاج إلى بعض الوثائق المحددة.",
    denialResolution: "قرار الرفض السابق",
    priorCriminalRecords: "السجل الجنائي من الإجراء السابق",
    priorCriminalNote: "حتى لو كانت منتهية الصلاحية، فهي صالحة لأنك بقيت في إسبانيا.",
    docTypePadron: "بطاقة التسجيل البلدي (padrón)",
    docTypeRental: "عقد الإيجار",
    docTypeMedical: "السجل الطبي",
    docTypeSchool: "تسجيل الأطفال في المدرسة",
    docTypeBank: "كشف الحساب البنكي",
    docTypeUtility: "فاتورة الخدمات (كهرباء، ماء، غاز، إنترنت)",
    docTypePayslip: "كشف الراتب أو عقد العمل",
    docTypeTransfer: "إيصال التحويل المالي",
    docTypeCourse: "شهادة دورة أو تدريب",
    docTypeTransport: "تذكرة مواصلات",
    docTypePolice: "تقرير شرطي",
    docTypeOfficial: "رسالة أو إشعار رسمي",
    docTypeOther: "أخرى",
    acceptedWidget: {
      title: "ما الوثائق التي تُعدّ إثباتاً للإقامة؟",
      subtitleOpen: "تأكد أن كل وثيقة تتضمن اسمك وتاريخاً. الوثائق التي لا تحتوي على اسم أو تاريخ غير صالحة.",
      tier1Label: "المستوى 1 — أقوى دليل",
      tier2Label: "المستوى 2 — دليل قوي",
      tier3Label: "المستوى 3 — دليل داعم",
      descPadron: "شهادة التسجيل البلدي (empadronamiento) من البلدية التي تسكن فيها. الشهادة التاريخية هي أقوى وثيقة منفردة لأنها تُظهر كامل تواريخ تسجيلك، وليس فقط التاريخ الحالي. وحدها قد لا تكون كافية دائماً، لكنها أساس معظم الطلبات القوية.",
    descRental: "عقد إيجار باسمك لسكن في إسبانيا. يكون أقوى إذا كان مسجلاً لدى سجل السكن في المجتمع الذاتي، أو إذا كان مرفقًا بإيصالات دفع شهرية تثبت الاستمرارية.",
    descMedical: "سجلات زيارات الطبيب أو المستشفى أو المركز الصحي العام مع تاريخ واسمك. تشمل البطاقة الصحية والتقارير الطبية والوصفات والمواعيد في النظام العام. وهي أدلة مفيدة جدًا لأنه يصعب تزويرها.",
    descSchool: "شهادات التسجيل أو الحضور أو الدراسة لك أو لأطفالك في مؤسسة تعليمية إسبانية. يشمل ذلك دور الحضانة والمدارس والمعاهد والأكاديميات الرسمية والجامعات.",
    descBank: "كشوف حساب من بنك إسباني تُظهر معاملاتك مع مرور الوقت. كلما غطت أشهرًا أكثر، كانت أقوى كدليل. الحسابات التي تُظهر نشاطًا منتظمًا في إسبانيا هي الأكثر إقناعًا.",
    descUtility: "فواتير الكهرباء أو الماء أو الغاز أو الإنترنت أو الهاتف المحمول باسمك وعلى عنوان في إسبانيا. الفواتير المتكررة خلال الأشهر الأخيرة تساعد على إثبات الحضور المستمر.",
    descPayslip: "كشوف الراتب أو عقود العمل التي تُظهر صاحب العمل والتاريخ واسمك. إذا كان لديك سجل عمل رسمي في إسبانيا، فهذه الوثائق تدعم أيضًا مسار العمل في التسوية، وليس فقط إثبات الإقامة.",
    descTransfer: "إيصالات تحويل الأموال المرسلة أو المستلمة من إسبانيا (Western Union وMoneyGram وWise وRia وغيرها). يجب أن يُظهر كل إيصال اسمك والموقع في إسبانيا والتاريخ.",
    descCourse: "شهادات أو تسجيلات أو دبلومات من دورات أو أكاديميات لغات أو برامج تدريبية في إسبانيا. يشمل ذلك دورات اللغة الإسبانية والتدريب المهني والبرامج الخاصة المعتمدة.",
    descTransport: "اشتراك نقل شهري أو تذاكر Renfe أو بطاقات نقل مُعاد شحنها أو تذاكر طيران أو حافلات. يجب أن تتضمن كل تذكرة اسمك وتاريخًا حتى تكون صالحة.",
    descPolice: "بلاغات أو محاضر أو أي وثيقة شرطة تُعرّف بك بالاسم وتحمل تاريخًا. يشمل ذلك البلاغات التي قدمتها أنت كضحية أو شاهد، وليس فقط الوثائق التي تذكرك كمحل تحقيق.",
    descOfficial: "رسائل أو إشعارات رسمية من أي إدارة عامة إسبانية (الضمان الاجتماعي، الضرائب، البلدية، المجتمع الذاتي، المحاكم، إلخ) موجهة إليك. المراسلات الرسمية المرسلة إلى عنوان في إسبانيا تُعد دليلًا قويًا على أنك تعيش هناك.",
    descOther: "أي وثيقة أخرى تحمل اسمك وتاريخًا وتثبت وجودك في إسبانيا: إيصالات شراء، تذاكر فعاليات، شهادات دينية، رسائل من جمعيات، إلخ.",
    },
    addDocument: "+ إضافة وثيقة",
    whatMonth: "من أي شهر هذه الوثيقة؟",
    uploadNote: "ارفع الوثائق المتوفرة لديك. لا تحتاج إلى كل شيء الآن، يمكنك إضافة المزيد في أي وقت. شهادة الإقامة البلدية (padrón) هي الوثيقة الأهم — إذا كانت لديك، ارفعها أولاً.",
    coverageLabel: "وثائق تغطي {{n}} من 5 أشهر",
    coverageComplete: "✓ تم تغطية الأشهر الخمسة",
    criminalDocType: "وثائق السجل الجنائي",
    criminalTitle: "وثائق السجل الجنائي",
    criminalInstructions: "ارفع أي وثائق تتعلق بسجلك — شهادات الإلغاء، وثائق المحكمة، أو أي شيء يوضح حالة قضيتك. سيراجعها مستشارك.",
    criminalUploadLabel: "وثائق السجل الجنائي",
    docEntryTitle: "وثيقة {{n}}",
    standardInstructions: "ارفع وثائق تُظهر أنك عشت في إسبانيا. أي وثيقة تحمل اسمك وتاريخاً تكفي.",
    asylumUploadLabel: "ارفع وثيقة اللجوء",
    asylumOptionalExpand: "إضافة وثائق إضافية",
    addDocBtn: "+ إضافة وثيقة",
    uploadDocLabel: "رفع الوثيقة",
    monthLabel: "من أي شهر هذه الوثيقة؟",
    removeDoc: "حذف",
    monthsCovered: "وثائق تغطي {{n}} من 5 أشهر",
    allMonthsCovered: "✓ تم تغطية الأشهر الخمسة",
    infoNote: "ارفع الوثائق المتوفرة لديك. لا تحتاج إلى كل شيء الآن، يمكنك إضافة المزيد في أي وقت. شهادة الإقامة البلدية (padrón) هي الوثيقة الأهم — إذا كانت لديك، ارفعها أولاً.",
  },
  section7b: {
    title: "ما المستندات التي ستحتاجها",
    subtitle: "تحتاج إلى إثبات الحضور المستمر في إسبانيا لمدة تقريبية {{duration}}. يجب أن تغطي المستندات أكبر عدد ممكن من الأشهر.",
    monthsApprox: "{{n}} أشهر",
    yearsApprox: "{{n}} سنوات",
    primaryTitle: "الإمبادرونامينتو (التسجيل البلدي)",
    primaryBadge: "أقوى دليل",
    primaryDesc: "شهادة تسجيلك البلدي. هذا هو المستند الأهم — يثبت مباشرة عنوانك وحضورك في إسبانيا. إذا كنت ستحمّل مستنداً واحداً فقط، فليكن هذا.",
    primaryHistorical: "اطلب شهادة تاريخية من بلديتك تُظهر كامل سجل تسجيلك.",
    supportingTitle: "المستندات الداعمة",
    rentalTitle: "عقد الإيجار / السكن",
    rentalDesc: "عقود الإيجار أو إيصالات الإيجار أو وثائق الملكية التي تُظهر عنوانك في إسبانيا.",
    rentalNote: "يغطي فترة الإيجار بأكملها في مستند واحد.",
    medicalTitle: "السجلات الطبية",
    medicalDesc: "بطاقة الصحة (tarjeta sanitaria)، زيارات الطبيب، سجلات المستشفى، الوصفات الطبية.",
    medicalNote: "كل زيارة تثبت حضورك في ذلك التاريخ المحدد.",
    bankTitle: "كشوف الحساب البنكية",
    bankDesc: "كشوف شهرية من حساب بنكي إسباني تُظهر معاملات في إسبانيا.",
    bankNote: "مفيد لتغطية أشهر متعددة — كل كشف يغطي شهراً واحداً.",
    utilityTitle: "فواتير الخدمات",
    utilityDesc: "فواتير الكهرباء والماء والغاز والإنترنت على عنوانك الإسباني.",
    utilityNote: "كل فاتورة تغطي شهراً واحداً. اجمع عدة فواتير لسد الفجوات.",
    employmentTitle: "وثائق العمل",
    employmentDesc: "عقد العمل، كشوف الراتب (nóminas)، التسجيل في الضمان الاجتماعي (vida laboral).",
    employmentNote: "الـ vida laboral ممتازة — تُظهر كامل تاريخ عملك في مستند واحد.",
    selfEmploymentTitle: "وثائق العمل الحر",
    selfEmploymentDesc: "تسجيل العمل الحر، الإقرارات الضريبية الفصلية (نموذج 130/303)، الفواتير.",
    selfEmploymentNote: "يُظهر alta de autónomo التاريخ الدقيق الذي بدأت فيه العمل.",
    familyTitle: "الوثائق العائلية",
    familyDesc: "شهادة الزواج، التسجيل المدرسي للأطفال، دفتر العائلة (libro de familia).",
    familyNote: "التسجيل المدرسي قوي جداً — يثبت الحضور المستمر طوال العام الدراسي.",
    otherTitle: "مستندات أخرى مقبولة",
    otherDesc: "تحويلات الأموال، التسجيل في الدورات، بطاقات النقل، تقارير الشرطة، أي وثيقة إسبانية رسمية تحمل اسمك وتاريخاً.",
    otherNote: "أضعف بشكل فردي لكنها تساعد في سد الفجوات بين المستندات الأقوى.",
    guidanceShort: "ركّز على الإمبادرونامينتو بالإضافة إلى 2-3 مستندات داعمة موزعة على أشهر مختلفة.",
    guidanceMedium: "حاول أن يكون لديك مستند واحد على الأقل كل ربع سنة. أقوى مجموعة لديك هي الإمبادرونامينتو + عقد الإيجار + وثائق العمل.",
    guidanceLong: "للإقامات الطويلة، شهادة الإمبادرونامينتو التاريخية ضرورية. أكملها بالـ vida laboral وكشوف الحساب البنكية التي تُظهر نشاطاً مستمراً.",
    residenceVideoLabel: "1 دقيقة · ما الوثائق التي تثبت إقامتك",
    noExitTitle: "لا تغادر إسبانيا قبل تقديم الطلب",
    noExitBody: "أي مغادرة للأراضي الإسبانية قد تقطع احتساب أشهر الحضور المستمر الخمسة المطلوبة. في حال الشك، استشر مستشارك قبل السفر.",
    whatCountsTitle: "ما الوثائق المقبولة؟",
    whatCountsBody: "أي وثيقة رسمية تحمل اسمك وعنواناً في إسبانيا وتاريخاً. لا تحتاج إلى وثائق مثالية — تحتاج إلى وثائق تغطي أكبر عدد ممكن من الأشهر.",
    rentalDetail: "ارفع العقد كاملاً بصيغة PDF. إذا كان لديك عدة عقود متتالية، ارفعها جميعاً. تأكد من ظهور اسمك والعنوان وتواريخ البداية والنهاية.",
    medicalDetail: "كل زيارة طبية أو وصفة أو تقرير مستشفى تُحتسب. كلما كان لديك أكثر، كان أفضل. يمكنك طلب سجل الزيارات من طبيبك.",
    bankDetail: "ارفع الكشوف الشهرية بصيغة PDF. كل كشف يغطي شهراً واحداً. إذا كان لديك عدة سنوات، أعطِ الأولوية للأحدث وللأشهر التي تحتاج إلى تغطيتها.",
    utilityDetail: "كل فاتورة تغطي الشهر الذي صدرت فيه. اجمع فواتير من أشهر مختلفة لتغطية أطول فترة ممكنة. تصلح فواتير الكهرباء والماء والغاز والإنترنت.",
    employmentDetail: "الـ vida laboral هي الوثيقة الأشمل — تُظهر كامل تاريخ عملك في إسبانيا في ملف PDF واحد. يمكنك الحصول عليها مجاناً من بوابة الضمان الاجتماعي الإلكترونية.",
    selfEmploymentDetail: "يُظهر alta de autónomo تاريخ البدء الدقيق. الإقرارات الفصلية (نموذج 130 أو 303) تثبت النشاط المستمر. ارفعها بترتيب زمني.",
    familyDetail: "شهادات التسجيل المدرسي قوية جداً — تثبت الحضور المستمر طوال العام الدراسي. اطلبها مباشرة من مدرسة أطفالك.",
    otherDetail: "هذه الوثائق أضعف بمفردها لكنها تساعد في سد الفجوات بين الوثائق الأقوى. كلما كان لديك أكثر من أشهر مختلفة، كان أفضل.",
  },
  review: {
    title: "مراجعة طلبك",
    subtitle: "إليك ما تم وما نحتاجه بعد",
    progressLabel: "الحقول المطلوبة مكتملة",
    statusComplete: "مكتمل",
    statusPartial: "جزئي",
    statusNotStarted: "لم يبدأ",
    notProvided: "غير مقدم",
    addLink: "إضافة",
    editLink: "تعديل",
    continueBtn: "المتابعة إلى الموافقة",
    sections: { s1: "البيانات الشخصية", s2: "المعلومات الشخصية", s3: "الحالة المدنية والعائلة", s4: "العنوان في إسبانيا", s5: "الوضع في إسبانيا", s6: "وثيقة الهوية", s7: "إثبات الإقامة" },
    voiceTitle: "لنراجع كل شيء قبل الانتهاء.",
    voiceTitle_rep: "لنراجع معلومات مقدم الطلب قبل الانتهاء.",
    saveBtn: "حفظ وإرسال",
    completionGreetComplete: "لقد سجّلت، {{name}}!",
    completionBodyComplete: "لقد استلمنا معلوماتك. الخطوة التالية هي رفع وثائقك — يمكنك القيام بذلك الآن أو العودة لاحقاً.",
    completionGreetMissing: "عمل رائع، {{name}}!",
    completionBodyMissing: "لقد حفظت كل شيء. سأرسل لك ملخصاً بما لا يزال مطلوباً — يمكنك الإنهاء متى شئت.",
    completionTextOrEmail: "سأتواصل معك عبر رسالة أو بريد إلكتروني.",
    completionPortalNote: "يمكنك العودة في أي وقت على:",
    completionTextLink: "سأرسل لك رابطاً عبر رسالة حتى تتمكن من العودة في أي وقت.",
    ctaDocs: "المتابعة إلى المستندات",
    ctaOrganizer: "الذهاب إلى المنظم",
    ctaNote: "يمكنك العودة في أي وقت. سنرسل لك رابطًا برسالة.",
    completionHeadline: "أنت جاهز، {{name}}!",
    completedLabel: "ما أتممته",
    item1: "المعلومات الشخصية",
    item2: "الوضع والأهلية",
    item3: "الحالة المدنية والعائلة",
    item4: "العنوان في إسبانيا",
    item5: "تم توقيع الاتفاقية",
    savedConfirmation: "تم حفظ كل شيء بأمان. يمكنك إغلاق هذه الصفحة والعودة في أي وقت.",
    whatsNext: "الخطوة التالية: اجمع وثائقك",
    whatsNextBody: "نحتاج إلى بعض الوثائق لإتمام طلبك: جواز سفرك، وإثبات الإقامة، وربما شهادة السجل الجنائي. يمكنك رفعها الآن أو العودة لاحقاً.",
  },
  section9: {
    title: "الموافقة والحفظ",
    subtitle: "اقتربنا من الانتهاء — يرجى التأكيد قبل حفظ طلبك.",
    gdprLabel: "لقد قرأت وأقبل سياسة الخصوصية وأوافق على معالجة بياناتي الشخصية لتقييم حالتي والتواصل معي بشأن طلبي.",
    saveBtn: "حفظ وإرسال",
    checkboxRequired: "يجب عليك قبول سياسة الخصوصية للمتابعة.",
    successTitleMissing: "تم حفظ تقدمك!",
    successMissing: "تم حفظ تقدمك. سنرسل لك ملخصاً بما لا يزال مطلوباً مع رابط للعودة واستكمال الطلب.",
    successTitleComplete: "أنت جاهز!",
    successComplete: "سيراجع مستشارك معلوماتك وسنتواصل معك إذا كنا بحاجة إلى أي شيء آخر.",
    advisorWillReview: "سيراجع طلبك وسيتواصل معك إذا احتجت شيء.",
    teamReview: "سيراجع طلبك فريقنا وسنتواصل معك إذا احتجنا شيء.",
    missingFieldsNote: "بعض الحقول لا تزال مفقودة — لا بأس. سنرسل لك متابعة.",
    privacyLink: "سياسة الخصوصية",
    voiceTitle: "شيء أخير — موافقتك على المتابعة.",
    voiceTitle_rep: "شيء أخير — موافقة مقدم الطلب على المتابعة.",
    advisorMsgComplete: "لدي كل ما أحتاجه. سأراجع طلبك وسأتواصل معك إذا احتجت شيء.",
    advisorMsgMissing: "لقد حفظت تقدمك. سأرسل لك تذكيراً بما لا يزال مطلوباً.",
    teamMsgComplete: "لدينا كل ما نحتاجه. سنراجع طلبك وسنتواصل معك إذا احتجنا شيء.",
    teamMsgMissing: "لقد حفظنا تقدمك. سنرسل لك تذكيراً بما لا يزال مطلوباً.",
  },
  advisorProfile: {
    moreAbout: "المزيد عن",
    role: "مستشارة هجرة",
    bioLine: "مستشارة هجرة · 🇻🇪",
    bioBlock: "أم لطفلين · محبة لـ Luna 🐕 · سائرة في الجبال ⛰️",
    quote: "شغفي هو مساعدة الناس على بناء حياة جديدة في إسبانيا — لقد فعلت ذلك بنفسي، وساعدت كثيرين على فعل الأمر ذاته!",
    from: "من فنزويلا في الأصل",
    personal1: "أم لطفلين رائعين",
    personal2: "محبة للكلاب — لدي كلبة منقذة اسمها لونا",
    personal3: "أحب المشي في الجبال خلال عطل نهاية الأسبوع",
    passion: "شغفي هو مساعدة الناس على بناء حياة جديدة في إسبانيا — لقد فعلت ذلك بنفسي!",
    contact: "سأتواصل معك عبر رسالة أو بريد إلكتروني 💬",
    instagram: "تابعني على انستغرام",
  },
  engagement: {
    reviewData: "مراجعة بياناتي ›",
    title: "اتفاقية التكليف",
    subtitle: "مطلوبة قبل البدء في معالجة قضيتك",
    whyTitle: "لماذا نوقّع هذا قبل البدء؟",
    whyBody: "تُضفي هذه الاتفاقية الطابع الرسمي على العلاقة المهنية بينك وبين مكتبنا. تحدد الخدمات المتفق عليها والأتعاب والتزامات الطرفين، مما يضمن الشفافية واليقين القانوني منذ البداية.",
    required: "مطلوب",
    signatureLabel: "توقيع العميل",
    clearSignature: "مسح التوقيع",
    signatureBlank: "ارسم توقيعك في المربع. لا يُقبل لوحة فارغة.",
    signatureHint: "وقّع هنا بالماوس أو بإصبعك",
    signatureBottom: "توقيع العميل",
    advisorRole: "مستشارة أولى، Global Mobility Law",
    assignedBadge: "مستشارتك المعيّنة",
    scopeLabel: "نطاق الخدمات",
    feeLabel: "الأتعاب المتفق عليها",
    feeTitle: "التسوية الاستثنائية 2026",
    feeNote: "يشمل الإدارة الكاملة للملف",
    feeAmount: "€199",
    feeWord: "أتعاب",
    feeAmountNote: "بالإضافة إلى الرسم الحكومي لتقديم الطلب (€38.28)",
    consentsLabel: "موافقتك",
    consent1Before: "لقد قرأت وأوافق على ",
    consent1Link: "الشروط العامة للتكليف المهني",
    consent1After: " وسياسة الأتعاب الموضحة أعلاه.",
    consent2: "أفوّض Global Mobility Law للتصرف بوصفها ممثلتي المهنية أمام سلطات الهجرة الإسبانية.",
    questionsText: "هل لديك أسئلة حول ما سبق؟",
    questionsBtn: "لدي أسئلة، يرجى المتابعة معي",
    questionsConfirm: "تم الاستلام. سنتابع معك",
    consent3: "أوافق على تلقّي اتصالات إعلامية حول حالة قضيتي والمستجدات ذات الصلة بشؤون الهجرة عبر البريد الإلكتروني.",
    optional: "(اختياري)",
    dateLabel: "تاريخ التوقيع:",
    dateAuto: "— مُنشأ تلقائياً",
    declarationsLabel: "إقرارات قانونية",
    validationHint: "للمتابعة: وافق على الموافقتين الإلزاميتين ووقّع الوثيقة.",
    readyMessage: "كل شيء جاهز — يمكنك المتابعة.",
    noReturnCommitment: "أؤكد أنه لا يوجد أي قرار يُلزمُني بمغادرة إسبانيا.",
    noHealthcareCoverage: "أؤكد أنني لا أملك تغطية صحية في إسبانيا على نفقة دولة أخرى أو طرف ثالث.",
    scope: {
      analysis: "تحليل وتقييم أولي لوضعك في الهجرة",
      preparation: "إعداد ومراجعة جميع الوثائق المطلوبة",
      submission: "تقديم الطلب أمام السلطة المختصة",
      followup: "متابعة الملف حتى القرار النهائي",
    },
  },
  docBriefing: {
    residenceTitle: "إثبات الإقامة: نظرة عامة",
    residenceSubtitle: "تحتاج إلى إثبات الحضور المستمر في إسبانيا لمدة تقريبية {{duration}}.",
    reviewThenContinue: "راجع الإرشادات أدناه، ثم اضغط متابعة لرفع مستنداتك.",
    criminalUploadDocsHeading: "ارفع المستندات",
    monthsApprox: "{{n}} أشهر",
    yearsApprox: "{{n}} سنوات",
    videoLabel: "1 دقيقة · ما الوثائق المقبولة",
    primaryTitle: "الإمبادرونامينتو (التسجيل البلدي)",
    primaryBadge: "أقوى دليل",
    primaryDesc: "شهادة تسجيلك البلدي. هذا هو المستند الأهم.",
    primaryHistorical: "اطلب شهادة تاريخية من بلديتك تُظهر كامل سجل تسجيلك.",
    supportingTitle: "المستندات الداعمة",
    identityTitle: "وثيقة الهوية",
    identitySubtitle: "نحتاج إلى نسخة من جواز سفرك أو بطاقة هويتك أو أي وثيقة هوية رسمية أخرى.",
    identityTip1: "جوازات السفر المنتهية الصلاحية مقبولة — ارفع ما لديك.",
    identityTip2: "تحتاج إلى نسخة كاملة من جميع صفحات جواز سفرك.",
    identityTip3: "الصيغة المفضلة هي ملف PDF واحد.",
    identityTip4: "تحتاج إلى جميع الصفحات — بما فيها الفارغة. تثبت أختام الدخول والخروج أنك لم تغادر إسبانيا.",
    identityTip5: "امسح جميع الصفحات ضوئياً في ملف PDF واحد. استخدم تطبيقاً مجانياً مثل Adobe Scan أو Google Drive على هاتفك.",
    identityTip6: "يمكنك أيضاً زيارة مركز نسخ أو محل اتصالات وطلب مسح جواز سفرك كاملاً إلى PDF.",
    rejectTitle: "الوثائق الرديئة الجودة تؤخر طلبك",
    rejectBody: "ترفض الإدارة جوازات السفر ذات الصور الضبابية أو الصفحات المفقودة أو النص غير المقروء. استخدم ماسحاً ضوئياً أو إضاءة جيدة. يجب أن تكون كل صفحة مقروءة.",
    criminalExpiry: "هذه الشهادات صالحة لمدة 3 أشهر فقط. لا تطلبها مبكراً جداً — احصل عليها قبل 2-3 أسابيع من موعد تقديم طلبك. إذا لم تكن متأكداً من التوقيت، اسألنا.",
    criminalTitle: "شهادة السجل الجنائي",
    criminalSubtitle: "نحتاج إلى شهادات سجل جنائي من كل دولة أقمت فيها خلال السنوات الخمس قبل قدومك إلى إسبانيا.",
    criminalWhichTitle: "ما الشهادات التي تحتاجها؟",
    criminalWhichBody: "شهادة واحدة من كل دولة أقمت فيها خلال السنوات الخمس قبل دخول إسبانيا. قد تكون من بلدك الأصلي فقط أو من عدة دول حسب حالتك. سنسألك عنها في الخطوة التالية.",
    criminalTip1: "اطلب الشهادة من قنصلية بلدك في إسبانيا أو من سجل الإدانات المركزي.",
    criminalTip2: "يجب أن تكون الشهادة مصادقاً عليها بالأبوستيل أو موثقة إذا كانت من بلد آخر.",
    criminalTip3: "إذا كان لديك سجل جنائي، ارفع الشهادة على أي حال — سيراجعها مستشارك.",
    criminalDiplomaticNote: "إذا لم تتمكن من الحصول على شهادة من دولة معينة خلال شهر، فلا تقلق. سنقدّم المستندات (الملحق I-1 و I-2) لطلبها عبر القنوات الدبلوماسية نيابةً عنك.",
    employmentTitle: "وثائق العمل",
    employmentSubtitle: "نحتاج إلى وثائق تثبت وضعك الوظيفي الحالي أو السابق في إسبانيا.",
    employmentTip1: "ارفع عقد عملك أو كشوف الراتب الأخيرة أو سجل الضمان الاجتماعي (vida laboral).",
    employmentTip2: "إذا كنت عاملاً مستقلاً، أدرج تسجيل العمل الحر والإقرارات الضريبية الفصلية.",
    employmentTip3: "إذا كنت تبحث عن عمل، فإن عرض عمل أو خطاب نية يكفي أيضاً.",
    familyTitle: "الوثائق العائلية",
    familySubtitle: "نحتاج إلى وثائق تثبت وضعك الأسري، خاصة إذا كان لديك زوج/ة أو أطفال في إسبانيا.",
    familyTip1: "ارفع دفتر العائلة أو شهادة الزواج أو شهادات ميلاد الأطفال.",
    familyTip2: "إذا كان أطفالك ملتحقين بمدرسة في إسبانيا، أدرج شهادة التسجيل المدرسي.",
    familyTip3: "يجب أن تكون الوثائق الأجنبية مصادقاً عليها بالأبوستيل ومترجمة إذا لزم الأمر.",
    asylumTitle: "وثائق اللجوء",
    asylumSubtitle: "بصفتك طالب لجوء، نحتاج إلى وثائقك الرسمية من إجراءات الحماية الدولية.",
    asylumTip1: "ارفع إيصال طلب اللجوء أو قرار قضيتك.",
    asylumTip2: "إذا كانت لديك بطاقة حمراء أو بيضاء لطالب اللجوء، أدرجها أيضاً.",
    asylumTip3: "لا تحتاج إلى إثبات 5 أشهر من الإقامة — وثائق اللجوء الخاصة بك كافية.",
    denialTitle: "وثائق الرفض السابق",
    denialSubtitle: "نظراً لأن لديك إجراء هجرة سابقاً تم رفضه، نحتاج إلى وثائق محددة من ذلك الملف.",
    denialTip1: "ارفع قرار الرفض من الإجراء السابق.",
    denialTip2: "أدرج السجل الجنائي من الإجراء السابق، حتى لو كان منتهي الصلاحية.",
    denialTip3: "أضف أي وثيقة تُظهر أنك بقيت في إسبانيا بعد الرفض.",
    continueToUpload: "المتابعة لرفع الوثائق",
    back: "رجوع",
    rentalTitle: "عقد الإيجار / السكن",
    rentalDesc: "عقود الإيجار أو إيصالات الإيجار أو وثائق الملكية التي تُظهر عنوانك في إسبانيا.",
    rentalNote: "يغطي فترة الإيجار بأكملها في مستند واحد.",
    medicalTitle: "السجلات الطبية",
    medicalDesc: "بطاقة الصحة، زيارات الطبيب، سجلات المستشفى، الوصفات الطبية.",
    medicalNote: "كل زيارة تثبت حضورك في ذلك التاريخ المحدد.",
    bankTitle: "كشوف الحساب البنكية",
    bankDesc: "كشوف شهرية من حساب بنكي إسباني.",
    bankNote: "كل كشف يغطي شهراً واحداً.",
    utilityTitle: "فواتير الخدمات",
    utilityDesc: "فواتير الكهرباء والماء والغاز والإنترنت على عنوانك الإسباني.",
    utilityNote: "كل فاتورة تغطي شهراً واحداً. اجمع عدة فواتير لسد الفجوات.",
    employmentTitle: "وثائق العمل",
    employmentDesc: "عقد العمل، كشوف الراتب، التسجيل في الضمان الاجتماعي (vida laboral).",
    employmentNote: "الـ vida laboral تُظهر كامل تاريخ عملك في مستند واحد.",
    selfEmploymentTitle: "وثائق العمل الحر",
    selfEmploymentDesc: "تسجيل العمل الحر، الإقرارات الضريبية الفصلية، الفواتير.",
    selfEmploymentNote: "يُظهر alta de autónomo التاريخ الدقيق الذي بدأت فيه العمل.",
    familyTitle: "الوثائق العائلية",
    familyDesc: "شهادة الزواج، التسجيل المدرسي للأطفال، دفتر العائلة.",
    familyNote: "التسجيل المدرسي يثبت الحضور المستمر طوال العام الدراسي.",
    otherTitle: "مستندات أخرى مقبولة",
    otherDesc: "تحويلات الأموال، التسجيل في الدورات، بطاقات النقل، تقارير الشرطة، أي وثيقة إسبانية رسمية تحمل اسمك وتاريخاً.",
    otherNote: "أضعف بشكل فردي لكنها تساعد في سد الفجوات.",
    guidanceShort: "ركّز على الإمبادرونامينتو بالإضافة إلى 2-3 مستندات داعمة موزعة على أشهر مختلفة.",
    guidanceMedium: "حاول أن يكون لديك مستند واحد على الأقل كل ربع سنة. أقوى مجموعة هي الإمبادرونامينتو + عقد الإيجار + وثائق العمل.",
    guidanceLong: "للإقامات الطويلة، شهادة الإمبادرونامينتو التاريخية ضرورية. أكملها بالـ vida laboral وكشوف الحساب البنكية.",
    residenceVideoLabel: "2 دقيقة · ما الوثائق التي تثبت حضورك في إسبانيا",
    noExitTitle: "لا يمكنك أن تكون قد غادرت إسبانيا",
    noExitBody: "يجب أن تكون إقامتك متواصلة خلال الأشهر الخمسة السابقة لطلبك. أي مغادرة للبلاد تكسر الشرط — حتى الرحلات القصيرة غير مسموح بها. إذا كنت قد غادرت، تواصل معنا قبل المتابعة.",
    whatCountsTitle: "ما الوثائق المقبولة؟",
    whatCountsBody: "أي وثيقة عامة أو خاصة تحمل اسمك وتاريخاً. لا تحتاج إلى وثيقة لكل شهر، لكن كلما كان لديك أكثر، كان ملفك أقوى. المثالي: وثيقة واحدة على الأقل في الشهر.",
    rentalDetail: "ارفع عقد الإيجار كاملاً. إذا كان لديك عدة عقود، ارفعها جميعاً. يجب أن يكون بصيغة PDF.",
    medicalDetail: "ارفع كل وثيقة طبية على حدة أو اجمعها في PDF واحد. تأكد من ظهور التاريخ بوضوح في كل وثيقة.",
    bankDetail: "ارفع كشف حساب كامل لكل شهر. يجب أن يظهر اسمك وعنوانك ورقم الحساب. صيغة PDF مفضلة.",
    utilityDetail: "ارفع كل فاتورة كملف منفصل أو اجمعها في PDF واحد. تأكد من ظهور اسمك وعنوانك والتاريخ.",
    employmentDetail: "اطلب vida laboral من الضمان الاجتماعي — وثيقة واحدة تغطي كل تاريخ عملك. أضف كشوف الراتب لتقوية ملفك.",
    selfEmploymentDetail: "ارفع شهادة alta de autónomo وآخر إقرارين ضريبيين فصليين. يمكنك تنزيلها من موقع Agencia Tributaria.",
    familyDetail: "ارفع شهادة الزواج أو دفتر العائلة. إذا كان أطفالك في المدرسة، أضف شهادة التسجيل المدرسي — فهي دليل قوي جداً.",
    otherDetail: "ارفع أي وثيقة رسمية تحمل اسمك وتاريخاً في إسبانيا. كلما كانت الوثيقة أرسمية، كانت أقوى.",
  },
  docFlow: {
    title: "قائمة الوثائق",
    subtitle: "هذا ما نحتاجه. اضغط على كل فئة للاطلاع على التعليمات والرفع.",
    catIdentity: "الهوية",
    catResidence: "إثبات الإقامة",
    catCriminal: "السجل الجنائي",
    catEmployment: "وثائق العمل",
    catFamily: "الوثائق العائلية",
    catAsylum: "وثائق اللجوء",
    catVulnerability: "تقرير الهشاشة",
    catOther: "مستندات أخرى",
    catDenial: "وثائق الرفض السابق",
    helpSheetTitle: "كيف يمكننا مساعدتك؟",
    helpSheetSubtitle: "سنتواصل معك قريبًا، عادةً خلال يوم عمل واحد.",
    helpSheetContextPrefix: "أنت في:",
    helpSheetPlaceholder: "أخبرنا بما تحتاجه…",
    helpSheetSend: "إرسال",
    helpSheetSending: "جارٍ الإرسال…",
    helpSheetSentTitle: "تم إرسال الرسالة",
    helpSheetSessionError: "تعذّر الوصول إلى جلستك. حاول مرة أخرى.",
    helpSheetSendError: "حدث خطأ ما. حاول مرة أخرى.",
    helpFabAria: "مساعدة — طرح سؤال",
    notStarted: "لم يبدأ",
    inProgress: "قيد التنفيذ",
    complete: "مكتمل",
    backToChecklist: "العودة إلى المستندات",
    goToOrganizer: "الذهاب إلى المنظم",
    videoLabel: "1 دقيقة · ما المستندات المقبولة",
    qualityTitle: "جودة وثائقك مهمة",
    qualityBody: "الوثائق الضبابية أو المظلمة أو المقطوعة هي السبب الرئيسي للتأخيرات. الطلبات التي تحتوي على وثائق غير مقروءة تُعاد للتصحيح، مما قد يضيف أسابيع إلى عملية معالجتك. خذ لحظة إضافية للتأكد من أن كل وثيقة واضحة ومقروءة.",
    photoTipsLink: "نصائح لصور مستندات جيدة",
    letsBegin: "لنبدأ",
    skip: "تخطي",
    continue: "متابعة",
  },
  sectionExit: {
    title: "هل انتهيت من هذا القسم؟",
    yes: "نعم",
    no: "لا",
    stuck: "أنا عالق/ة",
    stuckPlaceholder: "أخبر آنا بما يمنعك من المتابعة...",
    stuckSend: "إرسال",
    back: "رجوع",
    porMarkTitle: "هل نضع علامة على هذا القسم كمكتمل؟",
    porMarkSubtitle: "يمكنك تعديل ذلك لاحقًا عند الحاجة.",
    porMarkComplete: "وضع علامة كمكتمل والمتابعة",
    porComeBackLater: "حفظ والإنهاء لاحقًا",
  },
  docUpload: {
    title: "ارفع وثائقك",
    done: "تم — الذهاب إلى المنظم",
    backToChecklist: "العودة إلى قائمة المستندات",
    continue: "متابعة",
    goToOrganizer: "الذهاب إلى المنظم",
    backToRequirements: "العودة إلى المتطلبات",
    empHeading: "وثائق العمل",
    empHeadingEmployed: "وثائق العمل",
    empHeadingSelfEmployed: "وثائق العمل الحر",
    workTypeGatePrompt: "أخبرنا كيف تعمل حتى نعرف ما الذي نطلبه.",
    workTypeEmployed: "موظف",
    workTypeSelfEmployed: "تعمل لحسابك الخاص",
    workTypeEmployedHelper: "أنت تعمل لدى صاحب عمل أو لديك عقد / عرض.",
    workTypeSelfEmployedHelper: "أنت تعمل لحسابك أو تخطط لذلك كمستقل.",
    empDescriptionLabel: "صف نشاطك",
    empDescriptionPlaceholder: "مثال: عامل توصيل، طاهٍ، مصمم جرافيك",
    selfEmployedNote: "أنت تعمل لحسابك أو تخطط لذلك كمستقل.",
    empDocContract: "عقد عمل",
    empDocContractHelper: "عقود موقعة من أصحاب عمل حاليين أو سابقين في إسبانيا. أرفق الصفحة الأولى وصفحة التوقيع على الأقل.",
    empDocPayslip: "قسيمة راتب",
    empDocPayslipHelper: "قسائم راتب حديثة تظهر الراتب وصاحب العمل. آخر 3 إلى 6 أشهر هي الأكثر فائدة.",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper: "تقريرك الرسمي عن سجل العمل من الضمان الاجتماعي. يوضح جميع الوظائف التي شغلتها. دليل قوي جداً.",
    empDocJobOffer: "خطاب عرض عمل",
    empDocJobOfferHelper: "خطاب موقّع من صاحب عمل. يجب أن يتضمن الوظيفة وتاريخ البدء والراتب وبيانات صاحب العمل.",
    empDocOther: "مستندات أخرى",
    empDocOtherHelper: "أي مستندات أخرى تدعم وضعك المهني.",
    empOtherPlaceholder: "وصف قصير",
    empAddDoc: "إضافة مستند",
    empGateContinue: "المتابعة إلى رفع المستندات",
    empTipsTitle: "نصائح",
    empTipOptional: "حمّل فقط المستندات التي تنطبق على وضعك.",
    empTipVidaLaboral: "غالباً ما تكون vida laboral أقوى مستند للموظف.",
    empTipJobOffer: "قد يكفي عرض عمل إذا لم يكن لديك عقد بعد.",
    empTipInformal: "إذا كانت لديك فقط أدلة غير رسمية، فحمّل ما لديك.",
    empTipAutonomo: "للمستقل، أضف التسجيل والإقرارات الفصلية.",
    identityTitleNoPassport: "وثيقة الهوية",
    identityTitlePassport: "جواز السفر",
    identityGateQuestion: "ما نوع وثيقة الهوية التي لديك؟",
    identityGateHint: "استخدم جواز سفر صالحاً وغير منتهي الصلاحية إذا كان لديك واحد. اختر \"وثيقة أخرى\" فقط إذا لم يكن لديك.",
    identityGatePassport: "جواز السفر",
    identityGateOther: "وثيقة أخرى",
    identityGateContinue: "متابعة",
    identityTypeSwitchFromPassport: "ليس لدي جواز سفر ساري",
    identityTypeSwitchFromOther: "لدي جواز سفر ساري",
    identityStep1: "الخطوة 1: أدخل بياناتك",
    passportNumber: "رقم جواز السفر",
    passportFullName: "الاسم الكامل كما يظهر في جواز السفر",
    passportExpiry: "تاريخ انتهاء الصلاحية",
    nieLabel: "رقم NIE",
    nieHelper: "إذا كان لديك واحد",
    nationalIdType: "نوع الوثيقة",
    nationalIdNumber: "رقم الوثيقة",
    nationalIdCountry: "بلد الإصدار",
    nationalIdCountryPlaceholder: "اختر البلد...",
    typeNationalId: "بطاقة هوية وطنية",
    typeTravelDoc: "وثيقة سفر",
    typeCedulaInscripcion: "شهادة قيد",
    identityStep2NoPassport: "الخطوة 2: ارفع وثيقة هويتك",
    identityStep2BodyNoPassport: "ارفع أي وثيقة هوية لديك من بلدك الأصلي.",
    identityStep2: "الخطوة 2: ارفع جواز سفرك",
    identityStep2Body: "نحتاج إلى جميع الصفحات بما في ذلك الصفحات الفارغة. تساعد أختام الدخول والخروج في إثبات المدة التي قضيتها في إسبانيا. يجب رفع جميع الصفحات في ملف PDF واحد.",
    ocrTitle: "امسح ضوئيًا أو ارفع صفحة بيانات جواز السفر",
    ocrSubtitle:
      "للكثيرين، الماسح المدمج أسهل إذا لم يكن لديكم ملف PDF. سنحاول قراءة بياناتك وتعبئة النموذج.",
    ocrUploadZone: "التقط صورة أو اختر ملفًا",
    ocrUploadHint: "PDF أو صورة — صفحة الهوية فقط (التي فيها صورتك)",
    ocrSuccessTitle: "تم",
    ocrBody:
      "صفحة الهوية في جواز السفر فقط (صفحة الصورة). إذا نجحت القراءة، ستنتقل مباشرة إلى الخطوة التالية لمراجعة أو تعديل بياناتك. إن لم ينجح ذلك يمكنك المحاولة مرة أخرى أو التخطي وإدخال كل شيء يدويًا — لا شيء إجباري هنا. لاحقًا ستُرفع نسخة كاملة من جوازك في ملف PDF واحد.",
    ocrFreedomHint: "لا يُفرض عليك إتمام هذه الخطوة الآن — يمكنك التخطي في أي وقت.",
    ocrSkipManual: "تخطي — سأدخل بياناتي بنفسي",
    ocrUploadFileLabel: "رفع ملف",
    ocrUseCameraHint: "اختياري — صورة سريعة إذا لم يكن لديك مسح بعد",
    ocrUseCamera: "استخدام الكاميرا",
    ocrUseScanner: "مسح ضوئي (عدة صفحات)",
    ocrSkip: "تخطي",
    ocrContinue: "متابعة",
    passportAddTitle: "أضف جواز سفرك",
    passportAddBody1: "نحتاج إلى ملف PDF لجواز سفرك يشمل كل الصفحات.",
    passportAddBody2:
      "سيتم رفض الصور ذات الهوامش الزائدة أو ظهور الأصابع أو الوهج الشديد.",
    passportAddExamplesLink: "اطّلع على أمثلة للمسح المقبول",
    passportAddScannerCardTitle: "استخدم ماسحنا",
    passportAddScannerCardDesc:
      "التقط كل صفحة بالكاميرا. سندمجها في ملف PDF واحد.",
    passportAddPdfCardTitle: "رفع ملف PDF",
    passportAddPdfCardDesc: "ارفع ملف PDF يتضمن كل صفحات جواز سفرك.",
    passportScanExamplesCloseAria: "إغلاق",
    passportScanExamplesTitle: "أمثلة المسح",
    passportScanExamplesIntro:
      "اجعل مسحك مشابهًا للمثال المقبول. أي صورة تشبه الأمثلة المرفوضة ستحتاج إلى إعادة الالتقاط.",
    passportScanExamplesAcceptableLabel: "مقبول",
    passportScanExamplesAcceptableCaption:
      "من الحافة إلى الحافة، تركيز حاد، دون أصابع أو ظلال داخل الإطار.",
    passportScanExamplesRejectedLabel: "سيتم الرفض",
    passportScanExamplesRejectDarkTitle: "مظلم جدًا",
    passportScanExamplesRejectDarkDesc:
      "انتقل إلى إضاءة أفضل. تجنّب الظل فوق جواز السفر.",
    passportScanExamplesRejectBlurTitle: "ضبابي أو غير واضح",
    passportScanExamplesRejectBlurDesc:
      "ثبّت الكاميرا. المس جواز السفر على الشاشة للتركيز.",
    passportScanExamplesRejectFingersTitle: "أصابع داخل الإطار",
    passportScanExamplesRejectFingersDesc:
      "استخدم جسمًا صغيرًا لإبقاء الجواز مفتوحًا.",
    passportScanExamplesGotIt: "حسنًا",
    passportScanSavedTitle: "تم حفظ جواز السفر",
    passportScanSavedSubtitle: "{{filename}} · {{pages}}",
    passportScanSavedSubtitleFilenameOnly: "{{filename}}",
    passportScanSavedPagesOne: "صفحة واحدة",
    passportScanSavedPagesMany: "{{count}} صفحات",
    passportScanSavedView: "عرض",
    passportScanSavedReplace: "استبدال",
    identityReplaceConfirmTitle: "استبدال هذا المستند؟",
    identityReplaceConfirmBody:
      "سيُزال ملف PDF الحالي. يمكنك رفع ملف PDF جديد أو مسح جواز السفر مرة أخرى.",
    identityReplaceConfirmCancel: "إلغاء",
    identityReplaceConfirmReplace: "استبدال المستند",
    passportViewOpening: "جاري الفتح…",
    passportViewUnavailable: "تعذّر فتح ملف جواز السفر. أعد المحاولة أو استخدم استبدال لرفعه مجددًا.",
    passportViewPopupBlocked: "اسمح بالنوافذ المنبثقة لهذا الموقع لعرض ملف PDF.",
    passportViewerSheetTitle: "مستندك",
    passportViewerClose: "تم",
    identityAiUploadingSecure: "جاري حفظ مستندك وقراءة البيانات…",
    identityAiWaitHint:
      "عند الانتهاء ستنتقل إلى الخطوة التالية لمراجعة بياناتك. إذا حدث خطأ يمكنك إعادة المحاولة أو التخطي وإدخالها يدويًا.",
    passportPdfProcessingTitle: "جاري معالجة ملف PDF…",
    passportPdfPhaseUpload: "جاري الرفع بشكل آمن…",
    passportPdfPhaseAnalyze: "جاري قراءة بيانات جواز السفر…",
    passportPdfPhaseApply: "جاري تعبئة النموذج…",
    identityUploadRetry: "إعادة محاولة الرفع",
    identityUploadPickAnother: "اختيار ملف آخر",
    passportAiBannerTitle: "تم حفظ المستند — لم نتمكن من قراءته تلقائيًا.",
    passportAiBannerBody:
      "تم حفظ ملفك. راجع أو أدخل بياناتك أدناه، أعد المحاولة بالمسح أو الرفع، أو تخطَّ — لا حاجة للاستمرار في المحاولة. يمكننا مساعدتك لاحقًا في ملف PDF أوضح. إذا لم تطابق الحقول أدناه هذا المسح، قد تكون من جلسة سابقة — حدّثها أو أعد المحاولة.",
    passportAiBannerShopHint: "غالبًا ما يوفّر متجر نسخ محلي مسحًا واضحًا إذا كانت كاميرا الهاتف صعبة الاستخدام.",
    passportAiBannerRetry: "إعادة المحاولة بالمسح أو الرفع",
    passportAiSavedManualTitle: "تم الحفظ — أكّد أو أدخل بياناتك",
    identityTitle: "ارفع وثيقة هويتك",
    criminalTitle: "ارفع شهادة السجل الجنائي",
    asylumTitle: "ارفع وثائق اللجوء",
    denialTitle: "ارفع قرار الرفض",
    employmentTitle: "ارفع وثائق العمل",
    familyTitle: "ارفع الوثائق العائلية",
    vulnHeading: "تقرير الهشاشة",
    vulnIntroBody: "نموذج رسمي يؤكد وضعية الهشاشة لديك. ستحتاج إلى عامل اجتماعي أو منظمة غير حكومية مسجلة لإكماله.",
    vulnStep1: "الخطوة 1: احصل على النموذج",
    vulnStep1Body: "خذ هذا النموذج إلى عامل اجتماعي أو منظمة غير حكومية مسجلة حتى يملؤوا التقييم ويوقعوه ويختموه.",
    vulnDownloadBtn: "تنزيل",
    vulnEmailBtn: "بريد إلكتروني",
    vulnStep2: "الخطوة 2: ارفعه هنا",
    vulnStep2Body: "بعد توقيعه وختمه، ارفعه هنا.",
    vulnScannerBtn: "استخدام الماسح",
    vulnTipsTitle: "نصائح",
    vulnTipSocial: "يمكن لأي مكتب محلي للخدمات الاجتماعية إكمال هذا النموذج لك.",
    vulnTipNGO: "يمكن أيضاً لمنظمات غير حكومية مسجلة مثل كاريتاس أو الصليب الأحمر أو ACCEM أو CEAR إكماله.",
    vulnTipForward: "قد تتمكن من إرساله بالبريد الإلكتروني مباشرة إلى الخدمات الاجتماعية أو إلى منظمة غير حكومية، حسب كل مكتب.",
    vulnTipSigned: "تأكد من أن التقرير موقّع ومختوم ومؤرخ. النماذج غير الموقعة سيتم رفضها.",
    vulnTipID: "اصطحب جواز سفرك وأي وثيقة هوية أخرى عند الذهاب للحصول على التوقيع.",
    otherHeading: "مستندات أخرى",
    otherBody: "استخدم هذا القسم لرفع أي مستند إضافي يطلبه مستشارك ولا يندرج ضمن الفئات السابقة. مثل التوكيل الرسمي أو الترجمة المحلفة أو الأبوستيل.",
    otherAddDoc: "رفع مستند",
    documentTypeLabel: "نوع المستند",
    documentTypePlaceholder: "اختر…",
    otrosDescribeLabel: "صف المستند",
    otrosDescribePlaceholder: "وصف مختصر (مطلوب لـ «Otros»)",
    uploadRulesHint:
      "PDF أو DOC أو JPG أو TIF أو GIF. بحد أقصى 6 ميجابايت لكل ملف. الاسم: أحرف وأرقام ومسافات و _ - . ( )",
    multipleFilesHint: "يمكنك رفع عدة وثائق.",
    residenceHeading: "إثبات الإقامة",
    residenceDesc: "ارفع وثائق تُثبت وجودك في إسبانيا بشكل مستمر. كلما كان لديك وثائق أكثر، كان أفضل. يمكنك الرفع الآن أو العودة لاحقاً.",
    residencePickerTitle: "إضافة إثبات إقامة",
    residencePickerSubtitle: "اختر الوثيقة التي تريد رفعها. إذا لم تكن متأكدًا، اختر أقرب خيار أو حدّد \"أخرى\".",
    residenceFeatured: "الأقوى",
    residenceGroupOfficial: "وثائق رسمية",
    residenceGroupPersonal: "سجلات شخصية أخرى",
    residenceGroupOther: "أخرى",
    residenceGuidance: "تأكد من أن هذه الوثيقة تُظهر اسمك وتاريخًا بشكل واضح. قد لا يتم قبول الوثائق التي لا تحتوي على هذه البيانات.",
    residenceOtherLabel: "ما نوع هذه الوثيقة؟",
    residenceOtherPlaceholder: "مثال: اشتراك نادي رياضي، إيصال إيجار، تأكيد موعد",
    residenceOtherHelper: "اختياري. وصف قصير يساعد آنا على فهم ماهية الوثيقة.",
    residenceChange: "تغيير نوع الوثيقة",
    monthsCovered: "الأشهر المغطاة",
    monthsTip: "اضغط على الأشهر التي تغطيها كل وثيقة ترفعها لمتابعة تقدمك.",
    addDocument: "+ إضافة وثيقة",
    chooseFile: "اختيار ملف",
    uploadedDocs: "الوثائق المرفوعة",
    modalTitle: "إضافة وثيقة",
    modalDocType: "نوع الوثيقة",
    modalSelectType: "اختر النوع...",
    modalMonthRange: "الأشهر المغطاة",
    modalFrom: "من",
    modalTo: "إلى",
    modalFile: "الملف",
    modalAdd: "إضافة",
    typeEmpadronamiento: "شهادة التسجيل البلدي",
    typeRental: "عقد الإيجار",
    typeMedical: "سجل طبي",
    typeBank: "كشف حساب بنكي",
    typeUtility: "فاتورة خدمات",
    typeEmployment: "وثيقة عمل",
    typeOther: "أخرى",
    monthsAutoTip: "ديس 2025 إلى أبر 2026. تتحدث تلقائياً عند إضافة وثائق.",
    submissionMonthNote: "حسب تاريخ التقديم، قد نحتاج إلى العودة وإضافة مستندات شهر مايو.",
    to: "إلى",
    openScanner: "فتح الماسح",
    helpTitle: "نصائح سريعة",
    helpTip1: "شهادة التسجيل البلدي هي الوثيقة الأهم — ارفعها أولاً إذا كانت لديك.",
    helpTip2: "كل وثيقة يمكن أن تغطي عدة أشهر — عقد الإيجار يمكن أن يغطي الفترة كاملة.",
    helpTip3: "كشوف الحساب والفواتير والزيارات الطبية تصلح أيضاً — أي وثيقة تحمل اسمك وتاريخاً.",
    helpTip4: "لا تحتاج إلى كل شيء الآن — يمكنك العودة وإضافة المزيد في أي وقت.",
    seeFullRequirements: "العودة إلى النظرة العامة",
    backToOverview: "العودة إلى النظرة العامة",
    backToGuidance: "العودة إلى النظرة العامة",
    modalScanBtn: "فتح الماسح الضوئي",
    modalChangeFile: "تغيير",
    removeDoc: "حذف",
    deleteFileConfirm: "هل أنت متأكد أنك تريد حذف هذا الملف؟",
    criminalDesc: "تحتاج إلى شهادة سجل جنائي من بلدك الأصلي ومن أي بلد عشت فيه خلال السنوات الخمس الماضية.",
    criminalReq1: "اطلبها من قنصلية بلدك في إسبانيا أو اطلب من أحد أفراد عائلتك الحصول عليها من بلدك الأصلي",
    criminalReq2: "يجب أن تكون مصادقاً عليها بالأبوستيل أو موثقة",
    criminalReq2Pre: "يجب أن تكون ",
    criminalReq2Term: "مصادقاً عليها بالأبوستيل أو موثقة",
    criminalReq2Help: "الأبوستيل هو ختم رسمي يُصادق على وثيقتك للاستخدام الدولي. يُحصل عليه من الوزارة المختصة في بلدك الأصلي. إذا لم يكن بلدك طرفاً في اتفاقية لاهاي، فأنت بحاجة إلى توثيق قنصلي. إذا لم تكن متأكداً مما تحتاجه، اسألنا.",
    criminalReq3: "إذا لم تكن باللغة الإسبانية، فهي تحتاج إلى ترجمة رسمية مُحلَّفة",
    criminalReq3Pre: "إذا لم تكن باللغة الإسبانية، فهي تحتاج إلى ",
    criminalReq3Term: "ترجمة رسمية مُحلَّفة",
    criminalReq3Help: "المترجم المُحلَّف هو متخصص معيّن رسمياً من قِبل وزارة الخارجية الإسبانية. ترجماته لها صلاحية قانونية. يمكنك البحث عن واحد على maec.gob.es أو سؤالنا — يمكننا التوصية بأحدهم.",
    criminalDeclaracion: "لا تستطيع الحصول عليها؟ في بعض الحالات يُقبل إقرار بالقسم. تحدث معنا.",
    criminalDeclaracionPre: "لا تستطيع الحصول على إحداها؟ في بعض الحالات يُقبل ",
    criminalDeclaracionPost: ". تحدث معنا.",
    criminalHelpTitle: "نصائح",
    criminalHelp1: "بعض القنصليات تستغرق أسابيع — قدّم الطلب في أقرب وقت ممكن",
    criminalHelp2: "إذا كانت الشهادة تتجاوز صفحة واحدة، استخدم ماسحنا الضوئي لإنشاء ملف PDF",
    criminalVideoLabel: "1 دقيقة · كيفية الحصول على شهادة السجل الجنائي",
    asylumHeading: "وثائق اللجوء",
    asylumDesc:
      "أدخل رقم ملف OAR الخاص بك وارفع بطاقتك الحمراء أو إيصال الطلب. إذا تم رفض طلب اللجوء، فارفع أيضًا قرار الرفض.",
    asylumDocReceipt: "إيصال الطلب",
    asylumDocReceiptHelper: "أرفق جميع الصفحات",
    asylumDocCard: "البطاقة الحمراء",
    asylumDocCardHelper: "ارفع الجهتين.",
    asylumDocResolution: "قرار الرفض",
    asylumDocResolutionHelper: "إذا تم رفض طلب اللجوء",
    or: "أو",
  },
  criminal: {
    multiCountryTitle: "ما الشهادات التي تحتاجها؟",
    multiCountryBody: "واحدة لكل بلد عشت فيه خلال الخمس سنوات قبل دخولك إسبانيا. قد يكون بلدك الأصلي فقط أو عدة بلدان — يعتمد على وضعك. سنسألك عنها في الخطوة التالية.",
    diplomaticNote: "إذا لم تتمكن من الحصول على شهادة من بلد معيّن خلال شهر، فلا تقلق. سنقدّم المستندات (الملحق I-1 وI-2) لطلبها عبر القنوات الدبلوماسية نيابةً عنك.",
    uploadPageDesc: "نحتاج شهادات سجل جنائي من كل بلد أقمت فيه خلال السنوات الخمس السابقة لدخولك إسبانيا. أضف بلدك الأصلي إذا كنت تعيش فيه خلال تلك الفترة. لا تضمّن إسبانيا.",
    countriesQuestion: "في أي دول أقمت خلال السنوات الخمس السابقة لدخولك إسبانيا؟",
    countriesHint: "أضف جميع الدول التي أقمت فيها خلال السنوات الخمس السابقة لدخولك إسبانيا. أدرج بلدك الأصلي إذا كنت تقيم فيه خلال تلك الفترة.",
    selectCountryPlaceholder: "اختر دولة…",
    countrySearchPlaceholder: "بحث عن دولة…",
    countrySearchNoResults: "لا توجد نتائج",
    addCountry: "إضافة",
    uploadSectionTitle: "ارفع الشهادات",
    noSpainNote: "لا تضمّن إسبانيا. يتولى النظام تلقائياً معالجة الشهادة الإسبانية.",
    or: "أو",
    requestedNotReceivedLabel: "تم طلبه ولكن لم يتم استلامه.",
    requestedNotReceivedDesc: "مرّ أكثر من 30 يوماً منذ أن طلبت الشهادة. سأرفع إثبات الطلب (بريد إلكتروني أو رسالة أو إيصال).",
    requestedNotReceivedBadge: "تم طلبه، ولم يُستلم خلال 30 يوماً",
    requestProofPending: "إثبات الطلب قيد الانتظار",
    uploadedBadge: "تم رفع الشهادة",
    uploadReceiptBtn: "رفع الإيصال",
    scanReceiptBtn: "مسح الإيصال",
    uploadCertificateBtn: "رفع الشهادة",
    scanCertificateBtn: "مسح الشهادة",
    awaitingCertificate: "مر أكثر من 30 يومًا منذ طلب الشهادة",
    briefingTip1: "اطلب الشهادة من قنصلية بلدك في إسبانيا أو اطلب من أحد أفراد عائلتك الحصول عليها في بلدك الأصلي.",
    briefingTip2: "إذا عشت فقط في بلدك الأصلي قبل القدوم إلى إسبانيا، فأنت تحتاج شهادة واحدة فقط.",
    briefingTip3: "إذا كان لديك سجل جنائي، ارفع الشهادة على أي حال — سيراجعها مستشارك.",
  },
  docPhotoGuide: {
    title: "نصائح لتصوير الوثائق",
    link: "عرض نصائح لتصوير الوثائق",
    good: "صحيح — مسطح، إضاءة جيدة، كل النص مقروء",
    goodTitle: "مثال صحيح",
    badTitle: "أمثلة خاطئة",
    bad1: "ظل — النص مخفي. غيّر موضع مصدر الضوء.",
    bad2: "مقطوع أو مائل — تأكد من أن الوثيقة بأكملها داخل الإطار.",
    bad3: "مظلم جداً — لا يمكن قراءة النص. استخدم إضاءة أكثر.",
  },
  organizer: {
    title: "منظمي",
    needItems: "لا تزال تحتاج إلى {{n}} عناصر",
    allDone: "كل شيء مكتمل!",
    residence: "إثبات الإقامة",
    criminal: "شهادة السجل الجنائي",
    asylum: "وثائق اللجوء",
    notStarted: "لم يبدأ",
    inProgress: "قيد التنفيذ",
    complete: "مكتمل",
    upload: "رفع",
    viewCompleted: "عرض المكتملة",
    uploadDocuments: "رفع الوثائق",
    greeting: "أهلاً بعودتك، {{name}}",
    greetingSub: "إليك حالة طلبك.",
    caseProgress: "تقدم قضيتك",
    phaseInfo: "المعلومات",
    phaseDocs: "الوثائق",
    phaseSubmit: "التقديم",
    remaining: "متبقي",
    deadline: "الموعد النهائي للتقديم: 30 يونيو 2026",
    detailsLabel: "بياناتك",
    docsLabel: "المستندات",
    viewChecklist: "عرض قائمة المستندات",
    contactTitle: "هل تحتاج مساعدة؟",
    contactSub: "راسلنا على واتساب",
    submitInlineBannerTitle: "هل أنت مستعد لتقديم طلبك؟",
    submitInlineBannerBody:
      "إذا أنهيت جميع الأقسام، أخبرنا بذلك؛ سنراجع طلبك وسنتواصل معك قريبًا.",
    submitInlineBannerCta: "إرسال إلى {{advisor}}",
    submitInlineSending: "جارٍ الإرسال…",
    submitDoneHeading: "تم الإرسال إلى {{advisor}} في {{date}}",
    submitDoneHeadingNoDate: "تم الإرسال إلى {{advisor}}",
    submitDoneMessagePara1:
      "تعمل {{advisor}} على مراجعة طلبك وستراسلك برسالة إذا كانت لديها أسئلة أو إذا احتاجت إلى أي تحديث. سنرسل أيضًا نسخة إلى بريدك الإلكتروني.",
    submitDoneMessagePara2:
      "لقد انتهيت الآن. يمكنك الرد على البريد الإلكتروني أو العودة إلى هنا إذا كان لديك أي أسئلة.",
    incomplete: "غير مكتمل",
    stepConfirm: "تأكيد البيانات",
    stepSituation: "وضعك",
    stepDocument: "وثيقة الهوية",
    stepPassportPersonal: "جواز السفر والبيانات الشخصية",
    stepPersonal: "المعلومات الشخصية",
    stepCivil: "الحالة المدنية والعائلة",
    stepAsylum: "سؤال اللجوء",
    stepQualifying: "أيٌّ مما يلي يصف وضعك الحالي بشكل أفضل؟",
    stepAddress: "العنوان في إسبانيا",
    stepConsent: "الاتفاقية والتوقيع",
    signSubmitLabel: "وقّع وقدّم",
    signSubmitStatusPending: "قيد الانتظار",
    signSubmitStatusComplete: "مكتمل",
    deadlineLabel: "الموعد النهائي",
    nextAction: "تابع طلبك",
    nextActionSub: "أكمل المعلومات الناقصة",
    nextUpload: "ارفع وثائقك",
    nextUploadSub: "لديك وثائق في انتظار الرفع",
    progress: "التقدم",
    of: "من",
    contextBody: "أكمل كل قسم بالسرعة التي تناسبك — يتم حفظ تقدمك تلقائياً.",
    finishItems: "يرجى إكمال العناصر المتبقية. بمجرد الانتهاء، سأراجع كل شيء وأجهزه لتقديمه إلى مكتب الهجرة الإسباني. إذا كانت لديك أي أسئلة، أرسل لي ملاحظة في أسفل هذه الصفحة وسأرد عليك قريبًا.",
    signAvailableLater: "متاح في النهاية",
    helpStuckTitle: "أنا عالق/ة",
    helpStuckSub: "أخبرنا بما يحدث وسنرد عليك قريبًا.",
    helpStuckSubExpanded: "{lawyer} سيرد قريبًا.",
    helpPlaceholder: "اكتب هنا...",
    helpSendButton: "إرسال",
    helpSentTitle: "تم إرسال الرسالة",
    helpSentSub: "{lawyer} سيراها ويرد قريبًا.",
    helpDefaultLawyer: "محاميك",
  },
  upload: {
    scanTitle: "المسح الضوئي بهاتفك",
    scanSubtitle: "يكتشف الحواف تلقائياً وينشئ ملف PDF",
    scanBtn: "فتح الماسح الضوئي",
    appTitle: "استخدام تطبيق ماسح ضوئي",
    appSubtitle: "امسح الصفحات ضوئياً وارفعها كملف PDF",
    appGoogle: "Google Drive — مسح مستند ضوئياً",
    appAdobe: "Adobe Scan — مجاني على App Store وGoogle Play",
    appCopyShop: "أو قم بزيارة مركز نسخ / محل اتصالات قريب",
    fileTitle: "رفع ملف",
    fileSubtitle: "لدي بالفعل ملف PDF أو صورة جاهزة",
    scanHeroTitle: "امسح جواز سفرك ضوئياً هنا",
    scanHeroSubtitle: "نرشدك صفحة بصفحة. نكتشف الحواف وننشئ ملف PDF لك.",
    alternativesLabel: "خيارات أخرى",
    altApp: "استخدام تطبيق ماسح ضوئي",
    altAppHint: "Adobe Scan أو Google Drive (مجاني)",
    altShop: "زيارة مركز نسخ",
    altShopHint: "اطلب منهم مسح جواز سفرك كاملاً إلى PDF",
    altUpload: "لدي ملف بالفعل",
    altUploadHint: "ارفع ملف PDF أو صورة لديك بالفعل",
    videoLabel: "30 ثانية · كيفية مسح جواز سفرك",
    browseFiles: "تصفح الملفات",
    scanCriminalNote: "امسح كل صفحة من الشهادة ضوئياً. أدرج الأختام والتوقيعات.",
    scanDocTitle: "هل تحتاج إلى مسح مستند ضوئياً؟",
    scanDocSubtitle: "استخدم ماسحنا الضوئي للمستندات متعددة الصفحات",
  },
  auth: {
    signIn: "تسجيل الدخول",
    subtitle: "أدخل رقم هاتفك للمتابعة.",
    phone: "رقم الهاتف",
    sendCode: "إرسال الرمز",
    otpIntroTitle: "أدخل الرمز الذي أرسلناه",
    otpIntroBody: "أرسلنا رمزاً لمرة واحدة إلى هاتفك.",
    enterCode: "أدخل الرمز",
    sixDigitCodePlaceholder: "أدخل الرمز المكوّن من 6 أرقام",
    verify: "تحقق",
    resend: "إعادة إرسال الرمز",
    or: "أو",
    google: "المتابعة مع Google",
    apple: "المتابعة مع Apple",
    privacyNote: "بالمتابعة، فإنك توافق على",
  },
  faq: {
    title: "كيف يمكنني مساعدتك؟",
    about: "حول:",
    other: "لدي سؤال آخر",
    otherPlaceholder: "اكتب سؤالك…",
    otherHelper: "سنرد عليك برسالة أو بريد إلكتروني.",
    send: "إرسال",
    sent: "تم الإرسال! سيتواصل معك مستشارك قريباً.",
    sentTo: "تم الإرسال إلى",
    sentContact: "سأتواصل معك عبر رسالة أو بريد إلكتروني.",
    sentTeam: "تم الإرسال إلى فريقنا. سنرد عليك قريباً.",
    s1: {
      q1: "هل يمكنني تغيير هذه المعلومات لاحقاً؟",
      a1: "نعم، يمكنك تعديل أي حقل في أي وقت قبل التقديم النهائي. لا شيء مقفل.",
      q2: "هل يجب تضمين علامات التشكيل في الاسم؟",
      a2: "استخدم اسمك كما يظهر في جواز سفرك أو وثيقة هويتك تماماً.",
      q3: "هل بياناتي الشخصية آمنة؟",
      a3: "معلوماتك مشفرة ولا يمكن الوصول إليها إلا لك وللمستشار المعين لك.",
    },
    s2: {
      q1: "ماذا لو لم أعرف تاريخ ميلادي بدقة؟",
      a1: "استخدم التاريخ الموجود على جواز سفرك أو شهادة الميلاد. يمكن لمستشارك مساعدتك إذا كان لديك تاريخ تقريبي فقط.",
      q2: "أي جنسية يجب أن أدخل؟",
      a2: "أدخل جنسيتك الحالية كما تظهر في جواز سفرك.",
      q3: "ماذا يعني الجنس 'غير محدد'؟",
      a3: "اختر هذا الخيار إذا كانت وثيقتك لا تحدد ذكر أو أنثى — بعض جوازات السفر تستخدم 'X'.",
    },
    s3: {
      q1: "لماذا تحتاجون أسماء والديَّ؟",
      a1: "النموذج الحكومي الرسمي يتطلب أسماء الوالدين. يمكن تركه الآن — سيساعدك مستشارك لاحقاً.",
      q2: "ماذا لو لم أعرف اسم أحد الوالدين؟",
      a2: "اترك تلك الحقول فارغة. سيساعدك مستشارك عند مراجعة ملفك.",
    },
    s4: {
      q1: "ماذا لو لم يكن لدي عنوان ثابت؟",
      a1: "أدخل العنوان الذي تقيم فيه حالياً، حتى لو كان مؤقتاً. يمكنك تحديثه لاحقاً.",
      q2: "هل أحتاج إلى تسجيل بلدي؟",
      a2: "لا، التسجيل البلدي ليس مطلوباً لهذا الطلب. تُقبل أنواع كثيرة من الوثائق.",
      q3: "ماذا يعني 'مقيم منذ'؟",
      a3: "أدخل الشهر والسنة التقريبيين لبدء إقامتك في عنوانك الحالي. التقدير مقبول تماماً.",
    },
    s5: {
      q1: "هل أحتاج إلى عقد عمل للتقديم؟",
      a1: "لا. الطلب يغطي ثلاثة مسارات: العمل، أو لم شمل الأسرة، أو الهشاشة. عقد العمل خيار واحد فقط.",
      q2: "ما هو رقم NIE؟",
      a2: "NIE هو رقم تعريف الأجانب في إسبانيا. إذا لم يكن لديك واحد، اترك هذا الحقل فارغاً.",
      q3: "هل يُحتسب العمل الحر؟",
      a3: "نعم. تصريح النية للعمل الحر كافٍ في هذه المرحلة.",
    },
    s6: {
      q1: "هل يُقبل جواز سفر منتهي الصلاحية؟",
      a1: "نعم. يسمح القانون الإسباني باستخدام جوازات سفر منتهي الصلاحية لهذا الطلب. ارفع ما لديك.",
      q2: "ماذا لو فقدت جواز سفري؟",
      a2: "الحصول على جواز سفر جديد هو الطريقة الأفضل لإثبات هويتك. لإثبات الإقامة، يمكنك استخدام الفواتير أو كشوف الحساب.",
      q3: "ماذا لو لم يكن لدي أي وثيقة هوية؟",
      a3: "تواصل مع قنصلية بلدك للحصول على وثائق سفر طارئة. سيرشدك مستشارك حول الخيارات المتاحة.",
    },
    s7: {
      q1: "ما أنواع الوثائق المقبولة كدليل؟",
      a1: "الفواتير، كشوف الحساب البنكية، السجلات الطبية، عقود الإيجار، تذاكر المواصلات — أي وثيقة تحمل اسمك وتاريخاً في إسبانيا.",
      q2: "هل يجب إثبات كل شهر على حدة؟",
      a2: "لا. مجموعة من الوثائق تغطي فترات مختلفة على مدى 5 أشهر كافية.",
      q3: "ماذا لو كان لدي وثائق قليلة جداً؟",
      a3: "لا تستسلم — تُقبل أنواع كثيرة وسيساعدك مستشارك في إيجاد أفضل مجموعة. ارفع ما لديك.",
    },
    s8: {
      q1: "هل يمكنني تغيير إجاباتي بعد ذلك؟",
      a1: "نعم. يمكنك العودة وتعديل أي قسم في أي وقت قبل التقديم الرسمي النهائي.",
      q2: "ماذا يحدث بعد الحفظ؟",
      a2: "سيراجع مستشارك ملفك ويتواصل معك إذا كان هناك شيء ناقص أو يحتاج إلى توضيح.",
    },
    s9: {
      q1: "À quoi exactément estoy dando mi consentimiento?",
      a1: "Consientes que tus datos se utilicen para evaluar tu caso y para que tu asesor/a se ponga en contacto contigo. Tus datos no se comparten con terceros.",
      q2: "¿Puedo retirar mi consentimiento más adelante?",
      a2: "Sí. Contáctanos en cualquier momento y dejaremos de procesar tus datos de inmediato.",
    },
  },
};

/* ─────────────────────────── CHINESE ─────────────────────────── */
const ZH: Translations = {
  header: {
    firm: "Global Mobility", help: "需要帮助？", saved: "已保存 ✓", saving: "保存中…",
    close: "关闭",
    deleteTitle: "您确定吗？",
    deleteBody1: "您的所有个人数据、文件和案件历史将被删除。此操作无法撤销。",
    deleteBody2: "您的账户将立即停用，您的数据将在15天内删除。",
    deleteBody3: "如果您改变主意，请在删除完成前联系 hola@globalmobility.law 重新激活您的账户。",
    deleteConfirm: "是的，删除我的账户",
    deleteCountdown: "是的，删除我的账户 ({{n}})",
    cancel: "取消",
    addFamily: "添加家庭成员",
    addFamilySoon: "即将推出 — 请通过WhatsApp联系您的律师添加家庭成员。",
    privacy: "隐私政策",
    logout: "退出登录",
    deleteAccount: "删除我的账户",
  },
  exitModal: {
    title: "您的进度已保存！",
    body: "您可以随时返回并从上次停下的地方继续。",
    stay: "继续我的申请",
    leave: "退出",
  },
  progress: {
    of: "/",
    steps: ["确认信息", "您的情况", "地址", "审核提交"],
    applyingFor: "申请人：",
  },
  help: {
    title: "需要帮助？",
    placeholder: "请描述您的问题…",
    helper: "我们将通过短信或电子邮件回复您。",
    send: "发送消息",
    sent: "消息已发送！我们将尽快回复您。",
    section: "当前部分",
  },
  nav: {
    back: "返回",
    continue: "继续",
    submit: "继续",
    backToOrganizer: "返回我的管理器",
    breadcrumbOrganizer: "管理器",
    breadcrumbDocuments: "文件",
  },
  footer: {
    privacyPolicy: "隐私政策",
    termsOfService: "服务条款",
    cookiePolicy: "Cookie 政策",
    legalNotice: "法律声明",
    copyright: "© 2026 Global Mobility Group LLC",
  },
  documentScanner: {
    title: "扫描文件",
    desktopOnly: "需要拍摄多个页面并生成一个 PDF？请在手机上用浏览器打开此页面进行操作。",
    scannerBtn: "扫描",
    addPages: "添加页面",
    addAnotherPage: "添加下一页",
    fromGallery: "从相册选择",
    buildPdf: "生成 PDF",
    processing: "处理中…",
    scanMergeSaving: "正在保存扫描…",
    connectionProblemTitle: "连接问题",
    connectionProblemBody:
      "目前无法连接服务器，可能是手机信号较弱。若现在取消，已拍摄的扫描将丢失。",
    connectionProblemRetry: "重试",
    connectionProblemCancel: "取消并放弃扫描",
    devicePdfFailureKicker: "PDF 生成",
    devicePdfFailureTitle: "无法生成 PDF",
    devicePdfFailureBody:
      "在您的设备上生成文件时出了问题。您的照片仍保留在此处。请重试；如果手机内存不足，可先关闭浏览器的其他标签页。系统已向我们团队发送通知。",
    devicePdfFailureRetry: "重试",
    devicePdfFailureCancel: "取消",
    pageLabel: "第 {{n}} 页",
    remove: "删除",
    moveUp: "上移",
    moveDown: "下移",
    close: "关闭",
    needOnePage: "至少添加一页照片。",
    errorGeneric: "无法处理这些图片。请稍后重试或换更清晰的取景。",
    multiPageHint: "拍摄多页并按取景合成一个 PDF（所见即所得）。",
    cameraTitle: "扫描",
    cameraLoadingLibs: "正在加载扫描组件…",
    cameraStarting: "正在打开相机…",
    cameraTapToCapture: "对准文档，保持稳定，然后点击下方白色按钮拍摄。",
    cameraVideoNotReady: "相机预览未就绪。请关闭后重试。",
    cameraAlignHint: "请将证件对齐绿色取景框内",
    cameraAlignSubtitle: "紧凑取景：证件铺满绿色框、留白尽量少；勿拍到手指；只可轻微弯曲，勿大角度折叠。",
    cameraAlignSubtitleSpread: "多页模式：将证件置于绿色框内，四周留出均匀细边距。",
    cameraFrameModeAria: "取景辅助框形状",
    cameraFrameModeSingle: "单页",
    cameraFrameModeSpread: "展开双页",
    cameraDocDetected: "已识别文档 — 点击拍摄",
    cameraPermissionDenied: "无法访问摄像头，请在浏览器设置中授权。",
    cameraStreamEnded: "摄像头已停止（例如屏幕闲置或标签在后台）。请点击重试。",
    cameraPlayBlocked: "无法恢复预览，请点击重试。",
    cameraRetryButton: "重试",
    cameraNotFound: "未在此设备上找到摄像头。",
    cameraInitError: "无法启动摄像头。",
    captureError: "拍摄失败。",
    cameraShutter: "拍摄此页",
    cameraPageCaptured: "页面已保存 — 可继续拍摄或点击关闭。",
    flattenedBadge: "已校正",
    flattenedBadgeHint: "已在本机自动裁切并校正。",
    previewThumbnailAria: "放大预览",
    previewHint: "请确认照片清晰并包含完整页面。",
    previewRotate: "顺时针旋转 90°",
    previewRotateAria: "将此页顺时针旋转 90 度",
    rotateError: "无法旋转图片，请换一张照片或从相册重新选择。",
    previewRemovePage: "删除此页",
    cornerAdjust: "调整四角并拉平",
    cornerAdjustAria: "打开四角调整以校正透视并裁剪",
    cornerEditorTitle: "拉平并裁剪",
    cornerEditorHint: "拖动四角对齐文档边缘。我们会校正透视并裁掉多余留白。",
    cornerEditorApply: "应用",
    cornerEditorReset: "重置四角",
    cornerEditorCancel: "返回",
    cornerEditorBusy: "处理中…",
    cornerEditorError: "无法拉平此图，请尝试移动四角或重置。",
    cornerEditorInvalidQuad: "四角需在照片内构成有效的四边形。",
    cornerHandleAria: "角点",
    cornerEditorApplyContinue: "应用并继续",
    cornerEditorDone: "完成",
    cornerEditorAddPage: "+ 添加页面",
    cornerEditorResetHeader: "重置",
    passportCornerDragHint: "拖动四角对齐护照边缘。",
    passportCornerRetake: "重拍",
    passportIntroTitle: "我们来拍摄护照第一页",
    passportIntroHelp:
      "将护照翻到第一页。如有需要，可用尖细物体辅助撑开书页，并对摊开的两页拍摄一张照片。请勿用手指按住护照。",
    passportTakePhoto: "拍照",
    passportContinue: "继续",
    passportSubmitPhotos: "提交照片",
    passportRetakePhoto: "重拍",
    passportReviewPrev: "上一页",
    passportReviewNext: "下一页",
    passportReviewPageStatus: "{{current}} / {{total}}",
    passportPagesCapturedSummary: "已拍摄 {{n}} 页。",
    passportSheetReviewHint:
      "每张照片后会调整四角，然后可继续拍摄。全部完成后点「查看 / 调整」，再在下方「生成 PDF」。",
    passportReviewAdjust: "查看 / 调整",
    passportSheetMinimalHint:
      "照片仍保留在此。可「生成 PDF」完成，或点「取消」返回并重新选择上传或扫描。",
    passportPreviewReviewHint:
      "点「编辑」校正裁剪。若照片不合适，可在该页左上角「重拍」。点右上角「?」查看拍照示例。拖动缩略图排序，完成后「生成 PDF」。",
    reviewScreenTitle: "预览",
    reviewPageOf: "第 {{current}} / {{total}} 页",
    reviewPagesSectionLabel: "页面",
    reviewReorderHint: "横向滑动查看全部缩略图。按住缩略图上的 ⠿ 拖动手柄即可排序。",
    reviewReorderHoldA11y: "拖动手柄 — 按住并拖动以排序页面。",
    reviewInsertPage: "添加页面",
    reviewEdit: "编辑",
    passportReviewView: "查看",
    reviewDeletePageAria: "删除页面",
    reviewEditMenuTitle: "操作",
    reviewEditRemove: "移除页面",
    reviewDeleteConfirmTitle: "删除此页？",
    reviewDeleteConfirmBody: "该页将从扫描中移除，之后可以重新拍摄。",
    reviewDeleteConfirmDelete: "删除",
    reviewDeleteConfirmCancel: "取消",
    reviewLeaveConfirmTitle: "放弃已拍的照片？",
    reviewLeaveConfirmBody: "将丢失您至今已拍摄的所有页面。",
    reviewLeaveConfirmStay: "继续扫描",
    reviewLeaveConfirmDiscard: "放弃照片",
    passportLowPageWarnTitle: "是否已包含全部页面？",
    passportLowPageWarnBody:
      "我们通常需要您护照上每一页的照片，包括空白页。目前似乎仍有页面未拍摄。",
    passportLowPageWarnReview: "返回检查页面",
    passportLowPageWarnAnyway: "仍要生成 PDF",
    passportCameraHeader: "护照",
    passportCameraHintNext: "将摊开的双页对齐绿色框后，准备好再拍一张。",
    passportCaptureToast: "照片已保存 — 请检查裁剪。",
    passportCancel: "取消",
    passportPageTitle: "第 {{n}} 页",
    passportViewfinderPill:
      "将护照摊平平放并拍摄摊开的两页。",
    passportViewfinderPillSubsequent:
      "一次性拍摄下一页摊开画面。",
    passportCaptureFooterHint:
      "将书页平压在桌面上，下一步可调整边缘。",
    passportExamplesAria: "照片示例",
    passportViewExamplesLink: "查看照片示例",
    passportCameraHelpAria: "护照拍摄帮助",
    passportFlashAria: "开关闪光灯",
    passportHelpGotIt: "知道了",
  },
  help: {
    empadronamientoTerm: "市政登记证",
    empadronamiento: "这是您的市政登记证明。它证明您居住在西班牙的某个市镇。通常需要预约，前往市政厅申请。请索取历史证明 — 它会显示您所有的地址和日期。",
    vidaLaboralTerm: "vida laboral",
    vidaLaboral: "这是社会保障局的官方文件，显示您在西班牙的完整就业历史。免费获取，可在 sede.seg-social.gob.es 查询，或发送短信至 600 123 456，也可亲自前往社会保障局办公室。",
    altaAutonomoTerm: "自雇登记",
    altaAutonomo: "这是证明您在西班牙作为自雇工作者登记的文件。通过税务局获取，显示您开始经营活动的确切日期。",
    modelo130Term: "130/303申报表",
    modelo130: "这是自雇工作者的季度税务申报表。130表用于所得税（IRPF），303表用于增值税（IVA）。可从税务局电子办公室下载。",
    libroFamiliaTerm: "家庭手册",
    libroFamilia: "这是由民事登记处签发的官方文件，证明婚姻和子女关系。如果您在其他国家结婚或生育子女，需要提供经海牙认证并翻译的同等文件。",
    apostilladosTerm: "海牙认证",
    apostillados: "海牙认证（apostille）是根据《海牙公约》验证外国文件供国际使用的官方印章。需从签发文件的国家的主管部委或机构获取。如果您的国家不是《海牙公约》成员国，则需要领事认证代替。",
    tarjetaRojaTerm: "红卡",
    tarjetaRoja: "这是证明您已在西班牙申请国际保护（庇护）的文件。在申请处理期间每6个月续签一次。白卡类似，但用于续签。",
    declaracionJuradaTerm: "宣誓声明",
    declaracionJurada: "这是在公证人面前签署的声明，表明您没有犯罪记录。仅在因正当理由（战争、缺乏外交关系等）无法获得官方证明时才被接受。",
  },
  validation: {
    required: "此字段为必填项。",
    invalidDate: "日期无效。",
    invalidEmail: "电子邮件地址无效。",
    invalidPhone: "电话号码无效。",
    invalidNie: "请输入有效的 NIE。",
    invalidPostal: "邮政编码无效（5位数字）。",
    postalProvinceMismatch: "邮政编码前两位与所选省份不匹配。",
    invalidAddressStreet: "街道：1–100 个字符；仅字母、数字、空格、重音符号、句号和逗号。",
    invalidAddressStreetNumber: "门牌号：1–10 个字符；字母、数字或 S/N（含 /）。",
    invalidAddressFloorDoor: "户号：最多 10 个字符；仅字母和数字。",
    invalidAddressCity: "城市：1–40 个字符；仅字母、空格、连字符和重音符号。",
    minLength: "太短了。",
    invalidMonth: "月份必须在 01 至 12 之间。",
    invalidDay: "该日期对此月份无效。",
    futureDate: "日期不能晚于今天。",
    pastExpiryDate: "有效期必须是今天或未来日期。",
    yearOutOfRange: "年份不在允许范围内。",
    futureMonthYear: "日期不能晚于当前月份。",
    invalidYear: "年份无效。",
  },
  deadline: { label: "申请截止日期", date: "2026年6月30日" },
  optional: "可选",
  returning: {
    greeting: "欢迎回来，",
    progress: "您的申请进度",
    continue: "从上次停下的地方继续",
    restart: "从头开始审查",
  },
  landing: {
    greeting: "您好",
    firstVisitMessage: "让我们开始您的申请吧。大约只需10分钟。",
    firstVisitMessageWithAdvisor: "我将帮助您完成申请。大约只需10分钟。",
    thisIs: "我是",
    autoSave: "您可以随时离开并返回。您的工作会自动保存。",
    advisor: "您的顾问：",
    getStarted: "开始",
    continue: "继续",
    reviewFromStart: "从头开始审查",
    progress: {
      moreThan3: "您进展顺利 — 让我们继续吧。",
      exactly3: "您进展很好 — 还剩3个部分。",
      exactly2: "快完成了 — 还剩2个部分。",
      exactly1: "快完成了 — 还剩1个部分。",
      allDone: "一切看起来都完整了 — 让我们审查并完成吧。",
    },
    procedureBadge: "西班牙2026年特别合法化",
    videoLength: "1分钟 · 提供字幕",
    skipVideo: "跳过视频",
    familyMenuHint: "您可以随时从 ☰ 菜单添加家庭成员",
    purpose: "我将指导您完成居留申请所需的一切。我们将所有内容组织成简单的部分，按您自己的节奏进行，沿途上传您的文件。剩下的我来处理。如果您现在没有全部材料也不用担心，您的答案会自动保存，您可以随时返回并从上次停下的地方继续。",
    purposeShort: "我将指导您完成居留申请所需的一切。您的答案会自动保存，您可以随时返回。",
    purposeExpanded: "我们将所有内容组织成简单的部分。按您自己的节奏进行，沿途上传您的文件。剩下的我来处理。如果您现在没有全部材料也不用担心，您可以从上次停下的地方继续。",
    more: "更多",
    less: "收起",
    familyInfo: "如果您以家庭身份申请，每位18岁以上的人需要单独申请。未成年人包含在父母或法定监护人的申请中。",
    familyInterestText: "如果您与家人一起申请，请告诉我们并继续完成您的申请。我们会就家庭部分与您联系。",
    familyInterestBtn: "我对家庭申请感兴趣",
    familyInterestConfirm: "已收到，我们会联系您",
    familyMenuHintBold: "您可以随时从 ☰ 菜单添加家庭成员",
    defaultAdvisorName: "Ana Travieso",
    followInstagram: "在Instagram上关注我",
    consentIntro: "在开始之前，我需要您的同意才能处理您的信息。",
    consentLabel: "我同意",
    consentPrivacyLink: "隐私政策",
    consentSubtext: "(这是法律要求 — 您的数据在我们这里是安全的。)",
  },
  section1: {
    title: "确认您的信息",
    title_rep: "确认申请人的信息",
    subtitle_applicant: "请在继续之前确认您的信息是否正确。",
    subtitle_rep: "请确认申请人的信息是否正确。",
    isCorrect: "这些信息是否正确？",
    confirmBtn: "是的，继续",
    editLabel: "编辑",
    firstName: "名字", firstNamePlaceholder: "芳",
    lastName: "第一姓氏", lastNamePlaceholder: "王",
    segundoApellido: "第二姓氏", segundoApellidoPlaceholder: "李",
    segundoApellidoHelper: "如果证件上没有则可选。",
    email: "电子邮件", emailPlaceholder: "email@example.com",
    phone: "手机号码", phonePlaceholder: "600 000 000",
    voiceTitle: "让我们从基础信息开始。",
    voiceTitle_rep: "让我们从申请人的基础信息开始。",
    fillingForSelf: "您是在为自己填写吗？",
    forMyself: "是的，为我自己",
    forSomeoneElse: "不，为其他人",
    repNote: "好的。我们先确认您的信息，然后填写您帮助的人的信息。",
    repRelationship: "您与申请人的关系",
    repRelationshipPlaceholder: "家庭成员、朋友、社会工作者...",
    repRelSelectPlaceholder: "选择您的关系",
    repRelFamily: "家庭成员",
    repRelSpouse: "配偶 / 伴侣",
    repRelFriend: "朋友",
    repRelSocialWorker: "社会工作者",
    repRelLawyer: "律师",
    repRelNGO: "非政府组织 / 机构",
    repRelOther: "其他",
    applicantTitle: "申请人信息",
    applicantFirstName: "申请人的名字",
    applicantFirstNamePlaceholder: "名字",
    applicantLastName: "申请人的第一姓氏",
    applicantLastNamePlaceholder: "第一姓氏",
    applicantSegundoApellido: "第二姓氏",
    applicantSegundoApellidoPlaceholder: "第二姓氏",
    applicantEmail: "申请人的电子邮件",
    applicantEmailPlaceholder: "如果有的话",
    applicantPhone: "申请人的电话",
    applicantPhonePlaceholder: "如果有的话",
    repSectionLabel: "您的信息",
    sendPhoneOtp: "发送验证码",
    verifyPhoneOtp: "验证",
    otpCodePlaceholder: "6 位验证码",
    phoneVerifiedBadge: "已验证",
    phoneOtpHelper: "我们将通过短信（SMS，与注册相同）发送验证码以确认此号码并保存到您的个人资料。",
    phoneOtpSendError: "无法发送验证码，请重试。",
    phoneOtpCodeHint: "请输入我们发送给您的验证码。",
    phoneOtpSixDigitsHint: "请输入完整的 6 位验证码。",
    phoneAlreadyRegistered: "该号码已绑定其他账户，请使用其他手机号。",
  },
  section2: {
    title: "个人信息",
    title_rep: "申请人的个人信息",
    subtitle_applicant: "请按照官方证件上的信息填写您的个人资料。",
    subtitle_rep: "请填写申请人的个人资料。",
    dob: "出生日期", dobHelper: "DD / MM / AAAA",
    sex: "性别",
    sexOptions: { male: "男", female: "女", undetermined: "未指定" },
    nationality: "国籍", nationalityPlaceholder: "搜索国籍…",
    countryOfBirth: "出生国", countryOfBirthPlaceholder: "搜索国家…",
    placeOfBirth: "出生地", placeOfBirthPlaceholder: "例如：卡萨布兰卡",
    placeOfBirthHelper: "您出生的城市或城镇。",
    voiceTitle: "现在，请填写证件上的个人信息。",
    voiceTitle_rep: "现在，请填写申请人证件上的个人信息。",
  },
  section2e: {
    stepLabel: "您的情况",
    voiceTitle: "告诉我们您的情况，以便我们指导您的申请。",
    asylumRoutingTitle: "您是否在西班牙申请过庇护？",
    asylumRoutingSubtitle: "这里指的是在 2026 年 1 月 1 日之前向西班牙警方提交的国际保护申请。",
    asylumRoutingYesTitle: "是",
    asylumRoutingYesDescription: "我在西班牙申请过庇护。",
    asylumRoutingNoTitle: "否",
    asylumRoutingNoDescription: "我没有在西班牙申请过庇护。",
    qualifyingTitle: "以下哪项最能描述您目前的情况？",
    qualifyingSubtitle: "请选择最符合您情况的选项。",
    qualifyingWorkTitle: "通过工作",
    qualifyingWorkDescription: "我在西班牙工作，有工作机会，或者计划自己创业。",
    qualifyingFamilyTitle: "通过家庭",
    qualifyingFamilyDescription: "我与我的孩子或父母一起住在西班牙。",
    qualifyingVulnerabilityTitle: "通过脆弱处境",
    qualifyingVulnerabilityDescription: "我可以获得社会服务机构或支持组织出具的证明。",
    yes: "是",
    no: "否",
    arrivalDate: "您什么时候到达西班牙的？",
    arrivalDateHelper: "大概的到达日期。不需要精确。",
    submissionFixed: "您的申请可以从2026年4月1日起提交。",
    submissionFrom: "您的申请可以从{{date}}起提交。",
    hardStopArrival: "特别合法化要求在2026年1月1日之前在西班牙境内。但不用担心，可能还有其他选择适合您。",
    hardStopTooLate: "按此到达日期，所需的5个月将落在2026年6月30日截止日期之后。但可能还有其他途径可供选择。",
    validPermit: "您目前在西班牙持有有效居留许可吗？",
    hardStopPermit: "如果您已有有效居留许可，您可能需要续签或变更。我可以帮您解决这个问题。",
    asylum: "您是否在2026年1月1日之前在西班牙申请过庇护或国际保护？",
    helpAsylum: "请查看庇护申请回执或红色居留卡（tarjeta roja）上的日期，以确认申请时间。",
    oarLabel: "庇护案件编号（OAR）",
    oarPlaceholder: "例如：OAR/2024/12345",
    oarHelper: "可在申请回执或红色居留卡上找到。若暂时没有，可稍后再补充。",
    subRouteLabel: "以下哪项最符合您的情况？",
    subRouteWork: "我有工作或工作录用",
    subRouteFamily: "我与家人在西班牙生活",
    subRouteVulnerability: "我处于脆弱处境",
    subRouteWorkDesc: "我曾在西班牙工作、目前在职，或已有工作录用。",
    subRouteFamilyDesc: "我与配偶、子女或其他家庭成员在西班牙生活。",
    subRouteVulnerabilityDesc: "我处境困难，需要社会服务方面的支持。",
    workHistoryLabel: "哪一项最符合您的工作情况？",
    workHistoryWorked: "我之前在西班牙工作过",
    workHistoryCurrent: "我目前有工作",
    workHistoryOffer: "我有工作录用",
    workTypeLabel: "受雇还是自雇？",
    workTypeEmployed: "受雇",
    workTypeSelfEmployed: "自雇",
    selfEmploymentActivityLabel: "描述您的工作内容",
    selfEmploymentActivityPlaceholder: "例如：外卖骑手、厨师、平面设计师",
    selfEmploymentActivityHint: "这是 EX-32 自雇活动意向宣誓声明所必需的。",
    empHeading: "就业文件",
    empHeadingEmployed: "就业文件",
    empHeadingSelfEmployed: "自雇文件",
    workTypeGatePrompt: "请告诉我们您的工作方式，以便我们知道需要哪些材料。",
    workTypeEmployedHelper: "您为雇主工作，或有合同/工作机会。",
    workTypeSelfEmployedHelper: "您为自己工作，或计划以自雇形式工作。",
    empDescriptionLabel: "描述您的工作内容",
    empDescriptionPlaceholder: "例如：外卖骑手、厨师、平面设计师",
    selfEmployedNote: "您为自己工作，或计划以自雇形式工作。",
    empDocContract: "工作合同",
    empDocContractHelper: "来自西班牙现任或前任雇主的已签署合同。至少包括第一页和签字页。",
    empDocPayslip: "工资单",
    empDocPayslipHelper: "最近的工资单，显示工资和雇主。最近 3 到 6 个月最有用。",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper: "社会保障局的官方工作记录报告。显示您曾经的所有工作经历。证据很强。",
    empDocJobOffer: "工作录用信",
    empDocJobOfferHelper: "雇主签署的聘用承诺信，需包含职位、开始日期、薪资和雇主信息。",
    empDocOther: "其他文件",
    empDocOtherHelper: "任何支持您工作情况的其他文件。",
    empOtherPlaceholder: "简短说明",
    empAddDoc: "添加文件",
    empGateContinue: "继续上传文件",
    empTipsTitle: "提示",
    empTipOptional: "只上传适合您情况的文件，这里没有任何一项是必须的。",
    empTipVidaLaboral: "Vida laboral 通常是雇员最有力的单一文件。",
    empTipJobOffer: "如果还没有合同，工作机会也可能足够。",
    empTipInformal: "如果您只有非正式证明，请上传您拥有的内容。",
    empTipAutonomo: "对于自雇，请上传注册和季度申报表。",
    criminalRecord: "在过去五年内，您是否有犯罪记录？",
    helpCriminal:
      "请计入近五年内在任何国家的有罪判决或尚未结案的刑事案件，不限于西班牙。若选择「否」，后续文件步骤将需要无犯罪记录证明。",
    previousApplication: "您是否曾在西班牙提交过移民类申请？",
    helpPreviousApplication:
      "包括居留许可、扎根或其他在西班牙向内政/移民局递交的申请，即使曾被拒或已结案。",
    callbackBtn: "我想了解其他选择",
    callbackConfirm: "✓ 我们会尽快联系您",
    hasPassport: "您有有效护照吗？",
    hasPassportHelp: "您原籍国的护照。即使已经过期或即将过期也没关系。",
    noPassportTitle: "没关系，我来处理。",
    noPassportBody:
      "如果您没有护照，我会准备一份正式声明（Anexo I-2），并作为您的授权代表代您签署。您无需额外操作。",
    helpPermit: "本计划适用于目前没有有效居留许可的人士。若您已有许可，我们可以协助办理续签或变更。",
    permitOtherServices: "了解其他服务",
    permitNotSure: "不确定？仍可继续提交，我会审阅您的情况。",
    soon: "即将推出",
    showOtherOptions: "显示其他选项",
    scenario_1_label: "这是我的首次申请",
    scenario_1_h: "",
    scenario_2_label: "在西班牙出生的未成年人",
    scenario_2_h: "",
    scenario_3_label: "非在西班牙出生的未成年人",
    scenario_3_h: "",
    scenario_4_label: "家庭成员",
    scenario_4_h: "",
    scenario_7A_label: "我正在工作或已有工作录用",
    scenario_7A_h: "",
    scenario_7B_label: "我与家人在西班牙生活",
    scenario_7B_h: "",
    scenario_7C_label: "我有脆弱情况报告",
    scenario_7C_h: "",
    scenario_8_label: "在西班牙出生的未成年人",
    scenario_8_h: "",
    scenario_9_label: "非在西班牙出生的未成年人",
    scenario_9_h: "",
    scenario_10_label: "家庭成员",
    scenario_10_h: "",
    extensionGroup_da20: "我正在续签居留许可",
    familyGroup_label: "我正在为家庭成员申请",
    scenario_ext_jobSearch_label: "我正在找工作",
    scenario_ext_jobSearch_h_da20: "",
    scenario_ext_integration_label: "我有融入报告",
    scenario_ext_integration_h_da20: "",
    scenario_ext_illness_label: "我患有疾病",
    scenario_ext_illness_h_da20: "",
    scenario_ext_disability_label: "我有残疾",
    scenario_ext_disability_h_da20: "",
    scenario_ext_retirement_label: "我已退休",
    scenario_ext_retirement_h_da20: "",
  },
  section3: {
    title: "婚姻状况与家庭",
    title_rep: "申请人的婚姻状况与家庭",
    subtitle_applicant: "关于您家庭状况的几个简短问题。",
    subtitle_rep: "关于申请人家庭状况的几个简短问题。",
    civilStatus: "婚姻状况",
    civilOptions: { single: "单身", married: "已婚", widowed: "丧偶", divorced: "离婚", separated: "分居" },
    fatherName: "父亲的名字", fatherNamePlaceholder: "明",
    fatherNameHelper: "不含姓氏",
    fatherFirst: "名字",
    fatherLast1: "姓氏",
    fatherLast2: "第二姓氏",
    fatherUnknown: "我不知道他的名字",
    parentNotAvailable: "暂无",
    motherName: "母亲的名字", motherNamePlaceholder: "芳",
    motherNameHelper: "不含姓氏",
    motherFirst: "名字",
    motherLast1: "姓氏",
    motherLast2: "第二姓氏",
    motherUnknown: "我不知道她的名字",
    parentsNote: "父母信息为可选填写。",
    spouseQuestion: "您有配偶或登记伴侣吗？",
    spouseHelp: "如果您已婚或有合法登记的伴侣，请选择“是”。",
    spouseDetailsLabel: "配偶信息",
    spouseFirstName: "名字",
    spouseLastName: "第一姓氏",
    spouseSecondLastName: "第二姓氏",
    spouseDob: "出生日期",
    spouseNationality: "国籍",
    spouseLegalStatus: "对方在西班牙有合法身份吗？",
    spouseLegalStatusHelp: "如果您的配偶有 NIE 或西班牙合法居留许可，请选择“是”。",
    spouseNie: "NIE 或护照号码",
    childrenQuestion: "您有孩子吗？",
    childrenHelp: "包括亲生、收养或法律上由您抚养的子女。",
    childHeader: "子女",
    addChild: "添加子女",
    childFirstName: "名字",
    childLastName: "第一姓氏",
    childSecondLastName: "第二姓氏",
    childDob: "出生日期",
    childNationality: "国籍",
    childLivesInSpain: "是否与您一起住在西班牙？",
    childLivesInSpainHelp: "如果该子女目前与您一起住在西班牙，请选择“是”。",
    childInSchool: "是否在西班牙就学？",
    childInSchoolHelp: "如果该子女已在西班牙学校注册，请选择“是”。",
    removeChild: "删除子女",
    dependentParentsQuestion: "您有由您照料且与您同住的父母吗？",
    dependentParentsHelp: "指经济上依赖您并与您同住的父母。",
    parentHeader: "父母",
    addParent: "添加父母",
    parentFirstName: "名字",
    parentLastName: "第一姓氏",
    parentDob: "出生日期",
    parentNationality: "国籍",
    removeParent: "删除父母",
    sexLabel: "性别",
    sexPlaceholder: "请选择...",
    sexMale: "男",
    sexFemale: "女",
    sexUndetermined: "X / 未指定",
    unknownLabel: "不适用",
    dependentsQuestion: "您在西班牙是否有未成年子女或需要抚养的家庭成员与您同住？",
    dependentsHelper: "包括18岁以下的子女、有残疾的成年子女或由您照料的父母。",
    voiceTitle: "关于您家庭状况的几个简短问题。",
    voiceTitle_rep: "关于申请人家庭状况的几个简短问题。",
  },
  section4: {
    title: "地址",
    title_rep: "申请人地址",
    subtitle_applicant: "您在西班牙住在哪里？",
    subtitle_rep: "申请人在西班牙住在哪里？",
    streetType: "街道类型",
    streetTypePlaceholder: "选择街道类型",
    streetTypeOptionStreet: "街道",
    streetTypeOptionAvenue: "大道",
    streetTypeOptionSquare: "广场",
    streetTypeOptionRoad: "道路",
    streetTypeOptionPassage: "通道",
    streetTypeOptionOther: "其他",
    street: "住址",
    streetPlaceholder: "街道名称",
    streetNumber: "号",
    streetNumberPlaceholder: "号",
    floorPiso: "楼层",
    floorPisoPlaceholder: "—",
    floorDoor: "门",
    floorDoorPlaceholder: "A、B",
    city: "地区",
    cityPlaceholder: "城市或城镇",
    province: "省份",
    provincePlaceholder: "选择省份",
    municipality: "市镇",
    municipalityPlaceholder: "选择市镇",
    locality: "地方",
    localityPlaceholder: "选择地方",
    postalCode: "邮编",
    postalCodePlaceholder: "08001",
    country: "国家",
    countryPlaceholder: "西班牙",
    residenceSince: "您从何时起住在这里？",
    residenceSinceHelper: "您开始在此地址居住的大概月份和年份。",
    residenceSincePlaceholderMonth: "月",
    residenceSincePlaceholderYear: "年",
    residenceSinceOptional: "可选",
    voiceTitle: "您在西班牙住在哪里？",
    voiceTitle_rep: "申请人在西班牙住在哪里？",
  },
  section5: {
    title: "您在西班牙的情况",
    title_rep: "申请人在西班牙的情况",
    subtitle_applicant: "请告诉我们您目前在西班牙的情况。",
    subtitle_rep: "关于申请人当前情况的信息。",
    arrivalDate: "您什么时候到达西班牙？", arrivalDateHelper: "大概的到达日期（日/月/年）。",
    wantsToWork: "您想在西班牙工作吗？",
    wantsToWorkOptions: { yes: "是", no: "否" },
    workSituation: "您目前的情况是？",
    workSituation_rep: "他们目前的情况是？",
    workSituationOptions: { employed: "已有工作", looking: "正在求职", selfemployed: "自雇" },
    nie: "NIE", niePlaceholder: "X1234567A", nieHelper: "可选。过期也可以",
    nieAsk: "没有？来问我们",
    voiceTitle: "关于您情况的几个问题。",
    voiceTitle_rep: "关于申请人情况的几个问题。",
  },
  section6: {
    title: "您的护照",
    title_rep: "申请人的护照",
    subtitle: "拍摄资料页照片或上传 PDF。我们将自动填写您的信息。",
    subtitle_applicant: "关于您的身份证件或护照的信息。",
    subtitle_rep: "关于申请人身份证件的信息。",
    photoInstructions:
      "拍摄或上传带有您照片和个人信息的那一页。如果您已有完整护照 PDF，也可在此上传。请勿逐页单独拍照。",
    photoHint: "之后可以修改信息。完整护照（所有页面）将在稍后上传。",
    passportName: "护照上显示的姓名",
    docType: "证件类型",
    docTypeOptions: {
      passport: "护照",
      nationalId: "身份证",
      travelDoc: "旅行证",
      cedulaInscripcion: "登记证",
      other: "其他",
    },
    docTypeOtherLabel: "请说明证件类型",
    docTypeOtherPlaceholder: "例如：难民证件",
    passportNumber: "护照号码", passportNumberPlaceholder: "例如 ABC123456",
    passportNumberHelper: "与护照上显示的一致。",
    docExpiry: "到期日期", docExpiryHelper: "日 / 月 / 年",
    docExpiryAsk: "不确定？来问我们",
    expiredNote: "已过期的证件也可接受。",
    docUpload: "上传您的证件",
    docUploadHelper: "拍摄您护照主页的照片（带有照片和个人信息的那一页）。将其放在光线充足的平面上。",
    uploadBtn: "选择文件", cameraBtn: "使用相机",
    uploadedLabel: "已附加文件：", changeFile: "更改",
    nie: "NIE",
    niePlaceholder: "X1234567A",
    nieHelper: "可选。过期也可以",
    nieAsk: "没有？来问我们",
    docName: "文件上显示的姓名",
    docNameHelper: "确认与您的身份证件一致。",
    seeExample: "查看示例",
    photoGuideTitle: "照片要求",
    photoGood: "平放，光线好，所有文字清晰可见",
    photoGoodCaption: "正确 — 平放，光线充足，所有文字清晰可读，四个角均可见",
    photoBad1: "模糊",
    photoBad2: "反光或阴影",
    photoBad3: "被裁剪或有手指遮挡",
    carouselBad1: "模糊 — 文字无法辨认。拍照前请保持手机稳定并对焦。",
    carouselBad2: "反光或阴影 — 部分文字被遮挡。请使用自然光，避免直接反光。",
    carouselBad3: "被裁剪 — 文件部分内容缺失。请确保四个角完全可见。",
    carouselBad4: "手指遮挡 — 您的手指遮住了文件的一部分。请从边缘握持或将其平放在桌面上。",
    goodExampleTitle: "正确示例",
    badExampleTitle: "错误示例",
    photoGotIt: "明白了",
    voiceTitle: "现在是您的身份证件。",
    voiceTitle_rep: "现在是申请人的身份证件。",
  },
  section7: {
    title: "居留证明",
    title_rep: "申请人的居留证明",
    subtitle_applicant: "您有哪些文件可以证明您在西班牙的居留时间？",
    subtitle_rep: "申请人有哪些文件可以证明其在西班牙的居留时间？",
    checklistTitle: "选择您拥有的文件",
    checklistHelper: "您可以选择多个。全部为可选。",
    docTypes: { utility: "水电费账单", bank: "银行对账单", medical: "医疗记录", rental: "租赁合同", transport: "交通票据", other: "其他" },
    uploadTitle: "上传一份文件", uploadHelper: "您可以稍后添加更多文件。",
    uploadBtn: "选择文件", cameraBtn: "使用相机",
    uploadedLabel: "已附加文件：", changeFile: "更改",
    allOptional: "目前所有文件均为可选。",
    stayUploadAsk: "什么文件有效？来问我们",
    passportUploadGuideLabel: "如何上传完整护照？",
    passportUploadGuide: "我们需要您护照的所有页面合并为一个PDF文件，包括空白页。两种简单方式：\n\n📱 使用免费应用如 Adobe Scan 或 Google Drive，从手机将所有页面扫描成一个PDF。\n\n🏪 前往最近的复印店或电话亭，请他们将您的完整护照扫描为PDF。他们可以通过电子邮件发送给您或保存到您的手机。",
    voiceTitle: "最后一步——证明您曾在西班牙居住。",
    voiceTitle_rep: "最后一步——证明申请人曾在西班牙居住。",
    asylumTitle: "您的庇护文件",
    asylumInstructions: "作为庇护申请人，您无需证明5个月的居留。只需上传您的庇护文件即可。",
    longTermTitle: "证明您曾在西班牙居住",
    longTermInstructions: "您在西班牙已超过5年，因此只需几份近期文件来确认您的持续居留。",
    standardTitle: "证明您曾在西班牙居住",
    standardInstructions: "上传显示您曾在西班牙生活的文件。任何带有您姓名和日期的文件均可。",
    priorDenialTitle: "您的文件",
    priorDenialInstructions: "由于您有过之前的申请流程，我们需要一些特定文件。",
    asylumDocTypeLabel: "庇护文件类型",
    asylumDocTypes: { receipt: "庇护收据", resolution: "庇护决定", other: "其他庇护文件" },
    asylumUploadLabel: "上传您的庇护文件",
    asylumOptionalTitle: "您是否有其他显示您在西班牙时间的文件？",
    asylumOptionalHelper: "非必需，但可以加强您的案件。",
    asylumOptionalExpand: "添加额外文件",
    asylumOptionalCollapse: "隐藏额外文件",
    denialSectionATitle: "之前流程的文件",
    denialResolutionLabel: "之前的拒绝决定",
    denialCriminalLabel: "之前流程的犯罪记录",
    denialCriminalNote: "即使已过期，由于您留在西班牙，这些文件仍然有效。",
    denialSectionBTitle: "在西班牙的居留证明",
    denialSectionBHelper: "我们需要覆盖至少5个月的文件。",
    addDocBtn: "+ 添加文件",
    docTypeLabel: "文件类型",
    docTypeOptions: { utility: "水电费账单", bank: "银行对账单", medical: "医疗记录", rental: "租赁合同", transport: "交通票据", other: "其他" },
    monthCoverageLabel: "覆盖月份",
    monthCoverageHelper: "您上传的每份文件可以覆盖一个或多个月份。",
    uploadDocLabel: "上传文件",
    removeDoc: "删除",
    docEntryTitle: "文件 {{n}}",
    monthLabel: "这份文件是哪个月的？",
    tierLabel1: "最强",
    tierLabel2: "强",
    tierLabel3: "支持性",
    tierLabel4: "补充性",
    tieredDocTypes: {
      padron: "市政登记证",
      rental: "租赁合同",
      medical: "医疗记录",
      school: "子女学校入学证明",
      bank: "银行对账单",
      utility: "水电费账单（电、水、气、网络）",
      payslip: "工资单或劳动合同",
      transfer: "汇款收据",
      course: "课程或培训证书",
      transport: "交通票据",
      police: "警察报告",
      official: "官方信函或通知",
      other: "其他",
    },
    monthsCovered: "已上传文件覆盖 {{n}} / 5 个月",
    allMonthsCovered: "✓ 已覆盖全部5个月",
    infoNote: "上传您拥有的文件。您现在不需要全部文件，随时可以添加更多。市政登记证是最重要的文件——如果您有，请先上传。",
    videoLabel: "1分钟 · 哪些文件有效",
    // Flat alias keys
    title: "证明您曾在西班牙居住",
    voiceTitle: "最后一步——证明您在西班牙的居留时间。",
    instructions: "上传能证明您在西班牙生活的文件。任何带有您姓名和日期的文件都可以。",
    titleLongTerm: "证明您曾在西班牙居住",
    instructionsLongTerm: "您在西班牙已超过5年，因此只需几份近期文件来确认您的持续居留。",
    titleAsylum: "您的庇护文件",
    instructionsAsylum: "作为庇护申请人，您无需证明5个月的居留。只需上传您的庇护文件即可。",
    asylumOptionalNote: "非必需，但可以加强您的案件。",
    asylumReceipt: "庇护收据",
    asylumResolution: "庇护决定",
    asylumOther: "其他庇护文件",
    titlePriorDenial: "您的文件",
    instructionsPriorDenial: "由于您有过之前的申请流程，我们需要一些特定文件。",
    denialResolution: "之前的拒绝决定",
    priorCriminalRecords: "之前流程的犯罪记录",
    priorCriminalNote: "即使已过期，由于您留在西班牙，这些文件仍然有效。",
    docTypePadron: "市政登记证（padrón）",
    docTypeRental: "租赁合同",
    docTypeMedical: "医疗记录",
    docTypeSchool: "子女学校入学证明",
    docTypeBank: "银行对账单",
    docTypeUtility: "水电费账单（电、水、气、网络）",
    docTypePayslip: "工资单或劳动合同",
    docTypeTransfer: "汇款收据",
    docTypeCourse: "课程或培训证书",
    docTypeTransport: "交通票据",
    docTypePolice: "警察报告",
    docTypeOfficial: "官方信函或通知",
    docTypeOther: "其他",
    acceptedWidget: {
      title: "哪些文件可作为居留证明？",
      subtitleOpen: "请确保每份文件都包含您的姓名和日期。没有姓名或日期的文件无效。",
      tier1Label: "第 1 级 — 最强证据",
      tier2Label: "第 2 级 — 强证据",
      tier3Label: "第 3 级 — 辅助证据",
      descPadron: "市政登记证（empadronamiento），由您居住地市政府/市政厅出具。历史版市政登记证是最强的单一文件，因为它显示您的完整登记日期，而不仅是当前日期。仅凭它并不总是足够，但它几乎是所有强申请的基础。",
    descRental: "以你名义签署、用于西班牙住所的租赁合同。如果已在自治区住房登记处备案，或附有可证明持续居住的月度付款收据，则更有力。",
    descMedical: "带有日期和你姓名的医生、医院或公立医疗中心就诊记录。医保卡、医疗报告、处方和公立系统预约都算。因为较难伪造，这类证据特别有用。",
    descSchool: "你本人或子女在西班牙教育机构的注册、出勤或在读证明。包括托儿所、小学、中学、官方学院和大学。",
    descBank: "显示你长期交易记录的西班牙银行账户对账单。覆盖的月份越多，证据越强。具有规律西班牙本地交易活动的账户最有说服力。",
    descUtility: "以你名义开具并带有西班牙地址的电费、水费、燃气费、网络费或手机费账单。最近几个月持续出现的账单有助于证明你一直在西班牙。",
    descPayslip: "显示雇主、日期和你姓名的工资单或劳动合同。如果你在西班牙有正式工作经历，这些文件不仅能作为居留证明，也能支持正规化中的工作路径。",
    descTransfer: "从西班牙汇出或汇入的汇款收据（Western Union、MoneyGram、Wise、Ria 等）。每张收据都应显示你的姓名、西班牙地点和日期。",
    descCourse: "西班牙课程、语言学校或培训项目的证书、注册证明或文凭。包括西班牙语课程、职业培训和经认证的私立项目。",
    descTransport: "月票、Renfe 车票、可充值交通卡、机票或长途汽车票。每张票据都必须包含你的姓名和日期才有效。",
    descPolice: "警方报案、笔录或任何按姓名识别你并带有日期的警方文件。包括你作为受害者或证人提交的报案，而不仅仅是把你列为调查对象的文件。",
    descOfficial: "任何西班牙公共机构（社保、税务局、市政府、自治区政府、法院等）寄给你的官方信函或通知。寄往西班牙地址的官方通信，是你居住在当地的有力证据。",
    descOther: "任何其他带有你姓名和日期、可证明你在西班牙存在的文件：购物收据、活动门票、宗教证明、协会来信等。",
    },
    addDocument: "+ 添加文件",
    whatMonth: "这是哪个月的？",
    uploadNote: "上传您现有的文件。您不需要一次全部准备好，可以随时添加更多。市政登记证明（padrón）是最重要的文件——如果您有，请先上传。",
    coverageLabel: "已上传文件覆盖 {{n}}/5 个月",
    coverageComplete: "✓ 已覆盖全部5个月",
    criminalDocType: "犯罪记录文件",
    criminalTitle: "犯罪记录文件",
    criminalInstructions: "上传任何与您记录相关的文件——注销证明、法院文件，或任何显示您案件状态的材料。您的顾问会审核。",
    criminalUploadLabel: "犯罪记录文件",
    docEntryTitle: "文件 {{n}}",
    standardInstructions: "上传能证明您在西班牙生活的文件。任何带有您姓名和日期的文件都可以。",
    asylumUploadLabel: "上传您的庇护文件",
    asylumOptionalExpand: "添加其他文件",
    addDocBtn: "+ 添加文件",
    uploadDocLabel: "上传文件",
    monthLabel: "这是哪个月的？",
    removeDoc: "删除",
    monthsCovered: "已上传文件覆盖 {{n}}/5 个月",
    allMonthsCovered: "✓ 已覆盖全部5个月",
    infoNote: "上传您现有的文件。您不需要一次全部准备好，可以随时添加更多。市政登记证明（padrón）是最重要的文件——如果您有，请先上传。",
  },
  section7b: {
    title: "您需要哪些文件",
    subtitle: "您需要证明在西班牙连续居住约{{duration}}。文件应尽可能覆盖更多月份。",
    monthsApprox: "{{n}} 个月",
    yearsApprox: "{{n}} 年",
    primaryTitle: "Empadronamiento（市政登记）",
    primaryBadge: "最有力的证明",
    primaryDesc: "您的市政登记证明。这是最重要的文件——直接证明您在西班牙的地址和居住情况。如果您只上传一份文件，就选这个。",
    primaryHistorical: "向您的市政厅申请历史证明，显示您完整的登记记录。",
    supportingTitle: "辅助文件",
    rentalTitle: "租赁合同 / 住房",
    rentalDesc: "显示您在西班牙地址的租赁协议、租金收据或房产文件。",
    rentalNote: "一份文件即可覆盖整个租赁期。",
    medicalTitle: "医疗记录",
    medicalDesc: "医疗卡（tarjeta sanitaria）、就诊记录、住院记录、处方。",
    medicalNote: "每次就诊都能证明您在该特定日期的存在。",
    bankTitle: "银行对账单",
    bankDesc: "西班牙银行账户的月度对账单，显示在西班牙的交易记录。",
    bankNote: "适合覆盖多个月份——每份对账单覆盖一个月。",
    utilityTitle: "水电费账单",
    utilityDesc: "您西班牙地址的电费、水费、燃气费、网络费账单。",
    utilityNote: "每张账单覆盖一个月。收集多张以填补空缺。",
    employmentTitle: "就业文件",
    employmentDesc: "劳动合同、工资单（nóminas）、社会保障登记（vida laboral）。",
    employmentNote: "Vida laboral 非常有用——一份文件即可显示您的完整就业历史。",
    selfEmploymentTitle: "自雇文件",
    selfEmploymentDesc: "自雇登记、季度税务申报（模型130/303）、发票。",
    selfEmploymentNote: "您的 alta de autónomo 显示您开始工作的确切日期。",
    familyTitle: "家庭文件",
    familyDesc: "结婚证、子女学校入学证明、家庭手册（libro de familia）。",
    familyNote: "学校入学证明非常有力——证明整个学年的持续居住。",
    otherTitle: "其他可接受的文件",
    otherDesc: "汇款记录、课程注册、交通卡、警察报告、任何带有您姓名和日期的西班牙官方文件。",
    otherNote: "单独来看较弱，但有助于填补较强文件之间的空缺。",
    guidanceShort: "重点准备您的 empadronamiento，加上2-3份分布在不同月份的辅助文件。",
    guidanceMedium: "尽量每季度至少有一份文件。最强的组合是 empadronamiento + 租赁合同 + 就业记录。",
    guidanceLong: "对于较长的居留，历史 empadronamiento 证明至关重要。用 vida laboral 和显示持续活动的银行对账单加以补充。",
    rentalDetail: "以PDF格式上传完整合同。如果您有月租收据，请每月分开上传。使用我们的扫描仪处理多页文件。",
    medicalDetail: "可接受：医疗卡、医疗报告、处方、诊所预约记录。每份文件的清晰照片即可。每次就诊都能证明您在特定日期的存在。",
    bankDetail: "从您的网上银行下载月度对账单（PDF格式）。如果没有网上银行，请向银行申请打印对账单并扫描。",
    utilityDetail: "以您名义开具的电费、水费、燃气费或网络费账单。每张账单的清晰照片即可。如果通过电子邮件收到，可直接以PDF格式上传。",
    employmentDetail: "在 sede.seg-social.gob.es 在线申请您的 vida laboral，或发送短信至 600 123 456。这是一份显示您完整历史记录的PDF文件。工资单和合同：清晰照片或PDF均可。",
    selfEmploymentDetail: "您的自雇登记和季度税务申报（模型130/303）可从税务局电子办公室下载。",
    familyDetail: "家庭手册是多页文件——请使用我们的扫描仪或复印店。学校入学证明：向学校申请一份副本。",
    otherDetail: "汇款记录：保存收据。交通卡：实名卡有效。任何带有您姓名和日期的官方文件均有效。",
  },
  review: {
    title: "审核您的申请",
    subtitle: "以下是已完成的内容和我们仍需要的内容",
    progressLabel: "必填字段已完成",
    statusComplete: "已完成",
    statusPartial: "部分完成",
    statusNotStarted: "未开始",
    notProvided: "未提供",
    addLink: "添加",
    editLink: "编辑",
    continueBtn: "继续至同意页面",
    sections: { s1: "个人资料", s2: "个人信息", s3: "婚姻状况与家庭", s4: "在西班牙的地址", s5: "在西班牙的情况", s6: "身份证件", s7: "居留证明" },
    voiceTitle: "完成前让我们审查所有内容。",
    voiceTitle_rep: "完成前让我们审查申请人的信息。",
    saveBtn: "保存并发送",
    completionGreetComplete: "您已注册，{{name}}！",
    completionBodyComplete: "我们已收到您的信息。下一步是上传您的文件 — 您可以现在上传，也可以稍后返回。",
    completionGreetMissing: "做得好，{{name}}！",
    completionBodyMissing: "我已保存所有内容。我会发送消息告知您还缺少什么 — 您可以随时完成。",
    completionTextOrEmail: "我会通过短信或电子邮件与您联系。",
    completionPortalNote: "您随时可以通过以下链接返回：",
    completionTextLink: "我会通过短信给您发送一个链接，以便您随时可以返回。",
    ctaDocs: "继续上传文件",
    ctaOrganizer: "前往我的管理器",
    ctaNote: "您可以随时返回。我们会通过短信发送链接。",
    completionHeadline: "一切就绪，{{name}}！",
    completedLabel: "您已完成的内容",
    item1: "个人信息",
    item2: "情况与资格",
    item3: "婚姻与家庭信息",
    item4: "在西班牙的地址",
    item5: "协议已签署",
    savedConfirmation: "所有内容已安全保存。您可以关闭此页面，随时返回继续。",
    whatsNext: "下一步：准备您的文件",
    whatsNextBody: "我们需要一些文件来完成您的申请：您的护照、居留证明，以及可能需要的犯罪记录证明。您可以现在上传，也可以稍后返回。",
  },
  section9: {
    title: "同意并保存",
    subtitle: "即将完成 — 请在保存您的申请前确认以下内容。",
    gdprLabel: "我已阅读并接受隐私政策，并同意处理我的个人数据，用于评估我的案件及就我的申请与我联系。",
    saveBtn: "保存并发送",
    checkboxRequired: "您必须接受隐私政策才能继续。",
    successTitleMissing: "进度已保存！",
    successMissing: "您的进度已保存。我们将向您发送一份仍需补充内容的摘要，以及返回完成申请的链接。",
    successTitleComplete: "一切就绪！",
    successComplete: "您的信息将由您的顾问审核。如有需要，我们会与您联系。",
    advisorWillReview: "将审核您的申请，如有需要将与您联系。",
    teamReview: "您的申请将由我们的团队审核。如有需要，我们会与您联系。",
    missingFieldsNote: "还有一些字段未填写——没关系，我们会跟进。",
    privacyLink: "隐私政策",
    voiceTitle: "最后一件事——您同意继续。",
    voiceTitle_rep: "最后一件事——申请人同意继续。",
    advisorMsgComplete: "我已拥有所需的一切。我将审核您的申请，如有需要将与您联系。",
    advisorMsgMissing: "我已保存您的进度。我会向您发送缺少内容的提醒。",
    teamMsgComplete: "我们已拥有所需的一切。我们将审核您的申请，如有需要将与您联系。",
    teamMsgMissing: "我们已保存您的进度。我们会向您发送缺少内容的提醒。",
  },
  advisorProfile: {
    moreAbout: "更多关于",
    role: "移民顾问",
    bioLine: "移民顾问 · 🇻🇪",
    bioBlock: "两个孩子的妈妈 · 狗狗妈妈 Luna 🐕 · 周末徒步 ⛰️",
    quote: "我的热情是帮助人们在西班牙建立新生活——我自己做到了，也帮助了很多其他人实现这一目标！",
    from: "来自委内瑞拉",
    personal1: "两个孩子的妈妈",
    personal2: "爱狗人士 — 我有一只叫Luna的流浪狗",
    personal3: "周末喜欢在山里徒步",
    passion: "我的热情是帮助人们在西班牙建立新生活——我自己做到了！",
    contact: "我会通过短信或电子邮件与您联系 💬",
    instagram: "在Instagram上关注我",
  },
  engagement: {
    reviewData: "查看我的信息 ›",
    title: "委托协议",
    subtitle: "案件启动前的确认部分",
    whyTitle: "为什么要在开始前签署此协议？",
    whyBody: "本协议正式确立您与我们事务所之间的专业关系。它明确了双方约定的服务内容、费用及义务，从一开始就确保透明度和法律确定性。",
    required: "必填",
    signatureLabel: "客户签名",
    clearSignature: "清除签名",
    signatureBlank: "请在框内绘制签名。不接受空白画布。",
    signatureHint: "请用鼠标或手指在此处签名",
    signatureBottom: "客户签名",
    advisorRole: "高级顾问，Global Mobility Law",
    assignedBadge: "您的指定顾问",
    scopeLabel: "服务范围",
    feeLabel: "约定费用",
    feeTitle: "2026年特别居留合法化",
    feeNote: "包含完整案件管理",
    feeAmount: "€199",
    feeWord: "费用",
    feeAmountNote: "另加政府申请费（€38.28）",
    consentsLabel: "您的同意",
    consent1Before: "我已阅读并接受",
    consent1Link: "专业委托一般条款",
    consent1After: "以及上述费用政策。",
    consent2: "我授权 Global Mobility Law 作为我的专业代理人，代表我向西班牙移民当局办理相关事务。",
    questionsText: "对以上内容有任何问题吗？",
    questionsBtn: "我有问题，请跟进联系我",
    questionsConfirm: "已收到，我们会跟进联系您",
    consent3: "我同意通过电子邮件接收有关我案件进展及相关移民资讯的通知。",
    optional: "（可选）",
    dateLabel: "签署日期：",
    dateAuto: "— 自动生成",
    declarationsLabel: "法律声明",
    validationHint: "如需继续：请接受两项必填同意并签署文件。",
    readyMessage: "一切就绪 — 您可以继续。",
    noReturnCommitment: "我确认没有任何命令要求我离开西班牙。",
    noHealthcareCoverage: "我确认我在西班牙没有由其他国家或第三方承担的医疗保险覆盖。",
    scope: {
      analysis: "初步分析和评估您的移民情况",
      preparation: "准备和审查所有必需的文件",
      submission: "向主管机关提交申请",
      followup: "案件跟踪直至最终决定",
    },
  },
  docBriefing: {
    residenceTitle: "居留证明：概览",
    residenceSubtitle: "您需要证明在西班牙连续居住约{{duration}}。",
    reviewThenContinue: "请先查看以下说明，然后点击继续上传您的文件。",
    criminalUploadDocsHeading: "上传文件",
    monthsApprox: "{{n}} 个月",
    yearsApprox: "{{n}} 年",
    videoLabel: "1分钟 · 哪些文件有效",
    primaryTitle: "Empadronamiento（市政登记）",
    primaryBadge: "最有力的证明",
    primaryDesc: "您的市政登记证明。这是最重要的文件。",
    primaryHistorical: "向您的市政厅申请历史证明，显示您完整的登记记录。",
    supportingTitle: "辅助文件",
    identityTitle: "身份证件",
    identitySubtitle: "我们需要您的护照、身份证或其他官方身份证件的副本。",
    identityTip1: "过期护照也可接受——上传您现有的证件即可。",
    identityTip2: "您需要护照所有页面的完整副本。",
    identityTip3: "首选格式是单个PDF文件。",
    identityTip4: "您需要所有页面——包括空白页。入境/出境印章证明您没有离开西班牙。",
    identityTip5: "将所有页面扫描成一个PDF文件。在手机上使用免费应用如Adobe Scan或Google Drive。",
    identityTip6: "您也可以前往复印店，请他们将您的完整护照扫描为PDF。",
    rejectTitle: "低质量文件会延误您的申请",
    rejectBody: "行政部门会拒绝照片模糊、页面缺失或文字无法辨认的护照。请使用扫描仪或良好的照明。每一页都必须清晰可读。",
    criminalExpiry: "这些证明有效期仅为3个月。不要过早申请——在我们准备提交您的申请前2-3周获取即可。如果不确定时间，请联系我们。",
    criminalTitle: "犯罪记录证明",
    criminalSubtitle: "我们需要您在来西班牙之前5年内居住过的每个国家的无犯罪记录证明。",
    criminalWhichTitle: "您需要哪些证明？",
    criminalWhichBody: "在您进入西班牙前5年内，每个居住国家都需要一份。可能只需要您母国的一份，也可能需要多份，取决于您的情况。下一步我们会询问具体国家。",
    criminalTip1: "向您国家在西班牙的领事馆或中央刑事记录登记处申请证明。",
    criminalTip2: "如果证明来自其他国家，必须经过海牙认证或公证。",
    criminalTip3: "如果您有犯罪记录，仍请上传证明——您的顾问会审核。",
    criminalDiplomaticNote: "如果您在一个月内无法从某个国家取得证明，不用担心。我们会代表您提交文件（附件 I-1 和 I-2），通过外交渠道申请该证明。",
    employmentTitle: "就业文件",
    employmentSubtitle: "我们需要证明您在西班牙当前或过去就业情况的文件。",
    employmentTip1: "上传您的劳动合同、近期工资单或社会保障记录（vida laboral）。",
    employmentTip2: "如果您是自雇，请附上自雇登记和季度税务申报。",
    employmentTip3: "如果您正在求职，工作邀请函或意向书也可以。",
    familyTitle: "家庭文件",
    familySubtitle: "我们需要证明您家庭状况的文件，尤其是如果您在西班牙有配偶或子女。",
    familyTip1: "上传家庭手册、结婚证或子女出生证明。",
    familyTip2: "如果您的子女在西班牙就读，请附上学校入学证明。",
    familyTip3: "外国文件必须经过海牙认证，如有必要还需翻译。",
    asylumTitle: "庇护文件",
    asylumSubtitle: "作为庇护申请人，我们需要您国际保护程序的官方文件。",
    asylumTip1: "上传您的庇护申请收据或案件决定书。",
    asylumTip2: "如果您有红色或白色庇护申请人卡，也请一并附上。",
    asylumTip3: "您无需证明5个月的居留——您的庇护文件已经足够。",
    denialTitle: "之前拒绝文件",
    denialSubtitle: "由于您之前有被拒绝的移民申请，我们需要该案件的具体文件。",
    denialTip1: "上传之前申请的拒绝决定书。",
    denialTip2: "附上之前申请的犯罪记录，即使已过期也可以。",
    denialTip3: "添加任何证明您在被拒绝后仍留在西班牙的文件。",
    continueToUpload: "继续上传文件",
    back: "返回",
    rentalTitle: "租赁合同 / 住房",
    rentalDesc: "显示您在西班牙地址的租赁协议、租金收据或房产文件。",
    rentalNote: "一份文件即可覆盖整个租赁期。",
    medicalTitle: "医疗记录",
    medicalDesc: "医疗卡、就诊记录、住院记录、处方。",
    medicalNote: "每次就诊都能证明您在该特定日期的存在。",
    bankTitle: "银行对账单",
    bankDesc: "西班牙银行账户的月度对账单。",
    bankNote: "每份对账单覆盖一个月。",
    utilityTitle: "水电费账单",
    utilityDesc: "您西班牙地址的电费、水费、燃气费、网络费账单。",
    utilityNote: "每张账单覆盖一个月。收集多张以填补空缺。",
    employmentTitle: "就业文件",
    employmentDesc: "劳动合同、工资单、社会保障登记（vida laboral）。",
    employmentNote: "Vida laboral 一份文件即可显示您的完整就业历史。",
    selfEmploymentTitle: "自雇文件",
    selfEmploymentDesc: "自雇登记、季度税务申报、发票。",
    selfEmploymentNote: "您的 alta de autónomo 显示您开始工作的确切日期。",
    familyTitle: "家庭文件",
    familyDesc: "结婚证、子女学校入学证明、家庭手册。",
    familyNote: "学校入学证明证明整个学年的持续居住。",
    otherTitle: "其他可接受的文件",
    otherDesc: "汇款记录、课程注册、交通卡、警察报告、任何带有您姓名和日期的西班牙官方文件。",
    otherNote: "单独来看较弱，但有助于填补空缺。",
    guidanceShort: "重点准备您的 empadronamiento，加上2-3份分布在不同月份的辅助文件。",
    guidanceMedium: "尽量每季度至少有一份文件。最强的组合是 empadronamiento + 租赁合同 + 就业记录。",
    guidanceLong: "对于较长的居留，历史 empadronamiento 证明至关重要。用 vida laboral 和银行对账单加以补充。",
    residenceVideoLabel: "2分钟 · 哪些文件可证明您在西班牙的居留",
    noExitTitle: "您不能曾经离开过西班牙",
    noExitBody: "您的居留必须在申请前5个月内保持连续不间断。任何离境都会打破这一要求——即使是短途旅行也不允许。如果您曾经离境，请在继续之前与我们联系。",
    whatCountsTitle: "哪些文件有效？",
    whatCountsBody: "任何带有您姓名和日期的公共或私人文件均可。您不需要每个月都有一份文件，但拥有的越多，您的案件就越有力。目标是每月至少一份文件。",
    rentalDetail: "以PDF格式上传完整合同。如果您有月租收据，请每月分开上传。使用我们的扫描仪处理多页文件。",
    medicalDetail: "可接受：医疗卡、医疗报告、处方、诊所预约记录。每份文件的清晰照片即可。每次就诊都能证明您在特定日期的存在。",
    bankDetail: "从您的网上银行下载月度对账单（PDF格式）。如果没有网上银行，请向银行申请打印对账单并扫描。",
    utilityDetail: "以您名义开具的电费、水费、燃气费或网络费账单。每张账单的清晰照片即可。如果通过电子邮件收到，可直接以PDF格式上传。",
    employmentDetail: "在 sede.seg-social.gob.es 在线申请您的 vida laboral，或发送短信至 600 123 456。这是一份显示您完整历史记录的PDF文件。工资单和合同：清晰照片或PDF均可。",
    selfEmploymentDetail: "您的自雇登记和季度税务申报（模型130/303）可从税务局电子办公室下载。",
    familyDetail: "家庭手册是多页文件——请使用我们的扫描仪或复印店。学校入学证明：向学校申请一份副本。",
    otherDetail: "汇款记录：保存收据。交通卡：实名卡有效。任何带有您姓名和日期的官方文件均有效。",
  },
  docFlow: {
    title: "文件清单",
    subtitle: "这是我们需要的内容。点击每个类别查看说明并上传。",
    catIdentity: "身份",
    catResidence: "居住证明",
    catCriminal: "犯罪记录",
    catEmployment: "就业文件",
    catFamily: "家庭文件",
    catAsylum: "庇护文件",
    catVulnerability: "脆弱情况报告",
    catOther: "其他文件",
    catDenial: "之前拒绝文件",
    helpSheetTitle: "我们能如何帮助您？",
    helpSheetSubtitle: "我们会尽快回复，通常在 1 个工作日内。",
    helpSheetContextPrefix: "您当前在：",
    helpSheetPlaceholder: "请告诉我们您的需求…",
    helpSheetSend: "发送",
    helpSheetSending: "正在发送…",
    helpSheetSentTitle: "消息已发送",
    helpSheetSessionError: "无法连接您的会话，请重试。",
    helpSheetSendError: "出错了，请重试。",
    helpFabAria: "帮助 — 提问",
    notStarted: "未开始",
    inProgress: "进行中",
    complete: "已完成",
    backToChecklist: "返回文件",
    goToOrganizer: "前往我的管理器",
    videoLabel: "1分钟 · 哪些文件有效",
    qualityTitle: "文件质量至关重要",
    qualityBody: "模糊、昏暗或被裁剪的文件是造成延误的首要原因。含有无法辨认文件的申请会被退回要求更正，这可能会给您的流程增加数周时间。请花额外的时间确保每份文件清晰可读。",
    photoTipsLink: "查看文件拍照技巧",
    letsBegin: "开始吧",
    skip: "跳过",
    continue: "继续",
  },
  sectionExit: {
    title: "您完成这个部分了吗？",
    yes: "是",
    no: "否",
    stuck: "我遇到困难了",
    stuckPlaceholder: "告诉Ana是什么阻碍了您...",
    stuckSend: "发送",
    back: "返回",
    porMarkTitle: "将此部分标记为已完成？",
    porMarkSubtitle: "如有需要，您可以稍后再修改。",
    porMarkComplete: "标记为完成并继续",
    porComeBackLater: "保存并稍后完成",
  },

  docUpload: {
    title: "上传您的文件",
    done: "完成 — 前往我的管理器",
    backToChecklist: "返回文件清单",
    continue: "继续",
    goToOrganizer: "前往我的管理器",
    backToRequirements: "返回要求",
    empHeading: "就业文件",
    empHeadingEmployed: "就业文件",
    empHeadingSelfEmployed: "自雇文件",
    workTypeGatePrompt: "请告诉我们您的工作方式，以便我们知道需要哪些材料。",
    workTypeEmployed: "受雇",
    workTypeSelfEmployed: "自雇",
    workTypeEmployedHelper: "您为雇主工作，或有合同/工作机会。",
    workTypeSelfEmployedHelper: "您为自己工作，或计划以自雇形式工作。",
    empDescriptionLabel: "描述您的工作内容",
    empDescriptionPlaceholder: "例如：外卖骑手、厨师、平面设计师",
    selfEmployedNote: "您为自己工作，或计划以自雇形式工作。",
    empDocContract: "工作合同",
    empDocContractHelper: "来自西班牙现任或前任雇主的已签署合同。至少包括第一页和签字页。",
    empDocPayslip: "工资单",
    empDocPayslipHelper: "最近的工资单，显示工资和雇主。最近 3 到 6 个月最有用。",
    empDocVidaLaboral: "Vida laboral",
    empDocVidaLaboralHelper: "社会保障局的官方工作记录报告。显示您曾经的所有工作经历。证据很强。",
    empDocJobOffer: "工作录用信",
    empDocJobOfferHelper: "雇主签署的聘用承诺信，需包含职位、开始日期、薪资和雇主信息。",
    empDocOther: "其他文件",
    empDocOtherHelper: "任何支持您工作情况的其他文件。",
    empOtherPlaceholder: "简短说明",
    empAddDoc: "添加文件",
    empGateContinue: "继续上传文件",
    empTipsTitle: "提示",
    empTipOptional: "只上传适合您情况的文件，这里没有任何一项是必须的。",
    empTipVidaLaboral: "Vida laboral 通常是雇员最有力的单一文件。",
    empTipJobOffer: "如果还没有合同，工作机会也可能足够。",
    empTipInformal: "如果您只有非正式证明，请上传您拥有的内容。",
    empTipAutonomo: "对于自雇，请上传注册和季度申报表。",
    identityTitleNoPassport: "身份证件",
    identityTitlePassport: "护照",
    identityGateQuestion: "您持有什么类型的身份证件？",
    identityGateHint: "如果您有有效且未过期的护照，请使用护照。只有在您没有护照时才选择“其他证件”。",
    identityGatePassport: "护照",
    identityGateOther: "其他证件",
    identityGateContinue: "继续",
    identityTypeSwitchFromPassport: "我没有有效护照",
    identityTypeSwitchFromOther: "我有有效护照",
    identityStep1: "第 1 步：填写您的信息",
    passportNumber: "护照号码",
    passportFullName: "与护照上一致的完整姓名",
    passportExpiry: "到期日期",
    nieLabel: "NIE号码",
    nieHelper: "如果您有的话",
    nationalIdType: "证件类型",
    nationalIdNumber: "证件号码",
    nationalIdCountry: "签发国家",
    nationalIdCountryPlaceholder: "选择国家...",
    typeNationalId: "国民身份证",
    typeTravelDoc: "旅行证件",
    typeCedulaInscripcion: "登记证",
    identityStep2NoPassport: "第 2 步：上传您的身份证件",
    identityStep2BodyNoPassport: "上传您持有的任何来自原籍国的身份证件。",
    identityStep2: "第 2 步：上传您的护照",
    identityStep2Body: "我们需要所有页面，包括空白页。出入境盖章有助于证明您在西班牙停留的时间。所有页面必须作为一个 PDF 文件上传。",
    ocrTitle: "扫描或上传护照身份页",
    ocrSubtitle:
      "很多人还没有现成的 PDF，使用内置扫描通常最省事。我们会尝试读取信息并帮您填写表单。",
    ocrUploadZone: "拍照或选择文件",
    ocrUploadHint: "PDF 或图片 — 仅限含照片的护照身份页",
    ocrSuccessTitle: "已收到",
    ocrBody:
      "只需护照的身份页（含照片那一页）。若识别成功，我们会直接带您进入下一步核对或修改资料；若不成功，可以重试或跳过并自行填写 — 这一步不是必须的。之后您还需要上传整本护照（全部页面）的一个 PDF。",
    ocrFreedomHint: "无需现在完成 — 您随时可以跳过。",
    ocrSkipManual: "跳过 — 我将手动填写",
    ocrUploadFileLabel: "上传文件",
    ocrUseCameraHint: "可选 — 若暂时没有扫描件可快速拍一张",
    ocrUseCamera: "使用相机",
    ocrUseScanner: "扫描（多页）",
    ocrSkip: "跳过",
    ocrContinue: "继续",
    passportAddTitle: "添加护照",
    passportAddBody1: "我们需要包含护照每一页的扫描版 PDF。",
    passportAddBody2: "边框过多、拍到手指或反光的图片将被退回。",
    passportAddExamplesLink: "查看合格扫描示例",
    passportAddScannerCardTitle: "使用我们的扫描工具",
    passportAddScannerCardDesc: "用手机拍摄每一页，我们会帮您合并成一个 PDF。",
    passportAddPdfCardTitle: "上传 PDF",
    passportAddPdfCardDesc: "上传已包含护照全部页面的 PDF 文件。",
    passportScanExamplesCloseAria: "关闭",
    passportScanExamplesTitle: "扫描示例",
    passportScanExamplesIntro:
      "请尽量拍得与「合格」示例一致。若与下列「不合格」示例相似，需要重新拍摄。",
    passportScanExamplesAcceptableLabel: "合格",
    passportScanExamplesAcceptableCaption:
      "贴齐边缘、对焦清晰，画面中不要有手指或阴影。",
    passportScanExamplesRejectedLabel: "不合格",
    passportScanExamplesRejectDarkTitle: "过暗",
    passportScanExamplesRejectDarkDesc: "移到光线更好的位置，避免护照上出现阴影。",
    passportScanExamplesRejectBlurTitle: "模糊或失焦",
    passportScanExamplesRejectBlurDesc: "拿稳手机，在屏幕上点击护照区域对焦。",
    passportScanExamplesRejectFingersTitle: "手指入镜",
    passportScanExamplesRejectFingersDesc: "用小物件撑开护照，避免用手指按住。",
    passportScanExamplesGotIt: "知道了",
    passportScanSavedTitle: "护照已保存",
    passportScanSavedSubtitle: "{{filename}} · {{pages}}",
    passportScanSavedSubtitleFilenameOnly: "{{filename}}",
    passportScanSavedPagesOne: "1 页",
    passportScanSavedPagesMany: "{{count}} 页",
    passportScanSavedView: "查看",
    passportScanSavedReplace: "重新扫描",
    identityReplaceConfirmTitle: "要替换该文件吗？",
    identityReplaceConfirmBody: "当前的 PDF 将被移除。您可以上传新的 PDF 或重新扫描护照。",
    identityReplaceConfirmCancel: "取消",
    identityReplaceConfirmReplace: "替换文件",
    passportViewOpening: "正在打开…",
    passportViewUnavailable: "无法打开护照文件。请重试，或使用重新扫描再次上传。",
    passportViewPopupBlocked: "请允许此网站的弹出窗口以查看护照 PDF。",
    passportViewerSheetTitle: "您的证件",
    passportViewerClose: "完成",
    identityAiUploadingSecure: "正在保存文件并读取资料…",
    identityAiWaitHint:
      "完成后会进入下一步供您核对资料。若遇到问题，可重试或跳过并手动填写。",
    passportPdfProcessingTitle: "正在处理您的 PDF…",
    passportPdfPhaseUpload: "正在安全上传文件…",
    passportPdfPhaseAnalyze: "正在读取护照信息…",
    passportPdfPhaseApply: "正在填入表单…",
    identityUploadRetry: "重试上传",
    identityUploadPickAnother: "选择其他文件",
    passportAiBannerTitle: "文件已保存 — 我们无法自动识别内容。",
    passportAiBannerBody:
      "文件已保存。请在下方核对或填写资料，或再次尝试扫描/上传，也可跳过 — 不必反复重试。我们稍后可协助您补充更清晰的 PDF。若下方字段与本次扫描不符，可能是上次会话残留的信息：请修改或重试。",
    passportAiBannerShopHint: "若手机拍照不便，本地复印店通常能提供更清晰的扫描件。",
    passportAiBannerRetry: "再次尝试扫描或上传",
    passportAiSavedManualTitle: "已保存 — 请核对或填写信息",
    identityTitle: "上传您的身份证件",
    criminalTitle: "上传您的犯罪记录证明",
    asylumTitle: "上传您的庇护文件",
    denialTitle: "上传拒绝决定书",
    employmentTitle: "上传您的就业文件",
    familyTitle: "上传您的家庭文件",
    vulnHeading: "脆弱情况报告",
    vulnIntroBody: "这是一份确认您处于脆弱情况的官方表格。您需要由社工或注册 NGO 帮您填写。",
    vulnStep1: "第 1 步：获取表格",
    vulnStep1Body: "请把这份表格带给社工或注册 NGO，由他们完成评估、签字并盖章。",
    vulnDownloadBtn: "下载",
    vulnEmailBtn: "电子邮件",
    vulnStep2: "第 2 步：上传回来",
    vulnStep2Body: "签字并盖章后，请在这里上传。",
    vulnScannerBtn: "使用扫描仪",
    vulnTipsTitle: "提示",
    vulnTipSocial: "任何当地社会服务办公室都可以为您填写这份表格。",
    vulnTipNGO: "Caritas、Red Cross、ACCEM 或 CEAR 等注册 NGO 也可以填写。",
    vulnTipForward: "有些办公室允许您直接通过电子邮件发送给社会服务部门或 NGO，但具体做法因办公室而异。",
    vulnTipSigned: "请确保报告已签字、盖章并注明日期。未签字的表格会被拒绝。",
    vulnTipID: "去办理签字时，请带上您的护照和其他身份证件。",
    otherHeading: "其他",
    otherBody: "使用此部分上传顾问要求但不属于上述类别的任何附加文件。例如授权委托书、宣誓翻译或海牙认证。",
    otherAddDoc: "上传文件",
    documentTypeLabel: "文件类型",
    documentTypePlaceholder: "请选择…",
    otrosDescribeLabel: "描述文件",
    otrosDescribePlaceholder: "简短说明（选择「Otros」时必填）",
    uploadRulesHint:
      "PDF、DOC、JPG、TIF 或 GIF；单个文件不超过 6 MB；文件名仅限字母、数字、空格及 _ - . ( )",
    multipleFilesHint: "您可以上传多份文件。",
    residenceHeading: "居留证明",
    residenceDesc: "上传证明您在西班牙持续居住的文件。文件越多越好。您可以现在上传，也可以稍后返回。",
    residencePickerTitle: "添加居留证明",
    residencePickerSubtitle: "选择您要上传的文件。如果不确定，请选择最接近的选项或选择“其他”。",
    residenceFeatured: "最有力",
    residenceGroupOfficial: "官方文件",
    residenceGroupPersonal: "其他个人记录",
    residenceGroupOther: "其他",
    residenceGuidance: "请确保该文件清楚显示您的姓名和日期。缺少这些信息的文件可能不被接受。",
    residenceOtherLabel: "这是什么类型的文件？",
    residenceOtherPlaceholder: "例如：健身房会员、租金收据、预约确认",
    residenceOtherHelper: "可选。简短说明可帮助 Ana 理解该文件内容。",
    residenceChange: "更改文件类型",
    monthsCovered: "覆盖月份",
    monthsTip: "点击您上传的每份文件所覆盖的月份，方便追踪进度。",
    addDocument: "+ 添加文件",
    chooseFile: "选择文件",
    uploadedDocs: "已上传文件",
    modalTitle: "添加文件",
    modalDocType: "文件类型",
    modalSelectType: "选择类型...",
    modalMonthRange: "覆盖月份",
    modalFrom: "从",
    modalTo: "到",
    modalFile: "文件",
    modalAdd: "添加",
    typeEmpadronamiento: "市政登记证",
    typeRental: "租赁合同",
    typeMedical: "医疗记录",
    typeBank: "银行对账单",
    typeUtility: "水电费账单",
    typeEmployment: "就业文件",
    typeOther: "其他",
    monthsAutoTip: "2025年12月至2026年4月。添加文件后自动更新。",
    submissionMonthNote: "根据提交日期，我们可能需要回来再补传 5 月的文件。",
    to: "到",
    openScanner: "打开扫描仪",
    helpTitle: "快速提示",
    helpTip1: "市政登记证是最重要的文件——如果您有，请先上传。",
    helpTip2: "每份文件可以覆盖多个月份——租赁合同可以覆盖整个期间。",
    helpTip3: "银行对账单、水电费账单和就医记录也有效——任何带有您姓名和日期的文件均可。",
    helpTip4: "您不需要现在就准备好所有文件——随时可以返回添加更多。",
    seeFullRequirements: "返回概览",
    backToOverview: "返回概览",
    backToGuidance: "返回概览",
    modalScanBtn: "打开扫描仪",
    modalChangeFile: "更改",
    removeDoc: "删除",
    deleteFileConfirm: "确定要删除此文件吗？",
    criminalDesc: "您需要提供原籍国以及过去5年内居住过的任何国家的无犯罪记录证明。",
    criminalReq1: "向您国家在西班牙的领事馆申请，或请家人在原籍国代为办理",
    criminalReq2: "必须经过海牙认证或公证",
    criminalReq2Pre: "必须经过",
    criminalReq2Term: "海牙认证或公证",
    criminalReq2Help: "海牙认证（Apostille）是一种官方印章，用于验证您的文件以供国际使用。需在您原籍国的相关部委获取。如果您的国家不是《海牙公约》成员国，则需要领事认证。如果不确定需要哪种认证，请联系我们。",
    criminalReq3: "如果不是西班牙语，需要经过宣誓翻译",
    criminalReq3Pre: "如果不是西班牙语，需要",
    criminalReq3Term: "宣誓翻译",
    criminalReq3Help: "宣誓翻译员是由西班牙外交部正式任命的专业人员，其翻译具有法律效力。您可以在 maec.gob.es 上查找，或联系我们——我们可以为您推荐。",
    criminalDeclaracion: "无法获取？某些情况下可接受宣誓声明。请与我们联系。",
    criminalDeclaracionPre: "无法获取某份证明？某些情况下可接受",
    criminalDeclaracionPost: "。请与我们联系。",
    criminalHelpTitle: "提示",
    criminalHelp1: "部分领事馆需要数周时间——请尽早提出申请",
    criminalHelp2: "如果证明超过一页，请使用我们的扫描仪创建PDF文件",
    criminalVideoLabel: "1分钟 · 如何获取无犯罪记录证明",
    asylumHeading: "庇护文件",
    asylumDesc:
      "填写你的 OAR 案件编号，并上传红卡或申请回执。如果你的庇护申请被拒，也请上传拒绝决定。",
    asylumDocReceipt: "申请回执",
    asylumDocReceiptHelper: "请提供所有页面",
    asylumDocCard: "红卡",
    asylumDocCardHelper: "请上传正反两面。",
    asylumDocResolution: "拒绝决定",
    asylumDocResolutionHelper: "如果你的庇护申请被拒",
    or: "或",
  },
  criminal: {
    multiCountryTitle: "您需要哪些证明？",
    multiCountryBody: "每个您在进入西班牙前5年内居住过的国家各需一份。可能只是您的原籍国，也可能是多个——取决于您的情况。下一步我们会询问具体是哪些。",
    diplomaticNote: "如果您在一个月内无法从某个国家获得证明，不用担心。我们会代您提交文件（附件 I-1 和 I-2），通过外交渠道申请。",
    uploadPageDesc: "我们需要您在进入西班牙前5年内居住过的每个国家的无犯罪记录证明。如果您在此期间居住在原籍国，请将其包括在内。请勿包含西班牙。",
    countriesQuestion: "您在进入西班牙前的5年内居住过哪些国家？",
    countriesHint: "请添加您在进入西班牙前5年内居住过的所有国家。如果您在该期间居住在原籍国，也请将其包含在内。",
    selectCountryPlaceholder: "选择国家…",
    countrySearchPlaceholder: "搜索国家…",
    countrySearchNoResults: "无结果",
    addCountry: "添加",
    uploadSectionTitle: "上传证明",
    noSpainNote: "请勿添加西班牙。系统会自动处理西班牙证明。",
    or: "或",
    requestedNotReceivedLabel: "已申请但尚未收到。",
    requestedNotReceivedDesc: "自我申请该证明起已超过 30 天。我将上传申请证明（电子邮件、信件或收据）。",
    requestedNotReceivedBadge: "已申请，30 天内未收到",
    requestProofPending: "申请证明待上传",
    uploadedBadge: "证明已上传",
    uploadReceiptBtn: "上传回执",
    scanReceiptBtn: "扫描回执",
    uploadCertificateBtn: "上传证明",
    scanCertificateBtn: "扫描证明",
    awaitingCertificate: "证书申请后已超过 30 天",
    briefingTip1: "向您国家在西班牙的领事馆申请，或请家人在您的原籍国代为办理。",
    briefingTip2: "如果您来西班牙之前只在原籍国居住过，那么只需要一份证明。",
    briefingTip3: "如果您有犯罪记录，仍请上传证明——您的顾问会审核。",
  },
  docPhotoGuide: {
    title: "文件拍照技巧",
    link: "查看文件拍照技巧",
    good: "正确 — 平放，光线充足，所有文字清晰可读",
    goodTitle: "正确示例",
    badTitle: "错误示例",
    bad1: "阴影 — 文字被遮挡。请移动光源位置。",
    bad2: "被裁剪或倾斜 — 确保整个文件都在画面内。",
    bad3: "太暗 — 文字无法辨认。请使用更多光线。",
  },
  organizer: {
    title: "我的管理器",
    needItems: "您还需要 {{n}} 项",
    allDone: "全部完成！",
    residence: "居留证明",
    criminal: "犯罪记录证明",
    asylum: "庇护文件",
    notStarted: "未开始",
    inProgress: "进行中",
    complete: "已完成",
    upload: "上传",
    viewCompleted: "查看已完成",
    uploadDocuments: "上传文件",
    greeting: "欢迎回来，{{name}}",
    greetingSub: "这是您申请的当前状态。",
    caseProgress: "案件进度",
    phaseInfo: "信息",
    phaseDocs: "文件",
    phaseSubmit: "提交",
    remaining: "待完成",
    deadline: "申请截止日期：2026年6月30日",
    detailsLabel: "您的信息",
    docsLabel: "文件",
    viewChecklist: "查看文件清单",
    contactTitle: "需要帮助？",
    contactSub: "通过WhatsApp联系我们",
    submitInlineBannerTitle: "准备好提交申请了吗？",
    submitInlineBannerBody:
      "如果您已完成所有部分，请告知我们，我们会尽快审核并与您联系。",
    submitInlineBannerCta: "发送给 {{advisor}}",
    submitInlineSending: "发送中…",
    submitDoneHeading: "已于 {{date}} 提交给 {{advisor}}",
    submitDoneHeadingNoDate: "已提交给 {{advisor}}",
    submitDoneMessagePara1:
      "{{advisor}} 正在审阅您的申请；如有疑问或需要您更新材料，会通过短信与您联系。我们还会发送邮件副本给您。",
    submitDoneMessagePara2:
      "您暂时无需再进行其他操作。如有问题，请回复邮件或随时回到此处。",
    incomplete: "未完成",
    stepConfirm: "确认信息",
    stepSituation: "您的情况",
    stepDocument: "身份证件",
    stepPassportPersonal: "护照与个人信息",
    stepPersonal: "个人信息",
    stepCivil: "婚姻状况与家庭",
    stepAsylum: "庇护问题",
    stepQualifying: "以下哪项最能描述您目前的情况？",
    stepAddress: "在西班牙的地址",
    stepConsent: "协议与签名",
    signSubmitLabel: "签名并提交",
    signSubmitStatusPending: "待处理",
    signSubmitStatusComplete: "已完成",
    deadlineLabel: "截止日期",
    nextAction: "继续您的申请",
    nextActionSub: "完成缺失的信息",
    nextUpload: "上传您的文件",
    nextUploadSub: "您有待上传的文件",
    progress: "进度",
    of: "/",
    contextBody: "按自己的节奏完成每个部分——进度会自动保存。",
    finishItems: "请完成您的剩余项目。完成后，我将审核所有内容并准备提交给西班牙移民局。如果您有任何问题，请在本页底部给我留言，我会尽快回复您。",
    signAvailableLater: "最后提供",
    helpStuckTitle: "我遇到困难了",
    helpStuckSub: "告诉我们发生了什么，我们会尽快回复您。",
    helpStuckSubExpanded: "{lawyer}会尽快回复。",
    helpPlaceholder: "在此输入...",
    helpSendButton: "发送",
    helpSentTitle: "消息已发送",
    helpSentSub: "{lawyer}会查看并尽快回复。",
    helpDefaultLawyer: "您的律师",
  },
  upload: {
    scanTitle: "用手机扫描",
    scanSubtitle: "自动检测边缘并创建PDF文件",
    scanBtn: "打开扫描仪",
    appTitle: "使用扫描应用",
    appSubtitle: "扫描页面并以PDF格式上传",
    appGoogle: "Google Drive — 扫描文档",
    appAdobe: "Adobe Scan — 在App Store和Google Play免费下载",
    appCopyShop: "或前往附近的复印店",
    fileTitle: "上传文件",
    fileSubtitle: "我已有准备好的PDF或照片",
    scanHeroTitle: "在这里扫描您的护照",
    scanHeroSubtitle: "我们逐页引导您。我们自动检测边缘并为您创建PDF。",
    alternativesLabel: "其他选项",
    altApp: "使用扫描应用",
    altAppHint: "Adobe Scan 或 Google Drive（免费）",
    altShop: "前往复印店",
    altShopHint: "请他们将您的完整护照扫描为PDF",
    altUpload: "我已有文件",
    altUploadHint: "上传您已有的PDF或照片",
    videoLabel: "30秒 · 如何扫描护照",
    browseFiles: "浏览文件",
    scanCriminalNote: "扫描证书的每一页。包括印章和签名。",
    scanDocTitle: "需要扫描文件吗？",
    scanDocSubtitle: "使用我们的扫描仪处理多页文件",
  },
  auth: {
    signIn: "登录",
    subtitle: "输入您的手机号码以继续。",
    phone: "手机号码",
    sendCode: "发送验证码",
    otpIntroTitle: "请输入我们发送的验证码",
    otpIntroBody: "我们已向您的手机发送了一次性验证码。",
    enterCode: "输入验证码",
    sixDigitCodePlaceholder: "请输入6位验证码",
    verify: "验证",
    resend: "重新发送验证码",
    or: "或",
    google: "使用 Google 继续",
    apple: "使用 Apple 继续",
    privacyNote: "继续即表示您同意我们的",
  },
  faq: {
    title: "我能帮您什么？",
    about: "关于：",
    other: "我有其他问题",
    otherPlaceholder: "请输入您的问题…",
    otherHelper: "我们将通过短信或电子邮件回复您。",
    send: "发送",
    sent: "已发送！您的顾问将尽快与您联系。",
    sentTo: "已发送给",
    sentContact: "我会通过短信或电子邮件回复您。",
    sentTeam: "已发送给我们的团队。我们将尽快回复您。",
    s1: {
      q1: "我以后可以更改这些信息吗？",
      a1: "是的，您可以在最终提交之前随时编辑任何字段，没有任何内容会被锁定。",
      q2: "姓名中需要包含特殊符号吗？",
      a2: "请严格按照护照或身份证上显示的姓名填写，包括所有特殊字符。",
      q3: "我的个人信息安全吗？",
      a3: "您的信息已加密，只有您和您的指定顾问可以访问。",
    },
    s2: {
      q1: "如果我不知道确切的出生日期怎么办？",
      a1: "请使用护照或出生证明上显示的日期。如果只有大概日期，您的顾问可以帮助您。",
      q2: "我应该填写哪个国籍？",
      a2: "请填写护照上显示的当前国籍。如有多个国籍，请使用您要提交的证件上的国籍。",
      q3: "'未指定'性别是什么意思？",
      a3: "如果您的证件未指定男性或女性（例如某些护照使用'X'），请选择此选项。",
    },
    s3: {
      q1: "为什么需要父母的姓名？",
      a1: "官方政府表格要求填写父母的名字。现在可以跳过——您的顾问可以在之后帮助填写。",
      q2: "如果我不知道某位父母的名字怎么办？",
      a2: "留空这些字段即可，您的顾问在审核文件时会协助您。",
    },
    s4: {
      q1: "如果我没有固定地址怎么办？",
      a1: "填写您目前居住的地址，即使是临时的也没关系。如有变化，您可以稍后更新。",
      q2: "我需要户籍登记吗？",
      a2: "不需要，本申请不要求户籍登记。许多其他类型的文件均可接受。",
      q3: "'居住起始时间'是什么意思？",
      a3: "填写您开始在当前地址居住的大概月份和年份，估算值完全可以接受。",
    },
    s5: {
      q1: "申请时需要劳动合同吗？",
      a1: "不需要。申请涵盖三个途径：工作、家庭团聚或弱势群体。劳动合同只是其中一个选项。",
      q2: "什么是NIE号码？",
      a2: "NIE是西班牙的外国人身份号码。如果您没有，留空即可——这是可选字段。",
      q3: "自雇也算吗？",
      a3: "是的。在此阶段，提交自雇意向声明就已足够。",
    },
    s6: {
      q1: "过期护照可以接受吗？",
      a1: "是的，西班牙法律允许在本申请中使用过期护照。请上传您拥有的任何证件。",
      q2: "如果我丢失了护照怎么办？",
      a2: "补办新护照是证明身份的最佳方式。对于证明居留，可以使用账单或银行对账单。",
      q3: "如果我完全没有身份证件怎么办？",
      a3: "请联系您国家的领事馆获取紧急旅行证件。您的顾问将指导您了解可用选项。",
    },
    s7: {
      q1: "哪些类型的文件可作为证明？",
      a1: "水电费账单、银行对账单、医疗记录、租赁合同、交通票据——任何带有您姓名和西班牙日期的文件均可。",
      q2: "需要证明每个月吗？",
      a2: "不需要。涵盖5个月内不同时期的文件组合就足够了。",
      q3: "如果我的文件很少怎么办？",
      a3: "不要放弃——许多类型的文件都被接受。您的顾问会帮助找到最佳组合。请上传您拥有的文件。",
    },
    s8: {
      q1: "之后还可以修改答案吗？",
      a1: "是的，在最终官方提交之前，您可以随时返回编辑任何部分。",
      q2: "保存后会发生什么？",
      a2: "您的顾问将审核您的文件，如有缺失或需要说明，会与您联系。",
    },
    s9: {
      q1: "我具体同意了什么？",
      a1: "您同意将您的数据用于评估您的案件，以及由您的顾问就申请与您联系。您的数据不会与第三方共享。",
      q2: "我以后可以撤回同意吗？",
      a2: "是的，随时联系我们，我们将立即停止处理您的数据。",
    },
  },
};

/* ───────────────────── Context ───────────────────── */

interface LanguageContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
  tObj: (key: string) => Record<string, string>;
  tArr: (key: string) => string[];
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function resolvePath(obj: Translations, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && !Array.isArray(acc)) {
      return (acc as Translations)[key];
    }
    return undefined;
  }, obj);
}

const STORAGE_KEY = "i18nextLng";

function normalizeLang(value: unknown): string {
  if (!value) return "";
  return String(value).trim().toLowerCase().replace("_", "-").split("-")[0];
}

function safeLocalStorageGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore storage failures
  }
}

function getBrowserLangCandidates(): string[] {
  if (typeof navigator === "undefined") return [];
  const langs = Array.isArray(navigator.languages) ? navigator.languages : [];
  const first = navigator.language ? [navigator.language] : [];
  return [...langs, ...first].filter(Boolean) as string[];
}

const DICTS: Record<Lang, Translations> = { es: ES, en: EN, fr: FR, ar: AR, zh: ZH };
const SUPPORTED_LANGS = Object.keys(DICTS) as Lang[];

function resolveInitialLang(): Lang {
  const supportedSet = new Set<string>(SUPPORTED_LANGS);

  const stored = normalizeLang(safeLocalStorageGet(STORAGE_KEY));
  if (stored && supportedSet.has(stored)) return stored as Lang;

  for (const candidate of getBrowserLangCandidates()) {
    const base = normalizeLang(candidate);
    if (base && supportedSet.has(base)) {
      safeLocalStorageSet(STORAGE_KEY, base);
      return base as Lang;
    }
  }

  safeLocalStorageSet(STORAGE_KEY, "es");
  return "es";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => resolveInitialLang());

  const setLang = (l: Lang) => {
    setLangState(l);
    safeLocalStorageSet(STORAGE_KEY, l);
  };

  const dict = DICTS[lang];

  const t = (key: string): string => {
    const val = resolvePath(dict, key);
    if (typeof val === "string") return val;
    return key;
  };

  const tObj = (key: string): Record<string, string> => {
    const val = resolvePath(dict, key);
    if (val && typeof val === "object" && !Array.isArray(val)) return val as Record<string, string>;
    return {};
  };

  const tArr = (key: string): string[] => {
    const val = resolvePath(dict, key);
    if (Array.isArray(val)) return val as string[];
    return [];
  };

  const isRTL = lang === "ar";

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tObj, tArr, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be inside LanguageProvider");
  return ctx;
}
